import { modelLibURLPrefix, modelVersion, prebuiltAppConfig, CreateMLCEngine } from '@mlc-ai/web-llm';
import path from 'path';
import 'url';
import fs from 'fs';
import * as ONNX from 'onnxruntime-web';
import { Tensor } from 'onnxruntime-common';
import { Template } from '@huggingface/jinja';
import { phonemize as phonemize$1 } from 'phonemizer';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var MLCEngineWrapper = class {
  constructor() {
    this.mlcEngine = null;
    this.appConfig = null;
    this.mlcEngine = null;
  }
  async loadModel(modelConfig, options = {}) {
    try {
      const modelIdentifier = modelConfig.repo.replace("{quantization}", modelConfig.defaultQuantization).split("/")[1];
      if (modelConfig.modelLibrary) {
        this.appConfig = {
          model_list: [
            {
              model: "https://huggingface.co/" + modelConfig.repo.replace("{quantization}", modelConfig.defaultQuantization),
              model_id: modelIdentifier,
              model_lib: modelConfig?.modelLibrary?.startsWith("http") ? modelConfig.modelLibrary : modelLibURLPrefix + "/" + modelVersion + "/" + modelConfig.modelLibrary
            }
          ]
        };
      } else {
        this.appConfig = prebuiltAppConfig;
      }
      this.mlcEngine = await CreateMLCEngine(modelIdentifier, {
        initProgressCallback: options.onProgress,
        // Pass progress callback
        appConfig: this.appConfig,
        ...options
        // Pass other options
      });
    } catch (error) {
      console.error("Error loading MLC model:", error);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to load MLC model "${modelConfig}": ${message}`);
    }
  }
  async generateText(input, options = {}) {
    if (!this.mlcEngine) {
      throw new Error("MLC Engine not initialized.");
    }
    let messages = Array.isArray(input) ? input : [];
    if (typeof input === "string") {
      if (options.system_prompt) {
        messages.push({ role: "system", content: options.system_prompt });
      }
      messages.push({ role: "user", content: input });
    }
    options.max_tokens = options.max_tokens || 300;
    options.temperature = options.temperature || 0.6;
    options.top_p = options.top_p || 0.95;
    options.frequency_penalty = options.frequency_penalty || 0.5;
    options.presence_penalty = options.presence_penalty || 0.5;
    if (options.stream) {
      options.stream_options = { include_usage: true };
      return this.mlcEngine.chat.completions.create({ messages, ...options });
    }
    const result = await this.mlcEngine.chat.completions.create({ messages, ...options });
    return result.choices[0].message.content;
  }
};
var VERSION = "3.3.1";
var IS_BROWSER_ENV = typeof window !== "undefined" && typeof window.document !== "undefined";
var IS_WEBWORKER_ENV = typeof self !== "undefined" && self.constructor?.name === "DedicatedWorkerGlobalScope";
var IS_WEB_CACHE_AVAILABLE = typeof self !== "undefined" && "caches" in self;
var IS_WEBGPU_AVAILABLE = typeof navigator !== "undefined" && "gpu" in navigator;
var IS_WEBNN_AVAILABLE = typeof navigator !== "undefined" && "ml" in navigator;
var IS_PROCESS_AVAILABLE = typeof process !== "undefined";
var IS_NODE_ENV = IS_PROCESS_AVAILABLE && process?.release?.name === "node";
var IS_FS_AVAILABLE = false;
var IS_PATH_AVAILABLE = !isEmpty(path);
var apis = Object.freeze({
  /** Whether we are running in a browser environment (and not a web worker) */
  IS_BROWSER_ENV,
  /** Whether we are running in a web worker environment */
  IS_WEBWORKER_ENV,
  /** Whether the Cache API is available */
  IS_WEB_CACHE_AVAILABLE,
  /** Whether the WebGPU API is available */
  IS_WEBGPU_AVAILABLE,
  /** Whether the WebNN API is available */
  IS_WEBNN_AVAILABLE,
  /** Whether the Node.js process API is available */
  IS_PROCESS_AVAILABLE,
  /** Whether we are running in a Node.js environment */
  IS_NODE_ENV,
  /** Whether the filesystem API is available */
  IS_FS_AVAILABLE,
  /** Whether the path API is available */
  IS_PATH_AVAILABLE
});
var DEFAULT_CACHE_DIR = null;
var DEFAULT_LOCAL_MODEL_PATH = "/models/";
var localModelPath = DEFAULT_LOCAL_MODEL_PATH;
var env = {
  version: VERSION,
  /////////////////// Backends settings ///////////////////
  // NOTE: These will be populated later by the backends themselves.
  backends: {
    // onnxruntime-web/onnxruntime-node
    onnx: {}
  },
  /////////////////// Model settings ///////////////////
  allowRemoteModels: true,
  remoteHost: "https://huggingface.co/",
  remotePathTemplate: "{model}/resolve/{revision}/",
  allowLocalModels: !(IS_BROWSER_ENV || IS_WEBWORKER_ENV),
  localModelPath,
  useFS: IS_FS_AVAILABLE,
  /////////////////// Cache settings ///////////////////
  useBrowserCache: IS_WEB_CACHE_AVAILABLE,
  useFSCache: IS_FS_AVAILABLE,
  cacheDir: DEFAULT_CACHE_DIR,
  useCustomCache: false,
  customCache: null
  //////////////////////////////////////////////////////
};
function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}

// src/libs/transformers/utils/generic.ts
var Callable = (
  /** @type {any} */
  class {
    constructor() {
      let closure = function(...args) {
        return closure._call(...args);
      };
      return Object.setPrototypeOf(closure, new.target.prototype);
    }
    _call(...args) {
      throw Error("Must implement _call method in subclass");
    }
  }
);

// src/libs/transformers/utils/core.ts
function dispatchCallback(progress_callback, data) {
  if (progress_callback) progress_callback(data);
}
function reverseDictionary(data) {
  return Object.fromEntries(Object.entries(data).map(([key, value]) => [value, key]));
}
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function isIntegralNumber(x) {
  return Number.isInteger(x) || typeof x === "bigint";
}
function isNullishDimension(x) {
  return x === null || x === undefined || x === -1;
}
function calculateDimensions(arr) {
  const dimensions = [];
  let current = arr;
  while (Array.isArray(current)) {
    dimensions.push(current.length);
    current = current[0];
  }
  return dimensions;
}
function mergeArrays(...arrs) {
  return Array.prototype.concat.apply([], arrs);
}
function calculateReflectOffset(i, w) {
  return Math.abs((i + w) % (2 * w) - w);
}
function pick(o, props) {
  return Object.assign(
    {},
    ...props.map((prop) => {
      if (o[prop] !== undefined) {
        return { [prop]: o[prop] };
      }
    })
  );
}
function len(s) {
  let length = 0;
  for (const c of s) ++length;
  return length;
}
function count(arr, value) {
  let count2 = 0;
  for (const v of arr) {
    if (v === value) ++count2;
  }
  return count2;
}
function saveBlob(path3, blob) {
  const dataURL = URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.href = dataURL;
  downloadLink.download = path3;
  downloadLink.click();
  downloadLink.remove();
  URL.revokeObjectURL(dataURL);
}
var CONTENT_TYPE_MAP = {
  txt: "text/plain",
  html: "text/html",
  css: "text/css",
  js: "text/javascript",
  json: "application/json",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif"
};
var FileResponse = class _FileResponse {
  /**
   * Creates a new `FileResponse` object.
   * @param {string|URL} filePath
   */
  constructor(filePath) {
    this.filePath = filePath;
    this.headers = new Headers();
    this.exists = fs.existsSync(filePath);
    if (this.exists) {
      this.status = 200;
      this.statusText = "OK";
      let stats = fs.statSync(filePath);
      this.headers.set("content-length", stats.size.toString());
      this.updateContentType();
      let self2 = this;
      this.body = new ReadableStream({
        start(controller) {
          self2.arrayBuffer().then((buffer) => {
            controller.enqueue(new Uint8Array(buffer));
            controller.close();
          });
        }
      });
    } else {
      this.status = 404;
      this.statusText = "Not Found";
      this.body = null;
    }
  }
  /**
   * Updates the 'content-type' header property of the response based on the extension of
   * the file specified by the filePath property of the current object.
   * @returns {void}
   */
  updateContentType() {
    const extension = this.filePath.toString().split(".").pop()?.toLowerCase();
    this.headers.set(
      "content-type",
      CONTENT_TYPE_MAP[extension] ?? "application/octet-stream"
    );
  }
  /**
   * Clone the current FileResponse object.
   * @returns {FileResponse} A new FileResponse object with the same properties as the current object.
   */
  clone() {
    let response = new _FileResponse(this.filePath);
    response.exists = this.exists;
    response.status = this.status;
    response.statusText = this.statusText;
    response.headers = new Headers(this.headers);
    return response;
  }
  /**
   * Reads the contents of the file specified by the filePath property and returns a Promise that
   * resolves with an ArrayBuffer containing the file's contents.
   * @returns {Promise<ArrayBuffer>} A Promise that resolves with an ArrayBuffer containing the file's contents.
   * @throws {Error} If the file cannot be read.
   */
  async arrayBuffer() {
    const data = await fs.promises.readFile(this.filePath);
    return (
      /** @type {ArrayBuffer} */
      data.buffer
    );
  }
  /**
   * Reads the contents of the file specified by the filePath property and returns a Promise that
   * resolves with a Blob containing the file's contents.
   * @returns {Promise<Blob>} A Promise that resolves with a Blob containing the file's contents.
   * @throws {Error} If the file cannot be read.
   */
  async blob() {
    const data = await fs.promises.readFile(this.filePath);
    return new Blob([data], { type: this.headers.get("content-type") });
  }
  /**
   * Reads the contents of the file specified by the filePath property and returns a Promise that
   * resolves with a string containing the file's contents.
   * @returns {Promise<string>} A Promise that resolves with a string containing the file's contents.
   * @throws {Error} If the file cannot be read.
   */
  async text() {
    const data = await fs.promises.readFile(this.filePath, "utf8");
    return data;
  }
  /**
   * Reads the contents of the file specified by the filePath property and returns a Promise that
   * resolves with a parsed JavaScript object containing the file's contents.
   *
   * @returns {Promise<Object>} A Promise that resolves with a parsed JavaScript object containing the file's contents.
   * @throws {Error} If the file cannot be read.
   */
  async json() {
    return JSON.parse(await this.text());
  }
};
function isValidUrl(string, protocols = null, validHosts = null) {
  let url2;
  try {
    url2 = new URL(string);
  } catch (_) {
    return false;
  }
  if (protocols && !protocols.includes(url2.protocol)) {
    return false;
  }
  if (validHosts && !validHosts.includes(url2.hostname)) {
    return false;
  }
  return true;
}
async function getFile(urlOrPath) {
  if (typeof process !== "undefined" && process?.release?.name === "node") {
    const IS_CI = false;
    const version = env.version;
    const headers = new Headers();
    headers.set("User-Agent", `transformers.js/${version}; is_ci/${IS_CI};`);
    const isHFURL = isValidUrl(urlOrPath.toString(), ["http:", "https:"], ["huggingface.co", "hf.co"]);
    if (isHFURL) {
      const token = process.env?.HF_TOKEN ?? process.env?.HF_ACCESS_TOKEN;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    }
    return fetch(urlOrPath, { headers });
  } else {
    return fetch(urlOrPath);
  }
}
var ERROR_MAPPING = {
  // 4xx errors (https://developer.mozilla.org/en-US/docs/Web/HTTP/Status#client_error_responses)
  400: "Bad request error occurred while trying to load file",
  401: "Unauthorized access to file",
  403: "Forbidden access to file",
  404: "Could not locate file",
  408: "Request timeout error occurred while trying to load file",
  // 5xx errors (https://developer.mozilla.org/en-US/docs/Web/HTTP/Status#server_error_responses)
  500: "Internal server error error occurred while trying to load file",
  502: "Bad gateway error occurred while trying to load file",
  503: "Service unavailable error occurred while trying to load file",
  504: "Gateway timeout error occurred while trying to load file"
};
function handleError(status, remoteURL, fatal) {
  if (!fatal) {
    return null;
  }
  const message = ERROR_MAPPING[status] ?? `Error (${status}) occurred while trying to load file`;
  throw Error(`${message}: "${remoteURL}".`);
}
var FileCache = class {
  /**
   * Instantiate a `FileCache` object.
   * @param {string} path
   */
  constructor(path3) {
    this.path = path3;
    this.cache = /* @__PURE__ */ new Map();
  }
  /**
   * Checks whether the given request is in the cache.
   * @param {string} request
   * @returns {Promise<FileResponse | undefined>}
   */
  async match(request) {
    let filePath = path.join(this.path, request);
    let file = new FileResponse(filePath);
    if (file.exists) {
      return file;
    } else {
      return undefined;
    }
  }
  /**
   * Adds the given response to the cache.
   * @param {string} request
   * @param {Response|FileResponse} response
   * @returns {Promise<void>}
   */
  async put(request, response) {
    const buffer = Buffer.from(await response.arrayBuffer());
    let outputPath = path.join(this.path, request);
    try {
      await fs.promises.mkdir(path.dirname(outputPath), { recursive: true });
      await fs.promises.writeFile(outputPath, buffer);
    } catch (err) {
      console.warn("An error occurred while writing the file to cache:", err);
    }
  }
  // TODO add the rest?
  // addAll(requests: RequestInfo[]): Promise<void>;
  // delete(request: RequestInfo | URL, options?: CacheQueryOptions): Promise<boolean>;
  // keys(request?: RequestInfo | URL, options?: CacheQueryOptions): Promise<ReadonlyArray<Request>>;
  // match(request: RequestInfo | URL, options?: CacheQueryOptions): Promise<Response | undefined>;
  // matchAll(request?: RequestInfo | URL, options?: CacheQueryOptions): Promise<ReadonlyArray<Response>>;
};
async function tryCache(cache, ...names) {
  for (let name of names) {
    try {
      let result = await cache.match(name);
      if (result) return result;
    } catch (e) {
      continue;
    }
  }
  return undefined;
}
async function getModelFile(path_or_repo_id, filename, fatal = true, options = {}) {
  if (!env.allowLocalModels) {
    if (options.local_files_only) {
      throw Error(
        "Invalid configuration detected: local models are disabled (`env.allowLocalModels=false`) but you have requested to only use local models (`local_files_only=true`)."
      );
    }
  }
  dispatchCallback(options.progress_callback, {
    status: "initiate",
    name: path_or_repo_id,
    file: filename
  });
  let cache;
  if (!cache && env.useBrowserCache) {
    if (typeof caches === "undefined") {
      throw Error("Browser cache is not available in this environment.");
    }
    try {
      cache = await caches.open("transformers-cache");
    } catch (e) {
      console.warn("An error occurred while opening the browser cache:", e);
    }
  }
  const revision = options.revision ?? "main";
  let requestURL = pathJoin(path_or_repo_id, filename);
  let localPath = pathJoin(env.localModelPath, requestURL);
  let remoteURL = pathJoin(
    env.remoteHost,
    env.remotePathTemplate.replaceAll("{model}", path_or_repo_id).replaceAll("{revision}", encodeURIComponent(revision)),
    filename
  );
  let fsCacheKey = revision === "main" ? requestURL : pathJoin(path_or_repo_id, revision, filename);
  let cacheKey;
  let proposedCacheKey = cache instanceof FileCache ? fsCacheKey : remoteURL;
  let toCacheResponse = false;
  let response;
  if (cache) {
    response = await tryCache(cache, localPath, proposedCacheKey);
  }
  const cacheHit = response !== undefined;
  if (response === undefined) {
    if (env.allowLocalModels) {
      const isURL = isValidUrl(requestURL, ["http:", "https:"]);
      if (!isURL) {
        try {
          response = await getFile(localPath);
          cacheKey = localPath;
        } catch (e) {
          console.warn(`Unable to load from local path "${localPath}": "${e}"`);
        }
      } else if (options.local_files_only) {
        throw new Error(`\`local_files_only=true\`, but attempted to load a remote file from: ${requestURL}.`);
      } else ;
    }
    if (response === undefined || response.status === 404) {
      if (options.local_files_only || false) {
        if (fatal) {
          throw Error(
            `\`local_files_only=true\` or \`env.allowRemoteModels=false\` and file was not found locally at "${localPath}".`
          );
        } else {
          return null;
        }
      }
      response = await getFile(remoteURL);
      if (response.status !== 200) {
        return handleError(response.status, remoteURL, fatal);
      }
      cacheKey = proposedCacheKey;
    }
    toCacheResponse = !!(cache && // 1. A caching system is available
    typeof Response !== "undefined");
  }
  dispatchCallback(options.progress_callback, {
    status: "download",
    name: path_or_repo_id,
    file: filename
  });
  let buffer;
  if (!options.progress_callback) {
    buffer = new Uint8Array(await response.arrayBuffer());
  } else if (cacheHit && // The item is being read from the cache
  typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent)) {
    buffer = new Uint8Array(await response.arrayBuffer());
    dispatchCallback(options.progress_callback, {
      status: "progress",
      name: path_or_repo_id,
      file: filename,
      progress: 100,
      loaded: buffer.length,
      total: buffer.length
    });
  } else {
    if (response instanceof FileResponse) {
      throw new Error("FileResponse is not supported");
    }
    buffer = await readResponse(response, (data) => {
      dispatchCallback(options.progress_callback, {
        status: "progress",
        name: path_or_repo_id,
        file: filename,
        ...data
      });
    });
  }
  if (
    // Only cache web responses
    // i.e., do not cache FileResponses (prevents duplication)
    toCacheResponse && cacheKey && // Check again whether request is in cache. If not, we add the response to the cache
    await cache?.match(cacheKey) === undefined
  ) {
    await cache?.put(
      cacheKey,
      new Response(buffer, {
        headers: response.headers
      })
    ).catch((err) => {
      console.warn(`Unable to add response to browser cache: ${err}.`);
    });
  }
  dispatchCallback(options.progress_callback, {
    status: "done",
    name: path_or_repo_id,
    file: filename
  });
  return buffer;
}
async function getModelJSON(modelPath, fileName, fatal = true, options = {}) {
  let buffer = await getModelFile(modelPath, fileName, fatal, options);
  if (buffer === null) {
    return {};
  }
  let decoder = new TextDecoder("utf-8");
  let jsonData = decoder.decode(buffer);
  return JSON.parse(jsonData);
}
async function readResponse(response, progress_callback) {
  const contentLength = response.headers.get("Content-Length");
  if (contentLength === null) {
    console.warn("Unable to determine content-length from response headers. Will expand buffer when needed.");
  }
  let total = parseInt(contentLength ?? "0");
  let buffer = new Uint8Array(total);
  let loaded = 0;
  const reader = response.body?.getReader();
  if (!reader) throw new Error("Response body reader is undefined");
  async function read() {
    const { done, value } = await reader?.read();
    if (done) return;
    let newLoaded = loaded + value.length;
    if (newLoaded > total) {
      total = newLoaded;
      let newBuffer = new Uint8Array(total);
      newBuffer.set(buffer);
      buffer = newBuffer;
    }
    buffer.set(value, loaded);
    loaded = newLoaded;
    const progress = loaded / total * 100;
    progress_callback({
      progress,
      loaded,
      total
    });
    return read();
  }
  await read();
  return buffer;
}
function pathJoin(...parts) {
  parts = parts.map((part, index) => {
    if (index) {
      part = part.replace(new RegExp("^/"), "");
    }
    if (index !== parts.length - 1) {
      part = part.replace(new RegExp("/$"), "");
    }
    return part;
  });
  return parts.join("/");
}

// src/libs/transformers/utils/maths.ts
function permute_data(array, dims, axes) {
  const shape = new Array(axes.length);
  const stride = new Array(axes.length);
  for (let i = axes.length - 1, s = 1; i >= 0; --i) {
    stride[i] = s;
    shape[i] = dims[axes[i]];
    s *= shape[i];
  }
  const invStride = axes.map((_, i) => stride[axes.indexOf(i)]);
  const permutedData = new array.constructor(array.length);
  for (let i = 0; i < array.length; ++i) {
    let newIndex = 0;
    for (let j = dims.length - 1, k = i; j >= 0; --j) {
      newIndex += k % dims[j] * invStride[j];
      k = Math.floor(k / dims[j]);
    }
    permutedData[newIndex] = array[i];
  }
  return [permutedData, shape];
}
function softmax(arr) {
  const maxVal = max(arr)[0];
  const exps = arr.map((x) => {
    if (typeof maxVal === "bigint") {
      if (maxVal <= BigInt(Number.MAX_SAFE_INTEGER)) {
        return Math.exp(Number(x) - Number(maxVal));
      }
      throw new Error("BigInt value exceeds safe number range for exponential calculation");
    }
    return Math.exp(Number(x) - maxVal);
  });
  const sumExps = exps.reduce((acc, val) => acc + val, 0);
  const softmaxArr = exps.map((x) => x / sumExps);
  return (
    /** @type {T} */
    softmaxArr
  );
}
function log_softmax(arr) {
  const maxVal = max(arr)[0];
  let sumExps = 0;
  for (let i = 0; i < arr.length; ++i) {
    if (typeof maxVal === "bigint") {
      if (maxVal <= BigInt(Number.MAX_SAFE_INTEGER)) {
        sumExps += Math.exp(Number(arr[i]) - Number(maxVal));
      }
      throw new Error("BigInt value exceeds safe number range for exponential calculation");
    }
    sumExps += Math.exp(Number(arr[i]) - maxVal);
  }
  const logSum = Math.log(sumExps);
  const logSoftmaxArr = arr.map((x) => {
    if (typeof maxVal === "bigint") {
      return Number(x) - Number(maxVal) - logSum;
    }
    return Number(x) - maxVal - logSum;
  });
  return (
    /** @type {T} */
    logSoftmaxArr
  );
}
function min(arr) {
  if (arr.length === 0) throw Error("Array must not be empty");
  let min2 = arr[0];
  let indexOfMin = 0;
  for (let i = 1; i < arr.length; ++i) {
    if (arr[i] < min2) {
      min2 = arr[i];
      indexOfMin = i;
    }
  }
  return (
    /** @type {T extends bigint[]|BigTypedArray ? [bigint, number] : [number, number]} */
    [min2, indexOfMin]
  );
}
function max(arr) {
  if (arr.length === 0) throw Error("Array must not be empty");
  let max3 = arr[0];
  let indexOfMax = 0;
  for (let i = 1; i < arr.length; ++i) {
    if (arr[i] > max3) {
      max3 = arr[i];
      indexOfMax = i;
    }
  }
  return (
    /** @type {T extends bigint[]|BigTypedArray ? [bigint, number] : [number, number]} */
    [max3, indexOfMax]
  );
}
function isPowerOfTwo(number) {
  return number > 0 && (number & number - 1) === 0;
}
var P2FFT = class {
  constructor(size) {
    this.size = size | 0;
    if (this.size <= 1 || !isPowerOfTwo(this.size)) throw new Error("FFT size must be a power of two larger than 1");
    this._csize = size << 1;
    this.table = new Float64Array(this.size * 2);
    for (let i = 0; i < this.table.length; i += 2) {
      const angle = Math.PI * i / this.size;
      this.table[i] = Math.cos(angle);
      this.table[i + 1] = -Math.sin(angle);
    }
    let power = 0;
    for (let t = 1; this.size > t; t <<= 1) ++power;
    this._width = power % 2 === 0 ? power - 1 : power;
    this._bitrev = new Int32Array(1 << this._width);
    for (let j = 0; j < this._bitrev.length; ++j) {
      this._bitrev[j] = 0;
      for (let shift = 0; shift < this._width; shift += 2) {
        const revShift = this._width - shift - 2;
        this._bitrev[j] |= (j >>> shift & 3) << revShift;
      }
    }
  }
  /**
   * Create a complex number array with size `2 * size`
   *
   * @returns {Float64Array} A complex number array with size `2 * size`
   */
  createComplexArray() {
    return new Float64Array(this._csize);
  }
  /**
   * Converts a complex number representation stored in a Float64Array to an array of real numbers.
   *
   * @param {Float64Array} complex The complex number representation to be converted.
   * @param {number[]} [storage] An optional array to store the result in.
   * @returns {number[]} An array of real numbers representing the input complex number representation.
   */
  fromComplexArray(complex, storage) {
    const res = storage || new Array(complex.length >>> 1);
    for (let i = 0; i < complex.length; i += 2) res[i >>> 1] = complex[i];
    return res;
  }
  /**
   * Convert a real-valued input array to a complex-valued output array.
   * @param {Float64Array} input The real-valued input array.
   * @param {Float64Array} [storage] Optional buffer to store the output array.
   * @returns {Float64Array} The complex-valued output array.
   */
  toComplexArray(input, storage) {
    const res = storage || this.createComplexArray();
    for (let i = 0; i < res.length; i += 2) {
      res[i] = input[i >>> 1];
      res[i + 1] = 0;
    }
    return res;
  }
  /**
   * Performs a Fast Fourier Transform (FFT) on the given input data and stores the result in the output buffer.
   *
   * @param {Float64Array} out The output buffer to store the result.
   * @param {Float64Array} data The input data to transform.
   *
   * @throws {Error} Input and output buffers must be different.
   *
   * @returns {void}
   */
  transform(out, data) {
    if (out === data) throw new Error("Input and output buffers must be different");
    this._transform4(
      out,
      data,
      1
      /* DONE */
    );
  }
  /**
   * Performs a real-valued forward FFT on the given input buffer and stores the result in the given output buffer.
   * The input buffer must contain real values only, while the output buffer will contain complex values. The input and
   * output buffers must be different.
   *
   * @param {Float64Array} out The output buffer.
   * @param {Float64Array} data The input buffer containing real values.
   *
   * @throws {Error} If the input and output buffers are the same.
   */
  realTransform(out, data) {
    if (out === data) throw new Error("Input and output buffers must be different");
    this._realTransform4(
      out,
      data,
      1
      /* DONE */
    );
  }
  /**
   * Performs an inverse FFT transformation on the given `data` array, and stores the result in `out`.
   * The `out` array must be a different buffer than the `data` array. The `out` array will contain the
   * result of the transformation. The `data` array will not be modified.
   *
   * @param {Float64Array} out The output buffer for the transformed data.
   * @param {Float64Array} data The input data to transform.
   * @throws {Error} If `out` and `data` refer to the same buffer.
   * @returns {void}
   */
  inverseTransform(out, data) {
    if (out === data) throw new Error("Input and output buffers must be different");
    this._transform4(
      out,
      data,
      -1
      /* DONE */
    );
    for (let i = 0; i < out.length; ++i) out[i] /= this.size;
  }
  /**
   * Performs a radix-4 implementation of a discrete Fourier transform on a given set of data.
   *
   * @param {Float64Array} out The output buffer for the transformed data.
   * @param {Float64Array} data The input buffer of data to be transformed.
   * @param {number} inv A scaling factor to apply to the transform.
   * @returns {void}
   */
  _transform4(out, data, inv) {
    const size = this._csize;
    const width = this._width;
    let step = 1 << width;
    let len2 = size / step << 1;
    let outOff;
    let t;
    const bitrev = this._bitrev;
    if (len2 === 4) {
      for (outOff = 0, t = 0; outOff < size; outOff += len2, ++t) {
        const off = bitrev[t];
        this._singleTransform2(data, out, outOff, off, step);
      }
    } else {
      for (outOff = 0, t = 0; outOff < size; outOff += len2, ++t) {
        const off = bitrev[t];
        this._singleTransform4(data, out, outOff, off, step, inv);
      }
    }
    const table = this.table;
    for (step >>= 2; step >= 2; step >>= 2) {
      len2 = size / step << 1;
      const quarterLen = len2 >>> 2;
      for (outOff = 0; outOff < size; outOff += len2) {
        const limit = outOff + quarterLen - 1;
        for (let i = outOff, k = 0; i < limit; i += 2, k += step) {
          const A = i;
          const B = A + quarterLen;
          const C = B + quarterLen;
          const D = C + quarterLen;
          const Ar = out[A];
          const Ai = out[A + 1];
          const Br = out[B];
          const Bi = out[B + 1];
          const Cr = out[C];
          const Ci = out[C + 1];
          const Dr = out[D];
          const Di = out[D + 1];
          const tableBr = table[k];
          const tableBi = inv * table[k + 1];
          const MBr = Br * tableBr - Bi * tableBi;
          const MBi = Br * tableBi + Bi * tableBr;
          const tableCr = table[2 * k];
          const tableCi = inv * table[2 * k + 1];
          const MCr = Cr * tableCr - Ci * tableCi;
          const MCi = Cr * tableCi + Ci * tableCr;
          const tableDr = table[3 * k];
          const tableDi = inv * table[3 * k + 1];
          const MDr = Dr * tableDr - Di * tableDi;
          const MDi = Dr * tableDi + Di * tableDr;
          const T0r = Ar + MCr;
          const T0i = Ai + MCi;
          const T1r = Ar - MCr;
          const T1i = Ai - MCi;
          const T2r = MBr + MDr;
          const T2i = MBi + MDi;
          const T3r = inv * (MBr - MDr);
          const T3i = inv * (MBi - MDi);
          out[A] = T0r + T2r;
          out[A + 1] = T0i + T2i;
          out[B] = T1r + T3i;
          out[B + 1] = T1i - T3r;
          out[C] = T0r - T2r;
          out[C + 1] = T0i - T2i;
          out[D] = T1r - T3i;
          out[D + 1] = T1i + T3r;
        }
      }
    }
  }
  /**
   * Performs a radix-2 implementation of a discrete Fourier transform on a given set of data.
   *
   * @param {Float64Array} data The input buffer of data to be transformed.
   * @param {Float64Array} out The output buffer for the transformed data.
   * @param {number} outOff The offset at which to write the output data.
   * @param {number} off The offset at which to begin reading the input data.
   * @param {number} step The step size for indexing the input data.
   * @returns {void}
   */
  _singleTransform2(data, out, outOff, off, step) {
    const evenR = data[off];
    const evenI = data[off + 1];
    const oddR = data[off + step];
    const oddI = data[off + step + 1];
    out[outOff] = evenR + oddR;
    out[outOff + 1] = evenI + oddI;
    out[outOff + 2] = evenR - oddR;
    out[outOff + 3] = evenI - oddI;
  }
  /**
   * Performs radix-4 transformation on input data of length 8
   *
   * @param {Float64Array} data Input data array of length 8
   * @param {Float64Array} out Output data array of length 8
   * @param {number} outOff Index of output array to start writing from
   * @param {number} off Index of input array to start reading from
   * @param {number} step Step size between elements in input array
   * @param {number} inv Scaling factor for inverse transform
   *
   * @returns {void}
   */
  _singleTransform4(data, out, outOff, off, step, inv) {
    const step2 = step * 2;
    const step3 = step * 3;
    const Ar = data[off];
    const Ai = data[off + 1];
    const Br = data[off + step];
    const Bi = data[off + step + 1];
    const Cr = data[off + step2];
    const Ci = data[off + step2 + 1];
    const Dr = data[off + step3];
    const Di = data[off + step3 + 1];
    const T0r = Ar + Cr;
    const T0i = Ai + Ci;
    const T1r = Ar - Cr;
    const T1i = Ai - Ci;
    const T2r = Br + Dr;
    const T2i = Bi + Di;
    const T3r = inv * (Br - Dr);
    const T3i = inv * (Bi - Di);
    out[outOff] = T0r + T2r;
    out[outOff + 1] = T0i + T2i;
    out[outOff + 2] = T1r + T3i;
    out[outOff + 3] = T1i - T3r;
    out[outOff + 4] = T0r - T2r;
    out[outOff + 5] = T0i - T2i;
    out[outOff + 6] = T1r - T3i;
    out[outOff + 7] = T1i + T3r;
  }
  /**
   * Real input radix-4 implementation
   * @param {Float64Array} out Output array for the transformed data
   * @param {Float64Array} data Input array of real data to be transformed
   * @param {number} inv The scale factor used to normalize the inverse transform
   */
  _realTransform4(out, data, inv) {
    const size = this._csize;
    const width = this._width;
    let step = 1 << width;
    let len2 = size / step << 1;
    let outOff;
    let t;
    const bitrev = this._bitrev;
    if (len2 === 4) {
      for (outOff = 0, t = 0; outOff < size; outOff += len2, ++t) {
        const off = bitrev[t];
        this._singleRealTransform2(data, out, outOff, off >>> 1, step >>> 1);
      }
    } else {
      for (outOff = 0, t = 0; outOff < size; outOff += len2, ++t) {
        const off = bitrev[t];
        this._singleRealTransform4(data, out, outOff, off >>> 1, step >>> 1, inv);
      }
    }
    const table = this.table;
    for (step >>= 2; step >= 2; step >>= 2) {
      len2 = size / step << 1;
      const halfLen = len2 >>> 1;
      const quarterLen = halfLen >>> 1;
      const hquarterLen = quarterLen >>> 1;
      for (outOff = 0; outOff < size; outOff += len2) {
        for (let i = 0, k = 0; i <= hquarterLen; i += 2, k += step) {
          const A = outOff + i;
          const B = A + quarterLen;
          const C = B + quarterLen;
          const D = C + quarterLen;
          const Ar = out[A];
          const Ai = out[A + 1];
          const Br = out[B];
          const Bi = out[B + 1];
          const Cr = out[C];
          const Ci = out[C + 1];
          const Dr = out[D];
          const Di = out[D + 1];
          const MAr = Ar;
          const MAi = Ai;
          const tableBr = table[k];
          const tableBi = inv * table[k + 1];
          const MBr = Br * tableBr - Bi * tableBi;
          const MBi = Br * tableBi + Bi * tableBr;
          const tableCr = table[2 * k];
          const tableCi = inv * table[2 * k + 1];
          const MCr = Cr * tableCr - Ci * tableCi;
          const MCi = Cr * tableCi + Ci * tableCr;
          const tableDr = table[3 * k];
          const tableDi = inv * table[3 * k + 1];
          const MDr = Dr * tableDr - Di * tableDi;
          const MDi = Dr * tableDi + Di * tableDr;
          const T0r = MAr + MCr;
          const T0i = MAi + MCi;
          const T1r = MAr - MCr;
          const T1i = MAi - MCi;
          const T2r = MBr + MDr;
          const T2i = MBi + MDi;
          const T3r = inv * (MBr - MDr);
          const T3i = inv * (MBi - MDi);
          out[A] = T0r + T2r;
          out[A + 1] = T0i + T2i;
          out[B] = T1r + T3i;
          out[B + 1] = T1i - T3r;
          if (i === 0) {
            out[C] = T0r - T2r;
            out[C + 1] = T0i - T2i;
            continue;
          }
          if (i === hquarterLen) continue;
          const SA = outOff + quarterLen - i;
          const SB = outOff + halfLen - i;
          out[SA] = T1r - inv * T3i;
          out[SA + 1] = -T1i - inv * T3r;
          out[SB] = T0r - inv * T2r;
          out[SB + 1] = -T0i + inv * T2i;
        }
      }
    }
    const half = size >>> 1;
    for (let i = 2; i < half; i += 2) {
      out[size - i] = out[i];
      out[size - i + 1] = -out[i + 1];
    }
  }
  /**
   * Performs a single real input radix-2 transformation on the provided data
   *
   * @param {Float64Array} data The input data array
   * @param {Float64Array} out The output data array
   * @param {number} outOff The output offset
   * @param {number} off The input offset
   * @param {number} step The step
   *
   * @returns {void}
   */
  _singleRealTransform2(data, out, outOff, off, step) {
    const evenR = data[off];
    const oddR = data[off + step];
    out[outOff] = evenR + oddR;
    out[outOff + 1] = 0;
    out[outOff + 2] = evenR - oddR;
    out[outOff + 3] = 0;
  }
  /**
   * Computes a single real-valued transform using radix-4 algorithm.
   * This method is only called for len=8.
   *
   * @param {Float64Array} data The input data array.
   * @param {Float64Array} out The output data array.
   * @param {number} outOff The offset into the output array.
   * @param {number} off The offset into the input array.
   * @param {number} step The step size for the input array.
   * @param {number} inv The value of inverse.
   */
  _singleRealTransform4(data, out, outOff, off, step, inv) {
    const step2 = step * 2;
    const step3 = step * 3;
    const Ar = data[off];
    const Br = data[off + step];
    const Cr = data[off + step2];
    const Dr = data[off + step3];
    const T0r = Ar + Cr;
    const T1r = Ar - Cr;
    const T2r = Br + Dr;
    const T3r = inv * (Br - Dr);
    out[outOff] = T0r + T2r;
    out[outOff + 1] = 0;
    out[outOff + 2] = T1r;
    out[outOff + 3] = -T3r;
    out[outOff + 4] = T0r - T2r;
    out[outOff + 5] = 0;
    out[outOff + 6] = T1r;
    out[outOff + 7] = T3r;
  }
};
var NP2FFT = class {
  constructor(fft_length) {
    const a = 2 * (fft_length - 1);
    const b = 2 * (2 * fft_length - 1);
    const nextP2 = 2 ** Math.ceil(Math.log2(b));
    this.bufferSize = nextP2;
    this._a = a;
    const chirp = new Float64Array(b);
    const ichirp = new Float64Array(nextP2);
    this._chirpBuffer = new Float64Array(nextP2);
    this._buffer1 = new Float64Array(nextP2);
    this._buffer2 = new Float64Array(nextP2);
    this._outBuffer1 = new Float64Array(nextP2);
    this._outBuffer2 = new Float64Array(nextP2);
    const theta = -2 * Math.PI / fft_length;
    const baseR = Math.cos(theta);
    const baseI = Math.sin(theta);
    for (let i = 0; i < b >> 1; ++i) {
      const e = (i + 1 - fft_length) ** 2 / 2;
      const result_mod = Math.sqrt(baseR ** 2 + baseI ** 2) ** e;
      const result_arg = e * Math.atan2(baseI, baseR);
      const i2 = 2 * i;
      chirp[i2] = result_mod * Math.cos(result_arg);
      chirp[i2 + 1] = result_mod * Math.sin(result_arg);
      ichirp[i2] = chirp[i2];
      ichirp[i2 + 1] = -chirp[i2 + 1];
    }
    this._slicedChirpBuffer = chirp.subarray(a, b);
    this._f = new P2FFT(nextP2 >> 1);
    this._f.transform(this._chirpBuffer, ichirp);
  }
  _transform(output, input, real) {
    const ib1 = this._buffer1;
    const ib2 = this._buffer2;
    const ob2 = this._outBuffer1;
    const ob3 = this._outBuffer2;
    const cb = this._chirpBuffer;
    const sb = this._slicedChirpBuffer;
    const a = this._a;
    if (real) {
      for (let j = 0; j < sb.length; j += 2) {
        const j2 = j + 1;
        const j3 = j >> 1;
        const a_real = input[j3];
        ib1[j] = a_real * sb[j];
        ib1[j2] = a_real * sb[j2];
      }
    } else {
      for (let j = 0; j < sb.length; j += 2) {
        const j2 = j + 1;
        ib1[j] = input[j] * sb[j] - input[j2] * sb[j2];
        ib1[j2] = input[j] * sb[j2] + input[j2] * sb[j];
      }
    }
    this._f.transform(ob2, ib1);
    for (let j = 0; j < cb.length; j += 2) {
      const j2 = j + 1;
      ib2[j] = ob2[j] * cb[j] - ob2[j2] * cb[j2];
      ib2[j2] = ob2[j] * cb[j2] + ob2[j2] * cb[j];
    }
    this._f.inverseTransform(ob3, ib2);
    for (let j = 0; j < ob3.length; j += 2) {
      const a_real = ob3[j + a];
      const a_imag = ob3[j + a + 1];
      const b_real = sb[j];
      const b_imag = sb[j + 1];
      output[j] = a_real * b_real - a_imag * b_imag;
      output[j + 1] = a_real * b_imag + a_imag * b_real;
    }
  }
  transform(output, input) {
    this._transform(output, input, false);
  }
  realTransform(output, input) {
    this._transform(output, input, true);
  }
  get size() {
    return this.bufferSize;
  }
};
var FFT = class {
  constructor(fft_length) {
    this.fft_length = fft_length;
    this.isPowerOfTwo = isPowerOfTwo(fft_length);
    if (this.isPowerOfTwo) {
      this.fft = new P2FFT(fft_length);
      this.outputBufferSize = 2 * fft_length;
    } else {
      this.fft = new NP2FFT(fft_length);
      this.outputBufferSize = this.fft.size;
    }
  }
  realTransform(out, input) {
    this.fft.realTransform(out, input);
  }
  transform(out, input) {
    this.fft.transform(out, input);
  }
};
function medianFilter(data, windowSize) {
  if (windowSize % 2 === 0 || windowSize <= 0) {
    throw new Error("Window size must be a positive odd number");
  }
  const outputArray = new data.constructor(data.length);
  const buffer = new data.constructor(windowSize);
  const halfWindowSize = Math.floor(windowSize / 2);
  for (let i = 0; i < data.length; ++i) {
    let valuesIndex = 0;
    for (let j = -halfWindowSize; j <= halfWindowSize; ++j) {
      let index = i + j;
      if (index < 0) {
        index = Math.abs(index);
      } else if (index >= data.length) {
        index = 2 * (data.length - 1) - index;
      }
      buffer[valuesIndex++] = data[index];
    }
    buffer.sort();
    outputArray[i] = buffer[halfWindowSize];
  }
  return outputArray;
}
function round(num, decimals) {
  const pow = Math.pow(10, decimals);
  return Math.round(num * pow) / pow;
}
function bankers_round(x) {
  const r = Math.round(x);
  const br = Math.abs(x) % 1 === 0.5 ? r % 2 === 0 ? r : r - 1 : r;
  return br;
}
function dynamic_time_warping(matrix) {
  const output_length = matrix.length;
  const input_length = matrix[0].length;
  const outputShape = [output_length + 1, input_length + 1];
  const cost = Array.from({ length: outputShape[0] }, () => Array(outputShape[1]).fill(Infinity));
  cost[0][0] = 0;
  const trace = Array.from({ length: outputShape[0] }, () => Array(outputShape[1]).fill(-1));
  for (let j2 = 1; j2 < outputShape[1]; ++j2) {
    for (let i2 = 1; i2 < outputShape[0]; ++i2) {
      const c0 = cost[i2 - 1][j2 - 1];
      const c1 = cost[i2 - 1][j2];
      const c2 = cost[i2][j2 - 1];
      let c, t;
      if (c0 < c1 && c0 < c2) {
        c = c0;
        t = 0;
      } else if (c1 < c0 && c1 < c2) {
        c = c1;
        t = 1;
      } else {
        c = c2;
        t = 2;
      }
      cost[i2][j2] = matrix[i2 - 1][j2 - 1] + c;
      trace[i2][j2] = t;
    }
  }
  for (let i2 = 0; i2 < outputShape[1]; ++i2) {
    trace[0][i2] = 2;
  }
  for (let i2 = 0; i2 < outputShape[0]; ++i2) {
    trace[i2][0] = 1;
  }
  let i = output_length;
  let j = input_length;
  let text_indices = [];
  let time_indices = [];
  while (i > 0 || j > 0) {
    text_indices.push(i - 1);
    time_indices.push(j - 1);
    switch (trace[i][j]) {
      case 0:
        --i;
        --j;
        break;
      case 1:
        --i;
        break;
      case 2:
        --j;
        break;
      default:
        throw new Error(
          `Internal error in dynamic time warping. Unexpected trace[${i}, ${j}]. Please file a bug report.`
        );
    }
  }
  text_indices.reverse();
  time_indices.reverse();
  return [text_indices, time_indices];
}
var DEVICE_TO_EXECUTION_PROVIDER_MAPPING = Object.freeze({
  auto: null,
  // Auto-detect based on device and environment
  gpu: null,
  // Auto-detect GPU
  cpu: "cpu",
  // CPU
  wasm: "wasm",
  // WebAssembly
  webgpu: "webgpu",
  // WebGPU
  cuda: "cuda",
  // CUDA
  dml: "dml",
  // DirectML
  webnn: { name: "webnn", deviceType: "cpu" },
  // WebNN (default)
  "webnn-npu": { name: "webnn", deviceType: "npu" },
  // WebNN NPU
  "webnn-gpu": { name: "webnn", deviceType: "gpu" },
  // WebNN GPU
  "webnn-cpu": { name: "webnn", deviceType: "cpu" }
  // WebNN CPU
});
var supportedDevices = [];
var defaultDevices = [];
if (apis.IS_WEBNN_AVAILABLE) {
  supportedDevices.push("webnn-npu", "webnn-gpu", "webnn-cpu", "webnn");
}
if (apis.IS_WEBGPU_AVAILABLE) {
  supportedDevices.push("webgpu");
}
supportedDevices.push("wasm");
defaultDevices = ["wasm"];
var InferenceSession2 = ONNX.InferenceSession;
function deviceToExecutionProviders(device = null) {
  if (!device) return defaultDevices;
  switch (device) {
    case "auto":
      return supportedDevices;
    case "gpu":
      return supportedDevices.filter((x) => ["webgpu", "cuda", "dml", "webnn-gpu"].includes(x));
  }
  if (supportedDevices.includes(device)) {
    return [DEVICE_TO_EXECUTION_PROVIDER_MAPPING[device] ?? device];
  }
  throw new Error(`Unsupported device: "${device}". Should be one of: ${supportedDevices.join(", ")}.`);
}
var wasmInitPromise = null;
async function createInferenceSession(buffer, session_options, session_config) {
  if (wasmInitPromise) {
    await wasmInitPromise;
  }
  const sessionPromise = InferenceSession2.create(buffer, session_options);
  wasmInitPromise ?? (wasmInitPromise = sessionPromise);
  const session = await sessionPromise;
  session.config = session_config;
  return session;
}
function isONNXTensor(x) {
  return x instanceof ONNX.Tensor;
}
var ONNX_ENV = ONNX?.env;
if (ONNX_ENV?.wasm) {
  ONNX_ENV.wasm.wasmPaths = {
    wasm: `https://cdn.jsdelivr.net/npm/@huggingface/transformers@${env.version}/dist/ort-wasm-simd-threaded.jsep.wasm`,
    mjs: `https://cdn.jsdelivr.net/npm/@huggingface/transformers@${env.version}/dist/ort-wasm-simd-threaded.jsep.mjs`
  };
  ONNX_ENV.wasm.proxy = false;
  ONNX_ENV.wasm.numThreads = 1;
  if (typeof crossOriginIsolated === "undefined" || !crossOriginIsolated) {
    ONNX_ENV.wasm.numThreads = 1;
  }
}
if (ONNX_ENV?.webgpu) {
  ONNX_ENV.webgpu.powerPreference = "high-performance";
}
function isONNXProxy() {
  return ONNX_ENV?.wasm?.proxy;
}
env.backends.onnx = ONNX_ENV;

// src/libs/transformers/ops/registry.ts
var wrap = async (session_bytes, session_options, names) => {
  const session = await createInferenceSession(new Uint8Array(session_bytes), session_options, {});
  return (
    /** @type {any} */
    async (inputs) => {
      const proxied = isONNXProxy();
      const ortFeed = Object.fromEntries(
        Object.entries(inputs).map(([k, v]) => [k, (proxied ? v.clone() : v).ort_tensor])
      );
      const outputs = await session.run(ortFeed);
      if (Array.isArray(names)) {
        return names.map((n) => new Tensor3(outputs[n]));
      } else {
        return new Tensor3(outputs[
          /** @type {string} */
          names
        ]);
      }
    }
  );
};
var TensorOpRegistry = class {
  static get nearest_interpolate_4d() {
    if (!this._nearest_interpolate_4d) {
      this._nearest_interpolate_4d = wrap(
        [
          8,
          10,
          18,
          0,
          58,
          129,
          1,
          10,
          41,
          10,
          1,
          120,
          10,
          0,
          10,
          0,
          10,
          1,
          115,
          18,
          1,
          121,
          34,
          6,
          82,
          101,
          115,
          105,
          122,
          101,
          42,
          18,
          10,
          4,
          109,
          111,
          100,
          101,
          34,
          7,
          110,
          101,
          97,
          114,
          101,
          115,
          116,
          160,
          1,
          3,
          18,
          1,
          114,
          90,
          31,
          10,
          1,
          120,
          18,
          26,
          10,
          24,
          8,
          1,
          18,
          20,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          99,
          10,
          3,
          18,
          1,
          104,
          10,
          3,
          18,
          1,
          119,
          90,
          15,
          10,
          1,
          115,
          18,
          10,
          10,
          8,
          8,
          7,
          18,
          4,
          10,
          2,
          8,
          4,
          98,
          31,
          10,
          1,
          121,
          18,
          26,
          10,
          24,
          8,
          1,
          18,
          20,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          99,
          10,
          3,
          18,
          1,
          104,
          10,
          3,
          18,
          1,
          119,
          66,
          2,
          16,
          21
        ],
        this.session_options,
        "y"
      );
    }
    return this._nearest_interpolate_4d;
  }
  static get bilinear_interpolate_4d() {
    if (!this._bilinear_interpolate_4d) {
      this._bilinear_interpolate_4d = wrap(
        [
          8,
          9,
          18,
          0,
          58,
          128,
          1,
          10,
          40,
          10,
          1,
          120,
          10,
          0,
          10,
          0,
          10,
          1,
          115,
          18,
          1,
          121,
          34,
          6,
          82,
          101,
          115,
          105,
          122,
          101,
          42,
          17,
          10,
          4,
          109,
          111,
          100,
          101,
          34,
          6,
          108,
          105,
          110,
          101,
          97,
          114,
          160,
          1,
          3,
          18,
          1,
          114,
          90,
          31,
          10,
          1,
          120,
          18,
          26,
          10,
          24,
          8,
          1,
          18,
          20,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          99,
          10,
          3,
          18,
          1,
          104,
          10,
          3,
          18,
          1,
          119,
          90,
          15,
          10,
          1,
          115,
          18,
          10,
          10,
          8,
          8,
          7,
          18,
          4,
          10,
          2,
          8,
          4,
          98,
          31,
          10,
          1,
          121,
          18,
          26,
          10,
          24,
          8,
          1,
          18,
          20,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          99,
          10,
          3,
          18,
          1,
          104,
          10,
          3,
          18,
          1,
          119,
          66,
          2,
          16,
          20
        ],
        this.session_options,
        "y"
      );
    }
    return this._bilinear_interpolate_4d;
  }
  static get bicubic_interpolate_4d() {
    if (!this._bicubic_interpolate_4d) {
      this._bicubic_interpolate_4d = wrap(
        [
          8,
          9,
          18,
          0,
          58,
          127,
          10,
          39,
          10,
          1,
          120,
          10,
          0,
          10,
          0,
          10,
          1,
          115,
          18,
          1,
          121,
          34,
          6,
          82,
          101,
          115,
          105,
          122,
          101,
          42,
          16,
          10,
          4,
          109,
          111,
          100,
          101,
          34,
          5,
          99,
          117,
          98,
          105,
          99,
          160,
          1,
          3,
          18,
          1,
          114,
          90,
          31,
          10,
          1,
          120,
          18,
          26,
          10,
          24,
          8,
          1,
          18,
          20,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          99,
          10,
          3,
          18,
          1,
          104,
          10,
          3,
          18,
          1,
          119,
          90,
          15,
          10,
          1,
          115,
          18,
          10,
          10,
          8,
          8,
          7,
          18,
          4,
          10,
          2,
          8,
          4,
          98,
          31,
          10,
          1,
          121,
          18,
          26,
          10,
          24,
          8,
          1,
          18,
          20,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          99,
          10,
          3,
          18,
          1,
          104,
          10,
          3,
          18,
          1,
          119,
          66,
          2,
          16,
          20
        ],
        this.session_options,
        "y"
      );
    }
    return this._bicubic_interpolate_4d;
  }
  static get matmul() {
    if (!this._matmul) {
      this._matmul = wrap(
        [
          8,
          9,
          18,
          0,
          58,
          55,
          10,
          17,
          10,
          1,
          97,
          10,
          1,
          98,
          18,
          1,
          99,
          34,
          6,
          77,
          97,
          116,
          77,
          117,
          108,
          18,
          1,
          114,
          90,
          9,
          10,
          1,
          97,
          18,
          4,
          10,
          2,
          8,
          1,
          90,
          9,
          10,
          1,
          98,
          18,
          4,
          10,
          2,
          8,
          1,
          98,
          9,
          10,
          1,
          99,
          18,
          4,
          10,
          2,
          8,
          1,
          66,
          2,
          16,
          20
        ],
        this.session_options,
        "c"
      );
    }
    return this._matmul;
  }
  static get stft() {
    if (!this._stft) {
      this._stft = wrap(
        [
          8,
          7,
          18,
          0,
          58,
          148,
          1,
          10,
          38,
          10,
          1,
          115,
          10,
          1,
          106,
          10,
          1,
          119,
          10,
          1,
          108,
          18,
          1,
          111,
          34,
          4,
          83,
          84,
          70,
          84,
          42,
          15,
          10,
          8,
          111,
          110,
          101,
          115,
          105,
          100,
          101,
          100,
          24,
          1,
          160,
          1,
          2,
          18,
          1,
          115,
          90,
          26,
          10,
          1,
          115,
          18,
          21,
          10,
          19,
          8,
          1,
          18,
          15,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          115,
          10,
          3,
          18,
          1,
          99,
          90,
          11,
          10,
          1,
          106,
          18,
          6,
          10,
          4,
          8,
          7,
          18,
          0,
          90,
          16,
          10,
          1,
          119,
          18,
          11,
          10,
          9,
          8,
          1,
          18,
          5,
          10,
          3,
          18,
          1,
          119,
          90,
          11,
          10,
          1,
          108,
          18,
          6,
          10,
          4,
          8,
          7,
          18,
          0,
          98,
          31,
          10,
          1,
          111,
          18,
          26,
          10,
          24,
          8,
          1,
          18,
          20,
          10,
          3,
          18,
          1,
          98,
          10,
          3,
          18,
          1,
          102,
          10,
          3,
          18,
          1,
          100,
          10,
          3,
          18,
          1,
          99,
          66,
          2,
          16,
          17
        ],
        this.session_options,
        "o"
      );
    }
    return this._stft;
  }
  static get rfft() {
    if (!this._rfft) {
      this._rfft = wrap(
        [
          8,
          9,
          18,
          0,
          58,
          97,
          10,
          33,
          10,
          1,
          120,
          10,
          0,
          10,
          1,
          97,
          18,
          1,
          121,
          34,
          3,
          68,
          70,
          84,
          42,
          15,
          10,
          8,
          111,
          110,
          101,
          115,
          105,
          100,
          101,
          100,
          24,
          1,
          160,
          1,
          2,
          18,
          1,
          100,
          90,
          21,
          10,
          1,
          120,
          18,
          16,
          10,
          14,
          8,
          1,
          18,
          10,
          10,
          3,
          18,
          1,
          115,
          10,
          3,
          18,
          1,
          99,
          90,
          11,
          10,
          1,
          97,
          18,
          6,
          10,
          4,
          8,
          7,
          18,
          0,
          98,
          21,
          10,
          1,
          121,
          18,
          16,
          10,
          14,
          8,
          1,
          18,
          10,
          10,
          3,
          18,
          1,
          115,
          10,
          3,
          18,
          1,
          99,
          66,
          2,
          16,
          20
        ],
        this.session_options,
        "y"
      );
    }
    return this._rfft;
  }
  static get top_k() {
    if (!this._top_k) {
      this._top_k = wrap(
        [
          8,
          10,
          18,
          0,
          58,
          73,
          10,
          18,
          10,
          1,
          120,
          10,
          1,
          107,
          18,
          1,
          118,
          18,
          1,
          105,
          34,
          4,
          84,
          111,
          112,
          75,
          18,
          1,
          116,
          90,
          9,
          10,
          1,
          120,
          18,
          4,
          10,
          2,
          8,
          1,
          90,
          15,
          10,
          1,
          107,
          18,
          10,
          10,
          8,
          8,
          7,
          18,
          4,
          10,
          2,
          8,
          1,
          98,
          9,
          10,
          1,
          118,
          18,
          4,
          10,
          2,
          8,
          1,
          98,
          9,
          10,
          1,
          105,
          18,
          4,
          10,
          2,
          8,
          7,
          66,
          2,
          16,
          21
        ],
        this.session_options,
        [
          /* Values */
          "v",
          /* Indices */
          "i"
        ]
      );
    }
    return this._top_k;
  }
  static get slice() {
    if (!this._slice) {
      this._slice = wrap(
        [
          8,
          7,
          18,
          0,
          58,
          96,
          10,
          25,
          10,
          1,
          120,
          10,
          1,
          115,
          10,
          1,
          101,
          10,
          1,
          97,
          10,
          1,
          116,
          18,
          1,
          121,
          34,
          5,
          83,
          108,
          105,
          99,
          101,
          18,
          1,
          114,
          90,
          9,
          10,
          1,
          120,
          18,
          4,
          10,
          2,
          8,
          1,
          90,
          9,
          10,
          1,
          115,
          18,
          4,
          10,
          2,
          8,
          7,
          90,
          9,
          10,
          1,
          101,
          18,
          4,
          10,
          2,
          8,
          7,
          90,
          9,
          10,
          1,
          97,
          18,
          4,
          10,
          2,
          8,
          7,
          90,
          9,
          10,
          1,
          116,
          18,
          4,
          10,
          2,
          8,
          7,
          98,
          9,
          10,
          1,
          121,
          18,
          4,
          10,
          2,
          8,
          1,
          66,
          2,
          16,
          13
        ],
        this.session_options,
        "y"
      );
    }
    return this._slice;
  }
};
TensorOpRegistry.session_options = {
  // TODO: Allow for multiple execution providers
  // executionProviders: ['webgpu'],
};

// src/libs/transformers/utils/tensor.ts
var DataTypeMap = Object.freeze({
  float32: Float32Array,
  float16: Uint16Array,
  float64: Float64Array,
  string: Array,
  // string[]
  int8: Int8Array,
  uint8: Uint8Array,
  int16: Int16Array,
  uint16: Uint16Array,
  int32: Int32Array,
  uint32: Uint32Array,
  int64: BigInt64Array,
  uint64: BigUint64Array,
  bool: Uint8Array,
  uint4: Uint8Array,
  int4: Int8Array
});
var Tensor3 = class _Tensor {
  // Add numeric index signature
  /** @type {number[]} Dimensions of the tensor. */
  get dims() {
    return this.ort_tensor.dims;
  }
  set dims(value) {
    this.ort_tensor.dims = value;
  }
  /** @type {DataType} Type of the tensor. */
  get type() {
    return this.ort_tensor.type;
  }
  /** @type {DataArray} The data stored in the tensor. */
  get data() {
    return this.ort_tensor.data;
  }
  /** @type {number} The number of elements in the tensor. */
  get size() {
    return this.ort_tensor.size;
  }
  /** @type {string} The location of the tensor data. */
  get location() {
    return this.ort_tensor.location;
  }
  /**
   * Create a new Tensor or copy an existing Tensor.
   * @param {[DataType, DataArray, number[]]|[ONNXTensor]} args
   */
  constructor(...args) {
    if (isONNXTensor(args[0])) {
      this.ort_tensor = /** @type {ONNXTensor} */
      args[0];
    } else {
      this.ort_tensor = new Tensor(
        args[0],
        args[1],
        args[2]
      );
    }
    return new Proxy(this, {
      get: (obj, key) => {
        if (typeof key === "string") {
          let index = Number(key);
          if (Number.isInteger(index)) {
            return obj._getitem(index);
          }
        }
        return obj[key];
      },
      set: (obj, key, value) => {
        return obj[key] = value;
      }
    });
  }
  dispose() {
    this.ort_tensor.dispose();
  }
  /**
   * Returns an iterator object for iterating over the tensor data in row-major order.
   * If the tensor has more than one dimension, the iterator will yield subarrays.
   * @returns {Iterator} An iterator object for iterating over the tensor data in row-major order.
   */
  *[Symbol.iterator]() {
    const [iterLength, ...iterDims] = this.dims;
    if (iterDims.length > 0) {
      const iterSize = iterDims.reduce((a, b) => a * b);
      for (let i = 0; i < iterLength; ++i) {
        yield this._subarray(i, iterSize, iterDims);
      }
    } else {
      yield* this.data;
    }
  }
  /**
   * Index into a Tensor object.
   * @param {number} index The index to access.
   * @returns {Tensor} The data at the specified index.
   */
  _getitem(index) {
    const [iterLength, ...iterDims] = this.dims;
    index = safeIndex(index, iterLength);
    if (iterDims.length > 0) {
      const iterSize = iterDims.reduce((a, b) => a * b);
      return this._subarray(index, iterSize, iterDims);
    } else {
      return new _Tensor(this.type, [this.data[index]], iterDims);
    }
  }
  /**
   * @param {number|bigint} item The item to search for in the tensor
   * @returns {number} The index of the first occurrence of item in the tensor data.
   */
  indexOf(item) {
    const this_data = this.data;
    for (let index = 0; index < this_data.length; ++index) {
      if (this_data[index] == item) {
        return index;
      }
    }
    return -1;
  }
  /**
   * @param {number} index
   * @param {number} iterSize
   * @param {any} iterDims
   * @returns {Tensor}
   */
  _subarray(index, iterSize, iterDims) {
    const o1 = index * iterSize;
    const o2 = (index + 1) * iterSize;
    const data = "subarray" in this.data ? this.data.subarray(o1, o2) : this.data.slice(o1, o2);
    return new _Tensor(this.type, data, iterDims);
  }
  /**
   * Returns the value of this tensor as a standard JavaScript Number. This only works
   * for tensors with one element. For other cases, see `Tensor.tolist()`.
   * @returns {number|bigint} The value of this tensor as a standard JavaScript Number.
   * @throws {Error} If the tensor has more than one element.
   */
  item() {
    const this_data = this.data;
    if (this_data.length !== 1) {
      throw new Error(`a Tensor with ${this_data.length} elements cannot be converted to Scalar`);
    }
    return this_data[0];
  }
  /**
   * Convert tensor data to a n-dimensional JS list
   * @returns {Array}
   */
  tolist() {
    return reshape(this.data, this.dims);
  }
  /**
   * Return a new Tensor with the sigmoid function applied to each element.
   * @returns {Tensor} The tensor with the sigmoid function applied.
   */
  sigmoid() {
    return this.clone().sigmoid_();
  }
  /**
   * Applies the sigmoid function to the tensor in place.
   * @returns {Tensor} Returns `this`.
   */
  sigmoid_() {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] = 1 / (1 + Math.exp(-this_data[i]));
    }
    return this;
  }
  /**
   * Return a new Tensor with a callback function applied to each element.
   * @param {Function} callback - The function to apply to each element. It should take three arguments:
   *                              the current element, its index, and the tensor's data array.
   * @returns {Tensor} A new Tensor with the callback function applied to each element.
   */
  map(callback) {
    return this.clone().map_(callback);
  }
  /**
   * Apply a callback function to each element of the tensor in place.
   * @param {Function} callback - The function to apply to each element. It should take three arguments:
   *                              the current element, its index, and the tensor's data array.
   * @returns {Tensor} Returns `this`.
   */
  map_(callback) {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] = callback(this_data[i], i, this_data);
    }
    return this;
  }
  /**
   * Return a new Tensor with every element multiplied by a constant.
   * @param {number} val The value to multiply by.
   * @returns {Tensor} The new tensor.
   */
  mul(val) {
    return this.clone().mul_(val);
  }
  /**
   * Multiply the tensor by a constant in place.
   * @param {number} val The value to multiply by.
   * @returns {Tensor} Returns `this`.
   */
  mul_(val) {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] *= val;
    }
    return this;
  }
  /**
   * Return a new Tensor with every element divided by a constant.
   * @param {number} val The value to divide by.
   * @returns {Tensor} The new tensor.
   */
  div(val) {
    return this.clone().div_(val);
  }
  /**
   * Divide the tensor by a constant in place.
   * @param {number} val The value to divide by.
   * @returns {Tensor} Returns `this`.
   */
  div_(val) {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] /= val;
    }
    return this;
  }
  /**
   * Return a new Tensor with every element added by a constant.
   * @param {number} val The value to add by.
   * @returns {Tensor} The new tensor.
   */
  add(val) {
    return this.clone().add_(val);
  }
  /**
   * Add the tensor by a constant in place.
   * @param {number} val The value to add by.
   * @returns {Tensor} Returns `this`.
   */
  add_(val) {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] += val;
    }
    return this;
  }
  /**
   * Return a new Tensor with every element subtracted by a constant.
   * @param {number} val The value to subtract by.
   * @returns {Tensor} The new tensor.
   */
  sub(val) {
    return this.clone().sub_(val);
  }
  /**
   * Subtract the tensor by a constant in place.
   * @param {number} val The value to subtract by.
   * @returns {Tensor} Returns `this`.
   */
  sub_(val) {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] -= val;
    }
    return this;
  }
  /**
   * Creates a deep copy of the current Tensor.
   * @returns {Tensor} A new Tensor with the same type, data, and dimensions as the original.
   */
  clone() {
    return new _Tensor(this.type, this.data.slice(), this.dims.slice());
  }
  /**
   * Performs a slice operation on the Tensor along specified dimensions.
   *
   * Consider a Tensor that has a dimension of [4, 7]:
   * ```
   * [ 1,  2,  3,  4,  5,  6,  7]
   * [ 8,  9, 10, 11, 12, 13, 14]
   * [15, 16, 17, 18, 19, 20, 21]
   * [22, 23, 24, 25, 26, 27, 28]
   * ```
   * We can slice against the two dims of row and column, for instance in this
   * case we can start at the second element, and return to the second last,
   * like this:
   * ```
   * tensor.slice([1, -1], [1, -1]);
   * ```
   * which would return:
   * ```
   * [  9, 10, 11, 12, 13 ]
   * [ 16, 17, 18, 19, 20 ]
   * ```
   *
   * @param {...(number|number[]|null)} slices The slice specifications for each dimension.
   * - If a number is given, then a single element is selected.
   * - If an array of two numbers is given, then a range of elements [start, end (exclusive)] is selected.
   * - If null is given, then the entire dimension is selected.
   * @returns {Tensor} A new Tensor containing the selected elements.
   * @throws {Error} If the slice input is invalid.
   */
  slice(...slices) {
    const newTensorDims = [];
    const newOffsets = [];
    for (let sliceIndex = 0; sliceIndex < this.dims.length; ++sliceIndex) {
      let slice2 = slices[sliceIndex];
      if (slice2 === null || slice2 === undefined) {
        newOffsets.push([0, this.dims[sliceIndex]]);
        newTensorDims.push(this.dims[sliceIndex]);
      } else if (typeof slice2 === "number") {
        slice2 = safeIndex(slice2, this.dims[sliceIndex], sliceIndex);
        newOffsets.push([slice2, slice2 + 1]);
      } else if (Array.isArray(slice2) && slice2.length === 2) {
        let [start, end] = slice2;
        start = start === null ? 0 : safeIndex(start, this.dims[sliceIndex], sliceIndex, false);
        end = end === null ? this.dims[sliceIndex] : safeIndex(end, this.dims[sliceIndex], sliceIndex, false);
        if (start > end) {
          throw new Error(`Invalid slice: ${slice2}`);
        }
        const offsets = [Math.max(start, 0), Math.min(end, this.dims[sliceIndex])];
        newOffsets.push(offsets);
        newTensorDims.push(offsets[1] - offsets[0]);
      } else {
        throw new Error(`Invalid slice: ${slice2}`);
      }
    }
    const newDims = newOffsets.map(([start, end]) => end - start);
    const newBufferSize = newDims.reduce((a, b) => a * b);
    const this_data = this.data;
    const data = new this_data.constructor(newBufferSize);
    const stride = this.stride();
    for (let i = 0; i < newBufferSize; ++i) {
      let originalIndex = 0;
      for (let j = newDims.length - 1, num = i; j >= 0; --j) {
        const size = newDims[j];
        originalIndex += (num % size + newOffsets[j][0]) * stride[j];
        num = Math.floor(num / size);
      }
      data[i] = this_data[originalIndex];
    }
    return new _Tensor(this.type, data, newTensorDims);
  }
  /**
   * Return a permuted version of this Tensor, according to the provided dimensions.
   * @param  {...number} dims Dimensions to permute.
   * @returns {Tensor} The permuted tensor.
   */
  permute(...dims) {
    return permute(this, dims);
  }
  // TODO: implement transpose. For now (backwards compatibility), it's just an alias for permute()
  transpose(...dims) {
    return this.permute(...dims);
  }
  /**
   * Returns the sum of each row of the input tensor in the given dimension dim.
   *
   * @param {number} [dim=null] The dimension or dimensions to reduce. If `null`, all dimensions are reduced.
   * @param {boolean} keepdim Whether the output tensor has `dim` retained or not.
   * @returns The summed tensor
   */
  sum(dim = null, keepdim = false) {
    return this.norm(1, dim, keepdim);
  }
  /**
   * Returns the matrix norm or vector norm of a given tensor.
   * @param {number|string} [p='fro'] The order of norm
   * @param {number} [dim=null] Specifies which dimension of the tensor to calculate the norm across.
   * If dim is None, the norm will be calculated across all dimensions of input.
   * @param {boolean} [keepdim=false] Whether the output tensors have dim retained or not.
   * @returns {Tensor} The norm of the tensor.
   */
  norm(p = "fro", dim = null, keepdim = false) {
    if (p === "fro") {
      p = 2;
    } else if (typeof p === "string") {
      throw Error(`Unsupported norm: ${p}`);
    }
    const this_data = this.data;
    if (dim === null) {
      let val = this_data.reduce((a, b) => a + b ** p, 0) ** (1 / p);
      return new _Tensor(this.type, [val], []);
    }
    dim = safeIndex(dim, this.dims.length);
    const resultDims = this.dims.slice();
    resultDims[dim] = 1;
    const result = new this_data.constructor(this_data.length / this.dims[dim]);
    for (let i = 0; i < this_data.length; ++i) {
      let resultIndex = 0;
      for (let j = this.dims.length - 1, num = i, resultMultiplier = 1; j >= 0; --j) {
        const size = this.dims[j];
        if (j !== dim) {
          const index = num % size;
          resultIndex += index * resultMultiplier;
          resultMultiplier *= resultDims[j];
        }
        num = Math.floor(num / size);
      }
      result[resultIndex] += this_data[i] ** p;
    }
    if (p !== 1) {
      for (let i = 0; i < result.length; ++i) {
        result[i] = result[i] ** (1 / p);
      }
    }
    if (!keepdim) {
      resultDims.splice(dim, 1);
    }
    return new _Tensor(this.type, result, resultDims);
  }
  /**
   * Performs `L_p` normalization of inputs over specified dimension. Operates in place.
   * @param {number} [p=2] The exponent value in the norm formulation
   * @param {number} [dim=1] The dimension to reduce
   * @returns {Tensor} `this` for operation chaining.
   */
  normalize_(p = 2, dim = 1) {
    dim = safeIndex(dim, this.dims.length);
    const norm = this.norm(p, dim, true);
    const this_data = this.data;
    const norm_data = norm.data;
    for (let i = 0; i < this_data.length; ++i) {
      let resultIndex = 0;
      for (let j = this.dims.length - 1, num = i, resultMultiplier = 1; j >= 0; --j) {
        const size = this.dims[j];
        if (j !== dim) {
          const index = num % size;
          resultIndex += index * resultMultiplier;
          resultMultiplier *= this.dims[j];
        }
        num = Math.floor(num / size);
      }
      this_data[i] /= norm_data[resultIndex];
    }
    return this;
  }
  /**
   * Performs `L_p` normalization of inputs over specified dimension.
   * @param {number} [p=2] The exponent value in the norm formulation
   * @param {number} [dim=1] The dimension to reduce
   * @returns {Tensor} The normalized tensor.
   */
  normalize(p = 2, dim = 1) {
    return this.clone().normalize_(p, dim);
  }
  /**
   * Compute and return the stride of this tensor.
   * Stride is the jump necessary to go from one element to the next one in the specified dimension dim.
   * @returns {number[]} The stride of this tensor.
   */
  stride() {
    return dimsToStride(this.dims);
  }
  /**
   * Returns a tensor with all specified dimensions of input of size 1 removed.
   *
   * NOTE: The returned tensor shares the storage with the input tensor, so changing the contents of one will change the contents of the other.
   * If you would like a copy, use `tensor.clone()` before squeezing.
   *
   * @param {number} [dim=null] If given, the input will be squeezed only in the specified dimensions.
   * @returns {Tensor} The squeezed tensor
   */
  squeeze(dim = null) {
    return new _Tensor(this.type, this.data, calc_squeeze_dims(this.dims, dim));
  }
  /**
   * In-place version of @see {@link Tensor.squeeze}
   */
  squeeze_(dim = null) {
    this.dims = calc_squeeze_dims(this.dims, dim);
    return this;
  }
  /**
   * Returns a new tensor with a dimension of size one inserted at the specified position.
   *
   * NOTE: The returned tensor shares the same underlying data with this tensor.
   *
   * @param {number} dim The index at which to insert the singleton dimension
   * @returns {Tensor} The unsqueezed tensor
   */
  unsqueeze(dim) {
    return new _Tensor(this.type, this.data, calc_unsqueeze_dims(this.dims, dim));
  }
  /**
   * In-place version of @see {@link Tensor.unsqueeze}
   */
  unsqueeze_(dim) {
    this.dims = calc_unsqueeze_dims(this.dims, dim);
    return this;
  }
  /**
   * In-place version of @see {@link Tensor.flatten}
   */
  flatten_(start_dim = 0, end_dim = -1) {
    end_dim = (end_dim + this.dims.length) % this.dims.length;
    let dimsToKeepBefore = this.dims.slice(0, start_dim);
    let dimsToFlatten = this.dims.slice(start_dim, end_dim + 1);
    let dimsToKeepAfter = this.dims.slice(end_dim + 1);
    this.dims = [...dimsToKeepBefore, dimsToFlatten.reduce((a, b) => a * b, 1), ...dimsToKeepAfter];
    return this;
  }
  /**
   * Flattens input by reshaping it into a one-dimensional tensor.
   * If `start_dim` or `end_dim` are passed, only dimensions starting with `start_dim`
   * and ending with `end_dim` are flattened. The order of elements in input is unchanged.
   * @param {number} start_dim the first dim to flatten
   * @param {number} end_dim the last dim to flatten
   * @returns {Tensor} The flattened tensor.
   */
  flatten(start_dim = 0, end_dim = -1) {
    return this.clone().flatten_(start_dim, end_dim);
  }
  /**
   * Returns a new tensor with the same data as the `self` tensor but of a different `shape`.
   * @param  {...number} dims the desired size
   * @returns {Tensor} The tensor with the same data but different shape
   */
  view(...dims) {
    let inferredIndex = -1;
    for (let i = 0; i < dims.length; ++i) {
      if (dims[i] === -1) {
        if (inferredIndex !== -1) {
          throw new Error("Only one dimension can be inferred");
        }
        inferredIndex = i;
      }
    }
    const this_data = this.data;
    if (inferredIndex !== -1) {
      const productOther = dims.reduce((product2, curr, index) => {
        return index !== inferredIndex ? product2 * curr : product2;
      }, 1);
      dims[inferredIndex] = this_data.length / productOther;
    }
    return new _Tensor(this.type, this_data, dims);
  }
  neg_() {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] = -this_data[i];
    }
    return this;
  }
  neg() {
    return this.clone().neg_();
  }
  /**
   * In-place version of @see {@link Tensor.clamp}
   */
  clamp_(min2, max3) {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] = Math.min(Math.max(this_data[i], min2), max3);
    }
    return this;
  }
  /**
   * Clamps all elements in input into the range [ min, max ]
   * @param {number} min lower-bound of the range to be clamped to
   * @param {number} max upper-bound of the range to be clamped to
   * @returns {Tensor} the output tensor.
   */
  clamp(min2, max3) {
    return this.clone().clamp_(min2, max3);
  }
  /**
   * In-place version of @see {@link Tensor.round}
   */
  round_() {
    const this_data = this.data;
    for (let i = 0; i < this_data.length; ++i) {
      this_data[i] = Math.round(this_data[i]);
    }
    return this;
  }
  /**
   * Rounds elements of input to the nearest integer.
   * @returns {Tensor} the output tensor.
   */
  round() {
    return this.clone().round_();
  }
  mean(dim = null, keepdim = false) {
    return mean(this, dim, keepdim);
  }
  min(dim = null, keepdim = false) {
    if (dim !== null) {
      throw new Error("`dim !== null` not yet implemented.");
    }
    const value = min(this.data)[0];
    return new _Tensor(this.type, [value], []);
  }
  max(dim = null, keepdim = false) {
    if (dim !== null) {
      throw new Error("`dim !== null` not yet implemented.");
    }
    const value = max(this.data)[0];
    return new _Tensor(this.type, [value], []);
  }
  argmin(dim = null, keepdim = false) {
    if (dim !== null) {
      throw new Error("`dim !== null` not yet implemented.");
    }
    const index = min(this.data)[1];
    return new _Tensor("int64", [BigInt(index)], []);
  }
  argmax(dim = null, keepdim = false) {
    if (dim !== null) {
      throw new Error("`dim !== null` not yet implemented.");
    }
    const index = max(this.data)[1];
    return new _Tensor("int64", [BigInt(index)], []);
  }
  /**
   * Performs Tensor dtype conversion.
   * @param {DataType} type The desired data type.
   * @returns {Tensor} The converted tensor.
   */
  to(type) {
    if (this.type === type) return this;
    if (!DataTypeMap.hasOwnProperty(type)) {
      throw new Error(`Unsupported type: ${type}`);
    }
    let map_fn;
    const is_source_bigint = ["int64", "uint64"].includes(this.type);
    const is_dest_bigint = ["int64", "uint64"].includes(type);
    if (is_source_bigint && !is_dest_bigint) {
      map_fn = Number;
    } else if (!is_source_bigint && is_dest_bigint) {
      map_fn = BigInt;
    }
    return new _Tensor(type, DataTypeMap[type].from(this.data, map_fn), this.dims);
  }
};
function reshape(data, dimensions) {
  const totalElements = data.length;
  const dimensionSize = dimensions.reduce((a, b) => a * b);
  if (totalElements !== dimensionSize) {
    throw Error(`cannot reshape array of size ${totalElements} into shape (${dimensions})`);
  }
  let reshapedArray = data;
  for (let i = dimensions.length - 1; i >= 0; i--) {
    reshapedArray = reshapedArray.reduce(
      (acc, val) => {
        let lastArray = acc[acc.length - 1];
        if (lastArray.length < dimensions[i]) {
          lastArray.push(val);
        } else {
          acc.push([val]);
        }
        return acc;
      },
      [[]]
    );
  }
  return reshapedArray[0];
}
function permute(tensor, axes) {
  const [permutedData, shape] = permute_data(tensor.data, tensor.dims, axes);
  return new Tensor3(tensor.type, permutedData, shape);
}
async function interpolate_4d(input, { size = null, mode = "bilinear" } = {}) {
  if (input.dims.length !== 4) {
    throw new Error("`interpolate_4d` currently only supports 4D input.");
  }
  if (!size) {
    throw new Error("`interpolate_4d` requires a `size` argument.");
  }
  let targetDims;
  if (size.length === 2) {
    targetDims = [...input.dims.slice(0, 2), ...size];
  } else if (size.length === 3) {
    targetDims = [input.dims[0], ...size];
  } else if (size.length === 4) {
    targetDims = size;
  } else {
    throw new Error("`size` must be of length 2, 3, or 4.");
  }
  let op;
  if (mode === "nearest") {
    op = await TensorOpRegistry.nearest_interpolate_4d;
  } else if (mode === "bilinear") {
    op = await TensorOpRegistry.bilinear_interpolate_4d;
  } else if (mode === "bicubic") {
    op = await TensorOpRegistry.bicubic_interpolate_4d;
  } else {
    throw new Error(`Unsupported mode: ${mode}`);
  }
  const sizeTensor = new Tensor3("int64", new BigInt64Array(targetDims.map(BigInt)), [targetDims.length]);
  return await op({ x: input, s: sizeTensor });
}
async function matmul(a, b) {
  const op = await TensorOpRegistry.matmul;
  return await op({ a, b });
}
async function topk(x, k) {
  const op = await TensorOpRegistry.top_k;
  if (k == null) {
    k = x.dims.at(-1);
  } else {
    k = Math.min(k, x.dims.at(-1));
  }
  return await op({
    x,
    k: new Tensor3("int64", [BigInt(k)], [1])
  });
}
var arrayToIndexTensor = (array) => new Tensor3("int64", array, [array.length]);
async function slice(data, starts, ends, axes, steps = null) {
  const op = await TensorOpRegistry.slice;
  return await op({
    x: data,
    s: arrayToIndexTensor(starts),
    e: arrayToIndexTensor(ends),
    a: arrayToIndexTensor(axes),
    t: arrayToIndexTensor(steps ?? new Array(axes.length).fill(1))
  });
}
function mean_pooling(last_hidden_state, attention_mask) {
  const lastHiddenStateData = last_hidden_state.data;
  const attentionMaskData = attention_mask.data;
  const shape = [last_hidden_state.dims[0], last_hidden_state.dims[2]];
  const returnedData = new lastHiddenStateData.constructor(shape[0] * shape[1]);
  const [batchSize, seqLength, embedDim] = last_hidden_state.dims;
  let outIndex = 0;
  for (let i = 0; i < batchSize; ++i) {
    const offset = i * embedDim * seqLength;
    for (let k = 0; k < embedDim; ++k) {
      let sum = 0;
      let count2 = 0;
      const attnMaskOffset = i * seqLength;
      const offset2 = offset + k;
      for (let j = 0; j < seqLength; ++j) {
        const attn = Number(attentionMaskData[attnMaskOffset + j]);
        count2 += attn;
        sum += lastHiddenStateData[offset2 + j * embedDim] * attn;
      }
      const avg = sum / count2;
      returnedData[outIndex++] = avg;
    }
  }
  return new Tensor3(last_hidden_state.type, returnedData, shape);
}
function calc_squeeze_dims(dims, dim) {
  dims = dims.slice();
  if (dim === null) {
    dims = dims.filter((d) => d !== 1);
  } else if (typeof dim === "number") {
    if (dims[dim] === 1) {
      dims.splice(dim, 1);
    }
  } else if (Array.isArray(dim)) {
    dims = dims.filter((x, i) => {
      return x !== 1 || !dim.includes(i);
    });
  }
  return dims;
}
function calc_unsqueeze_dims(dims, dim) {
  dim = safeIndex(dim, dims.length + 1);
  dims = dims.slice();
  dims.splice(dim, 0, 1);
  return dims;
}
function safeIndex(index, size, dimension = null, boundsCheck = true) {
  if (boundsCheck && (index < -size || index >= size)) {
    throw new Error(
      `IndexError: index ${index} is out of bounds for dimension${dimension === null ? "" : " " + dimension} with size ${size}`
    );
  }
  if (index < 0) {
    index = (index % size + size) % size;
  }
  return index;
}
function cat(tensors, dim = 0) {
  dim = safeIndex(dim, tensors[0].dims.length);
  const resultDims = tensors[0].dims.slice();
  resultDims[dim] = tensors.reduce((a, b) => a + b.dims[dim], 0);
  const resultSize = resultDims.reduce((a, b) => a * b, 1);
  const result = new tensors[0].data.constructor(resultSize);
  const resultType = tensors[0].type;
  if (dim === 0) {
    let offset = 0;
    for (const tensor of tensors) {
      const tensorData = tensor.data;
      result.set(tensorData, offset);
      offset += tensorData.length;
    }
  } else {
    let currentDim = 0;
    for (let t = 0; t < tensors.length; ++t) {
      const { data, dims } = tensors[t];
      for (let i = 0; i < data.length; ++i) {
        let resultIndex = 0;
        for (let j = dims.length - 1, num = i, resultMultiplier = 1; j >= 0; --j) {
          const size = dims[j];
          let index = num % size;
          if (j === dim) {
            index += currentDim;
          }
          resultIndex += index * resultMultiplier;
          resultMultiplier *= resultDims[j];
          num = Math.floor(num / size);
        }
        result[resultIndex] = data[i];
      }
      currentDim += dims[dim];
    }
  }
  return new Tensor3(resultType, result, resultDims);
}
function stack(tensors, dim = 0) {
  return cat(
    tensors.map((t) => t.unsqueeze(dim)),
    dim
  );
}
function std_mean(input, dim = null, correction = 1, keepdim = false) {
  const inputData = (
    /** @type {Float32Array} */
    input.data
  );
  const inputDims = input.dims;
  if (dim === null) {
    const sum = Array.prototype.reduce.call(inputData, (a, b) => a + b, 0);
    const mean2 = sum / inputData.length;
    const std = Math.sqrt(
      Array.prototype.reduce.call(inputData, (a, b) => a + (b - mean2) ** 2, 0) / (inputData.length - correction)
    );
    const meanTensor2 = new Tensor3(
      input.type,
      [mean2],
      [
        /* scalar */
      ]
    );
    const stdTensor2 = new Tensor3(
      input.type,
      [std],
      [
        /* scalar */
      ]
    );
    return [stdTensor2, meanTensor2];
  }
  dim = safeIndex(dim, inputDims.length);
  const meanTensor = mean(input, dim, keepdim);
  const meanTensorData = meanTensor.data;
  const resultDims = inputDims.slice();
  resultDims[dim] = 1;
  const result = new inputData.constructor(inputData.length / inputDims[dim]);
  for (let i = 0; i < inputData.length; ++i) {
    let resultIndex = 0;
    for (let j = inputDims.length - 1, num = i, resultMultiplier = 1; j >= 0; --j) {
      const size = inputDims[j];
      if (j !== dim) {
        const index = num % size;
        resultIndex += index * resultMultiplier;
        resultMultiplier *= resultDims[j];
      }
      num = Math.floor(num / size);
    }
    result[resultIndex] += (inputData[i] - meanTensorData[resultIndex]) ** 2;
  }
  for (let i = 0; i < result.length; ++i) {
    result[i] = Math.sqrt(result[i] / (inputDims[dim] - correction));
  }
  if (!keepdim) {
    resultDims.splice(dim, 1);
  }
  const stdTensor = new Tensor3(input.type, result, resultDims);
  return [stdTensor, meanTensor];
}
function mean(input, dim = null, keepdim = false) {
  const inputData = (
    /** @type {Float32Array} */
    input.data
  );
  if (dim === null) {
    const val = inputData.reduce((a, b) => a + b, 0);
    return new Tensor3(
      input.type,
      [val / inputData.length],
      [
        /* scalar */
      ]
    );
  }
  const inputDims = input.dims;
  dim = safeIndex(dim, inputDims.length);
  const resultDims = inputDims.slice();
  resultDims[dim] = 1;
  const result = new inputData.constructor(inputData.length / inputDims[dim]);
  for (let i = 0; i < inputData.length; ++i) {
    let resultIndex = 0;
    for (let j = inputDims.length - 1, num = i, resultMultiplier = 1; j >= 0; --j) {
      const size = inputDims[j];
      if (j !== dim) {
        const index = num % size;
        resultIndex += index * resultMultiplier;
        resultMultiplier *= resultDims[j];
      }
      num = Math.floor(num / size);
    }
    result[resultIndex] += inputData[i];
  }
  if (inputDims[dim] !== 1) {
    for (let i = 0; i < result.length; ++i) {
      result[i] = result[i] / inputDims[dim];
    }
  }
  if (!keepdim) {
    resultDims.splice(dim, 1);
  }
  return new Tensor3(input.type, result, resultDims);
}
function dimsToStride(dims) {
  const stride = new Array(dims.length);
  for (let i = dims.length - 1, s2 = 1; i >= 0; --i) {
    stride[i] = s2;
    s2 *= dims[i];
  }
  return stride;
}
function fullHelper(size, fill_value, dtype, cls) {
  const numElements = size.reduce((a, b) => a * b, 1);
  return new Tensor3(
    dtype,
    new cls(numElements).fill(fill_value),
    size
  );
}
function full(size, fill_value) {
  let dtype;
  let typedArrayCls;
  if (typeof fill_value === "number") {
    dtype = "float32";
    typedArrayCls = Float32Array;
  } else if (typeof fill_value === "bigint") {
    dtype = "int64";
    typedArrayCls = BigInt64Array;
  } else if (typeof fill_value === "boolean") {
    dtype = "bool";
    typedArrayCls = Uint8Array;
  } else {
    throw new Error(`Unsupported data type: ${typeof fill_value}`);
  }
  return fullHelper(size, fill_value, dtype, typedArrayCls);
}
function full_like(tensor, fill_value) {
  return full(tensor.dims, fill_value);
}
function ones(size) {
  return fullHelper(size, 1n, "int64", BigInt64Array);
}
function ones_like(tensor) {
  return ones(tensor.dims);
}
function zeros(size) {
  return fullHelper(size, 0n, "int64", BigInt64Array);
}
function zeros_like(tensor) {
  return zeros(tensor.dims);
}
function quantize_embeddings(tensor, precision) {
  if (tensor.dims.length !== 2) {
    throw new Error("The tensor must have 2 dimensions");
  }
  if (tensor.dims.at(-1) % 8 !== 0) {
    throw new Error("The last dimension of the tensor must be a multiple of 8");
  }
  if (!["binary", "ubinary"].includes(precision)) {
    throw new Error("The precision must be either 'binary' or 'ubinary'");
  }
  const signed = precision === "binary";
  const dtype = signed ? "int8" : "uint8";
  const cls = signed ? Int8Array : Uint8Array;
  const inputData = tensor.data;
  const outputData = new cls(inputData.length / 8);
  for (let i = 0; i < inputData.length; ++i) {
    const bit = inputData[i] > 0 ? 1 : 0;
    const arrayIndex = Math.floor(i / 8);
    const bitPosition = i % 8;
    outputData[arrayIndex] |= bit << 7 - bitPosition;
    if (signed && bitPosition === 0) {
      outputData[arrayIndex] -= 128;
    }
  }
  return new Tensor3(dtype, outputData, [tensor.dims[0], tensor.dims[1] / 8]);
}

// src/libs/transformers/utils/data-structures.ts
var PriorityQueue = class {
  constructor(comparator = (a, b) => a > b, maxSize = Infinity) {
    this._heap = [];
    this._comparator = comparator;
    this._maxSize = maxSize;
  }
  /**
   * The size of the queue
   */
  get size() {
    return this._heap.length;
  }
  /**
   * Check if the queue is empty.
   * @returns {boolean} `true` if the queue is empty, `false` otherwise.
   */
  isEmpty() {
    return this.size === 0;
  }
  /**
   * Return the element with the highest priority in the queue.
   * @returns {any} The highest priority element in the queue.
   */
  peek() {
    return this._heap[0];
  }
  /**
   * Add one or more elements to the queue.
   * @param  {...any} values The values to push into the queue.
   * @returns {number} The new size of the queue.
   */
  push(...values) {
    return this.extend(values);
  }
  /**
   * Add multiple elements to the queue.
   * @param {any[]} values The values to push into the queue.
   * @returns {number} The new size of the queue.
   */
  extend(values) {
    for (const value of values) {
      if (this.size < this._maxSize) {
        this._heap.push(value);
        this._siftUp();
      } else {
        const smallest = this._smallest();
        if (this._comparator(value, this._heap[smallest])) {
          this._heap[smallest] = value;
          this._siftUpFrom(smallest);
        }
      }
    }
    return this.size;
  }
  /**
   * Remove and return the element with the highest priority in the queue.
   * @returns {any} The element with the highest priority in the queue.
   */
  pop() {
    const poppedValue = this.peek();
    const bottom = this.size - 1;
    if (bottom > 0) {
      this._swap(0, bottom);
    }
    this._heap.pop();
    this._siftDown();
    return poppedValue;
  }
  /**
   * Replace the element with the highest priority in the queue with a new value.
   * @param {*} value The new value.
   * @returns {*} The replaced value.
   */
  replace(value) {
    const replacedValue = this.peek();
    this._heap[0] = value;
    this._siftDown();
    return replacedValue;
  }
  /**
   * Compute the index for the parent of the node at index `i`.
   * @param {number} i The index of the node to get the parent of.
   * @returns {number} The index of the parent node.
   * @private
   */
  _parent(i) {
    return (i + 1 >>> 1) - 1;
  }
  /**
   * Compute the index for the left child of the node at index `i`.
   * @param {number} i The index of the node to get the left child of.
   * @returns {number} The index of the left child.
   * @private
   */
  _left(i) {
    return (i << 1) + 1;
  }
  /**
   * Compute the index for the right child of the node at index `i`.
   * @param {number} i The index of the node to get the right child of.
   * @returns {number} The index of the right child.
   * @private
   */
  _right(i) {
    return i + 1 << 1;
  }
  /**
   * Check if the element at index `i` is greater than the element at index `j`.
   * @param {number} i The index of the first element to compare.
   * @param {number} j The index of the second element to compare.
   * @returns {boolean} `true` if the element at index `i` is greater than the element at index `j`, `false` otherwise.
   * @private
   */
  _greater(i, j) {
    return this._comparator(this._heap[i], this._heap[j]);
  }
  /**
   * Swap the elements at indices `i` and `j`.
   * @param {number} i The index of the first element to swap.
   * @param {number} j The index of the second element to swap.
   * @private
   */
  _swap(i, j) {
    const temp = this._heap[i];
    this._heap[i] = this._heap[j];
    this._heap[j] = temp;
  }
  /**
   * Maintain the heap property by updating positions in the heap,
   * starting at the last element and moving up the heap.
   * @private
   */
  _siftUp() {
    this._siftUpFrom(this.size - 1);
  }
  /**
   * Helper function to sift up from a given node.
   * @param {number} node The index of the node to start sifting up from.
   */
  _siftUpFrom(node) {
    while (node > 0 && this._greater(node, this._parent(node))) {
      this._swap(node, this._parent(node));
      node = this._parent(node);
    }
  }
  /**
   * Maintain the heap property by updating positions in the heap,
   * starting at the first element and moving down the heap.
   * @private
   */
  _siftDown() {
    let node = 0;
    while (this._left(node) < this.size && this._greater(this._left(node), node) || this._right(node) < this.size && this._greater(this._right(node), node)) {
      const maxChild = this._right(node) < this.size && this._greater(this._right(node), this._left(node)) ? this._right(node) : this._left(node);
      this._swap(node, maxChild);
      node = maxChild;
    }
  }
  /**
   * Get the index of the smallest element in the heap. Since we use an array-based heap,
   * the index can be computed without needing to traverse the heap.
   * @private
   */
  _smallest() {
    return 2 ** Math.floor(Math.log2(this.size)) - 1;
  }
};
var CharTrie = class {
  constructor() {
    this._root = CharTrieNode.default();
  }
  /**
   * Adds one or more `texts` to the trie.
   * @param {string[]} texts The strings to add to the trie.
   */
  extend(texts) {
    for (const text of texts) {
      this.push(text);
    }
  }
  /**
   * Adds text to the trie.
   * @param {string} text The string to add to the trie.
   */
  push(text) {
    let node = this._root;
    for (const ch of text) {
      let child = node.children.get(ch);
      if (child === undefined) {
        child = CharTrieNode.default();
        node.children.set(ch, child);
      }
      node = child;
    }
    node.isLeaf = true;
  }
  /**
   * Searches the trie for all strings with a common prefix of `text`.
   * @param {string} text The common prefix to search for.
   * @yields {string} Each string in the trie that has `text` as a prefix.
   */
  *commonPrefixSearch(text) {
    let node = this._root;
    if (node === undefined) return;
    let prefix = "";
    for (const ch of text) {
      prefix += ch;
      const nextNode = node.children.get(ch);
      if (nextNode === undefined) return;
      node = nextNode;
      if (node.isLeaf) {
        yield prefix;
      }
    }
  }
};
var CharTrieNode = class _CharTrieNode {
  constructor(isLeaf, children) {
    this.isLeaf = isLeaf;
    this.children = children;
  }
  /**
   * Returns a new `CharTrieNode` instance with default values.
   * @returns {CharTrieNode} A new `CharTrieNode` instance with `isLeaf` set to `false` and an empty `children` map.
   */
  static default() {
    return new _CharTrieNode(false, /* @__PURE__ */ new Map());
  }
};
var TokenLattice = class {
  constructor(sentence, bosTokenId, eosTokenId) {
    this._sentence = sentence;
    this._chars = Array.from(sentence);
    this._len = this._chars.length;
    this._bosTokenId = bosTokenId;
    this._eosTokenId = eosTokenId;
    this._nodes = [];
    this.beginNodes = Array.from({ length: this._len + 1 }, () => []);
    this.endNodes = Array.from({ length: this._len + 1 }, () => []);
    const bos = new TokenLatticeNode(this._bosTokenId, 0, 0, 0, 0);
    const eos = new TokenLatticeNode(this._eosTokenId, 1, this._len, 0, 0);
    this._nodes.push(bos.clone());
    this._nodes.push(eos.clone());
    this.beginNodes[this._len].push(eos);
    this.endNodes[0].push(bos);
  }
  /**
   * Inserts a new token node into the token lattice.
   *
   * @param {number} pos The starting position of the token.
   * @param {number} length The length of the token.
   * @param {number} score The score of the token.
   * @param {number} tokenId The token ID of the token.
   */
  insert(pos, length, score, tokenId) {
    const nodeId = this._nodes.length;
    const node = new TokenLatticeNode(tokenId, nodeId, pos, length, score);
    this.beginNodes[pos].push(node);
    this.endNodes[pos + length].push(node);
    this._nodes.push(node);
  }
  /**
   * Implements the Viterbi algorithm to compute the most likely sequence of tokens.
   *
   * @returns {TokenLatticeNode[]} The most likely sequence of tokens.
   */
  viterbi() {
    const len2 = this._len;
    let pos = 0;
    while (pos <= len2) {
      if (this.beginNodes[pos].length == 0) {
        return [];
      }
      for (let rnode of this.beginNodes[pos]) {
        rnode.prev = null;
        let bestScore = 0;
        let bestNode = null;
        for (let lnode of this.endNodes[pos]) {
          const score = lnode.backtraceScore + rnode.score;
          if (bestNode === null || score > bestScore) {
            bestNode = lnode.clone();
            bestScore = score;
          }
        }
        if (bestNode !== null) {
          rnode.prev = bestNode;
          rnode.backtraceScore = bestScore;
        } else {
          return [];
        }
      }
      ++pos;
    }
    const results = [];
    const root = this.beginNodes[len2][0];
    const prev = root.prev;
    if (prev === null) {
      return [];
    }
    let node = prev.clone();
    while (node.prev !== null) {
      results.push(node.clone());
      const n = node.clone();
      if (!n.prev) break;
      node = n.prev.clone();
    }
    results.reverse();
    return results;
  }
  /**
   * @param {TokenLatticeNode} node
   * @returns {string} The array of nodes representing the most likely sequence of tokens.
   */
  piece(node) {
    return this._chars.slice(node.pos, node.pos + node.length).join("");
  }
  /**
   * @returns {string[]} The most likely sequence of tokens.
   */
  tokens() {
    const nodes = this.viterbi();
    return nodes.map((x) => this.piece(x));
  }
  /**
   * @returns {number[]} The most likely sequence of token ids.
   */
  tokenIds() {
    const nodes = this.viterbi();
    return nodes.map((x) => x.tokenId);
  }
};
var TokenLatticeNode = class _TokenLatticeNode {
  constructor(tokenId, nodeId, pos, length, score) {
    this.tokenId = tokenId;
    this.nodeId = nodeId;
    this.pos = pos;
    this.length = length;
    this.score = score;
    this.prev = null;
    this.backtraceScore = 0;
  }
  /**
   * Returns a clone of this node.
   * @returns {TokenLatticeNode} A clone of this node.
   */
  clone() {
    const n = new _TokenLatticeNode(this.tokenId, this.nodeId, this.pos, this.length, this.score);
    n.prev = this.prev;
    n.backtraceScore = this.backtraceScore;
    return n;
  }
};

// src/libs/transformers/models/whisper/common_whisper.ts
var WHISPER_LANGUAGES = [
  ["en", "english"],
  ["zh", "chinese"],
  ["de", "german"],
  ["es", "spanish"],
  ["ru", "russian"],
  ["ko", "korean"],
  ["fr", "french"],
  ["ja", "japanese"],
  ["pt", "portuguese"],
  ["tr", "turkish"],
  ["pl", "polish"],
  ["ca", "catalan"],
  ["nl", "dutch"],
  ["ar", "arabic"],
  ["sv", "swedish"],
  ["it", "italian"],
  ["id", "indonesian"],
  ["hi", "hindi"],
  ["fi", "finnish"],
  ["vi", "vietnamese"],
  ["he", "hebrew"],
  ["uk", "ukrainian"],
  ["el", "greek"],
  ["ms", "malay"],
  ["cs", "czech"],
  ["ro", "romanian"],
  ["da", "danish"],
  ["hu", "hungarian"],
  ["ta", "tamil"],
  ["no", "norwegian"],
  ["th", "thai"],
  ["ur", "urdu"],
  ["hr", "croatian"],
  ["bg", "bulgarian"],
  ["lt", "lithuanian"],
  ["la", "latin"],
  ["mi", "maori"],
  ["ml", "malayalam"],
  ["cy", "welsh"],
  ["sk", "slovak"],
  ["te", "telugu"],
  ["fa", "persian"],
  ["lv", "latvian"],
  ["bn", "bengali"],
  ["sr", "serbian"],
  ["az", "azerbaijani"],
  ["sl", "slovenian"],
  ["kn", "kannada"],
  ["et", "estonian"],
  ["mk", "macedonian"],
  ["br", "breton"],
  ["eu", "basque"],
  ["is", "icelandic"],
  ["hy", "armenian"],
  ["ne", "nepali"],
  ["mn", "mongolian"],
  ["bs", "bosnian"],
  ["kk", "kazakh"],
  ["sq", "albanian"],
  ["sw", "swahili"],
  ["gl", "galician"],
  ["mr", "marathi"],
  ["pa", "punjabi"],
  ["si", "sinhala"],
  ["km", "khmer"],
  ["sn", "shona"],
  ["yo", "yoruba"],
  ["so", "somali"],
  ["af", "afrikaans"],
  ["oc", "occitan"],
  ["ka", "georgian"],
  ["be", "belarusian"],
  ["tg", "tajik"],
  ["sd", "sindhi"],
  ["gu", "gujarati"],
  ["am", "amharic"],
  ["yi", "yiddish"],
  ["lo", "lao"],
  ["uz", "uzbek"],
  ["fo", "faroese"],
  ["ht", "haitian creole"],
  ["ps", "pashto"],
  ["tk", "turkmen"],
  ["nn", "nynorsk"],
  ["mt", "maltese"],
  ["sa", "sanskrit"],
  ["lb", "luxembourgish"],
  ["my", "myanmar"],
  ["bo", "tibetan"],
  ["tl", "tagalog"],
  ["mg", "malagasy"],
  ["as", "assamese"],
  ["tt", "tatar"],
  ["haw", "hawaiian"],
  ["ln", "lingala"],
  ["ha", "hausa"],
  ["ba", "bashkir"],
  ["jw", "javanese"],
  ["su", "sundanese"]
];
var WHISPER_LANGUAGE_MAPPING = new Map(WHISPER_LANGUAGES);
var WHISPER_TO_LANGUAGE_CODE_MAPPING = new Map([
  ...WHISPER_LANGUAGES.map(([k, v]) => [v, k]),
  ...[
    ["burmese", "my"],
    ["valencian", "ca"],
    ["flemish", "nl"],
    ["haitian", "ht"],
    ["letzeburgesch", "lb"],
    ["pushto", "ps"],
    ["panjabi", "pa"],
    ["moldavian", "ro"],
    ["moldovan", "ro"],
    ["sinhalese", "si"],
    ["castilian", "es"]
  ]
]);
function whisper_language_to_code(language) {
  language = language.toLowerCase();
  let language_code = WHISPER_TO_LANGUAGE_CODE_MAPPING.get(language);
  if (language_code === undefined) {
    if (WHISPER_LANGUAGE_MAPPING.has(language)) {
      language_code = language;
    } else {
      const is_language_code = language.length === 2;
      const langs = is_language_code ? WHISPER_LANGUAGE_MAPPING.keys() : WHISPER_LANGUAGE_MAPPING.values();
      throw new Error(`Language "${language}" is not supported. Must be one of: ${JSON.stringify(langs)}`);
    }
  }
  return language_code;
}

// src/libs/transformers/tokenizers.ts
async function loadTokenizer(pretrained_model_name_or_path, options) {
  const info = await Promise.all([
    getModelJSON(pretrained_model_name_or_path, "tokenizer.json", true, options),
    getModelJSON(pretrained_model_name_or_path, "tokenizer_config.json", true, options)
  ]);
  if (options.legacy !== null) {
    info[1].legacy = options.legacy;
  }
  return info;
}
function regexSplit(text, regex) {
  const result = [];
  let prev = 0;
  for (const match of text.matchAll(regex)) {
    const fullMatch = match[0];
    if (prev < match.index) {
      result.push(text.slice(prev, match.index));
    }
    if (fullMatch.length > 0) {
      result.push(fullMatch);
    }
    prev = match.index + fullMatch.length;
  }
  if (prev < text.length) {
    result.push(text.slice(prev));
  }
  return result;
}
function createPattern(pattern, invert = true) {
  if (pattern.Regex !== undefined) {
    let regex = pattern.Regex.replace(/\\([#&~])/g, "$1");
    for (const [key, value] of PROBLEMATIC_REGEX_MAP) {
      regex = regex.replaceAll(key, value);
    }
    return new RegExp(regex, "gu");
  } else if (pattern.String !== undefined) {
    const escaped = escapeRegExp(pattern.String);
    return new RegExp(invert ? escaped : `(${escaped})`, "gu");
  } else {
    console.warn("Unknown pattern type:", pattern);
    return null;
  }
}
function objectToMap(obj) {
  return new Map(Object.entries(obj));
}
function prepareTensorForDecode(tensor) {
  const dims = tensor.dims;
  switch (dims.length) {
    case 1:
      return tensor.tolist();
    case 2:
      if (dims[0] !== 1) {
        throw new Error(
          "Unable to decode tensor with `batch size !== 1`. Use `tokenizer.batch_decode(...)` for batched inputs."
        );
      }
      return tensor.tolist()[0];
    default:
      throw new Error(`Expected tensor to have 1-2 dimensions, got ${dims.length}.`);
  }
}
function clean_up_tokenization(text) {
  return text.replace(/ \./g, ".").replace(/ \?/g, "?").replace(/ \!/g, "!").replace(/ ,/g, ",").replace(/ \' /g, "'").replace(/ n\'t/g, "n't").replace(/ \'m/g, "'m").replace(/ \'s/g, "'s").replace(/ \'ve/g, "'ve").replace(/ \'re/g, "'re");
}
function remove_accents(text) {
  return text.replace(/\p{M}/gu, "");
}
function lowercase_and_remove_accent(text) {
  return remove_accents(text.toLowerCase());
}
function is_chinese_char(cp) {
  return cp >= 19968 && cp <= 40959 || cp >= 13312 && cp <= 19903 || cp >= 131072 && cp <= 173791 || cp >= 173824 && cp <= 177983 || cp >= 177984 && cp <= 178207 || cp >= 178208 && cp <= 183983 || cp >= 63744 && cp <= 64255 || cp >= 194560 && cp <= 195103;
}
function fuse_unk(arr, tokens_to_ids, unk_token_id) {
  const fused = [];
  let i = 0;
  while (i < arr.length) {
    fused.push(arr[i]);
    if ((tokens_to_ids.get(arr[i]) ?? unk_token_id) !== unk_token_id) {
      ++i;
      continue;
    }
    while (++i < arr.length && (tokens_to_ids.get(arr[i]) ?? unk_token_id) === unk_token_id) {
      if (tokens_to_ids.get(fused.at(-1)) !== unk_token_id) {
        fused[fused.length - 1] += arr[i];
      }
    }
  }
  return fused;
}
function whitespace_split(text) {
  return text.match(/\S+/g) || [];
}
var PUNCTUATION_REGEX = "\\p{P}\\u0021-\\u002F\\u003A-\\u0040\\u005B-\\u0060\\u007B-\\u007E";
var PUNCTUATION_ONLY_REGEX = new RegExp(`^[${PUNCTUATION_REGEX}]+$`, "gu");
var BLOOM_SPLIT_CHARS = ".,!?\u2026\u3002\uFF0C\u3001\u0964\u06D4\u060C";
var PROBLEMATIC_REGEX_MAP = /* @__PURE__ */ new Map([
  // This uses the case insensitive group modifier, which is not supported in JavaScript.
  // When parsing the regex, an "Invalid group" error is thrown.
  ["(?i:'s|'t|'re|'ve|'m|'ll|'d)", "(?:'([sS]|[tT]|[rR][eE]|[vV][eE]|[mM]|[lL][lL]|[dD]))"],
  // Used to override the default (invalid) regex of the bloom pretokenizer.
  // For more information, see https://github.com/huggingface/transformers.js/issues/94
  [` ?[^(\\s|[${BLOOM_SPLIT_CHARS}])]+`, ` ?[^\\s${BLOOM_SPLIT_CHARS}]+`]
]);
var AddedToken = class {
  constructor(config) {
    this.content = config.content;
    this.id = config.id;
    this.single_word = config.single_word ?? false;
    this.lstrip = config.lstrip ?? false;
    this.rstrip = config.rstrip ?? false;
    this.special = config.special ?? false;
    this.normalized = config.normalized ?? false;
  }
};
var TokenizerModel = class extends Callable {
  constructor(config) {
    super();
    this.config = config;
    this.vocab = [];
    this.tokens_to_ids = /* @__PURE__ */ new Map();
    this.unk_token_id = undefined;
    this.unk_token = undefined;
    this.end_of_word_suffix = undefined;
    this.fuse_unk = this.config.fuse_unk ?? false;
  }
  /**
   * Instantiates a new TokenizerModel instance based on the configuration object provided.
   * @param {Object} config The configuration object for the TokenizerModel.
   * @param {...*} args Optional arguments to pass to the specific TokenizerModel constructor.
   * @returns {TokenizerModel} A new instance of a TokenizerModel.
   * @throws Will throw an error if the TokenizerModel type in the config is not recognized.
   */
  static fromConfig(config, ...args) {
    switch (config.type) {
      case "WordPiece":
        return new WordPieceTokenizer(config);
      case "Unigram":
        return new Unigram(config, ...args);
      case "BPE":
        return new BPE(config);
      default:
        if (config.vocab) {
          if (Array.isArray(config.vocab)) {
            return new Unigram(config, ...args);
          } else {
            return new LegacyTokenizerModel(config, ...args);
          }
        }
        throw new Error(`Unknown TokenizerModel type: ${config.type}`);
    }
  }
  /**
   * Internal function to call the TokenizerModel instance.
   * @param {string[]} tokens The tokens to encode.
   * @returns {string[]} The encoded tokens.
   */
  _call(tokens) {
    tokens = this.encode(tokens);
    if (this.fuse_unk) {
      tokens = fuse_unk(tokens, this.tokens_to_ids, this.unk_token_id);
    }
    return tokens;
  }
  /**
   * Encodes a list of tokens into a list of token IDs.
   * @param {string[]} tokens The tokens to encode.
   * @returns {string[]} The encoded tokens.
   * @throws Will throw an error if not implemented in a subclass.
   */
  encode(tokens) {
    throw Error("encode should be implemented in subclass.");
  }
  /**
   * Converts a list of tokens into a list of token IDs.
   * @param {string[]} tokens The tokens to convert.
   * @returns {number[]} The converted token IDs.
   */
  convert_tokens_to_ids(tokens) {
    return tokens.map((t) => this.tokens_to_ids.get(t) ?? this.unk_token_id);
  }
  /**
   * Converts a list of token IDs into a list of tokens.
   * @param {number[]|bigint[]} ids The token IDs to convert.
   * @returns {string[]} The converted tokens.
   */
  convert_ids_to_tokens(ids) {
    return ids.map((i) => this.vocab[i] ?? this.unk_token);
  }
};
var WordPieceTokenizer = class extends TokenizerModel {
  constructor(config) {
    super(config);
    this.tokens_to_ids = objectToMap(config.vocab);
    this.unk_token_id = this.tokens_to_ids.get(config.unk_token);
    this.unk_token = config.unk_token;
    this.max_input_chars_per_word = config.max_input_chars_per_word ?? 100;
    this.vocab = new Array(this.tokens_to_ids.size);
    for (const [key, value] of this.tokens_to_ids) {
      this.vocab[value] = key;
    }
  }
  /**
   * Encodes an array of tokens using WordPiece encoding.
   * @param {string[]} tokens The tokens to encode.
   * @returns {string[]} An array of encoded tokens.
   */
  encode(tokens) {
    const outputTokens = [];
    for (const token of tokens) {
      const chars = [...token];
      if (chars.length > this.max_input_chars_per_word) {
        outputTokens.push(this.unk_token);
        continue;
      }
      let isUnknown = false;
      let start = 0;
      const subTokens = [];
      while (start < chars.length) {
        let end = chars.length;
        let currentSubstring = null;
        while (start < end) {
          let substr = chars.slice(start, end).join("");
          if (start > 0) {
            substr = this.config.continuing_subword_prefix + substr;
          }
          if (this.tokens_to_ids.has(substr)) {
            currentSubstring = substr;
            break;
          }
          --end;
        }
        if (currentSubstring === null) {
          isUnknown = true;
          break;
        }
        subTokens.push(currentSubstring);
        start = end;
      }
      if (isUnknown) {
        outputTokens.push(this.unk_token);
      } else {
        outputTokens.push(...subTokens);
      }
    }
    return outputTokens;
  }
};
var Unigram = class extends TokenizerModel {
  constructor(config, moreConfig) {
    super(config);
    const vocabSize = config.vocab.length;
    this.vocab = new Array(vocabSize);
    this.scores = new Array(vocabSize);
    for (let i = 0; i < vocabSize; ++i) {
      [this.vocab[i], this.scores[i]] = config.vocab[i];
    }
    this.unk_token_id = config.unk_id;
    this.unk_token = this.vocab[config.unk_id];
    this.tokens_to_ids = new Map(this.vocab.map((x, i) => [x, i]));
    this.bos_token = " ";
    this.bos_token_id = this.tokens_to_ids.get(this.bos_token);
    this.eos_token = moreConfig.eos_token;
    this.eos_token_id = this.tokens_to_ids.get(this.eos_token);
    this.unk_token = this.vocab[this.unk_token_id];
    this.minScore = min(this.scores)[0];
    this.unk_score = Number(this.minScore) - 10;
    this.scores[this.unk_token_id] = this.unk_score;
    this.trie = new CharTrie();
    this.trie.extend(this.vocab);
    this.fuse_unk = true;
  }
  /**
   * Populates lattice nodes.
   * @param {TokenLattice} lattice The token lattice to populate with nodes.
   */
  populateNodes(lattice) {
    const chars = lattice._chars;
    const mblen = 1;
    let beginPos = 0;
    while (beginPos < chars.length) {
      let hasSingleNode = false;
      const sliced = chars.slice(beginPos).join("");
      const prefixedTokens = this.trie.commonPrefixSearch(sliced);
      for (const token of prefixedTokens) {
        const tokenId = this.tokens_to_ids.get(token);
        const tokenScore = this.scores[tokenId];
        const n = len(token);
        lattice.insert(beginPos, n, tokenScore, tokenId);
        if (!hasSingleNode && n === mblen) {
          hasSingleNode = true;
        }
      }
      if (!hasSingleNode) {
        lattice.insert(beginPos, mblen, Number(this.unk_score), this.unk_token_id);
      }
      beginPos += mblen;
    }
  }
  /**
   * Encodes an array of tokens into an array of subtokens using the unigram model.
   *
   * @param {string} normalized The normalized string.
   * @returns {string[]} An array of subtokens obtained by encoding the input tokens using the unigram model.
   */
  tokenize(normalized) {
    const lattice = new TokenLattice(normalized, this.bos_token_id, this.eos_token_id);
    this.populateNodes(lattice);
    return lattice.tokens();
  }
  /**
   * Encodes an array of tokens using Unigram encoding.
   * @param {string[]} tokens The tokens to encode.
   * @returns {string[]} An array of encoded tokens.
   */
  encode(tokens) {
    const toReturn = [];
    for (const token of tokens) {
      const tokenized = this.tokenize(token);
      toReturn.push(...tokenized);
    }
    return toReturn;
  }
};
var BYTES_TO_UNICODE = (() => {
  const bs = [
    ...Array.from({ length: "~".charCodeAt(0) - "!".charCodeAt(0) + 1 }, (_, i) => i + "!".charCodeAt(0)),
    ...Array.from({ length: "\xAC".charCodeAt(0) - "\xA1".charCodeAt(0) + 1 }, (_, i) => i + "\xA1".charCodeAt(0)),
    ...Array.from({ length: "\xFF".charCodeAt(0) - "\xAE".charCodeAt(0) + 1 }, (_, i) => i + "\xAE".charCodeAt(0))
  ];
  const cs = bs.slice();
  let n = 0;
  for (let b = 0; b < 256; ++b) {
    if (!bs.includes(b)) {
      bs.push(b);
      cs.push(256 + n);
      n += 1;
    }
  }
  const ccs = cs.map((n2) => String.fromCharCode(n2));
  return Object.fromEntries(bs.map((b, i) => [b, ccs[i]]));
})();
var UNICODE_TO_BYTES = reverseDictionary(BYTES_TO_UNICODE);
var BPE = class extends TokenizerModel {
  constructor(config) {
    super(config);
    this.tokens_to_ids = objectToMap(config.vocab);
    this.unk_token_id = this.tokens_to_ids.get(config.unk_token);
    this.unk_token = config.unk_token;
    this.vocab = new Array(this.tokens_to_ids.size);
    for (const [key, value] of this.tokens_to_ids) {
      this.vocab[value] = key;
    }
    const use_new_merge_format = Array.isArray(config.merges[0]);
    this.merges = use_new_merge_format ? (
      /** @type {[string, string][]} */
      config.merges
    ) : config.merges.map((x) => (
      /** @type {[string, string]} */
      x.split(" ", 2)
    ));
    this.bpe_ranks = new Map(this.merges.map((x, i) => [JSON.stringify(x), i]));
    this.end_of_word_suffix = config.end_of_word_suffix;
    this.continuing_subword_suffix = config.continuing_subword_suffix ?? null;
    this.byte_fallback = this.config.byte_fallback ?? false;
    if (this.byte_fallback) {
      this.text_encoder = new TextEncoder();
    }
    this.ignore_merges = this.config.ignore_merges ?? false;
    this.cache = /* @__PURE__ */ new Map();
  }
  /**
   * Apply Byte-Pair-Encoding (BPE) to a given token. Efficient heap-based priority
   * queue implementation adapted from https://github.com/belladoreai/llama-tokenizer-js.
   * @param {string} token The token to encode.
   * @returns {string[]} The BPE encoded tokens.
   */
  bpe(token) {
    if (token.length === 0) {
      return [];
    }
    const cached = this.cache.get(token);
    if (cached !== undefined) {
      return cached;
    }
    const word = Array.from(token);
    if (this.end_of_word_suffix) {
      word[word.length - 1] += this.end_of_word_suffix;
    }
    let result = [];
    if (word.length > 1) {
      const queue = new PriorityQueue((a, b) => a.score < b.score);
      let startingNode = {
        token: word[0],
        bias: 0,
        prev: null,
        next: null
      };
      let previousNode = startingNode;
      for (let i = 1; i < word.length; ++i) {
        const currentNode = {
          bias: i / word.length,
          // Add fractional component to break ties
          token: word[i],
          prev: previousNode,
          next: null
        };
        previousNode.next = currentNode;
        this._add_node(queue, previousNode);
        previousNode = currentNode;
      }
      while (!queue.isEmpty()) {
        const node = queue.pop();
        if (node.deleted || !node.next || node.next.deleted) continue;
        node.deleted = true;
        node.next.deleted = true;
        if (node.prev) {
          const newPreviousNode = { ...node.prev };
          node.prev.deleted = true;
          node.prev = newPreviousNode;
          if (newPreviousNode.prev) {
            newPreviousNode.prev.next = newPreviousNode;
          } else {
            startingNode = newPreviousNode;
          }
        }
        const merged = {
          token: node.token + node.next.token,
          bias: node.bias,
          prev: node.prev,
          next: node.next.next
        };
        if (merged.prev) {
          merged.prev.next = merged;
          this._add_node(queue, merged.prev);
        } else {
          startingNode = merged;
        }
        if (merged.next) {
          merged.next.prev = merged;
          this._add_node(queue, merged);
        }
      }
      for (let currentNode = startingNode; currentNode !== null; currentNode = currentNode.next) {
        result.push(currentNode.token);
      }
    } else {
      result = word;
    }
    if (this.continuing_subword_suffix) {
      for (let i = 0; i < result.length - 1; ++i) {
        result[i] += this.continuing_subword_suffix;
      }
    }
    this.cache.set(token, result);
    return result;
  }
  /**
   * Helper function to add a node to the priority queue.
   * @param {PriorityQueue} queue
   * @param {BPENode} node
   * @private
   */
  _add_node(queue, node) {
    const rank = this.bpe_ranks.get(JSON.stringify([node.token, node.next.token]));
    if (rank !== undefined) {
      node.score = rank + node.bias;
      queue.push(node);
    }
  }
  /**
   * Encodes the input sequence of tokens using the BPE algorithm and returns the resulting subword tokens.
   * @param {string[]} tokens The input sequence of tokens to encode.
   * @returns {string[]} The resulting subword tokens after applying the BPE algorithm to the input sequence of tokens.
   */
  encode(tokens) {
    const outputTokens = [];
    for (const token of tokens) {
      if (this.ignore_merges && this.tokens_to_ids.has(token)) {
        outputTokens.push(token);
        continue;
      }
      const bpe_token_list = this.bpe(token);
      for (const t of bpe_token_list) {
        if (this.tokens_to_ids.has(t)) {
          outputTokens.push(t);
        } else if (this.byte_fallback) {
          const byteTokens = Array.from(this.text_encoder.encode(t)).map(
            (x) => `<0x${x.toString(16).toUpperCase().padStart(2, "0")}>`
          );
          if (byteTokens.every((x) => this.tokens_to_ids.has(x))) {
            outputTokens.push(...byteTokens);
          } else {
            outputTokens.push(this.unk_token);
          }
        } else {
          outputTokens.push(this.unk_token);
        }
      }
    }
    return outputTokens;
  }
};
var LegacyTokenizerModel = class extends TokenizerModel {
  constructor(config, moreConfig) {
    super(config);
    this.tokens_to_ids = objectToMap(
      moreConfig.target_lang ? config.vocab[moreConfig.target_lang] : config.vocab
    );
    this.bos_token = moreConfig.bos_token;
    this.bos_token_id = this.tokens_to_ids.get(this.bos_token);
    this.eos_token = moreConfig.eos_token;
    this.eos_token_id = this.tokens_to_ids.get(this.eos_token);
    this.pad_token = moreConfig.pad_token;
    this.pad_token_id = this.tokens_to_ids.get(this.pad_token);
    this.unk_token = moreConfig.unk_token;
    this.unk_token_id = this.tokens_to_ids.get(this.unk_token);
    this.vocab = new Array(this.tokens_to_ids.size);
    for (const [key, value] of this.tokens_to_ids) {
      this.vocab[value] = key;
    }
  }
  encode(tokens) {
    return tokens;
  }
};
var Normalizer = class extends Callable {
  constructor(config) {
    super();
    this.config = config;
  }
  /**
   * Factory method for creating normalizers from config objects.
   * @static
   * @param {Object} config The configuration object for the normalizer.
   * @returns {Normalizer} A Normalizer object.
   * @throws {Error} If an unknown Normalizer type is specified in the config.
   */
  static fromConfig(config) {
    if (config === null) return null;
    switch (config.type) {
      case "BertNormalizer":
        return new BertNormalizer(config);
      case "Precompiled":
        return new Precompiled(config);
      case "Sequence":
        return new NormalizerSequence(config);
      case "Replace":
        return new Replace(config);
      case "NFC":
        return new NFC(config);
      case "NFKC":
        return new NFKC(config);
      case "NFKD":
        return new NFKD(config);
      case "Strip":
        return new StripNormalizer(config);
      case "StripAccents":
        return new StripAccents(config);
      case "Lowercase":
        return new Lowercase(config);
      case "Prepend":
        return new Prepend(config);
      default:
        throw new Error(`Unknown Normalizer type: ${config.type}`);
    }
  }
  /**
   * Normalize the input text.
   * @abstract
   * @param {string} text The text to normalize.
   * @returns {string} The normalized text.
   * @throws {Error} If this method is not implemented in a subclass.
   */
  normalize(text) {
    throw Error("normalize should be implemented in subclass.");
  }
  /**
   * Alias for {@link Normalizer#normalize}.
   * @param {string} text The text to normalize.
   * @returns {string} The normalized text.
   */
  _call(text) {
    return this.normalize(text);
  }
};
var Replace = class extends Normalizer {
  /**
   * Normalize the input text by replacing the pattern with the content.
   * @param {string} text The input text to be normalized.
   * @returns {string} The normalized text after replacing the pattern with the content.
   */
  normalize(text) {
    const pattern = createPattern(this.config.pattern);
    return pattern === null ? text : text.replaceAll(pattern, this.config.content);
  }
};
var NFC = class extends Normalizer {
  /**
   * Normalize the input text by applying Unicode normalization form C (NFC).
   * @param {string} text The input text to be normalized.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    text = text.normalize("NFC");
    return text;
  }
};
var NFKC = class extends Normalizer {
  /**
   * Normalize text using NFKC normalization.
   * @param {string} text The text to be normalized.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    text = text.normalize("NFKC");
    return text;
  }
};
var NFKD = class extends Normalizer {
  /**
   * Normalize text using NFKD normalization.
   * @param {string} text The text to be normalized.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    text = text.normalize("NFKD");
    return text;
  }
};
var StripNormalizer = class extends Normalizer {
  /**
   * Strip leading and/or trailing whitespace from the input text.
   * @param {string} text The input text.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    if (this.config.strip_left && this.config.strip_right) {
      text = text.trim();
    } else {
      if (this.config.strip_left) {
        text = text.trimStart();
      }
      if (this.config.strip_right) {
        text = text.trimEnd();
      }
    }
    return text;
  }
};
var StripAccents = class extends Normalizer {
  /**
   * Remove all accents from the text.
   * @param {string} text The input text.
   * @returns {string} The normalized text without accents.
   */
  normalize(text) {
    text = remove_accents(text);
    return text;
  }
};
var Lowercase = class extends Normalizer {
  /**
   * Lowercases the input string.
   * @param {string} text The text to normalize.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    text = text.toLowerCase();
    return text;
  }
};
var Prepend = class extends Normalizer {
  /**
   * Prepends the input string.
   * @param {string} text The text to normalize.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    text = this.config.prepend + text;
    return text;
  }
};
var NormalizerSequence = class extends Normalizer {
  constructor(config) {
    super(config);
    this.normalizers = config.normalizers.map((x) => Normalizer.fromConfig(x));
  }
  /**
   * Apply a sequence of Normalizers to the input text.
   * @param {string} text The text to normalize.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    return this.normalizers.reduce((t, normalizer) => {
      return normalizer.normalize(t);
    }, text);
  }
};
var BertNormalizer = class extends Normalizer {
  /**
   * Adds whitespace around any CJK (Chinese, Japanese, or Korean) character in the input text.
   *
   * @param {string} text The input text to tokenize.
   * @returns {string} The tokenized text with whitespace added around CJK characters.
   */
  _tokenize_chinese_chars(text) {
    const output = [];
    for (let i = 0; i < text.length; ++i) {
      const char = text[i];
      const cp = char.charCodeAt(0);
      if (is_chinese_char(cp)) {
        output.push(" ");
        output.push(char);
        output.push(" ");
      } else {
        output.push(char);
      }
    }
    return output.join("");
  }
  /**
   * Strips accents from the given text.
   * @param {string} text The text to strip accents from.
   * @returns {string} The text with accents removed.
   */
  stripAccents(text) {
    return text.normalize("NFD").replace(/\p{Mn}/gu, "");
  }
  /**
   * Checks whether `char` is a control character.
   * @param {string} char The character to check.
   * @returns {boolean} Whether `char` is a control character.
   * @private
   */
  _is_control(char) {
    switch (char) {
      case "	":
      case "\n":
      case "\r":
        return false;
      default:
        return /^\p{Cc}|\p{Cf}|\p{Co}|\p{Cs}$/u.test(char);
    }
  }
  /**
   * Performs invalid character removal and whitespace cleanup on text.
   * @param {string} text The text to clean.
   * @returns {string} The cleaned text.
   * @private
   */
  _clean_text(text) {
    const output = [];
    for (const char of text) {
      const cp = char.charCodeAt(0);
      if (cp === 0 || cp === 65533 || this._is_control(char)) {
        continue;
      }
      if (/^\s$/.test(char)) {
        output.push(" ");
      } else {
        output.push(char);
      }
    }
    return output.join("");
  }
  /**
   * Normalizes the given text based on the configuration.
   * @param {string} text The text to normalize.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    if (this.config.clean_text) {
      text = this._clean_text(text);
    }
    if (this.config.handle_chinese_chars) {
      text = this._tokenize_chinese_chars(text);
    }
    if (this.config.lowercase) {
      text = text.toLowerCase();
      if (this.config.strip_accents !== false) {
        text = this.stripAccents(text);
      }
    } else if (this.config.strip_accents) {
      text = this.stripAccents(text);
    }
    return text;
  }
};
var PreTokenizer = class extends Callable {
  /**
   * Factory method that returns an instance of a subclass of `PreTokenizer` based on the provided configuration.
   *
   * @static
   * @param {Object} config A configuration object for the pre-tokenizer.
   * @returns {PreTokenizer} An instance of a subclass of `PreTokenizer`.
   * @throws {Error} If the provided configuration object does not correspond to any known pre-tokenizer.
   */
  static fromConfig(config) {
    if (config === null) return null;
    switch (config.type) {
      case "BertPreTokenizer":
        return new BertPreTokenizer(config);
      case "Sequence":
        return new PreTokenizerSequence(config);
      case "Whitespace":
        return new WhitespacePreTokenizer(config);
      case "WhitespaceSplit":
        return new WhitespaceSplit(config);
      case "Metaspace":
        return new MetaspacePreTokenizer(config);
      case "ByteLevel":
        return new ByteLevelPreTokenizer(config);
      case "Split":
        return new SplitPreTokenizer(config);
      case "Punctuation":
        return new PunctuationPreTokenizer(config);
      case "Digits":
        return new DigitsPreTokenizer(config);
      case "Replace":
        return new ReplacePreTokenizer(config);
      default:
        throw new Error(`Unknown PreTokenizer type: ${config.type}`);
    }
  }
  /**
   * Method that should be implemented by subclasses to define the specific pre-tokenization logic.
   *
   * @abstract
   * @param {string} text The text to pre-tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} The pre-tokenized text.
   * @throws {Error} If the method is not implemented in the subclass.
   */
  pre_tokenize_text(text, options) {
    throw Error("pre_tokenize_text should be implemented in subclass.");
  }
  /**
   * Tokenizes the given text into pre-tokens.
   * @param {string|string[]} text The text or array of texts to pre-tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of pre-tokens.
   */
  pre_tokenize(text, options) {
    return (Array.isArray(text) ? text.map((x) => this.pre_tokenize_text(x, options)) : [this.pre_tokenize_text(text, options)]).flat();
  }
  /**
   * Alias for {@link PreTokenizer#pre_tokenize}.
   * @param {string|string[]} text The text or array of texts to pre-tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of pre-tokens.
   */
  _call(text, options) {
    return this.pre_tokenize(text, options);
  }
};
var BertPreTokenizer = class extends PreTokenizer {
  constructor(config) {
    super();
    this.pattern = new RegExp(`[^\\s${PUNCTUATION_REGEX}]+|[${PUNCTUATION_REGEX}]`, "gu");
  }
  /**
   * Tokenizes a single text using the BERT pre-tokenization scheme.
   *
   * @param {string} text The text to tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens.
   */
  pre_tokenize_text(text, options) {
    return text.trim().match(this.pattern) || [];
  }
};
var ByteLevelPreTokenizer = class extends PreTokenizer {
  constructor(config) {
    super();
    this.config = config;
    this.add_prefix_space = this.config.add_prefix_space;
    this.trim_offsets = this.config.trim_offsets;
    this.use_regex = this.config.use_regex ?? true;
    this.pattern = /'s|'t|'re|'ve|'m|'ll|'d| ?\p{L}+| ?\p{N}+| ?[^\s\p{L}\p{N}]+|\s+(?!\S)|\s+/gu;
    this.byte_encoder = BYTES_TO_UNICODE;
    this.text_encoder = new TextEncoder();
  }
  /**
   * Tokenizes a single piece of text using byte-level tokenization.
   * @param {string} text The text to tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens.
   */
  pre_tokenize_text(text, options) {
    if (this.add_prefix_space && !text.startsWith(" ")) {
      text = " " + text;
    }
    const tokens = this.use_regex ? text.match(this.pattern) || [] : [text];
    return tokens.map(
      (token) => Array.from(this.text_encoder.encode(token), (byte) => this.byte_encoder[byte]).join("")
    );
  }
};
var SplitPreTokenizer = class extends PreTokenizer {
  constructor(config) {
    super();
    this.config = config;
    this.pattern = createPattern(this.config.pattern, this.config.invert);
  }
  /**
   * Tokenizes text by splitting it using the given pattern.
   * @param {string} text The text to tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens.
   */
  pre_tokenize_text(text, options) {
    if (this.pattern === null) {
      return [];
    }
    if (this.config.invert) {
      return text.match(this.pattern) || [];
    } else if (this.config.behavior?.toLowerCase() === "removed") {
      return text.split(this.pattern).filter((x) => x);
    } else {
      return regexSplit(text, this.pattern);
    }
  }
};
var PunctuationPreTokenizer = class extends PreTokenizer {
  constructor(config) {
    super();
    this.config = config;
    this.pattern = new RegExp(`[^${PUNCTUATION_REGEX}]+|[${PUNCTUATION_REGEX}]+`, "gu");
  }
  /**
   * Tokenizes text by splitting it using the given pattern.
   * @param {string} text The text to tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens.
   */
  pre_tokenize_text(text, options) {
    return text.match(this.pattern) || [];
  }
};
var DigitsPreTokenizer = class extends PreTokenizer {
  constructor(config) {
    super();
    this.config = config;
    const digit_pattern = `[^\\d]+|\\d${this.config.individual_digits ? "" : "+"}`;
    this.pattern = new RegExp(digit_pattern, "gu");
  }
  /**
   * Tokenizes text by splitting it using the given pattern.
   * @param {string} text The text to tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens.
   */
  pre_tokenize_text(text, options) {
    return text.match(this.pattern) || [];
  }
};
var PostProcessor = class extends Callable {
  constructor(config) {
    if (!config) {
      throw new Error("config is required");
    }
    super();
    this.config = config;
  }
  /**
   * Factory method to create a PostProcessor object from a configuration object.
   *
   * @param {Object} config Configuration object representing a PostProcessor.
   * @returns {PostProcessor} A PostProcessor object created from the given configuration.
   * @throws {Error} If an unknown PostProcessor type is encountered.
   */
  static fromConfig(config) {
    if (config === null) return null;
    switch (config.type) {
      case "TemplateProcessing":
        return new TemplateProcessing(config);
      case "ByteLevel":
        return new ByteLevelPostProcessor(config);
      case "Sequence":
        return new PostProcessorSequence(config);
      default:
        throw new Error(`Unknown PostProcessor type: ${config.type}`);
    }
  }
  /**
   * Method to be implemented in subclass to apply post-processing on the given tokens.
   *
   * @param {Array} tokens The input tokens to be post-processed.
   * @param {...*} args Additional arguments required by the post-processing logic.
   * @returns {PostProcessedOutput} The post-processed tokens.
   * @throws {Error} If the method is not implemented in subclass.
   */
  post_process(tokens, ...args) {
    throw Error("post_process should be implemented in subclass.");
  }
  /**
   * Alias for {@link PostProcessor#post_process}.
   * @param {Array} tokens The text or array of texts to post-process.
   * @param {...*} args Additional arguments required by the post-processing logic.
   * @returns {PostProcessedOutput} The post-processed tokens.
   */
  _call(tokens, ...args) {
    return this.post_process(tokens, ...args);
  }
};
var TemplateProcessing = class extends PostProcessor {
  constructor(config) {
    super(config);
    this.single = config.single;
    this.pair = config.pair;
  }
  /**
   * Replaces special tokens in the template with actual tokens.
   * @param {string[]} tokens The list of tokens for the first sequence.
   * @param {string[]} [tokens_pair=null] The list of tokens for the second sequence (optional).
   * @returns {PostProcessedOutput} An object containing the list of tokens with the special tokens replaced with actual tokens.
   */
  post_process(tokens, tokens_pair, {
    add_special_tokens = true
  } = {}) {
    const type = tokens_pair === null ? this.single : this.pair;
    let processedTokens = [];
    let types = [];
    for (const item of type) {
      if ("SpecialToken" in item) {
        if (add_special_tokens) {
          processedTokens.push(item.SpecialToken.id);
          types.push(item.SpecialToken.type_id);
        }
      } else if ("Sequence" in item) {
        if (item.Sequence.id === "A") {
          processedTokens = mergeArrays(processedTokens, tokens);
          types = mergeArrays(types, new Array(tokens.length).fill(item.Sequence.type_id));
        } else if (item.Sequence.id === "B") {
          processedTokens = mergeArrays(processedTokens, tokens_pair);
          types = mergeArrays(types, new Array(tokens_pair.length).fill(item.Sequence.type_id));
        }
      }
    }
    return { tokens: processedTokens, token_type_ids: types };
  }
};
var ByteLevelPostProcessor = class extends PostProcessor {
  /**
   * Post process the given tokens.
   * @param {string[]} tokens The list of tokens for the first sequence.
   * @param {string[]} [tokens_pair=null] The list of tokens for the second sequence (optional).
   * @returns {PostProcessedOutput} An object containing the post-processed tokens.
   */
  post_process(tokens, tokens_pair = null) {
    if (tokens_pair) {
      tokens = mergeArrays(tokens, tokens_pair);
    }
    return { tokens };
  }
};
var PostProcessorSequence = class extends PostProcessor {
  constructor(config) {
    super(config);
    this.processors = config.processors.map((x) => PostProcessor.fromConfig(x));
  }
  /**
   * Post process the given tokens.
   * @param {string[]} tokens The list of tokens for the first sequence.
   * @param {string[]} [tokens_pair=null] The list of tokens for the second sequence (optional).
   * @returns {PostProcessedOutput} An object containing the post-processed tokens.
   */
  post_process(tokens, tokens_pair = null, options = {}) {
    let token_type_ids;
    for (const processor of this.processors) {
      if (processor instanceof ByteLevelPostProcessor) {
        const output = processor.post_process(tokens);
        tokens = output.tokens;
        if (tokens_pair) {
          const pair_output = processor.post_process(tokens_pair);
          tokens_pair = pair_output.tokens;
        }
      } else {
        const output = processor.post_process(tokens, tokens_pair, options);
        tokens = output.tokens;
        token_type_ids = output.token_type_ids;
      }
    }
    return { tokens, token_type_ids };
  }
};
var Decoder = class extends Callable {
  // x: any;
  constructor(config) {
    super();
    this.config = config;
    this.added_tokens = [];
    this.end_of_word_suffix = null;
    this.trim_offsets = config.trim_offsets;
  }
  /**
   * Creates a decoder instance based on the provided configuration.
   *
   * @param {Object} config The configuration object.
   * @returns {Decoder} A decoder instance.
   * @throws {Error} If an unknown decoder type is provided.
   */
  static fromConfig(config) {
    if (config === null) return null;
    switch (config.type) {
      case "WordPiece":
        return new WordPieceDecoder(config);
      case "Metaspace":
        return new MetaspaceDecoder(config);
      case "ByteLevel":
        return new ByteLevelDecoder(config);
      case "Replace":
        return new ReplaceDecoder(config);
      case "ByteFallback":
        return new ByteFallback(config);
      case "Fuse":
        return new FuseDecoder(config);
      case "Strip":
        return new StripDecoder(config);
      case "Sequence":
        return new DecoderSequence(config);
      case "CTC":
        return new CTCDecoder(config);
      case "BPEDecoder":
        return new BPEDecoder(config);
      default:
        throw new Error(`Unknown Decoder type: ${config.type}`);
    }
  }
  /**
   * Calls the `decode` method.
   *
   * @param {string[]} tokens The list of tokens.
   * @returns {string} The decoded string.
   */
  _call(tokens) {
    return this.decode(tokens);
  }
  /**
   * Decodes a list of tokens.
   * @param {string[]} tokens The list of tokens.
   * @returns {string} The decoded string.
   */
  decode(tokens) {
    return this.decode_chain(tokens).join("");
  }
  /**
   * Apply the decoder to a list of tokens.
   *
   * @param {string[]} tokens The list of tokens.
   * @returns {string[]} The decoded list of tokens.
   * @throws {Error} If the `decode_chain` method is not implemented in the subclass.
   */
  decode_chain(tokens) {
    throw Error("`decode_chain` should be implemented in subclass.");
  }
};
var ReplaceDecoder = class extends Decoder {
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    const pattern = createPattern(this.config.pattern);
    return pattern === null ? tokens : tokens.map((token) => token.replaceAll(pattern, this.config.content));
  }
};
var ByteFallback = class extends Decoder {
  constructor(config) {
    super(config);
    this.text_decoder = new TextDecoder();
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    const new_tokens = [];
    let previous_byte_tokens = [];
    for (const token of tokens) {
      let bytes = null;
      if (token.length === 6 && token.startsWith("<0x") && token.endsWith(">")) {
        const byte = parseInt(token.slice(3, 5), 16);
        if (!isNaN(byte)) {
          bytes = byte;
        }
      }
      if (bytes !== null) {
        previous_byte_tokens.push(bytes);
      } else {
        if (previous_byte_tokens.length > 0) {
          const string = this.text_decoder.decode(Uint8Array.from(previous_byte_tokens));
          new_tokens.push(string);
          previous_byte_tokens = [];
        }
        new_tokens.push(token);
      }
    }
    if (previous_byte_tokens.length > 0) {
      const string = this.text_decoder.decode(Uint8Array.from(previous_byte_tokens));
      new_tokens.push(string);
      previous_byte_tokens = [];
    }
    return new_tokens;
  }
};
var FuseDecoder = class extends Decoder {
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    return [tokens.join("")];
  }
};
var StripDecoder = class extends Decoder {
  constructor(config) {
    super(config);
    this.content = this.config.content;
    this.start = this.config.start;
    this.stop = this.config.stop;
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    return tokens.map((token) => {
      let start_cut = 0;
      for (let i = 0; i < this.start; ++i) {
        if (token[i] === this.content) {
          start_cut = i + 1;
          continue;
        } else {
          break;
        }
      }
      let stop_cut = token.length;
      for (let i = 0; i < this.stop; ++i) {
        const index = token.length - i - 1;
        if (token[index] === this.content) {
          stop_cut = index;
          continue;
        } else {
          break;
        }
      }
      return token.slice(start_cut, stop_cut);
    });
  }
};
var WordPieceDecoder = class extends Decoder {
  constructor(config) {
    super(config);
    this.cleanup = config.cleanup;
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    return tokens.map((token, i) => {
      if (i !== 0) {
        if (token.startsWith(this.config.prefix)) {
          token = token.replace(this.config.prefix, "");
        } else {
          token = " " + token;
        }
      }
      if (this.cleanup) {
        token = clean_up_tokenization(token);
      }
      return token;
    });
  }
};
var ByteLevelDecoder = class extends Decoder {
  constructor(config) {
    super(config);
    this.byte_decoder = UNICODE_TO_BYTES;
    this.text_decoder = new TextDecoder("utf-8", {
      fatal: false,
      ignoreBOM: true
    });
    this.end_of_word_suffix = null;
  }
  /**
   * Convert an array of tokens to string by decoding each byte.
   * @param {string[]} tokens Array of tokens to be decoded.
   * @returns {string} The decoded string.
   */
  convert_tokens_to_string(tokens) {
    const text = tokens.join("");
    const byteArray = new Uint8Array([...text].map((c) => this.byte_decoder[c]));
    const decoded_text = this.text_decoder.decode(byteArray);
    return decoded_text;
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    const sub_texts = [];
    let current_sub_text = [];
    for (const token of tokens) {
      if (this.added_tokens.find((x) => x.content === token) !== undefined) {
        if (current_sub_text.length > 0) {
          sub_texts.push(this.convert_tokens_to_string(current_sub_text));
          current_sub_text = [];
        }
        sub_texts.push(token);
      } else {
        current_sub_text.push(token);
      }
    }
    if (current_sub_text.length > 0) {
      sub_texts.push(this.convert_tokens_to_string(current_sub_text));
    }
    return sub_texts;
  }
};
var CTCDecoder = class extends Decoder {
  constructor(config) {
    super(config);
    this.pad_token = this.config.pad_token;
    this.word_delimiter_token = this.config.word_delimiter_token;
    this.cleanup = this.config.cleanup;
  }
  /**
   * Converts a connectionist-temporal-classification (CTC) output tokens into a single string.
   * @param {string[]} tokens Array of tokens to be decoded.
   * @returns {string} The decoded string.
   */
  convert_tokens_to_string(tokens) {
    if (tokens.length === 0) return "";
    const grouped_tokens = [tokens[0]];
    for (let i = 1; i < tokens.length; ++i) {
      if (tokens[i] !== grouped_tokens.at(-1)) {
        grouped_tokens.push(tokens[i]);
      }
    }
    const filtered_tokens = grouped_tokens.filter((token) => token !== this.pad_token);
    let text = filtered_tokens.join("");
    if (this.cleanup) {
      text = clean_up_tokenization(text).replaceAll(this.word_delimiter_token, " ").trim();
    }
    return text;
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    return [this.convert_tokens_to_string(tokens)];
  }
};
var DecoderSequence = class extends Decoder {
  /**
   * Creates a new instance of DecoderSequence.
   * @param {Object} config The configuration object.
   * @param {Object[]} config.decoders The list of decoders to apply.
   */
  constructor(config) {
    super(config);
    this.decoders = config.decoders.map((x) => Decoder.fromConfig(x));
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    return this.decoders.reduce((toks, decoder) => {
      return decoder.decode_chain(toks);
    }, tokens);
  }
};
var BPEDecoder = class extends Decoder {
  constructor(config) {
    super(config);
    this.suffix = this.config.suffix;
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    return tokens.map((token, i) => {
      return token.replaceAll(this.suffix, i === tokens.length - 1 ? "" : " ");
    });
  }
};
var VitsDecoder = class extends Decoder {
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    let decoded = "";
    for (let i = 1; i < tokens.length; i += 2) {
      decoded += tokens[i];
    }
    return [decoded];
  }
};
var MetaspacePreTokenizer = class extends PreTokenizer {
  constructor(config) {
    super();
    this.addPrefixSpace = config.add_prefix_space;
    this.replacement = config.replacement;
    this.strRep = config.str_rep || this.replacement;
    this.prepend_scheme = config.prepend_scheme ?? "always";
  }
  /**
   * This method takes a string, replaces spaces with the replacement character,
   * adds a prefix space if requested, and returns a new list of tokens.
   * @param {string} text The text to pre-tokenize.
   * @param {Object} [options] The options for the pre-tokenization.
   * @param {number} [options.section_index] The index of the section to pre-tokenize.
   * @returns {string[]} A new list of pre-tokenized tokens.
   */
  pre_tokenize_text(text, { section_index = undefined } = {}) {
    let normalized = text.replaceAll(" ", this.strRep);
    if (
      // We add a prefix space if:
      //  (1) The addPrefixSpace option is enabled and the normalized
      //      token does not already start with the replacement character.
      this.addPrefixSpace && !normalized.startsWith(this.replacement) && // and (2) either:
      //  (a) prepend_scheme is 'always'
      //  (b) prepend_scheme is 'first' and this is the first section
      (this.prepend_scheme === "always" || this.prepend_scheme === "first" && section_index === 0)
    ) {
      normalized = this.strRep + normalized;
    }
    return [normalized];
  }
};
var MetaspaceDecoder = class extends Decoder {
  constructor(config) {
    super(config);
    this.addPrefixSpace = config.add_prefix_space;
    this.replacement = config.replacement;
  }
  /** @type {Decoder['decode_chain']} */
  decode_chain(tokens) {
    const result = [];
    for (let i = 0; i < tokens.length; ++i) {
      let normalized = tokens[i].replaceAll(this.replacement, " ");
      if (this.addPrefixSpace && i == 0 && normalized.startsWith(" ")) {
        normalized = normalized.substring(1);
      }
      result.push(normalized);
    }
    return result;
  }
};
var Precompiled = class extends Normalizer {
  /**
   * Create a new instance of Precompiled normalizer.
   * @param {Object} config The configuration object.
   * @param {any} config.precompiled_charsmap Precompiled chars mapping.
   */
  constructor(config) {
    super(config);
    this.charsmap = config.precompiled_charsmap;
  }
  /**
   * Normalizes the given text by applying the precompiled charsmap.
   * @param {string} text The text to normalize.
   * @returns {string} The normalized text.
   */
  normalize(text) {
    text = text.replace(/[\u0001-\u0008\u000B\u000E-\u001F\u007F\u008F\u009F]/gm, "");
    text = text.replace(
      /[\u0009\u000A\u000C\u000D\u00A0\u1680\u2000-\u200F\u2028\u2029\u202F\u205F\u2581\u3000\uFEFF\uFFFD]/gm,
      " "
    );
    if (text.includes("\uFF5E")) {
      const parts = text.split("\uFF5E");
      text = parts.map((part) => part.normalize("NFKC")).join("\uFF5E");
    } else {
      text = text.normalize("NFKC");
    }
    return text;
  }
};
var PreTokenizerSequence = class extends PreTokenizer {
  constructor(config) {
    super();
    this.tokenizers = config.pretokenizers.map((x) => PreTokenizer.fromConfig(x));
    this.config = config;
  }
  /**
   * Applies each pre-tokenizer in the sequence to the input text in turn.
   * @param {string} text The text to pre-tokenize.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} The pre-tokenized text.
   */
  pre_tokenize_text(text, options) {
    return this.tokenizers.reduce(
      (preTokenizedText, tokenizer) => {
        return tokenizer.pre_tokenize(preTokenizedText, options);
      },
      [text]
    );
  }
};
var WhitespacePreTokenizer = class extends PreTokenizer {
  /**
   * Creates an instance of WhitespacePreTokenizer.
   * @param {Object} config The configuration object for the pre-tokenizer.
   */
  constructor(config) {
    super();
  }
  /**
   * Pre-tokenizes the input text by splitting it on word boundaries.
   * @param {string} text The text to be pre-tokenized.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens produced by splitting the input text on whitespace.
   */
  pre_tokenize_text(text, options) {
    return text.match(/\w+|[^\w\s]+/g) || [];
  }
};
var WhitespaceSplit = class extends PreTokenizer {
  /**
   * Creates an instance of WhitespaceSplit.
   * @param {Object} config The configuration object for the pre-tokenizer.
   */
  constructor(config) {
    super();
  }
  /**
   * Pre-tokenizes the input text by splitting it on whitespace characters.
   * @param {string} text The text to be pre-tokenized.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens produced by splitting the input text on whitespace.
   */
  pre_tokenize_text(text, options) {
    return whitespace_split(text);
  }
};
var ReplacePreTokenizer = class extends PreTokenizer {
  constructor(config) {
    super();
    this.config = config;
    this.pattern = createPattern(this.config.pattern);
    this.content = this.config.content;
  }
  /**
   * Pre-tokenizes the input text by replacing certain characters.
   * @param {string} text The text to be pre-tokenized.
   * @param {Object} [options] Additional options for the pre-tokenization logic.
   * @returns {string[]} An array of tokens produced by replacing certain characters.
   */
  pre_tokenize_text(text, options) {
    if (this.pattern === null) {
      return [text];
    }
    return [text.replaceAll(this.pattern, this.config.content)];
  }
};
var SPECIAL_TOKEN_ATTRIBUTES = [
  "bos_token",
  "eos_token",
  "unk_token",
  "sep_token",
  "pad_token",
  "cls_token",
  "mask_token"
  // additional_special_tokens (TODO)
];
function padHelper(item, length, value_fn, side) {
  for (const key of Object.keys(item)) {
    const diff = length - item[key].length;
    const value = value_fn(key);
    const padData = new Array(diff).fill(value);
    item[key] = side === "right" ? mergeArrays(item[key], padData) : mergeArrays(padData, item[key]);
  }
}
function truncateHelper(item, length) {
  for (const key of Object.keys(item)) {
    item[key].length = length;
  }
}
var PreTrainedTokenizer = class extends Callable {
  constructor(tokenizerJSON, tokenizerConfig) {
    super();
    this.return_token_type_ids = false;
    this.padding_side = "right";
    this._tokenizer_config = tokenizerConfig;
    this.normalizer = Normalizer.fromConfig(tokenizerJSON.normalizer);
    this.pre_tokenizer = PreTokenizer.fromConfig(tokenizerJSON.pre_tokenizer);
    this.model = TokenizerModel.fromConfig(tokenizerJSON.model, tokenizerConfig);
    this.post_processor = PostProcessor.fromConfig(tokenizerJSON.post_processor);
    this.decoder = Decoder.fromConfig(tokenizerJSON.decoder);
    this.special_tokens = [];
    this.all_special_ids = [];
    this.added_tokens = [];
    for (const addedToken of tokenizerJSON.added_tokens) {
      const token = new AddedToken(addedToken);
      this.added_tokens.push(token);
      this.model.tokens_to_ids.set(token.content, token.id);
      this.model.vocab[token.id] = token.content;
      if (token.special) {
        this.special_tokens.push(token.content);
        this.all_special_ids.push(token.id);
      }
    }
    this.additional_special_tokens = tokenizerConfig.additional_special_tokens ?? [];
    this.special_tokens.push(...this.additional_special_tokens);
    this.special_tokens = [...new Set(this.special_tokens)];
    if (this.decoder) {
      this.decoder.added_tokens = this.added_tokens;
      this.decoder.end_of_word_suffix = this.model.end_of_word_suffix;
    }
    this.added_tokens_regex = this.added_tokens.length > 0 ? new RegExp(
      this.added_tokens.slice().sort((a, b) => b.content.length - a.content.length).map((x) => `${x.lstrip ? "\\s*" : ""}(${escapeRegExp(x.content)})${x.rstrip ? "\\s*" : ""}`).join("|")
    ) : null;
    this.mask_token = this.getToken("mask_token");
    this.mask_token_id = this.model.tokens_to_ids.get(this.mask_token);
    this.pad_token = this.getToken("pad_token", "eos_token");
    this.pad_token_id = this.model.tokens_to_ids.get(this.pad_token);
    this.sep_token = this.getToken("sep_token");
    this.sep_token_id = this.model.tokens_to_ids.get(this.sep_token);
    this.unk_token = this.getToken("unk_token");
    this.unk_token_id = this.model.tokens_to_ids.get(this.unk_token);
    this.bos_token = this.getToken("bos_token");
    this.bos_token_id = this.model.tokens_to_ids.get(this.bos_token);
    this.eos_token = this.getToken("eos_token");
    this.eos_token_id = this.model.tokens_to_ids.get(this.eos_token);
    this.model_max_length = tokenizerConfig.model_max_length;
    this.remove_space = tokenizerConfig.remove_space;
    this.clean_up_tokenization_spaces = tokenizerConfig.clean_up_tokenization_spaces ?? true;
    this.do_lowercase_and_remove_accent = tokenizerConfig.do_lowercase_and_remove_accent ?? false;
    if (tokenizerConfig.padding_side) {
      this.padding_side = tokenizerConfig.padding_side;
    }
    this.legacy = false;
    this.chat_template = tokenizerConfig.chat_template ?? null;
    if (Array.isArray(this.chat_template)) {
      const chat_template = /* @__PURE__ */ Object.create(null);
      for (const { name, template } of this.chat_template) {
        if (typeof name !== "string" || typeof template !== "string") {
          throw new Error('Chat template must be a list of objects with "name" and "template" properties');
        }
        chat_template[name] = template;
      }
      this.chat_template = chat_template;
    }
    this._compiled_template_cache = /* @__PURE__ */ new Map();
  }
  /**
   * Returns the value of the first matching key in the tokenizer config object.
   * @param {...string} keys One or more keys to search for in the tokenizer config object.
   * @returns {string|null} The value associated with the first matching key, or null if no match is found.
   * @throws {Error} If an object is found for a matching key and its __type property is not "AddedToken".
   * @private
   */
  getToken(...keys) {
    for (const key of keys) {
      const item = this._tokenizer_config[key];
      if (!item) continue;
      if (typeof item === "object") {
        if (item.__type === "AddedToken") {
          return item.content;
        } else {
          throw Error(`Unknown token: ${item}`);
        }
      } else {
        return item;
      }
    }
    return null;
  }
  /**
   * Loads a pre-trained tokenizer from the given `pretrained_model_name_or_path`.
   *
   * @param {string} pretrained_model_name_or_path The path to the pre-trained tokenizer.
   * @param {PretrainedTokenizerOptions} options Additional options for loading the tokenizer.
   *
   * @throws {Error} Throws an error if the tokenizer.json or tokenizer_config.json files are not found in the `pretrained_model_name_or_path`.
   * @returns {Promise<PreTrainedTokenizer>} A new instance of the `PreTrainedTokenizer` class.
   */
  static async from_pretrained(pretrained_model_name_or_path, {
    progress_callback = null,
    config = null,
    cache_dir = null,
    local_files_only = false,
    revision = "main",
    legacy: boolean = false
  } = {}) {
    const info = await loadTokenizer(pretrained_model_name_or_path, {
      progress_callback,
      config,
      cache_dir,
      local_files_only,
      revision,
      legacy: false
    });
    return new this(...info);
  }
  /**
   * @typedef {number[]|number[][]|Tensor} BatchEncodingItem
   *
   * @typedef {Object} BatchEncoding Holds the output of the tokenizer's call function.
   * @property {BatchEncodingItem} input_ids List of token ids to be fed to a model.
   * @property {BatchEncodingItem} attention_mask List of indices specifying which tokens should be attended to by the model.
   * @property {BatchEncodingItem} [token_type_ids] List of token type ids to be fed to a model.
   */
  /**
   * Encode/tokenize the given text(s).
   * @param {string|string[]} text The text to tokenize.
   * @param {Object} options An optional object containing the following properties:
   * @param {string|string[]} [options.text_pair=null] Optional second sequence to be encoded. If set, must be the same type as text.
   * @param {boolean|'max_length'} [options.padding=false] Whether to pad the input sequences.
   * @param {boolean} [options.add_special_tokens=true] Whether or not to add the special tokens associated with the corresponding model.
   * @param {boolean} [options.truncation=null] Whether to truncate the input sequences.
   * @param {number} [options.max_length=null] Maximum length of the returned list and optionally padding length.
   * @param {boolean} [options.return_tensor=true] Whether to return the results as Tensors or arrays.
   * @param {boolean} [options.return_token_type_ids=null] Whether to return the token type ids.
   * @returns {BatchEncoding} Object to be passed to the model.
   */
  _call(text, {
    text_pair = null,
    add_special_tokens = true,
    padding = false,
    truncation = null,
    max_length = null,
    return_tensor = true,
    // Different to HF
    return_token_type_ids = null
  } = {}) {
    const isBatched = Array.isArray(text);
    let encodedTokens;
    if (isBatched) {
      if (text.length === 0) {
        throw Error("text array must be non-empty");
      }
      if (text_pair !== null) {
        if (!Array.isArray(text_pair)) {
          throw Error("text_pair must also be an array");
        } else if (text.length !== text_pair.length) {
          throw Error("text and text_pair must have the same length");
        }
        encodedTokens = text.map(
          (t, i) => this._encode_plus(t, { text_pair: text_pair[i], add_special_tokens, return_token_type_ids })
        );
      } else {
        encodedTokens = text.map((x) => this._encode_plus(x, { add_special_tokens, return_token_type_ids }));
      }
    } else {
      if (text === null || text === undefined) {
        throw Error("text may not be null or undefined");
      }
      if (Array.isArray(text_pair)) {
        throw Error(
          "When specifying `text_pair`, since `text` is a string, `text_pair` must also be a string (i.e., not an array)."
        );
      }
      encodedTokens = [this._encode_plus(text, { text_pair, add_special_tokens, return_token_type_ids })];
    }
    if (max_length === null) {
      if (padding === "max_length") {
        max_length = this.model_max_length;
      } else {
        max_length = max(encodedTokens.map((x) => x.input_ids.length))[0];
      }
    } else {
      if (!truncation) {
        console.warn(
          `Truncation was not explicitly activated but \`max_length\` is provided a specific value, please use \`truncation=true\` to explicitly truncate examples to max length.`
        );
      }
    }
    max_length = Math.min(max_length, this.model_max_length ?? Infinity);
    if (padding || truncation) {
      for (let i = 0; i < encodedTokens.length; ++i) {
        if (encodedTokens[i].input_ids.length === max_length) {
          continue;
        } else if (encodedTokens[i].input_ids.length > max_length) {
          if (truncation) {
            truncateHelper(encodedTokens[i], max_length);
          }
        } else {
          if (padding) {
            padHelper(
              encodedTokens[i],
              max_length,
              (key) => key === "input_ids" ? this.pad_token_id : 0,
              this.padding_side
            );
          }
        }
      }
    }
    const result = {};
    if (return_tensor) {
      if (!(padding && truncation)) {
        if (encodedTokens.some((x) => {
          for (const key of Object.keys(x)) {
            if (x[key].length !== encodedTokens[0][key]?.length) {
              return true;
            }
          }
          return false;
        })) {
          throw Error(
            "Unable to create tensor, you should probably activate truncation and/or padding with 'padding=true' and 'truncation=true' to have batched tensors with the same length."
          );
        }
      }
      const dims = [encodedTokens.length, encodedTokens[0].input_ids.length];
      for (const key of Object.keys(encodedTokens[0])) {
        result[key] = new Tensor3(
          "int64",
          BigInt64Array.from(encodedTokens.flatMap((x) => x[key]).map(BigInt)),
          dims
        );
      }
    } else {
      for (const key of Object.keys(encodedTokens[0])) {
        result[key] = encodedTokens.map((x) => x[key]);
      }
      if (!isBatched) {
        for (const key of Object.keys(result)) {
          result[key] = result[key][0];
        }
      }
    }
    return (
      /** @type {BatchEncoding} */
      result
    );
  }
  /**
   * Encodes a single text using the preprocessor pipeline of the tokenizer.
   *
   * @param {string|null} text The text to encode.
   * @returns {string[]|null} The encoded tokens.
   */
  _encode_text(text) {
    if (text === null) return null;
    const sections = this.added_tokens_regex ? text.split(this.added_tokens_regex).filter((x) => x) : [text];
    const tokens = sections.map((x, section_index) => {
      const addedToken = this.added_tokens.find((t) => t.content === x);
      if (addedToken !== undefined) {
        return x;
      } else {
        if (this.remove_space === true) {
          x = x.trim().split(/\s+/).join(" ");
        }
        if (this.do_lowercase_and_remove_accent) {
          x = lowercase_and_remove_accent(x);
        }
        if (this.normalizer !== null) {
          x = this.normalizer(x);
        }
        if (x.length === 0) {
          return [];
        }
        const sectionTokens = this.pre_tokenizer !== null ? this.pre_tokenizer(x, {
          section_index
        }) : [x];
        const tokens2 = this.model(sectionTokens);
        return tokens2;
      }
    }).flat();
    return tokens;
  }
  /**
   * Encodes a single text or a pair of texts using the model's tokenizer.
   *
   * @param {string} text The text to encode.
   * @param {Object} options An optional object containing the following properties:
   * @param {string} [options.text_pair=null] The optional second text to encode.
   * @param {boolean} [options.add_special_tokens=true] Whether or not to add the special tokens associated with the corresponding model.
   * @param {boolean} [options.return_token_type_ids=null] Whether to return token_type_ids.
   * @returns {EncodingSingle} An object containing the encoded text.
   * @private
   */
  _encode_plus(text, {
    text_pair = null,
    add_special_tokens = true,
    return_token_type_ids = null
  } = {}) {
    const { tokens, token_type_ids } = this._tokenize_helper(text, { pair: text_pair, add_special_tokens });
    const input_ids = this.model.convert_tokens_to_ids(tokens);
    const result = {
      input_ids,
      attention_mask: new Array(input_ids.length).fill(1)
    };
    if ((return_token_type_ids ?? this.return_token_type_ids) && token_type_ids) {
      result.token_type_ids = token_type_ids;
    }
    return result;
  }
  /**
   * Internal helper function to tokenize a text, and optionally a pair of texts.
   * @param {string} text The text to tokenize.
   * @param {Object} options An optional object containing the following properties:
   * @param {string} [options.pair=null] The optional second text to tokenize.
   * @param {boolean} [options.add_special_tokens=false] Whether or not to add the special tokens associated with the corresponding model.
   * @returns {{tokens: string[], token_type_ids?: number[]}} An object containing the tokens and optionally the token type IDs.
   */
  _tokenize_helper(text, {
    pair = null,
    add_special_tokens = false
  } = {}) {
    const tokens = this._encode_text(text);
    const tokens2 = this._encode_text(pair);
    return this.post_processor ? this.post_processor(tokens, tokens2, { add_special_tokens }) : { tokens: mergeArrays(tokens ?? [], tokens2 ?? []) };
  }
  /**
   * Converts a string into a sequence of tokens.
   * @param {string} text The sequence to be encoded.
   * @param {Object} options An optional object containing the following properties:
   * @param {string} [options.pair] A second sequence to be encoded with the first.
   * @param {boolean} [options.add_special_tokens=false] Whether or not to add the special tokens associated with the corresponding model.
   * @returns {string[]} The list of tokens.
   */
  tokenize(text, { pair = null, add_special_tokens = false } = {}) {
    return this._tokenize_helper(text, { pair, add_special_tokens }).tokens;
  }
  /**
   * Encodes a single text or a pair of texts using the model's tokenizer.
   *
   * @param {string} text The text to encode.
   * @param {Object} options An optional object containing the following properties:
   * @param {string} [options.text_pair=null] The optional second text to encode.
   * @param {boolean} [options.add_special_tokens=true] Whether or not to add the special tokens associated with the corresponding model.
   * @param {boolean} [options.return_token_type_ids=null] Whether to return token_type_ids.
   * @returns {number[]} An array of token IDs representing the encoded text(s).
   */
  encode(text, { text_pair = null, add_special_tokens = true, return_token_type_ids = null } = {}) {
    return this._encode_plus(text, {
      text_pair,
      add_special_tokens,
      return_token_type_ids
    }).input_ids;
  }
  /**
   * Decode a batch of tokenized sequences.
   * @param {number[][]|Tensor} batch List/Tensor of tokenized input sequences.
   * @param {Object} decode_args (Optional) Object with decoding arguments.
   * @returns {string[]} List of decoded sequences.
   */
  batch_decode(batch, decode_args = {}) {
    if (batch instanceof Tensor3) {
      batch = batch.tolist();
    }
    return batch.map((x) => this.decode(x, decode_args));
  }
  /**
   * Decodes a sequence of token IDs back to a string.
   *
   * @param {number[]|bigint[]|Tensor} token_ids List/Tensor of token IDs to decode.
   * @param {Object} [decode_args={}]
   * @param {boolean} [decode_args.skip_special_tokens=false] If true, special tokens are removed from the output string.
   * @param {boolean} [decode_args.clean_up_tokenization_spaces=true] If true, spaces before punctuations and abbreviated forms are removed.
   *
   * @returns {string} The decoded string.
   * @throws {Error} If `token_ids` is not a non-empty array of integers.
   */
  decode(token_ids, decode_args = {}) {
    if (token_ids instanceof Tensor3) {
      token_ids = prepareTensorForDecode(token_ids);
    }
    if (!Array.isArray(token_ids) || token_ids.length === 0 || !isIntegralNumber(token_ids[0])) {
      throw Error("token_ids must be a non-empty array of integers.");
    }
    return this.decode_single(token_ids, decode_args);
  }
  /**
   * Decode a single list of token ids to a string.
   * @param {number[]|bigint[]} token_ids List of token ids to decode
   * @param {Object} decode_args Optional arguments for decoding
   * @param {boolean} [decode_args.skip_special_tokens=false] Whether to skip special tokens during decoding
   * @param {boolean} [decode_args.clean_up_tokenization_spaces=null] Whether to clean up tokenization spaces during decoding.
   * If null, the value is set to `this.decoder.cleanup` if it exists, falling back to `this.clean_up_tokenization_spaces` if it exists, falling back to `true`.
   * @returns {string} The decoded string
   */
  decode_single(token_ids, { skip_special_tokens = false, clean_up_tokenization_spaces = null }) {
    let tokens = this.model.convert_ids_to_tokens(token_ids);
    if (skip_special_tokens) {
      tokens = tokens.filter((x) => !this.special_tokens.includes(x));
    }
    let decoded = this.decoder ? this.decoder(tokens) : tokens.join(" ");
    if (this.decoder && this.decoder.end_of_word_suffix) {
      decoded = decoded.replaceAll(this.decoder.end_of_word_suffix, " ");
      if (skip_special_tokens) {
        decoded = decoded.trim();
      }
    }
    if (clean_up_tokenization_spaces ?? this.clean_up_tokenization_spaces) {
      decoded = clean_up_tokenization(decoded);
    }
    return decoded;
  }
  /**
   * Retrieve the chat template string used for tokenizing chat messages. This template is used
   * internally by the `apply_chat_template` method and can also be used externally to retrieve the model's chat
   * template for better generation tracking.
   *
   * @param {Object} options An optional object containing the following properties:
   * @param {string} [options.chat_template=null]
   * A Jinja template or the name of a template to use for this conversion.
   * It is usually not necessary to pass anything to this argument,
   * as the model's template will be used by default.
   * @param {Object[]} [options.tools=null]
   * A list of tools (callable functions) that will be accessible to the model. If the template does not
   * support function calling, this argument will have no effect. Each tool should be passed as a JSON Schema,
   * giving the name, description and argument types for the tool. See our
   * [chat templating guide](https://huggingface.co/docs/transformers/main/en/chat_templating#automated-function-conversion-for-tool-use)
   * for more information.
   * @returns {string} The chat template string.
   */
  get_chat_template({ chat_template = null, tools = null } = {}) {
    if (this.chat_template && typeof this.chat_template === "object") {
      const template_dict = this.chat_template;
      if (chat_template !== null && Object.hasOwn(template_dict, chat_template)) {
        chat_template = template_dict[chat_template];
      } else if (chat_template === null) {
        if (tools !== null && "tool_use" in template_dict) {
          chat_template = template_dict["tool_use"];
        } else if ("default" in template_dict) {
          chat_template = template_dict["default"];
        } else {
          throw Error(
            `This model has multiple chat templates with no default specified! Please either pass a chat template or the name of the template you wish to use to the 'chat_template' argument. Available template names are ${Object.keys(template_dict).sort()}.`
          );
        }
      }
    } else if (chat_template === null) {
      if (this.chat_template) {
        chat_template = this.chat_template;
      } else {
        throw Error(
          "Cannot use apply_chat_template() because tokenizer.chat_template is not set and no template argument was passed! For information about writing templates and setting the tokenizer.chat_template attribute, please see the documentation at https://huggingface.co/docs/transformers/main/en/chat_templating"
        );
      }
    }
    return chat_template;
  }
  /**
   * Converts a list of message objects with `"role"` and `"content"` keys to a list of token
   * ids. This method is intended for use with chat models, and will read the tokenizer's chat_template attribute to
   * determine the format and control tokens to use when converting.
   *
   * See [here](https://huggingface.co/docs/transformers/chat_templating) for more information.
   *
   * **Example:** Applying a chat template to a conversation.
   *
   * ```javascript
   * import { AutoTokenizer } from "@huggingface/transformers";
   *
   * const tokenizer = await AutoTokenizer.from_pretrained("Xenova/mistral-tokenizer-v1");
   *
   * const chat = [
   *   { "role": "user", "content": "Hello, how are you?" },
   *   { "role": "assistant", "content": "I'm doing great. How can I help you today?" },
   *   { "role": "user", "content": "I'd like to show off how chat templating works!" },
   * ]
   *
   * const text = tokenizer.apply_chat_template(chat, { tokenize: false });
   * // "<s>[INST] Hello, how are you? [/INST]I'm doing great. How can I help you today?</s> [INST] I'd like to show off how chat templating works! [/INST]"
   *
   * const input_ids = tokenizer.apply_chat_template(chat, { tokenize: true, return_tensor: false });
   * // [1, 733, 16289, 28793, 22557, 28725, 910, 460, 368, 28804, 733, 28748, 16289, 28793, 28737, 28742, 28719, 2548, 1598, 28723, 1602, 541, 315, 1316, 368, 3154, 28804, 2, 28705, 733, 16289, 28793, 315, 28742, 28715, 737, 298, 1347, 805, 910, 10706, 5752, 1077, 3791, 28808, 733, 28748, 16289, 28793]
   * ```
   *
   * @param {Message[]} conversation A list of message objects with `"role"` and `"content"` keys,
   * representing the chat history so far.
   * @param {Object} options An optional object containing the following properties:
   * @param {string} [options.chat_template=null] A Jinja template to use for this conversion. If
   * this is not passed, the model's chat template will be used instead.
   * @param {Object[]} [options.tools=null]
   * A list of tools (callable functions) that will be accessible to the model. If the template does not
   * support function calling, this argument will have no effect. Each tool should be passed as a JSON Schema,
   * giving the name, description and argument types for the tool. See our
   * [chat templating guide](https://huggingface.co/docs/transformers/main/en/chat_templating#automated-function-conversion-for-tool-use)
   * for more information.
   * @param {Record<string, string>[]} [options.documents=null]
   * A list of dicts representing documents that will be accessible to the model if it is performing RAG
   * (retrieval-augmented generation). If the template does not support RAG, this argument will have no
   * effect. We recommend that each document should be a dict containing "title" and "text" keys. Please
   * see the RAG section of the [chat templating guide](https://huggingface.co/docs/transformers/main/en/chat_templating#arguments-for-RAG)
   * for examples of passing documents with chat templates.
   * @param {boolean} [options.add_generation_prompt=false] Whether to end the prompt with the token(s) that indicate
   * the start of an assistant message. This is useful when you want to generate a response from the model.
   * Note that this argument will be passed to the chat template, and so it must be supported in the
   * template for this argument to have any effect.
   * @param {boolean} [options.tokenize=true] Whether to tokenize the output. If false, the output will be a string.
   * @param {boolean} [options.padding=false] Whether to pad sequences to the maximum length. Has no effect if tokenize is false.
   * @param {boolean} [options.truncation=false] Whether to truncate sequences to the maximum length. Has no effect if tokenize is false.
   * @param {number} [options.max_length=null] Maximum length (in tokens) to use for padding or truncation. Has no effect if tokenize is false.
   * If not specified, the tokenizer's `max_length` attribute will be used as a default.
   * @param {boolean} [options.return_tensor=true] Whether to return the output as a Tensor or an Array. Has no effect if tokenize is false.
   * @param {boolean} [options.return_dict=true] Whether to return a dictionary with named outputs. Has no effect if tokenize is false.
   * @param {Object} [options.tokenizer_kwargs={}] Additional options to pass to the tokenizer.
   * @returns {string | Tensor | number[]| number[][]|BatchEncoding} The tokenized output.
   */
  apply_chat_template(conversation, {
    tools = null,
    documents = null,
    chat_template = null,
    add_generation_prompt = false,
    tokenize = true,
    padding = false,
    truncation = false,
    max_length = null,
    return_tensor = true,
    return_dict = false,
    tokenizer_kwargs = {},
    ...kwargs
  } = {}) {
    chat_template = this.get_chat_template({ chat_template, tools });
    if (typeof chat_template !== "string") {
      throw Error(`chat_template must be a string, but got ${typeof chat_template}`);
    }
    let compiledTemplate = this._compiled_template_cache.get(chat_template);
    if (compiledTemplate === undefined) {
      compiledTemplate = new Template(chat_template);
      this._compiled_template_cache.set(chat_template, compiledTemplate);
    }
    const special_tokens_map = /* @__PURE__ */ Object.create(null);
    for (const key of SPECIAL_TOKEN_ATTRIBUTES) {
      const value = this.getToken(key);
      if (value) {
        special_tokens_map[key] = value;
      }
    }
    const rendered = compiledTemplate.render({
      messages: conversation,
      add_generation_prompt,
      tools,
      documents,
      ...special_tokens_map,
      ...kwargs
    });
    if (tokenize) {
      const out = this._call(rendered, {
        add_special_tokens: false,
        padding,
        truncation,
        max_length,
        return_tensor,
        ...tokenizer_kwargs
      });
      return return_dict ? out : out.input_ids;
    }
    return rendered;
  }
};
var BertTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var AlbertTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var MobileBertTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var SqueezeBertTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var DebertaTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var DebertaV2Tokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var HerbertTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var ConvBertTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var RoFormerTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var DistilBertTokenizer = class extends PreTrainedTokenizer {
};
var CamembertTokenizer = class extends PreTrainedTokenizer {
};
var XLMTokenizer = class extends PreTrainedTokenizer {
  constructor(tokenizerJSON, tokenizerConfig) {
    super(tokenizerJSON, tokenizerConfig);
    this.return_token_type_ids = true;
    console.warn(
      'WARNING: `XLMTokenizer` is not yet supported by Hugging Face\'s "fast" tokenizers library. Therefore, you may experience slightly inaccurate results.'
    );
  }
};
var ElectraTokenizer = class extends PreTrainedTokenizer {
  constructor() {
    super(...arguments);
    this.return_token_type_ids = true;
  }
};
var T5Tokenizer = class extends PreTrainedTokenizer {
};
var GPT2Tokenizer = class extends PreTrainedTokenizer {
};
var BartTokenizer = class extends PreTrainedTokenizer {
};
var MBartTokenizer = class extends PreTrainedTokenizer {
  constructor(tokenizerJSON, tokenizerConfig) {
    super(tokenizerJSON, tokenizerConfig);
    this.languageRegex = /^[a-z]{2}_[A-Z]{2}$/;
    this.language_codes = this.special_tokens.filter((x) => this.languageRegex.test(x));
    this.lang_to_token = (x) => x;
  }
  /**
   * Helper function to build translation inputs for an `MBartTokenizer`.
   * @param {string|string[]} raw_inputs The text to tokenize.
   * @param {Object} tokenizer_options Options to be sent to the tokenizer
   * @param {Object} generate_kwargs Generation options.
   * @returns {Object} Object to be passed to the model.
   */
  _build_translation_inputs(raw_inputs, tokenizer_options, generate_kwargs) {
    return _build_translation_inputs(this, raw_inputs, tokenizer_options, generate_kwargs);
  }
};
var MBart50Tokenizer = class extends MBartTokenizer {
};
var RobertaTokenizer = class extends PreTrainedTokenizer {
};
var BloomTokenizer = class extends PreTrainedTokenizer {
};
var SPIECE_UNDERLINE = "\u2581";
var LlamaTokenizer = class extends PreTrainedTokenizer {
  constructor(tokenizerJSON, tokenizerConfig) {
    super(tokenizerJSON, tokenizerConfig);
    this.padding_side = "left";
    this.legacy = tokenizerConfig.legacy ?? true;
    if (!this.legacy) {
      this.normalizer = null;
      this.pre_tokenizer = new MetaspacePreTokenizer({
        replacement: SPIECE_UNDERLINE,
        add_prefix_space: true,
        prepend_scheme: "first"
      });
    }
  }
  /**
   * Helper function to handle legacy encoding of SPM tokenizers.
   * Adapted from https://github.com/huggingface/transformers/blob/e6dcf8abd6f65bb4b6dfc1831b20d9ba49ce00e2/src/transformers/models/t5/tokenization_t5.py#L374-L387
   * @param {string} text The text to encode.
   * @returns {string[]} The encoded tokens.
   */
  _encode_text(text) {
    if (text === null) return null;
    if (this.legacy || text.length === 0) {
      return super._encode_text(text);
    }
    let tokens = super._encode_text(SPIECE_UNDERLINE + text.replaceAll(SPIECE_UNDERLINE, " ")) ?? [];
    if (tokens.length > 1 && tokens[0] === SPIECE_UNDERLINE && this.special_tokens.includes(tokens[1])) {
      tokens = tokens.slice(1);
    }
    return tokens;
  }
};
var CodeLlamaTokenizer = class extends PreTrainedTokenizer {
};
var XLMRobertaTokenizer = class extends PreTrainedTokenizer {
};
var MPNetTokenizer = class extends PreTrainedTokenizer {
};
var FalconTokenizer = class extends PreTrainedTokenizer {
};
var GPTNeoXTokenizer = class extends PreTrainedTokenizer {
};
var EsmTokenizer = class extends PreTrainedTokenizer {
};
var Qwen2Tokenizer = class extends PreTrainedTokenizer {
};
var GemmaTokenizer = class extends PreTrainedTokenizer {
};
var Grok1Tokenizer = class extends PreTrainedTokenizer {
};
function _build_translation_inputs(self2, raw_inputs, tokenizer_options, generate_kwargs) {
  if (!("language_codes" in self2) || !Array.isArray(self2.language_codes)) {
    throw new Error("Tokenizer must have `language_codes` attribute set and it should be an array of language ids.");
  }
  if (!("languageRegex" in self2) || !(self2.languageRegex instanceof RegExp)) {
    throw new Error("Tokenizer must have `languageRegex` attribute set and it should be a regular expression.");
  }
  if (!("lang_to_token" in self2) || typeof self2.lang_to_token !== "function") {
    throw new Error("Tokenizer must have `lang_to_token` attribute set and it should be a function.");
  }
  const src_lang_token = generate_kwargs.src_lang;
  const tgt_lang_token = generate_kwargs.tgt_lang;
  if (!self2.language_codes.includes(tgt_lang_token)) {
    throw new Error(
      `Target language code "${tgt_lang_token}" is not valid. Must be one of: {${self2.language_codes.join(", ")}}`
    );
  }
  if (src_lang_token !== undefined) {
    if (!self2.language_codes.includes(src_lang_token)) {
      throw new Error(
        `Source language code "${src_lang_token}" is not valid. Must be one of: {${self2.language_codes.join(", ")}}`
      );
    }
    for (const item of self2.post_processor.config.single) {
      if ("SpecialToken" in item && self2.languageRegex.test(item.SpecialToken.id)) {
        item.SpecialToken.id = self2.lang_to_token(src_lang_token);
        break;
      }
    }
  }
  generate_kwargs.forced_bos_token_id = self2.model.convert_tokens_to_ids([self2.lang_to_token(tgt_lang_token)])[0];
  return self2._call(raw_inputs, tokenizer_options);
}
var NllbTokenizer = class extends PreTrainedTokenizer {
  constructor(tokenizerJSON, tokenizerConfig) {
    super(tokenizerJSON, tokenizerConfig);
    this.languageRegex = /^[a-z]{3}_[A-Z][a-z]{3}$/;
    this.language_codes = this.special_tokens.filter((x) => this.languageRegex.test(x));
    this.lang_to_token = (x) => x;
  }
  /**
   * Helper function to build translation inputs for an `NllbTokenizer`.
   * @param {string|string[]} raw_inputs The text to tokenize.
   * @param {Object} tokenizer_options Options to be sent to the tokenizer
   * @param {Object} generate_kwargs Generation options.
   * @returns {Object} Object to be passed to the model.
   */
  _build_translation_inputs(raw_inputs, tokenizer_options, generate_kwargs) {
    return _build_translation_inputs(this, raw_inputs, tokenizer_options, generate_kwargs);
  }
};
var M2M100Tokenizer = class extends PreTrainedTokenizer {
  constructor(tokenizerJSON, tokenizerConfig) {
    super(tokenizerJSON, tokenizerConfig);
    this.languageRegex = /^__[a-z]{2,3}__$/;
    this.language_codes = this.special_tokens.filter((x) => this.languageRegex.test(x)).map((x) => x.slice(2, -2));
    this.lang_to_token = (x) => `__${x}__`;
  }
  /**
   * Helper function to build translation inputs for an `M2M100Tokenizer`.
   * @param {string|string[]} raw_inputs The text to tokenize.
   * @param {Object} tokenizer_options Options to be sent to the tokenizer
   * @param {Object} generate_kwargs Generation options.
   * @returns {Object} Object to be passed to the model.
   */
  _build_translation_inputs(raw_inputs, tokenizer_options, generate_kwargs) {
    return _build_translation_inputs(this, raw_inputs, tokenizer_options, generate_kwargs);
  }
};
var WhisperTokenizer = class extends PreTrainedTokenizer {
  get timestamp_begin() {
    return this.model.convert_tokens_to_ids(["<|notimestamps|>"])[0] + 1;
  }
  /**
   * Decodes automatic speech recognition (ASR) sequences.
   * @param {Array<{tokens: bigint[], token_timestamps?: number[], stride: number[]}>} sequences The sequences to decode.
   * @param {Object} options The options to use for decoding.
   * @returns {Array<string|{chunks?: undefined|Array<{language: string|null, timestamp: Array<number|null>, text: string}>}>} The decoded sequences.
   */
  _decode_asr(sequences, {
    return_timestamps = null,
    return_language = false,
    time_precision = null,
    force_full_sequences = true
  } = {}) {
    if (time_precision === null) {
      throw Error("Must specify time_precision");
    }
    let last_language = null;
    const returnWordTimestamps = return_timestamps === "word";
    function new_chunk() {
      return { language: last_language, "timestamp": [null, null], text: "", words: [] };
    }
    const chunks = [];
    let chunk = new_chunk();
    let time_offset = 0;
    const timestamp_begin = this.timestamp_begin;
    const total_timestamp_tokens = 1500;
    const timestamp_end = timestamp_begin + total_timestamp_tokens;
    let previous_tokens = [];
    let previous_token_timestamps = [];
    let skip = false;
    let right_stride_start = null;
    const all_special_ids = new Set(this.all_special_ids);
    for (const output of sequences) {
      const token_ids = output.tokens;
      const token_timestamps = returnWordTimestamps ? output.token_timestamps : null;
      let last_timestamp = null;
      let first_timestamp = timestamp_begin;
      if ("stride" in output) {
        const [chunk_len, stride_left, stride_right] = output.stride;
        time_offset -= stride_left;
        right_stride_start = chunk_len - stride_right;
        if (stride_left) {
          first_timestamp = stride_left / time_precision + timestamp_begin;
        }
        if (stride_right) {
          for (let i = token_ids.length - 1; i >= 0; --i) {
            const token = Number(token_ids[i]);
            if (token >= timestamp_begin) {
              if (last_timestamp !== null && (token - timestamp_begin) * time_precision < right_stride_start) {
                break;
              }
              last_timestamp = token;
            }
          }
        }
      }
      let current_tokens = [];
      let current_token_timestamps = null;
      for (let i = 0; i < token_ids.length; ++i) {
        const token = Number(token_ids[i]);
        if (all_special_ids.has(token)) {
          const text = this.decode([token]);
          const language = WHISPER_LANGUAGE_MAPPING.get(text.slice(2, -2));
          if (language !== undefined) {
            if (last_language !== null && language !== last_language && !return_timestamps) {
              previous_tokens.push(...current_tokens);
              const resolved_tokens = this.findLongestCommonSequence([previous_tokens], null)[0];
              const resolved_text = this.decode(resolved_tokens ?? [], {});
              chunk.text = resolved_text;
              chunks.push(chunk);
              previous_tokens = [];
              current_tokens = [];
              chunk = new_chunk();
            }
            last_language = chunk.language = language;
          }
        } else if (token >= timestamp_begin && token <= timestamp_end) {
          const time = (token - timestamp_begin) * time_precision + time_offset;
          const rounded_time = round(time, 2);
          if (last_timestamp !== null && token >= last_timestamp) {
            skip = true;
          } else if (skip || previous_tokens.length > 0 && token < first_timestamp) {
            skip = false;
          } else if (chunk.timestamp[0] === null) {
            chunk.timestamp[0] = rounded_time;
          } else {
            if (rounded_time === chunk.timestamp[0]) ; else {
              chunk.timestamp[1] = rounded_time;
              previous_tokens.push(...current_tokens);
              if (returnWordTimestamps && current_token_timestamps) {
                previous_token_timestamps.push(...current_token_timestamps);
              }
              const [resolved_tokens, resolved_token_timestamps] = this.findLongestCommonSequence(
                [previous_tokens],
                [previous_token_timestamps]
              );
              const resolved_text = this.decode(resolved_tokens ?? [], {});
              chunk.text = resolved_text;
              if (returnWordTimestamps) {
                chunk.words = this.collateWordTimestamps(resolved_tokens ?? [], [resolved_token_timestamps ?? []], last_language);
              }
              chunks.push(chunk);
              previous_tokens = [];
              current_tokens = [];
              previous_token_timestamps = [];
              current_token_timestamps = [];
              chunk = new_chunk();
            }
          }
        } else {
          current_tokens.push(token);
          if (returnWordTimestamps) {
            let start_time = round(token_timestamps[i] + time_offset, 2);
            let end_time = null;
            if (i + 1 < token_timestamps.length) {
              end_time = round(token_timestamps[i + 1] + time_offset, 2);
              const decoded_text = this.decode([token], {});
              if (PUNCTUATION_ONLY_REGEX.test(decoded_text)) {
                end_time = round(Math.min(start_time + time_precision, end_time), 2);
              }
            } else {
              end_time = null;
            }
            current_token_timestamps.push([start_time, end_time]);
          }
        }
      }
      if ("stride" in output) {
        const [chunk_len, stride_left, stride_right] = output.stride;
        time_offset += chunk_len - stride_right;
      }
      if (current_tokens.length > 0) {
        previous_tokens.push(...current_tokens);
        if (returnWordTimestamps && current_token_timestamps) {
          previous_token_timestamps.push(...current_token_timestamps);
        }
      } else if (previous_tokens.every((p) => p.length === 0)) {
        chunk = new_chunk();
        previous_tokens = [];
        current_tokens = [];
        previous_token_timestamps = [];
        current_token_timestamps = [];
      }
    }
    if (previous_tokens.length > 0) {
      if (force_full_sequences && return_timestamps) {
        throw new Error(
          "Whisper did not predict an ending timestamp, which can happen if audio is cut off in the middle of a word. Also make sure WhisperTimeStampLogitsProcessor was used during generation."
        );
      }
      const [resolved_tokens, resolved_token_timestamps] = this.findLongestCommonSequence(
        [previous_tokens],
        [previous_token_timestamps]
      );
      const resolved_text = this.decode(resolved_tokens, {});
      chunk.text = resolved_text;
      if (returnWordTimestamps) {
        chunk.words = this.collateWordTimestamps(resolved_tokens, resolved_token_timestamps, last_language);
      }
      chunks.push(chunk);
    }
    let optional = /* @__PURE__ */ Object.create(null);
    const full_text = chunks.map((chunk2) => chunk2.text).join("");
    if (return_timestamps || return_language) {
      for (let i = 0; i < chunks.length; ++i) {
        const chunk2 = chunks[i];
        if (!return_timestamps) {
          delete chunk2["timestamp"];
        }
        if (!return_language) {
          delete chunk2["language"];
        }
      }
      if (returnWordTimestamps) {
        const new_chunks = [];
        for (const chunk2 of chunks) {
          for (const word of chunk2.words) {
            new_chunks.push(word);
          }
        }
        optional = { chunks: new_chunks };
      } else {
        optional = { chunks };
      }
    }
    return [full_text, optional];
  }
  /**
   * Finds the longest common sequence among the provided sequences.
   * @param {number[][]} sequences An array of sequences of token ids to compare.
   * @returns {number[][]} The longest common sequence found.
   * @throws {Error} If there is a bug within the function.
   * @private
   */
  findLongestCommonSequence(sequences, token_timestamp_sequences = null) {
    let leftSequence = sequences[0];
    let leftLength = leftSequence.length;
    let totalSequence = [];
    const use_token_timestamp_sequences = Array.isArray(token_timestamp_sequences) && token_timestamp_sequences.length > 0;
    let total_token_timestamp_sequence = use_token_timestamp_sequences ? [] : null;
    let left_token_timestamp_sequence = use_token_timestamp_sequences ? token_timestamp_sequences[0] : null;
    for (let i = 1; i < sequences.length; ++i) {
      const rightSequence = sequences[i];
      let max3 = 0;
      let maxIndices = [leftLength, leftLength, 0, 0];
      const rightLength = rightSequence.length;
      for (let j = 1; j < leftLength + rightLength; ++j) {
        const leftStart2 = Math.max(0, leftLength - j);
        const leftStop2 = Math.min(leftLength, leftLength + rightLength - j);
        const left = leftSequence.slice(leftStart2, leftStop2);
        const rightStart2 = Math.max(0, j - leftLength);
        const rightStop2 = Math.min(rightLength, j);
        const right = rightSequence.slice(rightStart2, rightStop2);
        if (left.length !== right.length) {
          throw new Error(
            "There is a bug within whisper `decode_asr` function, please report it. Dropping to prevent bad inference."
          );
        }
        let matches;
        if (use_token_timestamp_sequences) {
          matches = left.filter(
            (elem, idx) => elem === right[idx] && left_token_timestamp_sequence[leftStart2 + idx] <= token_timestamp_sequences[i][rightStart2 + idx]
          ).length;
        } else {
          matches = left.filter((elem, idx) => elem === right[idx]).length;
        }
        const eps = j / 1e4;
        const matching = matches / j + eps;
        if (matches > 1 && matching > max3) {
          max3 = matching;
          maxIndices = [leftStart2, leftStop2, rightStart2, rightStop2];
        }
      }
      const [leftStart, leftStop, rightStart, rightStop] = maxIndices;
      const leftMid = Math.floor((leftStop + leftStart) / 2);
      const rightMid = Math.floor((rightStop + rightStart) / 2);
      totalSequence.push(...leftSequence.slice(0, leftMid));
      leftSequence = rightSequence.slice(rightMid);
      leftLength = leftSequence.length;
      if (use_token_timestamp_sequences) {
        total_token_timestamp_sequence.push(...[left_token_timestamp_sequence?.slice(0, leftMid) ?? []]);
        left_token_timestamp_sequence = token_timestamp_sequences[i].slice(rightMid);
      }
    }
    totalSequence.push(...leftSequence);
    if (use_token_timestamp_sequences) {
      total_token_timestamp_sequence.push(...[left_token_timestamp_sequence ?? []]);
      return [totalSequence, total_token_timestamp_sequence];
    } else {
      return [totalSequence, []];
    }
  }
  /** @private */
  collateWordTimestamps(tokens, token_timestamps, language) {
    const [words, _, token_indices] = this.combineTokensIntoWords(tokens, language);
    const timings = [];
    for (let i = 0; i < words.length; ++i) {
      const indices = token_indices[i];
      timings.push({
        text: words[i],
        timestamp: [token_timestamps[indices.at(0)][0], token_timestamps[indices.at(-1)][1]]
      });
    }
    return timings;
  }
  /**
   * Groups tokens by word. Returns a tuple containing a list of strings with the words,
   * and a list of `token_id` sequences with the tokens making up each word.
   * @param {number[]} tokens
   * @param {string} [language]
   * @param {string} prepend_punctionations
   * @param {string} append_punctuations
   *
   * @private
   */
  combineTokensIntoWords(tokens, language, prepend_punctionations = `"'"\xA1\xBF([{-`, append_punctuations = `"'.\u3002,\uFF0C!\uFF01?\uFF1F:\uFF1A"")]}\u3001`) {
    language = language ?? "english";
    let words, word_tokens, token_indices;
    if (["chinese", "japanese", "thai", "lao", "myanmar"].includes(language)) {
      [words, word_tokens, token_indices] = this.splitTokensOnUnicode(tokens);
    } else {
      [words, word_tokens, token_indices] = this.splitTokensOnSpaces(tokens);
    }
    return this.mergePunctuations(words, word_tokens, token_indices, prepend_punctionations, append_punctuations);
  }
  /** @type {PreTrainedTokenizer['decode']} */
  decode(token_ids, decode_args = {}) {
    let text;
    if (decode_args?.decode_with_timestamps) {
      if (token_ids instanceof Tensor3) {
        token_ids = prepareTensorForDecode(token_ids);
      }
      text = this.decodeWithTimestamps(token_ids, decode_args);
    } else {
      text = super.decode(token_ids, decode_args);
    }
    return text;
  }
  /**
   * @param {number[]|bigint[]} token_ids List of token IDs to decode.
   * @param {Object} decode_args Optional arguments for decoding
   * @private
   */
  decodeWithTimestamps(token_ids, decode_args) {
    const time_precision = decode_args?.time_precision ?? 0.02;
    const timestamp_begin = (Array.from(this.all_special_ids).at(-1) ?? 0) + 1;
    let outputs = Array();
    for (let token of token_ids) {
      token = Number(token);
      if (token >= timestamp_begin) {
        const timestamp = ((token - timestamp_begin) * time_precision).toFixed(2);
        outputs.push(`<|${timestamp}|>`);
        outputs.push([]);
      } else {
        outputs[outputs.length - 1].push(token);
      }
    }
    outputs = outputs.map((s) => typeof s === "string" ? s : super.decode(s, decode_args));
    return outputs.join("");
  }
  /**
   * Combine tokens into words by splitting at any position where the tokens are decoded as valid unicode points.
   * @param {number[]} tokens
   * @returns {*}
   * @private
   */
  splitTokensOnUnicode(tokens) {
    const decoded_full = this.decode(tokens, {
      // @ts-ignore
      decode_with_timestamps: true
    });
    const replacement_char = "\uFFFD";
    const words = [];
    const word_tokens = [];
    const token_indices = [];
    let current_tokens = [];
    let current_indices = [];
    let unicode_offset = 0;
    for (let token_idx = 0; token_idx < tokens.length; ++token_idx) {
      const token = tokens[token_idx];
      current_tokens.push(token);
      current_indices.push(token_idx);
      const decoded = this.decode(current_tokens, {
        // @ts-ignore
        decode_with_timestamps: true
      });
      if (!decoded.includes(replacement_char) || decoded_full[unicode_offset + decoded.indexOf(replacement_char)] === replacement_char) {
        words.push(decoded);
        word_tokens.push(current_tokens);
        token_indices.push(current_indices);
        current_tokens = [];
        current_indices = [];
        unicode_offset += decoded.length;
      }
    }
    return [words, word_tokens, token_indices];
  }
  /**
   * Combine tokens into words by splitting at whitespace and punctuation tokens.
   * @param {number[]} tokens
   * @private
   */
  splitTokensOnSpaces(tokens) {
    const [subwords, subword_tokens_list, subword_indices_list] = this.splitTokensOnUnicode(tokens);
    const words = [];
    const word_tokens = [];
    const token_indices = [];
    const punctuationRegex = new RegExp(`^[${PUNCTUATION_REGEX}]$`, "gu");
    for (let i = 0; i < subwords.length; ++i) {
      const subword = subwords[i];
      const subword_tokens = subword_tokens_list[i];
      const subword_indices = subword_indices_list[i];
      const special = subword_tokens[0] >= this.model.tokens_to_ids.get("<|endoftext|>");
      const with_space = subword.startsWith(" ");
      const trimmed = subword.trim();
      const punctuation = punctuationRegex.test(trimmed);
      if (special || with_space || punctuation || words.length === 0) {
        words.push(subword);
        word_tokens.push(subword_tokens);
        token_indices.push(subword_indices);
      } else {
        const ix = words.length - 1;
        words[ix] += subword;
        word_tokens[ix].push(...subword_tokens);
        token_indices[ix].push(...subword_indices);
      }
    }
    return [words, word_tokens, token_indices];
  }
  /**
   * Merges punctuation tokens with neighboring words.
   * @param {string[]} words
   * @param {number[][]} tokens
   * @param {number[][]} indices
   * @param {string} prepended
   * @param {string} appended
   * @private
   */
  mergePunctuations(words, tokens, indices, prepended, appended) {
    const newWords = structuredClone(words);
    const newTokens = structuredClone(tokens);
    const newIndices = structuredClone(indices);
    let i = newWords.length - 2;
    let j = newWords.length - 1;
    while (i >= 0) {
      if (newWords[i].startsWith(" ") && prepended.includes(newWords[i].trim())) {
        newWords[j] = newWords[i] + newWords[j];
        newTokens[j] = mergeArrays(newTokens[i], newTokens[j]);
        newIndices[j] = mergeArrays(newIndices[i], newIndices[j]);
        newWords[i] = "";
        newTokens[i] = [];
        newIndices[i] = [];
      } else {
        j = i;
      }
      --i;
    }
    i = 0;
    j = 1;
    while (j < newWords.length) {
      if (!newWords[i].endsWith(" ") && appended.includes(newWords[j])) {
        newWords[i] += newWords[j];
        newTokens[i] = mergeArrays(newTokens[i], newTokens[j]);
        newIndices[i] = mergeArrays(newIndices[i], newIndices[j]);
        newWords[j] = "";
        newTokens[j] = [];
        newIndices[j] = [];
      } else {
        i = j;
      }
      ++j;
    }
    return [newWords.filter((x) => x), newTokens.filter((x) => x.length > 0), newIndices.filter((x) => x.length > 0)];
  }
};
var CodeGenTokenizer = class extends PreTrainedTokenizer {
};
var CLIPTokenizer = class extends PreTrainedTokenizer {
};
var SiglipTokenizer = class extends PreTrainedTokenizer {
};
var MarianTokenizer = class extends PreTrainedTokenizer {
  constructor(tokenizerJSON, tokenizerConfig) {
    super(tokenizerJSON, tokenizerConfig);
    this.languageRegex = /^(>>\w+<<)\s*/g;
    this.supported_language_codes = this.model.vocab.filter((x) => this.languageRegex.test(x));
    console.warn(
      'WARNING: `MarianTokenizer` is not yet supported by Hugging Face\'s "fast" tokenizers library. Therefore, you may experience slightly inaccurate results.'
    );
  }
  /**
   * Encodes a single text. Overriding this method is necessary since the language codes
   * must be removed before encoding with sentencepiece model.
   * @see https://github.com/huggingface/transformers/blob/12d51db243a00726a548a43cc333390ebae731e3/src/transformers/models/marian/tokenization_marian.py#L204-L213
   *
   * @param {string|null} text The text to encode.
   * @returns {Array} The encoded tokens.
   */
  _encode_text(text) {
    if (text === null) return null;
    const [matchInfo, ...remainder] = text.trim().split(this.languageRegex);
    if (remainder.length === 0) {
      return super._encode_text(matchInfo);
    } else if (remainder.length === 2) {
      const [language, text2] = remainder;
      if (!this.supported_language_codes.includes(language)) {
        console.warn(
          `Unsupported language code "${language}" detected, which may lead to unexpected behavior. Should be one of: ${JSON.stringify(this.supported_language_codes)}`
        );
      }
      return mergeArrays([language], super._encode_text(text2));
    }
  }
};
var Wav2Vec2CTCTokenizer = class extends PreTrainedTokenizer {
};
var BlenderbotTokenizer = class extends PreTrainedTokenizer {
};
var BlenderbotSmallTokenizer = class extends PreTrainedTokenizer {
};
var SpeechT5Tokenizer = class extends PreTrainedTokenizer {
};
var NougatTokenizer = class extends PreTrainedTokenizer {
};
var VitsTokenizer = class extends PreTrainedTokenizer {
  constructor(tokenizerJSON, tokenizerConfig) {
    super(tokenizerJSON, tokenizerConfig);
    this.decoder = new VitsDecoder({});
  }
};
var CohereTokenizer = class extends PreTrainedTokenizer {
};
var MgpstrTokenizer = class extends PreTrainedTokenizer {
};
var AutoTokenizer = class {
  /**
   * Instantiate one of the tokenizer classes of the library from a pretrained model.
   *
   * The tokenizer class to instantiate is selected based on the `tokenizer_class` property of the config object
   * (either passed as an argument or loaded from `pretrained_model_name_or_path` if possible)
   *
   * @param {string} pretrained_model_name_or_path The name or path of the pretrained model. Can be either:
   * - A string, the *model id* of a pretrained tokenizer hosted inside a model repo on huggingface.co.
   *   Valid model ids can be located at the root-level, like `bert-base-uncased`, or namespaced under a
   *   user or organization name, like `dbmdz/bert-base-german-cased`.
   * - A path to a *directory* containing tokenizer files, e.g., `./my_model_directory/`.
   * @param {PretrainedTokenizerOptions} options Additional options for loading the tokenizer.
   *
   * @returns {Promise<PreTrainedTokenizer>} A new instance of the PreTrainedTokenizer class.
   */
  static async from_pretrained(pretrained_model_name_or_path, {
    progress_callback = null,
    config = null,
    cache_dir = null,
    local_files_only = false,
    revision = "main",
    legacy = undefined
  } = {}) {
    const [tokenizerJSON, tokenizerConfig] = await loadTokenizer(pretrained_model_name_or_path, {
      progress_callback,
      config,
      cache_dir,
      local_files_only,
      revision,
      legacy
    });
    const tokenizerName = tokenizerConfig.tokenizer_class?.replace(/Fast$/, "") ?? "PreTrainedTokenizer";
    let cls = this.TOKENIZER_CLASS_MAPPING[tokenizerName];
    if (!cls) {
      console.warn(`Unknown tokenizer class "${tokenizerName}", attempting to construct from base class.`);
      cls = PreTrainedTokenizer;
    }
    return new cls(tokenizerJSON, tokenizerConfig);
  }
};
AutoTokenizer.TOKENIZER_CLASS_MAPPING = {
  T5Tokenizer,
  DistilBertTokenizer,
  CamembertTokenizer,
  DebertaTokenizer,
  DebertaV2Tokenizer,
  BertTokenizer,
  HerbertTokenizer,
  ConvBertTokenizer,
  RoFormerTokenizer,
  XLMTokenizer,
  ElectraTokenizer,
  MobileBertTokenizer,
  SqueezeBertTokenizer,
  AlbertTokenizer,
  GPT2Tokenizer,
  BartTokenizer,
  MBartTokenizer,
  MBart50Tokenizer,
  RobertaTokenizer,
  WhisperTokenizer,
  CodeGenTokenizer,
  CLIPTokenizer,
  SiglipTokenizer,
  MarianTokenizer,
  BloomTokenizer,
  NllbTokenizer,
  M2M100Tokenizer,
  LlamaTokenizer,
  CodeLlamaTokenizer,
  XLMRobertaTokenizer,
  MPNetTokenizer,
  FalconTokenizer,
  GPTNeoXTokenizer,
  EsmTokenizer,
  Wav2Vec2CTCTokenizer,
  BlenderbotTokenizer,
  BlenderbotSmallTokenizer,
  SpeechT5Tokenizer,
  NougatTokenizer,
  VitsTokenizer,
  Qwen2Tokenizer,
  GemmaTokenizer,
  Grok1Tokenizer,
  CohereTokenizer,
  MgpstrTokenizer,
  // Base case:
  PreTrainedTokenizer
};

// src/libs/transformers/configs.ts
async function loadConfig(pretrained_model_name_or_path, options) {
  return await getModelJSON(pretrained_model_name_or_path, "config.json", true, options);
}
function getNormalizedConfig(config) {
  const mapping = {};
  let init_normalized_config = {};
  switch (config.model_type) {
    // Sub-configs
    case "llava":
    case "paligemma":
    case "florence2":
    case "llava_onevision":
    case "idefics3":
      init_normalized_config = getNormalizedConfig(config.text_config);
      break;
    case "moondream1":
      init_normalized_config = getNormalizedConfig(config.phi_config);
      break;
    case "musicgen":
      init_normalized_config = getNormalizedConfig(config.decoder);
      break;
    case "multi_modality":
      init_normalized_config = getNormalizedConfig(config.language_config);
      break;
    // Decoder-only models
    case "gpt2":
    case "gptj":
    case "jais":
    case "codegen":
    case "gpt_bigcode":
      mapping["num_heads"] = "n_head";
      mapping["num_layers"] = "n_layer";
      mapping["hidden_size"] = "n_embd";
      break;
    case "gpt_neox":
    case "stablelm":
    case "opt":
    case "falcon":
      mapping["num_heads"] = "num_attention_heads";
      mapping["num_layers"] = "num_hidden_layers";
      mapping["hidden_size"] = "hidden_size";
      break;
    case "llama":
    case "olmo":
    case "olmo2":
    case "mobilellm":
    case "granite":
    case "cohere":
    case "mistral":
    case "starcoder2":
    case "qwen2":
    case "qwen2_vl":
    case "phi":
    case "phi3":
    case "phi3_v":
      mapping["num_heads"] = "num_key_value_heads";
      mapping["num_layers"] = "num_hidden_layers";
      mapping["hidden_size"] = "hidden_size";
      mapping["num_attention_heads"] = "num_attention_heads";
      break;
    case "gemma":
    case "gemma2":
      mapping["num_heads"] = "num_key_value_heads";
      mapping["num_layers"] = "num_hidden_layers";
      mapping["dim_kv"] = "head_dim";
      break;
    case "openelm":
      mapping["num_heads"] = "num_kv_heads";
      mapping["num_layers"] = "num_transformer_layers";
      mapping["dim_kv"] = "head_dim";
      break;
    case "gpt_neo":
    case "donut-swin":
      mapping["num_heads"] = "num_heads";
      mapping["num_layers"] = "num_layers";
      mapping["hidden_size"] = "hidden_size";
      break;
    case "bloom":
      mapping["num_heads"] = "n_head";
      mapping["num_layers"] = "n_layer";
      mapping["hidden_size"] = "hidden_size";
      break;
    case "mpt":
      mapping["num_heads"] = "n_heads";
      mapping["num_layers"] = "n_layers";
      mapping["hidden_size"] = "d_model";
      break;
    case "exaone":
      mapping["num_heads"] = "num_key_value_heads";
      mapping["num_layers"] = "num_layers";
      mapping["dim_kv"] = "head_dim";
      mapping["num_attention_heads"] = "num_attention_heads";
      break;
    // Encoder-decoder models
    case "t5":
    case "mt5":
    case "longt5":
      mapping["num_decoder_layers"] = "num_decoder_layers";
      mapping["num_decoder_heads"] = "num_heads";
      mapping["decoder_dim_kv"] = "d_kv";
      mapping["num_encoder_layers"] = "num_layers";
      mapping["num_encoder_heads"] = "num_heads";
      mapping["encoder_dim_kv"] = "d_kv";
      break;
    case "bart":
    case "mbart":
    case "marian":
    case "whisper":
    case "m2m_100":
    case "blenderbot":
    case "blenderbot-small":
    case "florence2_language":
      mapping["num_decoder_layers"] = "decoder_layers";
      mapping["num_decoder_heads"] = "decoder_attention_heads";
      mapping["decoder_hidden_size"] = "d_model";
      mapping["num_encoder_layers"] = "encoder_layers";
      mapping["num_encoder_heads"] = "encoder_attention_heads";
      mapping["encoder_hidden_size"] = "d_model";
      break;
    case "speecht5":
      mapping["num_decoder_layers"] = "decoder_layers";
      mapping["num_decoder_heads"] = "decoder_attention_heads";
      mapping["decoder_hidden_size"] = "hidden_size";
      mapping["num_encoder_layers"] = "encoder_layers";
      mapping["num_encoder_heads"] = "encoder_attention_heads";
      mapping["encoder_hidden_size"] = "hidden_size";
      break;
    case "trocr":
      mapping["num_encoder_layers"] = mapping["num_decoder_layers"] = "decoder_layers";
      mapping["num_encoder_heads"] = mapping["num_decoder_heads"] = "decoder_attention_heads";
      mapping["encoder_hidden_size"] = mapping["decoder_hidden_size"] = "d_model";
      break;
    case "musicgen_decoder":
    case "moonshine":
      mapping["num_encoder_layers"] = mapping["num_decoder_layers"] = "num_hidden_layers";
      mapping["num_encoder_heads"] = mapping["num_decoder_heads"] = "num_attention_heads";
      mapping["encoder_hidden_size"] = mapping["decoder_hidden_size"] = "hidden_size";
      break;
    case "vision-encoder-decoder":
      const decoderConfig = getNormalizedConfig(config.decoder);
      const add_encoder_pkv = "num_decoder_layers" in decoderConfig;
      const result = pick(config, ["model_type", "is_encoder_decoder"]);
      if (add_encoder_pkv) {
        result.num_decoder_layers = decoderConfig.num_decoder_layers;
        result.num_decoder_heads = decoderConfig.num_decoder_heads;
        result.decoder_hidden_size = decoderConfig.decoder_hidden_size;
        result.num_encoder_layers = decoderConfig.num_encoder_layers;
        result.num_encoder_heads = decoderConfig.num_encoder_heads;
        result.encoder_hidden_size = decoderConfig.encoder_hidden_size;
      } else {
        result.num_layers = decoderConfig.num_layers;
        result.num_heads = decoderConfig.num_heads;
        result.hidden_size = decoderConfig.hidden_size;
      }
      return result;
  }
  const normalized_config = {
    ...init_normalized_config,
    ...pick(config, ["model_type", "multi_query", "is_encoder_decoder"])
  };
  for (const key in mapping) {
    normalized_config[key] = config[mapping[key]];
  }
  return normalized_config;
}
function getKeyValueShapes(config, { prefix = "past_key_values", batch_size = 1 } = {}) {
  const decoderFeeds = {};
  const normalized_config = config.normalized_config;
  if (normalized_config.is_encoder_decoder && "num_encoder_heads" in normalized_config && "num_decoder_heads" in normalized_config) {
    const encoder_dim_kv = normalized_config.encoder_dim_kv ?? normalized_config.encoder_hidden_size / normalized_config.num_encoder_heads;
    const decoder_dim_kv = normalized_config.decoder_dim_kv ?? normalized_config.decoder_hidden_size / normalized_config.num_decoder_heads;
    const encoder_dims = [batch_size, normalized_config.num_encoder_heads, 0, encoder_dim_kv];
    const decoder_dims = [batch_size, normalized_config.num_decoder_heads, 0, decoder_dim_kv];
    for (let i = 0; i < normalized_config.num_decoder_layers; ++i) {
      decoderFeeds[`${prefix}.${i}.encoder.key`] = encoder_dims;
      decoderFeeds[`${prefix}.${i}.encoder.value`] = encoder_dims;
      decoderFeeds[`${prefix}.${i}.decoder.key`] = decoder_dims;
      decoderFeeds[`${prefix}.${i}.decoder.value`] = decoder_dims;
    }
  } else {
    const num_heads = normalized_config.num_heads;
    const num_layers = normalized_config.num_layers;
    const dim_kv = normalized_config.dim_kv ?? normalized_config.hidden_size / (normalized_config.num_attention_heads ?? num_heads);
    if (normalized_config.model_type === "falcon") {
      const dims = [batch_size * num_heads, 0, dim_kv];
      for (let i = 0; i < num_layers; ++i) {
        decoderFeeds[`${prefix}.${i}.key`] = dims;
        decoderFeeds[`${prefix}.${i}.value`] = dims;
      }
    } else if (normalized_config.multi_query) {
      const dims = [batch_size * num_heads, 0, 2 * dim_kv];
      for (let i = 0; i < num_layers; ++i) {
        decoderFeeds[`${prefix}.${i}.key_value`] = dims;
      }
    } else if (normalized_config.model_type === "bloom") {
      const keyDims = [batch_size * num_heads, dim_kv, 0];
      const valueDims = [batch_size * num_heads, 0, dim_kv];
      for (let i = 0; i < num_layers; ++i) {
        decoderFeeds[`${prefix}.${i}.key`] = keyDims;
        decoderFeeds[`${prefix}.${i}.value`] = valueDims;
      }
    } else if (normalized_config.model_type === "openelm") {
      for (let i = 0; i < num_layers; ++i) {
        const dims = [batch_size, num_heads[i], 0, dim_kv];
        decoderFeeds[`${prefix}.${i}.key`] = dims;
        decoderFeeds[`${prefix}.${i}.value`] = dims;
      }
    } else {
      const dims = [batch_size, num_heads, 0, dim_kv];
      for (let i = 0; i < num_layers; ++i) {
        decoderFeeds[`${prefix}.${i}.key`] = dims;
        decoderFeeds[`${prefix}.${i}.value`] = dims;
      }
    }
  }
  return decoderFeeds;
}
var PretrainedConfig = class _PretrainedConfig {
  /**
   * Create a new PreTrainedTokenizer instance.
   * @param {Record<string, any>} configJSON The JSON of the config.
   */
  constructor(configJSON) {
    /** @type {string|null} */
    this.model_type = null;
    /** @type {boolean} */
    this.is_encoder_decoder = false;
    Object.assign(this, configJSON);
    this.normalized_config = getNormalizedConfig(this);
  }
  /**
   * Loads a pre-trained config from the given `pretrained_model_name_or_path`.
   *
   * @param {string} pretrained_model_name_or_path The path to the pre-trained config.
   * @param {PretrainedOptions} options Additional options for loading the config.
   * @throws {Error} Throws an error if the config.json is not found in the `pretrained_model_name_or_path`.
   *
   * @returns {Promise<PretrainedConfig>} A new instance of the `PretrainedConfig` class.
   */
  static async from_pretrained(pretrained_model_name_or_path, {
    progress_callback = null,
    config = null,
    cache_dir = null,
    local_files_only = false,
    revision = "main"
  } = {}) {
    if (config && !(config instanceof _PretrainedConfig)) {
      config = new _PretrainedConfig(config);
    }
    const data = config ?? await loadConfig(pretrained_model_name_or_path, {
      progress_callback,
      config,
      cache_dir,
      local_files_only,
      revision
    });
    return new this(data);
  }
};
var AutoConfig = class {
  /** @type {typeof PretrainedConfig.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options) {
    return PretrainedConfig.from_pretrained(pretrained_model_name_or_path, options);
  }
};

// src/libs/transformers/utils/devices.ts
var DEVICE_TYPES = Object.freeze({
  auto: "auto",
  // Auto-detect based on device and environment
  gpu: "gpu",
  // Auto-detect GPU
  cpu: "cpu",
  // CPU
  wasm: "wasm",
  // WebAssembly
  webgpu: "webgpu",
  // WebGPU
  cuda: "cuda",
  // CUDA
  dml: "dml",
  // DirectML
  webnn: "webnn",
  // WebNN (default)
  "webnn-npu": "webnn-npu",
  // WebNN NPU
  "webnn-gpu": "webnn-gpu",
  // WebNN GPU
  "webnn-cpu": "webnn-cpu"
  // WebNN CPU
});

// src/libs/transformers/utils/dtypes.ts
var isWebGpuFp16Supported = /* @__PURE__ */ function() {
  let cachedResult;
  return async function() {
    if (cachedResult === undefined) {
      if (!apis.IS_WEBGPU_AVAILABLE) {
        cachedResult = false;
      } else {
        try {
          const adapter = await navigator.gpu.requestAdapter();
          if (!adapter) {
            cachedResult = false;
          } else {
            cachedResult = adapter.features.has("shader-f16");
          }
        } catch (e) {
          cachedResult = false;
        }
      }
    }
    return cachedResult;
  };
}();
var DATA_TYPES = Object.freeze({
  auto: "auto",
  // Auto-detect based on environment
  fp32: "fp32",
  fp16: "fp16",
  q8: "q8",
  int8: "int8",
  uint8: "uint8",
  q4: "q4",
  bnb4: "bnb4",
  q4f16: "q4f16"
  // fp16 model with int4 block weight quantization
});
var DEFAULT_DEVICE_DTYPE_MAPPING = Object.freeze({
  // NOTE: If not specified, will default to fp32
  [DEVICE_TYPES.wasm]: DATA_TYPES.q8
});
var DEFAULT_DTYPE_SUFFIX_MAPPING = Object.freeze({
  [DATA_TYPES.fp32]: "",
  [DATA_TYPES.fp16]: "_fp16",
  [DATA_TYPES.int8]: "_int8",
  [DATA_TYPES.uint8]: "_uint8",
  [DATA_TYPES.q8]: "_quantized",
  [DATA_TYPES.q4]: "_q4",
  [DATA_TYPES.q4f16]: "_q4f16",
  [DATA_TYPES.bnb4]: "_bnb4"
});

// src/libs/transformers/utils/constants.ts
var GITHUB_ISSUE_URL = "https://github.com/sauravpanda/browserai/issues";
var FEATURE_EXTRACTOR_NAME = "preprocessor_config.json";
var IMAGE_PROCESSOR_NAME = "preprocessor_config.json";
var PROCESSOR_NAME = "processor_config.json";

// src/libs/transformers/generation/logits_process.ts
var LogitsProcessor = class extends Callable {
  /**
   * Apply the processor to the input logits.
   *
   * @abstract
   * @param {bigint[][]} input_ids The input ids.
   * @param {Tensor} logits The logits to process.
   * @throws {Error} Throws an error if `_call` is not implemented in the subclass.
   */
  _call(input_ids, logits) {
    throw Error("`_call` should be implemented in a subclass");
  }
};
var LogitsWarper = class extends Callable {
  /**
   * Apply the processor to the input logits.
   *
   * @abstract
   * @param {bigint[][]} input_ids The input ids.
   * @param {Tensor} logits The logits to process.
   * @throws {Error} Throws an error if `_call` is not implemented in the subclass.
   */
  _call(input_ids, logits) {
    throw Error("`_call` should be implemented in a subclass");
  }
};
var LogitsProcessorList = class extends Callable {
  constructor() {
    super();
    /**
     * Constructs a new instance of `LogitsProcessorList`.
     */
    this.processors = [];
    this.processors = [];
  }
  /**
   * Adds a new logits processor to the list.
   *
   * @param {LogitsProcessor} item The logits processor function to add.
   */
  push(item) {
    this.processors.push(item);
  }
  /**
   * Adds multiple logits processors to the list.
   *
   * @param {LogitsProcessor[]} items The logits processor functions to add.
   */
  extend(items) {
    this.processors.push(...items);
  }
  /**
   * Applies all logits processors in the list to a batch of logits, modifying them in-place.
   *
   * @param {bigint[][]} input_ids The input IDs for the language model.
   * @param {Tensor} logits
   */
  _call(input_ids, logits) {
    let toReturn = logits;
    for (const processor of this.processors) {
      toReturn = processor._call(input_ids, toReturn);
    }
    return toReturn;
  }
  [Symbol.iterator]() {
    return this.processors.values();
  }
};
var ForcedBOSTokenLogitsProcessor = class extends LogitsProcessor {
  constructor(bos_token_id) {
    super();
    this.bos_token_id = bos_token_id;
  }
  /**
   * Apply the BOS token forcing to the logits.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The logits with BOS token forcing.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      if (input_ids[i].length === 1) {
        const batch_logits_data = (
          /** @type {Float32Array} */
          logits[i].data
        );
        batch_logits_data.fill(-Infinity);
        batch_logits_data[this.bos_token_id] = 0;
      }
    }
    return logits;
  }
};
var ForcedEOSTokenLogitsProcessor = class extends LogitsProcessor {
  /**
   * Create a ForcedEOSTokenLogitsProcessor.
   * @param {number} max_length The maximum length of the sequence to be generated.
   * @param {number|number[]} eos_token_id The id(s) of the *end-of-sequence* token.
   */
  constructor(max_length, eos_token_id) {
    super();
    this.max_length = max_length;
    this.eos_token_id = Array.isArray(eos_token_id) ? eos_token_id : [eos_token_id];
  }
  /**
   * Apply the processor to input_ids and logits.
   *
   * @param {bigint[][]} input_ids The input ids.
   * @param {Tensor} logits The logits tensor.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      if (input_ids[i].length === this.max_length - 1) {
        const batch_logits_data = (
          /** @type {Float32Array} */
          logits[i].data
        );
        batch_logits_data.fill(-Infinity);
        for (const eos_token of Array.isArray(this.eos_token_id) ? this.eos_token_id : [this.eos_token_id]) {
          batch_logits_data[eos_token] = 0;
        }
      }
    }
    return logits;
  }
};
var SuppressTokensAtBeginLogitsProcessor = class extends LogitsProcessor {
  /**
   * Create a SuppressTokensAtBeginLogitsProcessor.
   * @param {number[]} begin_suppress_tokens The IDs of the tokens to suppress.
   * @param {number} begin_index The number of tokens to generate before suppressing tokens.
   */
  constructor(begin_suppress_tokens, begin_index) {
    super();
    this.begin_suppress_tokens = begin_suppress_tokens;
    this.begin_index = begin_index;
  }
  /**
   * Apply the BOS token forcing to the logits.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The logits with BOS token forcing.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      if (input_ids[i].length === this.begin_index) {
        const batch_logits_data = (
          /** @type {Float32Array} */
          logits[i].data
        );
        for (const token_id of this.begin_suppress_tokens) {
          batch_logits_data[token_id] = -Infinity;
        }
      }
    }
    return logits;
  }
};
var WhisperTimeStampLogitsProcessor = class extends LogitsProcessor {
  constructor(generate_config, init_tokens) {
    super();
    this.eos_token_id = Array.isArray(generate_config.eos_token_id) ? generate_config.eos_token_id[0] ?? 0 : generate_config.eos_token_id ?? 0;
    this.no_timestamps_token_id = generate_config.no_timestamps_token_id;
    this.timestamp_begin = this.no_timestamps_token_id ? this.no_timestamps_token_id + 1 : 0;
    this.begin_index = init_tokens.length;
    if (init_tokens.at(-1) === this.no_timestamps_token_id) {
      this.begin_index -= 1;
    }
    this.max_initial_timestamp_index = generate_config.max_initial_timestamp_index;
  }
  /**
   * Modify the logits to handle timestamp tokens.
   * @param {bigint[][]} input_ids The input sequence of tokens.
   * @param {Tensor} logits The logits output by the model.
   * @returns {Tensor} The modified logits.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      const batch_logits_data = (
        /** @type {Float32Array} */
        logits[i].data
      );
      if (this.no_timestamps_token_id !== null) {
        batch_logits_data[this.no_timestamps_token_id] = -Infinity;
      }
      if (input_ids[i].length === this.begin_index - 1) {
        batch_logits_data.fill(-Infinity);
        batch_logits_data[this.timestamp_begin] = 0;
        continue;
      }
      const seq = input_ids[i].slice(this.begin_index);
      const last_was_timestamp = seq.length >= 1 && seq[seq.length - 1] >= this.timestamp_begin;
      const penultimate_was_timestamp = seq.length < 2 || seq[seq.length - 2] >= this.timestamp_begin;
      if (last_was_timestamp) {
        if (penultimate_was_timestamp) {
          batch_logits_data.subarray(this.timestamp_begin).fill(-Infinity);
        } else {
          const endToken = Array.isArray(this.eos_token_id) ? this.eos_token_id[0] : this.eos_token_id;
          batch_logits_data.subarray(0, endToken).fill(-Infinity);
        }
      }
      if (input_ids[i].length === this.begin_index && this.max_initial_timestamp_index !== null) {
        const last_allowed = this.timestamp_begin + this.max_initial_timestamp_index;
        batch_logits_data.subarray(last_allowed + 1).fill(-Infinity);
      }
      const logprobs = log_softmax(batch_logits_data);
      const timestamp_logprob = Math.log(
        batch_logits_data.subarray(this.timestamp_begin).map(Math.exp).reduce((a, b) => a + b)
      );
      const max_text_token_logprob = max(logprobs.subarray(0, this.timestamp_begin))[0];
      if (timestamp_logprob > max_text_token_logprob) {
        batch_logits_data.subarray(0, this.timestamp_begin).fill(-Infinity);
      }
    }
    return logits;
  }
};
var NoRepeatNGramLogitsProcessor = class extends LogitsProcessor {
  constructor(no_repeat_ngram_size) {
    super();
    this.no_repeat_ngram_size = no_repeat_ngram_size;
  }
  /**
   * Generate n-grams from a sequence of token ids.
   * @param {bigint[]} prevInputIds List of previous input ids
   * @returns {Map<string, number[]>} Map of generated n-grams
   */
  getNgrams(prevInputIds) {
    const curLen = prevInputIds.length;
    const ngrams = [];
    for (let j = 0; j < curLen + 1 - this.no_repeat_ngram_size; ++j) {
      const ngram = [];
      for (let k = 0; k < this.no_repeat_ngram_size; ++k) {
        ngram.push(prevInputIds[j + k]);
      }
      ngrams.push(ngram.map(Number));
    }
    const generatedNgram = /* @__PURE__ */ new Map();
    for (const ngram of ngrams) {
      const prevNgram = ngram.slice(0, ngram.length - 1);
      const prevNgramKey = JSON.stringify(prevNgram);
      const prevNgramValue = generatedNgram.get(prevNgramKey) ?? [];
      prevNgramValue.push(ngram[ngram.length - 1]);
      generatedNgram.set(prevNgramKey, prevNgramValue);
    }
    return generatedNgram;
  }
  /**
   * Generate n-grams from a sequence of token ids.
   * @param {Map<string, number[]>} bannedNgrams Map of banned n-grams
   * @param {bigint[]} prevInputIds List of previous input ids
   * @returns {number[]} Map of generated n-grams
   */
  getGeneratedNgrams(bannedNgrams, prevInputIds) {
    const ngramIdx = prevInputIds.slice(prevInputIds.length + 1 - this.no_repeat_ngram_size, prevInputIds.length);
    const banned = bannedNgrams.get(JSON.stringify(ngramIdx.map(Number))) ?? [];
    return banned;
  }
  /**
   * Calculate banned n-gram tokens
   * @param {bigint[]} prevInputIds List of previous input ids
   * @returns {number[]} Map of generated n-grams
   */
  calcBannedNgramTokens(prevInputIds) {
    const bannedTokens = [];
    if (prevInputIds.length + 1 < this.no_repeat_ngram_size) {
      return bannedTokens;
    } else {
      const generatedNgrams = this.getNgrams(prevInputIds);
      const bannedTokens2 = this.getGeneratedNgrams(generatedNgrams, prevInputIds);
      return bannedTokens2;
    }
  }
  /**
   * Apply the no-repeat-ngram processor to the logits.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The logits with no-repeat-ngram processing.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      const batch_logits_data = (
        /** @type {Float32Array} */
        logits[i].data
      );
      const bannedTokens = this.calcBannedNgramTokens(input_ids[i]);
      for (const token of bannedTokens) {
        batch_logits_data[token] = -Infinity;
      }
    }
    return logits;
  }
};
var RepetitionPenaltyLogitsProcessor = class extends LogitsProcessor {
  constructor(penalty) {
    super();
    this.penalty = penalty;
  }
  /**
   * Apply the repetition penalty to the logits.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The logits with repetition penalty processing.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      const batch_logits_data = (
        /** @type {Float32Array} */
        logits[i].data
      );
      for (const input_id of new Set(input_ids[i])) {
        const token = Number(input_id);
        if (batch_logits_data[token] < 0) {
          batch_logits_data[token] *= this.penalty;
        } else {
          batch_logits_data[token] /= this.penalty;
        }
      }
    }
    return logits;
  }
};
var MinLengthLogitsProcessor = class extends LogitsProcessor {
  constructor(min_length, eos_token_id) {
    super();
    this.min_length = min_length;
    this.eos_token_id = Array.isArray(eos_token_id) ? eos_token_id : [eos_token_id];
  }
  /**
   * Apply logit processor.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The processed logits.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      if (input_ids[i].length < this.min_length) {
        const batch_logits_data = (
          /** @type {Float32Array} */
          logits[i].data
        );
        for (const eos_token of Array.isArray(this.eos_token_id) ? this.eos_token_id : [this.eos_token_id]) {
          batch_logits_data[eos_token] = -Infinity;
        }
      }
    }
    return logits;
  }
};
var MinNewTokensLengthLogitsProcessor = class extends LogitsProcessor {
  constructor(prompt_length_to_skip, min_new_tokens, eos_token_id) {
    super();
    this.prompt_length_to_skip = prompt_length_to_skip;
    this.min_new_tokens = min_new_tokens;
    this.eos_token_id = Array.isArray(eos_token_id) ? eos_token_id : [eos_token_id];
  }
  /**
   * Apply logit processor.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The processed logits.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      const new_tokens_length = input_ids[i].length - this.prompt_length_to_skip;
      if (new_tokens_length < this.min_new_tokens) {
        const batch_logits_data = (
          /** @type {Float32Array} */
          logits[i].data
        );
        for (const eos_token of Array.isArray(this.eos_token_id) ? this.eos_token_id : [this.eos_token_id]) {
          batch_logits_data[eos_token] = -Infinity;
        }
      }
    }
    return logits;
  }
};
var NoBadWordsLogitsProcessor = class extends LogitsProcessor {
  constructor(bad_words_ids, eos_token_id) {
    super();
    this.bad_words_ids = bad_words_ids;
    this.eos_token_id = Array.isArray(eos_token_id) ? eos_token_id : [eos_token_id];
  }
  /**
   * Apply logit processor.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The processed logits.
   */
  _call(input_ids, logits) {
    for (let i = 0; i < input_ids.length; ++i) {
      const batch_logits_data = (
        /** @type {Float32Array} */
        logits[i].data
      );
      const ids = input_ids[i];
      for (const bad_word_ids of this.bad_words_ids) {
        let mark = true;
        for (let j = 1; j <= bad_word_ids.length - 1 && bad_word_ids.length < ids.length; ++j) {
          if (bad_word_ids.at(-j - 1) != ids.at(-j)) {
            mark = false;
            break;
          }
        }
        if (mark) {
          const lastId = bad_word_ids.at(-1);
          if (lastId !== undefined) {
            batch_logits_data[lastId] = -Infinity;
          }
        }
      }
    }
    return logits;
  }
};
var ClassifierFreeGuidanceLogitsProcessor = class extends LogitsProcessor {
  constructor(guidance_scale) {
    super();
    if (guidance_scale <= 1) {
      throw new Error(
        `Require guidance scale >1 to use the classifier free guidance processor, got guidance scale ${guidance_scale}.`
      );
    }
    this.guidance_scale = guidance_scale;
  }
  /**
   * Apply logit processor.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The processed logits.
   */
  _call(input_ids, logits) {
    if (logits.dims[0] !== 2 * input_ids.length) {
      throw new Error(
        `Logits should have twice the batch size of the input ids, the first half of batches corresponding to the conditional inputs, and the second half of batches corresponding to the unconditional inputs. Got batch size ${logits.dims[0]} for the logits and ${input_ids.length} for the input ids.`
      );
    }
    const unguided_bsz = input_ids.length;
    const cond_logits = logits.slice([0, unguided_bsz], null);
    const uncond_logits = logits.slice([unguided_bsz, logits.dims[0]], null);
    for (let i = 0; i < uncond_logits.data.length; ++i) {
      uncond_logits.data[i] += (cond_logits.data[i] - uncond_logits.data[i]) * this.guidance_scale;
    }
    return uncond_logits;
  }
};
var TemperatureLogitsWarper = class extends LogitsWarper {
  constructor(temperature) {
    super();
    this.temperature = temperature;
  }
  /**
   * Apply logit warper.
   * @param {bigint[][]} input_ids The input IDs.
   * @param {Tensor} logits The logits.
   * @returns {Tensor} The processed logits.
   */
  _call(input_ids, logits) {
    const batch_logits_data = (
      /** @type {Float32Array} */
      logits.data
    );
    for (let i = 0; i < batch_logits_data.length; ++i) {
      batch_logits_data[i] /= this.temperature;
    }
    return logits;
  }
};
var TopPLogitsWarper = class extends LogitsWarper {
  constructor(top_p, { filter_value = -Infinity, min_tokens_to_keep = 1 } = {}) {
    super();
    if (top_p < 0 || top_p > 1) {
      throw new Error(`\`top_p\` must be a float > 0 and < 1, but is ${top_p}`);
    }
    if (!Number.isInteger(min_tokens_to_keep) || min_tokens_to_keep < 1) {
      throw new Error(`\`min_tokens_to_keep\` must be a positive integer, but is ${min_tokens_to_keep}`);
    }
    this.top_p = top_p;
    this.filter_value = filter_value;
    this.min_tokens_to_keep = min_tokens_to_keep;
  }
};
var TopKLogitsWarper = class extends LogitsWarper {
  constructor(top_k, { filter_value = -Infinity, min_tokens_to_keep = 1 } = {}) {
    super();
    if (!Number.isInteger(top_k) || top_k < 0) {
      throw new Error(`\`top_k\` must be a positive integer, but is ${top_k}`);
    }
    this.top_k = Math.max(top_k, min_tokens_to_keep);
    this.filter_value = filter_value;
  }
};

// src/libs/transformers/generation/configuration_utils.ts
var GenerationConfig = class {
  /**
   *
   * @param {GenerationConfig|import('../configs.js').PretrainedConfig} config
   */
  constructor(config) {
    // Parameters that control the length of the output
    /**
     * The maximum length the generated tokens can have.
     * Corresponds to the length of the input prompt + `max_new_tokens`.
     * Its effect is overridden by `max_new_tokens`, if also set.
     * @type {number}
     * @default 20
     */
    this.max_length = 20;
    /**
     * The maximum numbers of tokens to generate, ignoring the number of tokens in the prompt.
     * @type {number}
     * @default null
     */
    this.max_new_tokens = null;
    /**
     * The minimum length of the sequence to be generated.
     * Corresponds to the length of the input prompt + `min_new_tokens`.
     * Its effect is overridden by `min_new_tokens`, if also set.
     * @type {number}
     * @default 0
     */
    this.min_length = 0;
    /**
     * The minimum numbers of tokens to generate, ignoring the number of tokens in the prompt.
     * @type {number}
     * @default null
     */
    this.min_new_tokens = null;
    /**
     * Controls the stopping condition for beam-based methods, like beam-search. It accepts the following values:
     * - `true`, where the generation stops as soon as there are `num_beams` complete candidates;
     * - `false`, where an heuristic is applied and the generation stops when is it very unlikely to find better candidates;
     * - `"never"`, where the beam search procedure only stops when there cannot be better candidates (canonical beam search algorithm).
     * @type {boolean|"never"}
     * @default false
     */
    this.early_stopping = false;
    /**
     * The maximum amount of time you allow the computation to run for in seconds.
     * Generation will still finish the current pass after allocated time has been passed.
     * @type {number}
     * @default null
     */
    this.max_time = null;
    // Parameters that control the generation strategy used
    /**
     * Whether or not to use sampling; use greedy decoding otherwise.
     * @type {boolean}
     * @default false
     */
    this.do_sample = false;
    /**
     * Number of beams for beam search. 1 means no beam search.
     * @type {number}
     * @default 1
     */
    this.num_beams = 1;
    /**
     * Number of groups to divide `num_beams` into in order to ensure diversity among different groups of beams.
     * See [this paper](https://arxiv.org/pdf/1610.02424.pdf) for more details.
     * @type {number}
     * @default 1
     */
    this.num_beam_groups = 1;
    /**
     * The values balance the model confidence and the degeneration penalty in contrastive search decoding.
     * @type {number}
     * @default null
     */
    this.penalty_alpha = null;
    /**
     * Whether or not the model should use the past last key/values attentions (if applicable to the model) to speed up decoding.
     * @type {boolean}
     * @default true
     */
    this.use_cache = true;
    // Parameters for manipulation of the model output logits
    /**
     * The value used to modulate the next token probabilities.
     * @type {number}
     * @default 1.0
     */
    this.temperature = 1;
    /**
     * The number of highest probability vocabulary tokens to keep for top-k-filtering.
     * @type {number}
     * @default 50
     */
    this.top_k = 50;
    /**
     * If set to float < 1, only the smallest set of most probable tokens with probabilities that add up to `top_p` or higher are kept for generation.
     * @type {number}
     * @default 1.0
     */
    this.top_p = 1;
    /**
     * Local typicality measures how similar the conditional probability of predicting a target token next is to the expected conditional probability of predicting a random token next, given the partial text already generated.
     * If set to float < 1, the smallest set of the most locally typical tokens with probabilities that add up to `typical_p` or higher are kept for generation.
     * See [this paper](https://arxiv.org/pdf/2202.00666.pdf) for more details.
     * @type {number}
     * @default 1.0
     */
    this.typical_p = 1;
    /**
     * If set to float strictly between 0 and 1, only tokens with a conditional probability greater than `epsilon_cutoff` will be sampled.
     * In the paper, suggested values range from 3e-4 to 9e-4, depending on the size of the model.
     * See [Truncation Sampling as Language Model Desmoothing](https://arxiv.org/abs/2210.15191) for more details.
     * @type {number}
     * @default 0.0
     */
    this.epsilon_cutoff = 0;
    /**
     * Eta sampling is a hybrid of locally typical sampling and epsilon sampling.
     * If set to float strictly between 0 and 1, a token is only considered if it is greater than either `eta_cutoff` or `sqrt(eta_cutoff) * exp(-entropy(softmax(next_token_logits)))`.
     * The latter term is intuitively the expected next token probability, scaled by `sqrt(eta_cutoff)`. In the paper, suggested values range from 3e-4 to 2e-3, depending on the size of the model.
     * See [Truncation Sampling as Language Model Desmoothing](https://arxiv.org/abs/2210.15191) for more details.
     * @type {number}
     * @default 0.0
     */
    this.eta_cutoff = 0;
    /**
     * This value is subtracted from a beam's score if it generates a token same as any beam from other group at a particular time.
     * Note that `diversity_penalty` is only effective if `group beam search` is enabled.
     * @type {number}
     * @default 0.0
     */
    this.diversity_penalty = 0;
    /**
     * The parameter for repetition penalty. 1.0 means no penalty.
     * See [this paper](https://arxiv.org/pdf/1909.05858.pdf) for more details.
     * @type {number}
     * @default 1.0
     */
    this.repetition_penalty = 1;
    /**
     * The paramater for encoder_repetition_penalty.
     * An exponential penalty on sequences that are not in the original input.
     * 1.0 means no penalty.
     * @type {number}
     * @default 1.0
     */
    this.encoder_repetition_penalty = 1;
    /**
     * Exponential penalty to the length that is used with beam-based generation.
     * It is applied as an exponent to the sequence length, which in turn is used to divide the score of the sequence.
     * Since the score is the log likelihood of the sequence (i.e. negative), `length_penalty` > 0.0 promotes longer sequences, while `length_penalty` < 0.0 encourages shorter sequences.
     * @type {number}
     * @default 1.0
     */
    this.length_penalty = 1;
    /**
     * If set to int > 0, all ngrams of that size can only occur once.
     * @type {number}
     * @default 0
     */
    this.no_repeat_ngram_size = 0;
    /**
     * List of token ids that are not allowed to be generated.
     * In order to get the token ids of the words that should not appear in the generated text, use
     * `tokenizer(bad_words, { add_prefix_space: true, add_special_tokens: false }).input_ids`.
     * @type {number[][]}
     * @default null
     */
    this.bad_words_ids = null;
    /**
     * List of token ids that must be generated.
     * If given a `number[][]`, this is treated as a simple list of words that must be included, the opposite to `bad_words_ids`.
     * If given `number[][][]`, this triggers a [disjunctive constraint](https://github.com/huggingface/transformers/issues/14081), where one can allow different forms of each word.
     * @type {number[][]|number[][][]}
     * @default null
     */
    this.force_words_ids = null;
    /**
     * Whether to renormalize the logits after applying all the logits processors or warpers (including the custom ones).
     * It's highly recommended to set this flag to `true` as the search algorithms suppose the score logits are normalized but some logit processors or warpers break the normalization.
     * @type {boolean}
     * @default false
     */
    this.renormalize_logits = false;
    /**
     * Custom constraints that can be added to the generation to ensure that the output will contain the use of certain tokens as defined by `Constraint` objects, in the most sensible way possible.
     * @type {Object[]}
     * @default null
     */
    this.constraints = null;
    /**
     * The id of the token to force as the first generated token after the `decoder_start_token_id`.
     * Useful for multilingual models like mBART where the first generated token needs to be the target language token.
     * @type {number}
     * @default null
     */
    this.forced_bos_token_id = null;
    /**
     * The id of the token to force as the last generated token when `max_length` is reached.
     * Optionally, use a list to set multiple *end-of-sequence* tokens.
     * @type {number|number[]}
     * @default null
     */
    this.forced_eos_token_id = null;
    /**
     * Whether to remove possible *nan* and *inf* outputs of the model to prevent the generation method to crash. Note that using `remove_invalid_values` can slow down generation.
     * @type {boolean}
     */
    this.remove_invalid_values = false;
    /**
     * This Tuple adds an exponentially increasing length penalty, after a certain amount of tokens have been generated.
     * The tuple shall consist of: `(start_index, decay_factor)` where `start_index` indicates where penalty starts and `decay_factor` represents the factor of exponential decay.
     * @type {[number, number]}
     * @default null
     */
    this.exponential_decay_length_penalty = null;
    /**
     * A list of tokens that will be suppressed at generation.
     * The `SuppressTokens` logit processor will set their log probs to `-inf` so that they are not sampled.
     * @type {number[]}
     * @default null
     */
    this.suppress_tokens = null;
    /**
     * A streamer that will be used to stream the generation.
     * @type {import('./streamers.js').TextStreamer}
     * @default null
     */
    this.streamer = null;
    /**
     * A list of tokens that will be suppressed at the beginning of the generation.
     * The `SuppressBeginTokens` logit processor will set their log probs to `-inf` so that they are not sampled.
     * @type {number[]}
     * @default null
     */
    this.begin_suppress_tokens = null;
    /**
     * A list of pairs of integers which indicates a mapping from generation indices to token indices that will be forced before sampling.
     * For example, `[[1, 123]]` means the second generated token will always be a token of index 123.
     * @type {[number, number][]}
     * @default null
     */
    this.forced_decoder_ids = null;
    /**
     * The guidance scale for classifier free guidance (CFG). CFG is enabled by setting `guidance_scale > 1`.
     * Higher guidance scale encourages the model to generate samples that are more closely linked to the input
     * prompt, usually at the expense of poorer quality.
     * @type {number}
     * @default null
     */
    this.guidance_scale = null;
    // Parameters that define the output variables of `generate`
    /**
     * The number of independently computed returned sequences for each element in the batch.
     * @type {number}
     * @default 1
     */
    this.num_return_sequences = 1;
    /**
     * Whether or not to return the attentions tensors of all attention layers.
     * See `attentions` under returned tensors for more details.
     * @type {boolean}
     * @default false
     */
    this.output_attentions = false;
    /**
     * Whether or not to return the hidden states of all layers.
     * See `hidden_states` under returned tensors for more details.
     * @type {boolean}
     * @default false
     */
    this.output_hidden_states = false;
    /**
     * Whether or not to return the prediction scores.
     * See `scores` under returned tensors for more details.
     * @type {boolean}
     * @default false
     */
    this.output_scores = false;
    /**
     * Whether or not to return a `ModelOutput` instead of a plain tuple.
     * @type {boolean}
     * @default false
     */
    this.return_dict_in_generate = false;
    // Special tokens that can be used at generation time
    /**
     * The id of the *padding* token.
     * @type {number}
     * @default null
     */
    this.pad_token_id = null;
    /**
     * The id of the *beginning-of-sequence* token.
     * @type {number}
     * @default null
     */
    this.bos_token_id = null;
    /**
     * The id of the *end-of-sequence* token.
     * Optionally, use a list to set multiple *end-of-sequence* tokens.
     * @type {number|number[]}
     * @default null
     */
    this.eos_token_id = null;
    // Generation parameters exclusive to encoder-decoder models
    /**
     * If set to int > 0, all ngrams of that size that occur in the `encoder_input_ids` cannot occur in the `decoder_input_ids`.
     * @type {number}
     * @default 0
     */
    this.encoder_no_repeat_ngram_size = 0;
    /**
     * If an encoder-decoder model starts decoding with a different token than *bos*, the id of that token.
     * @type {number}
     * @default null
     */
    this.decoder_start_token_id = null;
    // Wild card
    /**
     * Additional generation kwargs will be forwarded to the `generate` function of the model.
     * Kwargs that are not present in `generate`'s signature will be used in the model forward pass.
     * @type {Object}
     * @default {}
     */
    this.generation_kwargs = {};
    Object.assign(this, pick(config, Object.getOwnPropertyNames(this)));
  }
};

// src/libs/transformers/utils/image.ts
var createCanvasFunction;
var ImageDataClass;
var loadImageFunction;
var IS_BROWSER_OR_WEBWORKER = apis.IS_BROWSER_ENV || apis.IS_WEBWORKER_ENV;
if (IS_BROWSER_OR_WEBWORKER) {
  createCanvasFunction = (width, height) => {
    if (!self.OffscreenCanvas) {
      throw new Error("OffscreenCanvas not supported by this browser.");
    }
    return new self.OffscreenCanvas(width, height);
  };
  loadImageFunction = self.createImageBitmap;
  ImageDataClass = self.ImageData;
} else {
  throw new Error("Unable to load image processing library.");
}
var RESAMPLING_MAPPING = {
  0: "nearest",
  1: "lanczos",
  2: "bilinear",
  3: "bicubic",
  4: "box",
  5: "hamming"
};
var CONTENT_TYPE_MAP2 = /* @__PURE__ */ new Map([
  ["png", "image/png"],
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["gif", "image/gif"]
]);
var RawImage = class _RawImage {
  /**
   * Create a new `RawImage` object.
   * @param {Uint8ClampedArray|Uint8Array} data The pixel data.
   * @param {number} width The width of the image.
   * @param {number} height The height of the image.
   * @param {1|2|3|4} channels The number of channels.
   */
  constructor(data, width, height, channels) {
    this.data = data;
    this.width = width;
    this.height = height;
    this.channels = channels;
  }
  /**
   * Returns the size of the image (width, height).
   * @returns {[number, number]} The size of the image (width, height).
   */
  get size() {
    return [this.width, this.height];
  }
  /**
   * Helper method for reading an image from a variety of input types.
   * @param {RawImage|string|URL} input
   * @returns The image object.
   *
   * **Example:** Read image from a URL.
   * ```javascript
   * let image = await RawImage.read('https://huggingface.co/datasets/Xenova/transformers.js-docs/resolve/main/football-match.jpg');
   * // RawImage {
   * //   "data": Uint8ClampedArray [ 25, 25, 25, 19, 19, 19, ... ],
   * //   "width": 800,
   * //   "height": 533,
   * //   "channels": 3
   * // }
   * ```
   */
  static async read(input) {
    if (input instanceof _RawImage) {
      return input;
    } else if (typeof input === "string" || input instanceof URL) {
      return await this.fromURL(input);
    } else {
      throw new Error(`Unsupported input type: ${typeof input}`);
    }
  }
  /**
   * Read an image from a canvas.
   * @param {HTMLCanvasElement|OffscreenCanvas} canvas The canvas to read the image from.
   * @returns {RawImage} The image object.
   */
  static fromCanvas(canvas) {
    if (!IS_BROWSER_OR_WEBWORKER) {
      throw new Error("fromCanvas() is only supported in browser environments.");
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get 2D context from canvas");
    }
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    return new _RawImage(data, canvas.width, canvas.height, 4);
  }
  /**
   * Read an image from a URL or file path.
   * @param {string|URL} url The URL or file path to read the image from.
   * @returns {Promise<RawImage>} The image object.
   */
  static async fromURL(url2) {
    const response = await getFile(url2);
    if (response.status !== 200) {
      throw new Error(`Unable to read image from "${url2}" (${response.status} ${response.statusText})`);
    }
    const blob = await response.blob();
    return this.fromBlob(blob);
  }
  /**
   * Helper method to create a new Image from a blob.
   * @param {Blob} blob The blob to read the image from.
   * @returns {Promise<RawImage>} The image object.
   */
  static async fromBlob(blob) {
    const img = await loadImageFunction(blob);
    const ctx = createCanvasFunction(img.width, img.height).getContext("2d");
    ctx.drawImage(img, 0, 0);
    return new this(ctx.getImageData(0, 0, img.width, img.height).data, img.width, img.height, 4);
  }
  /**
   * Helper method to create a new Image from a tensor
   * @param {Tensor} tensor
   */
  static fromTensor(tensor, channel_format = "CHW") {
    if (tensor.dims.length !== 3) {
      throw new Error(`Tensor should have 3 dimensions, but has ${tensor.dims.length} dimensions.`);
    }
    if (channel_format === "CHW") {
      tensor = tensor.transpose(1, 2, 0);
    } else if (channel_format === "HWC") ; else {
      throw new Error(`Unsupported channel format: ${channel_format}`);
    }
    if (!(tensor.data instanceof Uint8ClampedArray || tensor.data instanceof Uint8Array)) {
      throw new Error(`Unsupported tensor type: ${tensor.type}`);
    }
    switch (tensor.dims[2]) {
      case 1:
      case 2:
      case 3:
      case 4:
        return new _RawImage(tensor.data, tensor.dims[1], tensor.dims[0], tensor.dims[2]);
      default:
        throw new Error(`Unsupported number of channels: ${tensor.dims[2]}`);
    }
  }
  /**
   * Convert the image to grayscale format.
   * @returns {RawImage} `this` to support chaining.
   */
  grayscale() {
    if (this.channels === 1) {
      return this;
    }
    const newData = new Uint8ClampedArray(this.width * this.height * 1);
    switch (this.channels) {
      case 3:
      // rgb to grayscale
      case 4:
        for (let i = 0, offset = 0; i < this.data.length; i += this.channels) {
          const red = this.data[i];
          const green = this.data[i + 1];
          const blue = this.data[i + 2];
          newData[offset++] = Math.round(0.2989 * red + 0.587 * green + 0.114 * blue);
        }
        break;
      default:
        throw new Error(`Conversion failed due to unsupported number of channels: ${this.channels}`);
    }
    return this._update(newData, this.width, this.height, 1);
  }
  /**
   * Convert the image to RGB format.
   * @returns {RawImage} `this` to support chaining.
   */
  rgb() {
    if (this.channels === 3) {
      return this;
    }
    const newData = new Uint8ClampedArray(this.width * this.height * 3);
    switch (this.channels) {
      case 1:
        for (let i = 0, offset = 0; i < this.data.length; ++i) {
          newData[offset++] = this.data[i];
          newData[offset++] = this.data[i];
          newData[offset++] = this.data[i];
        }
        break;
      case 4:
        for (let i = 0, offset = 0; i < this.data.length; i += 4) {
          newData[offset++] = this.data[i];
          newData[offset++] = this.data[i + 1];
          newData[offset++] = this.data[i + 2];
        }
        break;
      default:
        throw new Error(`Conversion failed due to unsupported number of channels: ${this.channels}`);
    }
    return this._update(newData, this.width, this.height, 3);
  }
  /**
   * Convert the image to RGBA format.
   * @returns {RawImage} `this` to support chaining.
   */
  rgba() {
    if (this.channels === 4) {
      return this;
    }
    const newData = new Uint8ClampedArray(this.width * this.height * 4);
    switch (this.channels) {
      case 1:
        for (let i = 0, offset = 0; i < this.data.length; ++i) {
          newData[offset++] = this.data[i];
          newData[offset++] = this.data[i];
          newData[offset++] = this.data[i];
          newData[offset++] = 255;
        }
        break;
      case 3:
        for (let i = 0, offset = 0; i < this.data.length; i += 3) {
          newData[offset++] = this.data[i];
          newData[offset++] = this.data[i + 1];
          newData[offset++] = this.data[i + 2];
          newData[offset++] = 255;
        }
        break;
      default:
        throw new Error(`Conversion failed due to unsupported number of channels: ${this.channels}`);
    }
    return this._update(newData, this.width, this.height, 4);
  }
  /**
   * Apply an alpha mask to the image. Operates in place.
   * @param {RawImage} mask The mask to apply. It should have a single channel.
   * @returns {RawImage} The masked image.
   * @throws {Error} If the mask is not the same size as the image.
   * @throws {Error} If the image does not have 4 channels.
   * @throws {Error} If the mask is not a single channel.
   */
  putAlpha(mask) {
    if (mask.width !== this.width || mask.height !== this.height) {
      throw new Error(`Expected mask size to be ${this.width}x${this.height}, but got ${mask.width}x${mask.height}`);
    }
    if (mask.channels !== 1) {
      throw new Error(`Expected mask to have 1 channel, but got ${mask.channels}`);
    }
    const this_data = this.data;
    const mask_data = mask.data;
    const num_pixels = this.width * this.height;
    if (this.channels === 3) {
      const newData = new Uint8ClampedArray(num_pixels * 4);
      for (let i = 0, in_offset = 0, out_offset = 0; i < num_pixels; ++i) {
        newData[out_offset++] = this_data[in_offset++];
        newData[out_offset++] = this_data[in_offset++];
        newData[out_offset++] = this_data[in_offset++];
        newData[out_offset++] = mask_data[i];
      }
      return this._update(newData, this.width, this.height, 4);
    } else if (this.channels === 4) {
      for (let i = 0; i < num_pixels; ++i) {
        this_data[4 * i + 3] = mask_data[i];
      }
      return this;
    }
    throw new Error(`Expected image to have 3 or 4 channels, but got ${this.channels}`);
  }
  /**
   * Resize the image to the given dimensions. This method uses the canvas API to perform the resizing.
   * @param {number} width The width of the new image. `null` or `-1` will preserve the aspect ratio.
   * @param {number} height The height of the new image. `null` or `-1` will preserve the aspect ratio.
   * @param {Object} options Additional options for resizing.
   * @param {0|1|2|3|4|5|string} [options.resample] The resampling method to use.
   * @returns {Promise<RawImage>} `this` to support chaining.
   */
  async resize(width, height, { resample = 2 } = {}) {
    if (this.width === width && this.height === height) {
      return this;
    }
    let resampleMethod = RESAMPLING_MAPPING[resample] ?? resample;
    const nullish_width = isNullishDimension(width);
    const nullish_height = isNullishDimension(height);
    if (nullish_width && nullish_height) {
      return this;
    } else if (nullish_width) {
      width = height / this.height * this.width;
    } else if (nullish_height) {
      height = width / this.width * this.height;
    }
    if (IS_BROWSER_OR_WEBWORKER) {
      const numChannels = this.channels;
      const canvas = this.toCanvas();
      const ctx = createCanvasFunction(width, height).getContext("2d");
      ctx.drawImage(canvas, 0, 0, width, height);
      const resizedImage = new _RawImage(ctx.getImageData(0, 0, width, height).data, width, height, 4);
      return resizedImage.convert(numChannels);
    } else {
      let img = this.toSharp();
      switch (resampleMethod) {
        case "box":
        case "hamming":
          if (resampleMethod === "box" || resampleMethod === "hamming") {
            console.warn(`Resampling method ${resampleMethod} is not yet supported. Using bilinear instead.`);
            resampleMethod = "bilinear";
          }
        case "nearest":
        case "bilinear":
        case "bicubic":
          img = img.affine([width / this.width, 0, 0, height / this.height], {
            interpolator: resampleMethod
          });
          break;
        case "lanczos":
          img = img.resize({
            width,
            height,
            fit: "fill",
            kernel: "lanczos3"
            // PIL Lanczos uses a kernel size of 3
          });
          break;
        default:
          throw new Error(`Resampling method ${resampleMethod} is not supported.`);
      }
      return await loadImageFunction(img);
    }
  }
  async pad([left, right, top, bottom]) {
    left = Math.max(left, 0);
    right = Math.max(right, 0);
    top = Math.max(top, 0);
    bottom = Math.max(bottom, 0);
    if (left === 0 && right === 0 && top === 0 && bottom === 0) {
      return this;
    }
    if (IS_BROWSER_OR_WEBWORKER) {
      const numChannels = this.channels;
      const canvas = this.toCanvas();
      const newWidth = this.width + left + right;
      const newHeight = this.height + top + bottom;
      const ctx = createCanvasFunction(newWidth, newHeight).getContext("2d");
      ctx.drawImage(canvas, 0, 0, this.width, this.height, left, top, this.width, this.height);
      const paddedImage = new _RawImage(ctx.getImageData(0, 0, newWidth, newHeight).data, newWidth, newHeight, 4);
      return paddedImage.convert(numChannels);
    }
  }
  async crop([x_min, y_min, x_max, y_max]) {
    x_min = Math.max(x_min, 0);
    y_min = Math.max(y_min, 0);
    x_max = Math.min(x_max, this.width - 1);
    y_max = Math.min(y_max, this.height - 1);
    if (x_min === 0 && y_min === 0 && x_max === this.width - 1 && y_max === this.height - 1) {
      return this;
    }
    const crop_width = x_max - x_min + 1;
    const crop_height = y_max - y_min + 1;
    if (IS_BROWSER_OR_WEBWORKER) {
      const numChannels = this.channels;
      const canvas = this.toCanvas();
      const ctx = createCanvasFunction(crop_width, crop_height).getContext("2d");
      ctx.drawImage(canvas, x_min, y_min, crop_width, crop_height, 0, 0, crop_width, crop_height);
      const resizedImage = new _RawImage(
        ctx.getImageData(0, 0, crop_width, crop_height).data,
        crop_width,
        crop_height,
        4
      );
      return resizedImage.convert(numChannels);
    }
  }
  async center_crop(crop_width, crop_height) {
    if (this.width === crop_width && this.height === crop_height) {
      return this;
    }
    const width_offset = (this.width - crop_width) / 2;
    const height_offset = (this.height - crop_height) / 2;
    if (IS_BROWSER_OR_WEBWORKER) {
      const numChannels = this.channels;
      const canvas = this.toCanvas();
      const ctx = createCanvasFunction(crop_width, crop_height).getContext("2d");
      let sourceX = 0;
      let sourceY = 0;
      let destX = 0;
      let destY = 0;
      if (width_offset >= 0) {
        sourceX = width_offset;
      } else {
        destX = -width_offset;
      }
      if (height_offset >= 0) {
        sourceY = height_offset;
      } else {
        destY = -height_offset;
      }
      ctx.drawImage(canvas, sourceX, sourceY, crop_width, crop_height, destX, destY, crop_width, crop_height);
      const resizedImage = new _RawImage(
        ctx.getImageData(0, 0, crop_width, crop_height).data,
        crop_width,
        crop_height,
        4
      );
      return resizedImage.convert(numChannels);
    }
  }
  async toBlob(type = "image/png", quality = 1) {
    if (!IS_BROWSER_OR_WEBWORKER) {
      throw new Error("toBlob() is only supported in browser environments.");
    }
    const canvas = this.toCanvas();
    return await canvas.convertToBlob({ type, quality });
  }
  toTensor(channel_format = "CHW") {
    let tensor = new Tensor3("uint8", new Uint8Array(this.data), [this.height, this.width, this.channels]);
    if (channel_format === "HWC") ; else if (channel_format === "CHW") {
      tensor = tensor.permute(2, 0, 1);
    } else {
      throw new Error(`Unsupported channel format: ${channel_format}`);
    }
    return tensor;
  }
  toCanvas() {
    if (!IS_BROWSER_OR_WEBWORKER) {
      throw new Error("toCanvas() is only supported in browser environments.");
    }
    const cloned = this.clone().rgba();
    const clonedCanvas = createCanvasFunction(cloned.width, cloned.height);
    const data = new ImageDataClass(cloned.data, cloned.width, cloned.height);
    clonedCanvas.getContext("2d").putImageData(data, 0, 0);
    return clonedCanvas;
  }
  /**
   * Split this image into individual bands. This method returns an array of individual image bands from an image.
   * For example, splitting an "RGB" image creates three new images each containing a copy of one of the original bands (red, green, blue).
   *
   * Inspired by PIL's `Image.split()` [function](https://pillow.readthedocs.io/en/latest/reference/Image.html#PIL.Image.Image.split).
   * @returns {RawImage[]} An array containing bands.
   */
  split() {
    const { data, width, height, channels } = this;
    const data_type = (
      /** @type {any} */
      data.constructor
    );
    const per_channel_length = data.length / channels;
    const split_data = Array.from(
      { length: channels },
      () => new data_type(per_channel_length)
    );
    for (let i = 0; i < per_channel_length; ++i) {
      const data_offset = channels * i;
      for (let j = 0; j < channels; ++j) {
        split_data[j][i] = data[data_offset + j];
      }
    }
    return split_data.map((data2) => new _RawImage(data2, width, height, 1));
  }
  /**
   * Helper method to update the image data.
   * @param {Uint8ClampedArray} data The new image data.
   * @param {number} width The new width of the image.
   * @param {number} height The new height of the image.
   * @param {1|2|3|4|null} [channels] The new number of channels of the image.
   * @private
   */
  _update(data, width, height, channels = null) {
    this.data = data;
    this.width = width;
    this.height = height;
    if (channels !== null) {
      this.channels = channels;
    }
    return this;
  }
  /**
   * Clone the image
   * @returns {RawImage} The cloned image
   */
  clone() {
    return new _RawImage(this.data.slice(), this.width, this.height, this.channels);
  }
  /**
   * Helper method for converting image to have a certain number of channels
   * @param {number} numChannels The number of channels. Must be 1, 3, or 4.
   * @returns {RawImage} `this` to support chaining.
   */
  convert(numChannels) {
    if (this.channels === numChannels) return this;
    switch (numChannels) {
      case 1:
        this.grayscale();
        break;
      case 3:
        this.rgb();
        break;
      case 4:
        this.rgba();
        break;
      default:
        throw new Error(`Conversion failed due to unsupported number of channels: ${this.channels}`);
    }
    return this;
  }
  /**
   * Save the image to the given path.
   * @param {string} path The path to save the image to.
   */
  async save(path3) {
    if (IS_BROWSER_OR_WEBWORKER) {
      if (apis.IS_WEBWORKER_ENV) {
        throw new Error("Unable to save an image from a Web Worker.");
      }
      const extension = path3.split(".").pop()?.toLowerCase();
      const mime = CONTENT_TYPE_MAP2.get(extension) ?? "image/png";
      const blob = await this.toBlob(mime);
      const dataURL = URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = dataURL;
      downloadLink.download = path3;
      downloadLink.click();
      downloadLink.remove();
    } else {
      throw new Error("Unable to save the image because filesystem is disabled in this environment.");
    }
  }
  toSharp() {
    if (IS_BROWSER_OR_WEBWORKER) {
      throw new Error("toSharp() is only supported in server-side environments.");
    }
  }
};
RawImage.read.bind(RawImage);

// src/libs/transformers/generation/stopping_criteria.ts
var StoppingCriteria = class extends Callable {
  /**
   *
   * @param {number[][]} input_ids (`number[][]` of shape `(batch_size, sequence_length)`):
   * Indices of input sequence tokens in the vocabulary.
   * @param {number[][]} scores scores (`number[][]` of shape `(batch_size, config.vocab_size)`):
   * Prediction scores of a language modeling head. These can be scores for each vocabulary token before SoftMax
   * or scores for each vocabulary token after SoftMax.
   * @returns {boolean[]} A list of booleans indicating whether each sequence should be stopped.
   */
  _call(input_ids, scores) {
    throw Error("StoppingCriteria needs to be subclassed");
  }
};
var StoppingCriteriaList = class _StoppingCriteriaList extends Callable {
  constructor() {
    super();
    this.criteria = [];
  }
  /**
   * Adds a new stopping criterion to the list.
   *
   * @param {StoppingCriteria} item The stopping criterion to add.
   */
  push(item) {
    this.criteria.push(item);
  }
  /**
   * Adds multiple stopping criteria to the list.
   *
   * @param {StoppingCriteria|StoppingCriteriaList|StoppingCriteria[]} items The stopping criteria to add.
   */
  extend(items) {
    if (items instanceof _StoppingCriteriaList) {
      items = items.criteria;
    } else if (items instanceof StoppingCriteria) {
      items = [items];
    }
    this.criteria.push(...items);
  }
  _call(input_ids, scores) {
    const is_done = new Array(input_ids.length).fill(false);
    for (const criterion of this.criteria) {
      const criterion_done = criterion._call(input_ids, scores);
      for (let i = 0; i < is_done.length; ++i) {
        is_done[i] || (is_done[i] = criterion_done[i]);
      }
    }
    return is_done;
  }
  [Symbol.iterator]() {
    return this.criteria.values();
  }
};
var MaxLengthCriteria = class extends StoppingCriteria {
  constructor(max_length, max_position_embeddings = null) {
    super();
    this.max_length = max_length;
    this.max_position_embeddings = max_position_embeddings;
  }
  _call(input_ids) {
    return input_ids.map((ids) => ids.length >= this.max_length);
  }
};
var EosTokenCriteria = class extends StoppingCriteria {
  constructor(eos_token_id) {
    super();
    if (!Array.isArray(eos_token_id)) {
      eos_token_id = [eos_token_id];
    }
    this.eos_token_id = eos_token_id;
  }
  /**
   *
   * @param {number[][]} input_ids
   * @param {number[][]} scores
   * @returns {boolean[]}
   */
  _call(input_ids, scores) {
    return input_ids.map((ids) => {
      const last = ids.at(-1);
      if (Array.isArray(this.eos_token_id)) {
        return this.eos_token_id.some((eos_id) => last == eos_id);
      } else {
        return last == this.eos_token_id;
      }
    });
  }
};

// src/libs/transformers/generation/logits_sampler.ts
var LogitsSampler = class extends Callable {
  constructor(generation_config) {
    super();
    this.generation_config = generation_config;
  }
  /**
   * Executes the sampler, using the specified logits.
   * @param {Tensor} logits
   * @returns {Promise<[bigint, number][]>}
   */
  async _call(logits) {
    return this.sample(logits);
  }
  /**
   * Abstract method for sampling the logits.
   * @param {Tensor} logits
   * @throws {Error} If not implemented in subclass.
   * @returns {Promise<[bigint, number][]>}
   */
  async sample(logits) {
    throw Error("sample should be implemented in subclasses.");
  }
  /**
   * Returns the specified logits as an array, with temperature applied.
   * @param {Tensor} logits
   * @param {number} index
   * @returns {Float32Array}
   */
  getLogits(logits, index) {
    let vocabSize = logits.dims.at(-1);
    let logs = (
      /** @type {Float32Array} */
      logits.data
    );
    if (index === -1) {
      logs = logs.slice(-vocabSize);
    } else {
      let startIndex = index * vocabSize;
      logs = logs.slice(startIndex, startIndex + vocabSize);
    }
    return logs;
  }
  /**
   * Selects an item randomly based on the specified probabilities.
   * @param {import("../transformers.js").DataArray} probabilities An array of probabilities to use for selection.
   * @returns {number} The index of the selected item.
   */
  randomSelect(probabilities) {
    let sumProbabilities = 0;
    for (let i = 0; i < probabilities.length; ++i) {
      sumProbabilities += probabilities[i];
    }
    let r = Math.random() * sumProbabilities;
    for (let i = 0; i < probabilities.length; ++i) {
      r -= probabilities[i];
      if (r <= 0) {
        return i;
      }
    }
    return 0;
  }
  /**
   * Returns a Sampler object based on the specified options.
   * @param {GenerationConfig} generation_config An object containing options for the sampler.
   * @returns {LogitsSampler} A Sampler object.
   */
  static getSampler(generation_config) {
    if (generation_config.do_sample) {
      return new MultinomialSampler(generation_config);
    } else if (generation_config.num_beams > 1) {
      return new BeamSearchSampler(generation_config);
    } else {
      if (generation_config.num_return_sequences > 1) {
        throw Error(
          `num_return_sequences has to be 1 when doing greedy search, but is ${generation_config.num_return_sequences}.`
        );
      }
      return new GreedySampler(generation_config);
    }
  }
};
var GreedySampler = class extends LogitsSampler {
  /**
   * Sample the maximum probability of a given logits tensor.
   * @param {Tensor} logits
   * @returns {Promise<[bigint, number][]>} An array with a single tuple, containing the index of the maximum value and a meaningless score (since this is a greedy search).
   */
  async sample(logits) {
    const argmax = max(logits.data)[1];
    return [[BigInt(argmax), 0]];
  }
};
var MultinomialSampler = class extends LogitsSampler {
  /**
   * Sample from the logits.
   * @param {Tensor} logits
   * @returns {Promise<[bigint, number][]>}
   */
  async sample(logits) {
    let k = logits.dims.at(-1);
    if (this.generation_config.top_k > 0) {
      k = Math.min(this.generation_config.top_k, k);
    }
    const [v, i] = await topk(logits, k);
    const probabilities = softmax(
      /** @type {Float32Array} */
      v.data
    );
    return Array.from({ length: this.generation_config.num_beams }, () => {
      const sampledIndex = this.randomSelect(probabilities);
      return [
        i.data[sampledIndex],
        // token id
        Math.log(probabilities[sampledIndex])
        // score
      ];
    });
  }
};
var BeamSearchSampler = class extends LogitsSampler {
  /**
   * Sample from the logits.
   * @param {Tensor} logits
   * @returns {Promise<[bigint, number][]>}
   */
  async sample(logits) {
    let k = logits.dims.at(-1);
    if (this.generation_config.top_k > 0) {
      k = Math.min(this.generation_config.top_k, k);
    }
    const [v, i] = await topk(logits, k);
    const probabilities = softmax(
      /** @type {Float32Array} */
      v.data
    );
    return Array.from({ length: this.generation_config.num_beams }, (_, x) => {
      return [
        i.data[x],
        // token id
        Math.log(probabilities[x])
        // score
      ];
    });
  }
};

// src/libs/transformers/models/whisper/generation_whisper.ts
var WhisperGenerationConfig = class extends GenerationConfig {
  constructor() {
    super(...arguments);
    /**
     * Whether to return the timestamps with the text. This enables the `WhisperTimestampsLogitsProcessor`.
     * @type {boolean}
     */
    this.return_timestamps = null;
    /**
     * Whether to return token-level timestamps
     * with the text. This can be used with or without the `return_timestamps` option. To get word-level
     * timestamps, use the tokenizer to group the tokens into words.
     * @type {boolean}
     */
    this.return_token_timestamps = null;
    /**
     * The number of audio frames available in this chunk. This is only used generating word-level timestamps.
     * @type {number}
     */
    this.num_frames = null;
    /**
     * Alignment heads to predict word-level timestamps. This is a list of [layer, head] pairs that
     * select the cross-attention heads that are highly correlated to word-level timing.
     * @type {[number, number][]}
     */
    this.alignment_heads = null;
    /**
     * Task to use for generation, either "translate" or "transcribe".
     * @type {string}
     */
    this.task = null;
    /**
     * Language token to use for generation, can be either in the form of `<|en|>`, `en` or `english`.
     * You can find all the possible language tokens in the `model.generation_config.lang_to_id` dictionary.
     * @type {string}
     */
    this.language = null;
    /**
     * The id of the `"<|notimestamps|>"` token.
     * @type {number}
     */
    this.no_timestamps_token_id = null;
    /**
     * Rank-1 list of token IDs created by passing text to [`~WhisperProcessor.get_prompt_ids`] that is
     * provided as a prompt to each chunk. This can be used to provide or "prompt-engineer" a context for
     * transcription, e.g. custom vocabularies or proper nouns to make it more likely to predict those words
     * correctly. It cannot be used in conjunction with `decoder_start_token_id` as it overwrites this value.
     * @type {number[]}
     */
    this.prompt_ids = null;
    /**
     * Whether the model is multilingual or not.
     * @type {boolean}
     */
    this.is_multilingual = null;
    /**
     * (Optional) A mapping from language tokens to their corresponding IDs.
     * Only required if the model is multilingual.
     * @type {Record<string, number>|null}
     */
    this.lang_to_id = null;
    /**
     * (Optional) A mapping from task tokens to their corresponding IDs.
     * @type {Record<string, number>|null}
     */
    this.task_to_id = null;
    /**
     * Used to set the maximum value of the initial timestamp. This is used to prevent the model from
     * predicting timestamps that are too far in the future.
     * @type {number}
     */
    this.max_initial_timestamp_index = 1;
  }
};

// src/libs/transformers/models.ts
var MODEL_TYPES = {
  EncoderOnly: 0,
  EncoderDecoder: 1,
  Seq2Seq: 2,
  Vision2Seq: 3,
  DecoderOnly: 4,
  MaskGeneration: 5,
  ImageTextToText: 6,
  Musicgen: 7,
  MultiModality: 8,
  Phi3V: 9
};
var MODEL_TYPE_MAPPING = /* @__PURE__ */ new Map();
var MODEL_NAME_TO_CLASS_MAPPING = /* @__PURE__ */ new Map();
var MODEL_CLASS_TO_NAME_MAPPING = /* @__PURE__ */ new Map();
async function getSession(pretrained_model_name_or_path, fileName, options) {
  const custom_config = options.config?.["transformers.js_config"] ?? {};
  let device = options.device ?? custom_config.device;
  if (device && typeof device !== "string") {
    if (device.hasOwnProperty(fileName)) {
      device = device[fileName];
    } else {
      console.warn(`device not specified for "${fileName}". Using the default device.`);
      device = null;
    }
  }
  const selectedDevice = (
    /** @type {import("./utils/devices.js").DeviceType} */
    device ?? (apis.IS_NODE_ENV ? "cpu" : "wasm")
  );
  const executionProviders = deviceToExecutionProviders(selectedDevice);
  let dtype = options.dtype ?? custom_config.dtype;
  if (typeof dtype !== "string") {
    if (dtype && dtype.hasOwnProperty(fileName)) {
      dtype = dtype[fileName];
    } else {
      dtype = DEFAULT_DEVICE_DTYPE_MAPPING[selectedDevice] ?? DATA_TYPES.fp32;
      console.warn(
        `dtype not specified for "${fileName}". Using the default dtype (${dtype}) for this device (${selectedDevice}).`
      );
    }
  }
  if (dtype === DATA_TYPES.auto) {
    let config_dtype = custom_config.dtype;
    if (typeof config_dtype !== "string") {
      config_dtype = config_dtype[fileName];
    }
    if (config_dtype && config_dtype !== DATA_TYPES.auto && DATA_TYPES.hasOwnProperty(config_dtype)) {
      dtype = config_dtype;
    } else {
      dtype = DEFAULT_DEVICE_DTYPE_MAPPING[selectedDevice] ?? DATA_TYPES.fp32;
    }
  }
  const selectedDtype = (
    /** @type {import("./utils/dtypes.js").DataType} */
    dtype
  );
  if (!DEFAULT_DTYPE_SUFFIX_MAPPING.hasOwnProperty(selectedDtype)) {
    throw new Error(`Invalid dtype: ${selectedDtype}. Should be one of: ${Object.keys(DATA_TYPES).join(", ")}`);
  } else if (selectedDtype === DATA_TYPES.fp16 && selectedDevice === "webgpu" && !await isWebGpuFp16Supported()) {
    throw new Error(`The device (${selectedDevice}) does not support fp16.`);
  }
  const kv_cache_dtype = custom_config.kv_cache_dtype ? typeof custom_config.kv_cache_dtype === "string" ? custom_config.kv_cache_dtype : custom_config.kv_cache_dtype[selectedDtype] ?? "float32" : undefined;
  if (kv_cache_dtype && !["float32", "float16"].includes(kv_cache_dtype)) {
    throw new Error(`Invalid kv_cache_dtype: ${kv_cache_dtype}. Should be one of: float32, float16`);
  }
  const session_config = {
    dtype: selectedDtype,
    kv_cache_dtype
  };
  const suffix = DEFAULT_DTYPE_SUFFIX_MAPPING[selectedDtype];
  const modelFileName = `${options.subfolder ?? ""}/${fileName}${suffix}.onnx`;
  const session_options = { ...options.session_options };
  session_options.executionProviders ?? (session_options.executionProviders = executionProviders);
  const free_dimension_overrides = custom_config.free_dimension_overrides;
  if (free_dimension_overrides) {
    session_options.freeDimensionOverrides ?? (session_options.freeDimensionOverrides = free_dimension_overrides);
  } else if (selectedDevice.startsWith("webnn") && !session_options.freeDimensionOverrides) {
    console.warn(
      'WebNN does not currently support dynamic shapes and requires `free_dimension_overrides` to be set in config.json as a field within "transformers.js_config". When `free_dimension_overrides` is not set, you may experience significant performance degradation.'
    );
  }
  const bufferPromise = getModelFile(pretrained_model_name_or_path, modelFileName, true, options);
  const use_external_data_format = options.use_external_data_format ?? custom_config.use_external_data_format;
  let externalDataPromises = [];
  if (use_external_data_format && (use_external_data_format === true || typeof use_external_data_format === "object" && use_external_data_format.hasOwnProperty(fileName) && use_external_data_format[fileName] === true)) {
    if (apis.IS_NODE_ENV) {
      throw new Error("External data format is not yet supported in Node.js");
    }
    const path3 = `${fileName}${suffix}.onnx_data`;
    const fullPath = `${options.subfolder ?? ""}/${path3}`;
    externalDataPromises.push(
      new Promise(async (resolve, reject) => {
        const data = await getModelFile(pretrained_model_name_or_path, fullPath, true, options);
        resolve({ path: path3, data });
      })
    );
  } else if (session_options.externalData !== undefined) {
    externalDataPromises = session_options.externalData.map(async (ext) => {
      if (typeof ext.data === "string") {
        const ext_buffer = await getModelFile(pretrained_model_name_or_path, ext.data, true, options);
        return { ...ext, data: ext_buffer };
      }
      return ext;
    });
  }
  if (externalDataPromises.length > 0) {
    session_options.externalData = await Promise.all(externalDataPromises);
  }
  if (selectedDevice === "webgpu") {
    const shapes = getKeyValueShapes(options.config, {
      prefix: "present"
    });
    if (Object.keys(shapes).length > 0 && !isONNXProxy()) {
      const preferredOutputLocation = {};
      for (const key in shapes) {
        preferredOutputLocation[key] = "gpu-buffer";
      }
      session_options.preferredOutputLocation = preferredOutputLocation;
    }
  }
  const buffer = await bufferPromise;
  return { buffer, session_options, session_config };
}
async function constructSessions(pretrained_model_name_or_path, names, options) {
  return Object.fromEntries(
    await Promise.all(
      Object.keys(names).map(async (name) => {
        const { buffer, session_options, session_config } = await getSession(
          pretrained_model_name_or_path,
          names[name],
          options
        );
        const session = await createInferenceSession(buffer, session_options, session_config);
        return [name, session];
      })
    )
  );
}
async function getOptionalConfigs(pretrained_model_name_or_path, names, options) {
  return Object.fromEntries(
    await Promise.all(
      Object.keys(names).map(async (name) => {
        const config = await getModelJSON(pretrained_model_name_or_path, names[name], false, options);
        return [name, config];
      })
    )
  );
}
function validateInputs(session, inputs) {
  const checkedInputs = /* @__PURE__ */ Object.create(null);
  const missingInputs = [];
  for (const inputName of session.inputNames) {
    const tensor = inputs[inputName];
    if (!(tensor instanceof Tensor3)) {
      missingInputs.push(inputName);
      continue;
    }
    checkedInputs[inputName] = isONNXProxy() ? tensor.clone() : tensor;
  }
  if (missingInputs.length > 0) {
    throw new Error(
      `An error occurred during model execution: "Missing the following inputs: ${missingInputs.join(", ")}.`
    );
  }
  const numInputsProvided = Object.keys(inputs).length;
  const numInputsNeeded = session.inputNames.length;
  if (numInputsProvided > numInputsNeeded) {
    let ignored = Object.keys(inputs).filter((inputName) => !session.inputNames.includes(inputName));
    console.warn(
      `WARNING: Too many inputs were provided (${numInputsProvided} > ${numInputsNeeded}). The following inputs will be ignored: "${ignored.join(", ")}".`
    );
  }
  return checkedInputs;
}
async function sessionRun(session, inputs) {
  const checkedInputs = validateInputs(session, inputs);
  try {
    const ortFeed = Object.fromEntries(
      Object.entries(checkedInputs).map(([k, v]) => [k, v.ort_tensor])
    );
    let output = await session.run(ortFeed);
    output = replaceTensors(output);
    return output;
  } catch (e) {
    const formatted = Object.fromEntries(
      Object.entries(checkedInputs).map(([k, v]) => [
        k,
        {
          // Extract these properties from the underlying ORT tensor
          type: v.type,
          dims: v.dims,
          data: v.data
        }
      ])
    );
    console.error(`An error occurred during model execution: "${e}".`);
    console.error("Inputs given to model:", formatted);
    throw e;
  }
}
function replaceTensors(obj) {
  for (let prop in obj) {
    if (isONNXTensor(obj[prop])) {
      obj[prop] = new Tensor3(obj[prop]);
    } else if (typeof obj[prop] === "object") {
      replaceTensors(obj[prop]);
    }
  }
  return obj;
}
function toI64Tensor(items) {
  if (items instanceof Tensor3) {
    return items;
  }
  if (items.length === 0) {
    throw Error("items must be non-empty");
  }
  if (Array.isArray(items[0])) {
    if (items.some((x) => x.length !== items[0].length)) {
      throw Error(
        "Unable to create tensor, you should probably activate truncation and/or padding with 'padding=True' and/or 'truncation=True' to have batched tensors with the same length."
      );
    }
    return new Tensor3("int64", BigInt64Array.from(items.flat().map((x) => BigInt(x))), [
      items.length,
      items[0].length
    ]);
  } else {
    return new Tensor3("int64", BigInt64Array.from(items.map((x) => BigInt(x))), [1, items.length]);
  }
}
function boolTensor(value) {
  return new Tensor3("bool", [value], [1]);
}
async function seq2seqForward(self2, model_inputs) {
  let { encoder_outputs, input_ids, decoder_input_ids, ...other_decoder_inputs } = model_inputs;
  if (!encoder_outputs) {
    const encoder_inputs = pick(model_inputs, self2.sessions["model"].inputNames);
    encoder_outputs = (await encoderForward(self2, encoder_inputs)).last_hidden_state;
  }
  other_decoder_inputs.input_ids = decoder_input_ids;
  other_decoder_inputs.encoder_hidden_states = encoder_outputs;
  if (self2.sessions["decoder_model_merged"].inputNames.includes("encoder_attention_mask")) {
    other_decoder_inputs.encoder_attention_mask = model_inputs.attention_mask;
  }
  const decoderResults = await decoderForward(self2, other_decoder_inputs, true);
  return decoderResults;
}
async function encoderForward(self2, model_inputs) {
  const session = self2.sessions["model"];
  const encoderFeeds = pick(model_inputs, session.inputNames);
  if (session.inputNames.includes("inputs_embeds") && !encoderFeeds.inputs_embeds) {
    if (!model_inputs.input_ids) {
      throw new Error("Both `input_ids` and `inputs_embeds` are missing in the model inputs.");
    }
    encoderFeeds.inputs_embeds = await self2.encode_text({ input_ids: model_inputs.input_ids });
  }
  if (session.inputNames.includes("token_type_ids") && !encoderFeeds.token_type_ids) {
    encoderFeeds.token_type_ids = new Tensor3(
      "int64",
      new BigInt64Array(encoderFeeds.input_ids.data.length),
      encoderFeeds.input_ids.dims
    );
  }
  return await sessionRun(session, encoderFeeds);
}
async function decoderForward(self2, model_inputs, is_encoder_decoder = false) {
  const session = self2.sessions[is_encoder_decoder ? "decoder_model_merged" : "model"];
  const { past_key_values, ...new_model_inputs } = model_inputs;
  if (session.inputNames.includes("use_cache_branch")) {
    new_model_inputs.use_cache_branch = boolTensor(!!past_key_values);
  }
  if (session.inputNames.includes("position_ids") && new_model_inputs.attention_mask && !new_model_inputs.position_ids) {
    const start_index = self2.config?.model_type === "paligemma" ? 1 : 0;
    new_model_inputs.position_ids = createPositionIds(new_model_inputs, past_key_values, start_index);
  }
  self2.addPastKeyValues(new_model_inputs, past_key_values);
  const fixed = pick(new_model_inputs, session.inputNames);
  return await sessionRun(session, fixed);
}
function default_merge_input_ids_with_image_features({
  image_token_id,
  inputs_embeds,
  image_features,
  input_ids,
  attention_mask
}) {
  const image_tokens = input_ids.tolist().map(
    (ids) => ids.reduce((acc, x, idx) => {
      if (x == image_token_id) acc.push(idx);
      return acc;
    }, [])
  );
  const n_image_tokens = image_tokens.reduce((acc, x) => acc + x.length, 0);
  const n_image_features = image_features.dims[0];
  if (n_image_tokens !== n_image_features) {
    throw new Error(
      `Image features and image tokens do not match: tokens: ${n_image_tokens}, features ${n_image_features}`
    );
  }
  let img = 0;
  for (let i = 0; i < image_tokens.length; ++i) {
    const tokens = image_tokens[i];
    const embeds = inputs_embeds[i];
    for (let j = 0; j < tokens.length; ++j) {
      embeds[tokens[j]].data.set(image_features[img++].data);
    }
  }
  return { inputs_embeds, attention_mask };
}
async function imageTextToTextForward(self2, {
  // Produced by the tokenizer/processor:
  input_ids = null,
  attention_mask = null,
  pixel_values = null,
  // Used during generation:
  position_ids = null,
  inputs_embeds = null,
  past_key_values = null,
  // Generic generation parameters
  generation_config = null,
  logits_processor = null,
  // TODO: needed?
  ...kwargs
}) {
  if (!inputs_embeds) {
    inputs_embeds = await self2.encode_text({ input_ids, ...kwargs });
    if (pixel_values && input_ids?.dims[1] !== 1) {
      const image_features = await self2.encode_image({ pixel_values, ...kwargs });
      ({ inputs_embeds, attention_mask } = self2._merge_input_ids_with_image_features({
        image_features,
        inputs_embeds,
        input_ids,
        attention_mask
      }));
    } else if (past_key_values && pixel_values && input_ids?.dims[1] === 1) {
      const target_length = input_ids?.dims[1] ?? 1;
      const past_length = Object.values(past_key_values)[0].dims.at(-2);
      attention_mask = cat(
        [
          ones([input_ids.dims[0], past_length]),
          attention_mask?.slice(null, [attention_mask.dims[1] - target_length, attention_mask.dims[1]])
        ],
        1
      );
    }
  }
  if (!position_ids) {
    if (self2.config.model_type === "qwen2_vl") {
      const { image_grid_thw, video_grid_thw } = kwargs;
      [position_ids] = self2.get_rope_index(input_ids, image_grid_thw, video_grid_thw, attention_mask);
    }
  }
  const outputs = await decoderForward(
    self2,
    {
      inputs_embeds,
      past_key_values,
      attention_mask,
      position_ids,
      generation_config,
      logits_processor
    },
    true
  );
  return outputs;
}
function cumsum_masked_fill(attention_mask, start_index = 0) {
  const [bz, seq_len] = attention_mask.dims;
  const attn_mask_data = attention_mask.data;
  const data = new BigInt64Array(attn_mask_data.length);
  for (let i = 0; i < bz; ++i) {
    const start = i * seq_len;
    let sum = BigInt(start_index);
    for (let j = 0; j < seq_len; ++j) {
      const index = start + j;
      if (attn_mask_data[index] === 0n) {
        data[index] = BigInt(1);
      } else {
        data[index] = sum;
        sum += attn_mask_data[index];
      }
    }
  }
  return { data, dims: attention_mask.dims };
}
function createPositionIds(model_inputs, past_key_values = null, start_index = 0) {
  const { input_ids, inputs_embeds, attention_mask } = model_inputs;
  const { data, dims } = cumsum_masked_fill(attention_mask, start_index);
  let position_ids = new Tensor3("int64", data, dims);
  if (past_key_values) {
    const offset = -(input_ids ?? inputs_embeds).dims.at(1);
    position_ids = position_ids.slice(null, [offset]);
  }
  return position_ids;
}
function decoder_prepare_inputs_for_generation(self2, input_ids, model_inputs, generation_config) {
  if (model_inputs.past_key_values) {
    const past_length = Object.values(model_inputs.past_key_values)[0].dims.at(-2);
    const { input_ids: input_ids2, attention_mask } = model_inputs;
    if (attention_mask && attention_mask.dims[1] > input_ids2.dims[1]) ; else if (past_length < input_ids2.dims[1]) {
      model_inputs.input_ids = input_ids2.slice(null, [past_length, null]);
    } else {
      if (
        // NOTE: Only used by VLMs (!= so that null matches undefined)
        self2.config.image_token_index != null && // Equivalent to `self.config.image_token_index in input_ids` (== so that int matches bigint)
        input_ids2.data.some((x) => x == self2.config.image_token_index)
      ) {
        const num_image_tokens = self2.config.num_image_tokens;
        if (!num_image_tokens) {
          throw new Error("`num_image_tokens` is missing in the model configuration.");
        }
        const num_new_tokens = input_ids2.dims[1] - (past_length - num_image_tokens);
        model_inputs.input_ids = input_ids2.slice(null, [-num_new_tokens, null]);
        model_inputs.attention_mask = ones([1, past_length + num_new_tokens]);
      }
    }
  }
  return model_inputs;
}
function encoder_decoder_prepare_inputs_for_generation(self2, input_ids, model_inputs, generation_config) {
  if (model_inputs.past_key_values) {
    input_ids = input_ids.map((x) => [x.at(-1)]);
  }
  return {
    ...model_inputs,
    decoder_input_ids: toI64Tensor(input_ids)
  };
}
function image_text_to_text_prepare_inputs_for_generation(self2, ...args) {
  if (self2.config.is_encoder_decoder) {
    return encoder_decoder_prepare_inputs_for_generation(self2, ...args);
  } else {
    return decoder_prepare_inputs_for_generation(self2, ...args);
  }
}
function multimodality_prepare_inputs_for_generation(self2, input_ids, model_inputs, generation_config) {
  const has_past_key_values = !!model_inputs.past_key_values;
  if (generation_config.guidance_scale !== null && generation_config.guidance_scale > 1) {
    if (has_past_key_values) {
      model_inputs.input_ids = cat([model_inputs.input_ids, model_inputs.input_ids], 0);
    } else {
      model_inputs.input_ids = cat(
        [model_inputs.input_ids, full_like(model_inputs.input_ids, BigInt(generation_config.pad_token_id))],
        0
      );
      model_inputs.attention_mask = cat([model_inputs.attention_mask, full_like(model_inputs.attention_mask, 0n)], 0);
    }
  }
  if (has_past_key_values || !model_inputs.pixel_values) {
    model_inputs.pixel_values = full([0, 0, 3, 384, 384], 1);
  }
  if (has_past_key_values) {
    const num_img_tokens = 0;
    const num_text_tokens = 1;
    const batch_size = 1;
    model_inputs.images_seq_mask = new Tensor3(
      "bool",
      new Array(num_img_tokens + num_text_tokens).fill(true).fill(false, 0, num_text_tokens),
      [batch_size, num_img_tokens + num_text_tokens]
    );
    model_inputs.images_emb_mask = new Tensor3("bool", new Array(num_img_tokens).fill(false), [
      batch_size,
      1,
      num_img_tokens
    ]);
  }
  return model_inputs;
}
var PreTrainedModel = class _PreTrainedModel extends Callable {
  constructor(config, sessions, configs) {
    super();
    this.main_input_name = "input_ids";
    this.forward_params = ["input_ids", "attention_mask"];
    this.config = config;
    this.sessions = sessions;
    this.configs = configs;
    const modelName = MODEL_CLASS_TO_NAME_MAPPING.get(this.constructor);
    const modelType = MODEL_TYPE_MAPPING.get(modelName);
    this.can_generate = false;
    this._forward = _PreTrainedModel.prototype._forward;
    this._prepare_inputs_for_generation = _PreTrainedModel.prototype._prepare_inputs_for_generation;
    switch (modelType) {
      case MODEL_TYPES.DecoderOnly:
        this.can_generate = true;
        this._forward = decoderForward;
        this._prepare_inputs_for_generation = decoder_prepare_inputs_for_generation;
        break;
      case MODEL_TYPES.Seq2Seq:
      case MODEL_TYPES.Vision2Seq:
      case MODEL_TYPES.Musicgen:
        this.can_generate = true;
        this._forward = seq2seqForward;
        this._prepare_inputs_for_generation = encoder_decoder_prepare_inputs_for_generation;
        break;
      case MODEL_TYPES.EncoderDecoder:
        this._forward = seq2seqForward;
        break;
      case MODEL_TYPES.ImageTextToText:
        this.can_generate = true;
        this._forward = imageTextToTextForward;
        this._prepare_inputs_for_generation = image_text_to_text_prepare_inputs_for_generation;
        break;
      case MODEL_TYPES.Phi3V:
        this.can_generate = true;
        this._prepare_inputs_for_generation = image_text_to_text_prepare_inputs_for_generation;
        break;
      case MODEL_TYPES.MultiModality:
        this.can_generate = true;
        this._prepare_inputs_for_generation = multimodality_prepare_inputs_for_generation;
        break;
      default:
        this._forward = encoderForward;
        break;
    }
    if (this.can_generate) {
      this.forward_params.push("past_key_values");
    }
    this.custom_config = this.config["transformers_js_config"] ?? {};
  }
  /**
   * Disposes of all the ONNX sessions that were created during inference.
   * @returns {Promise<unknown[]>} An array of promises, one for each ONNX session that is being disposed.
   * @todo Use https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/FinalizationRegistry
   */
  async dispose() {
    const promises = [];
    for (const session of Object.values(this.sessions)) {
      if (session?.handler?.dispose) {
        promises.push(session.handler.dispose());
      }
    }
    return await Promise.all(promises);
  }
  /**
   * Instantiate one of the model classes of the library from a pretrained model.
   *
   * The model class to instantiate is selected based on the `model_type` property of the config object
   * (either passed as an argument or loaded from `pretrained_model_name_or_path` if possible)
   *
   * @param {string} pretrained_model_name_or_path The name or path of the pretrained model. Can be either:
   * - A string, the *model id* of a pretrained model hosted inside a model repo on huggingface.co.
   *   Valid model ids can be located at the root-level, like `bert-base-uncased`, or namespaced under a
   *   user or organization name, like `dbmdz/bert-base-german-cased`.
   * - A path to a *directory* containing model weights, e.g., `./my_model_directory/`.
   * @param {import('./utils/hub.js').PretrainedModelOptions} options Additional options for loading the model.
   *
   * @returns {Promise<PreTrainedModel>} A new instance of the `PreTrainedModel` class.
   */
  static async from_pretrained(pretrained_model_name_or_path, {
    progress_callback = null,
    config = null,
    cache_dir = null,
    local_files_only = false,
    revision = "main",
    model_file_name = null,
    subfolder = "onnx",
    device = null,
    dtype = null,
    use_external_data_format = null,
    session_options = {}
  } = {}) {
    let options = {
      progress_callback,
      config,
      cache_dir,
      local_files_only,
      revision,
      model_file_name,
      subfolder,
      device,
      dtype,
      use_external_data_format,
      session_options
    };
    const modelName = MODEL_CLASS_TO_NAME_MAPPING.get(this);
    const modelType = MODEL_TYPE_MAPPING.get(modelName);
    config = options.config = await AutoConfig.from_pretrained(pretrained_model_name_or_path, options);
    let info;
    if (modelType === MODEL_TYPES.DecoderOnly) {
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            model: options.model_file_name ?? "model"
          },
          options
        ),
        getOptionalConfigs(
          pretrained_model_name_or_path,
          {
            generation_config: "generation_config.json"
          },
          options
        )
      ]);
    } else if (modelType === MODEL_TYPES.Seq2Seq || modelType === MODEL_TYPES.Vision2Seq) {
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            model: "encoder_model",
            decoder_model_merged: "decoder_model_merged"
          },
          options
        ),
        getOptionalConfigs(
          pretrained_model_name_or_path,
          {
            generation_config: "generation_config.json"
          },
          options
        )
      ]);
    } else if (modelType === MODEL_TYPES.MaskGeneration) {
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            model: "vision_encoder",
            prompt_encoder_mask_decoder: "prompt_encoder_mask_decoder"
          },
          options
        )
      ]);
    } else if (modelType === MODEL_TYPES.EncoderDecoder) {
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            model: "encoder_model",
            decoder_model_merged: "decoder_model_merged"
          },
          options
        )
      ]);
    } else if (modelType === MODEL_TYPES.ImageTextToText) {
      const sessions = {
        embed_tokens: "embed_tokens",
        vision_encoder: "vision_encoder",
        decoder_model_merged: "decoder_model_merged"
      };
      if (config.is_encoder_decoder) {
        sessions["model"] = "encoder_model";
      }
      info = await Promise.all([
        constructSessions(pretrained_model_name_or_path, sessions, options),
        getOptionalConfigs(
          pretrained_model_name_or_path,
          {
            generation_config: "generation_config.json"
          },
          options
        )
      ]);
    } else if (modelType === MODEL_TYPES.Musicgen) {
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            model: "text_encoder",
            decoder_model_merged: "decoder_model_merged",
            encodec_decode: "encodec_decode"
          },
          options
        ),
        getOptionalConfigs(
          pretrained_model_name_or_path,
          {
            generation_config: "generation_config.json"
          },
          options
        )
      ]);
    } else if (modelType === MODEL_TYPES.MultiModality) {
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            prepare_inputs_embeds: "prepare_inputs_embeds",
            model: "language_model",
            lm_head: "lm_head",
            gen_head: "gen_head",
            gen_img_embeds: "gen_img_embeds",
            image_decode: "image_decode"
          },
          options
        ),
        getOptionalConfigs(
          pretrained_model_name_or_path,
          {
            generation_config: "generation_config.json"
          },
          options
        )
      ]);
    } else if (modelType === MODEL_TYPES.Phi3V) {
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            prepare_inputs_embeds: "prepare_inputs_embeds",
            model: "model",
            vision_encoder: "vision_encoder"
          },
          options
        ),
        getOptionalConfigs(
          pretrained_model_name_or_path,
          {
            generation_config: "generation_config.json"
          },
          options
        )
      ]);
    } else {
      if (modelType !== MODEL_TYPES.EncoderOnly) {
        const type = modelName ?? config?.model_type;
        if (type !== "custom") {
          console.warn(
            `Model type for '${type}' not found, assuming encoder-only architecture. Please report this at ${GITHUB_ISSUE_URL}.`
          );
        }
      }
      info = await Promise.all([
        constructSessions(
          pretrained_model_name_or_path,
          {
            model: options.model_file_name ?? "model"
          },
          options
        )
      ]);
    }
    return new this(config, ...info);
  }
  /**
   * Runs the model with the provided inputs
   * @param {Object} model_inputs Object containing input tensors
   * @returns {Promise<Object>} Object containing output tensors
   */
  async _call(model_inputs) {
    return await this.forward(model_inputs);
  }
  /**
   * Forward method for a pretrained model. If not overridden by a subclass, the correct forward method
   * will be chosen based on the model type.
   * @param {Object} model_inputs The input data to the model in the format specified in the ONNX model.
   * @returns {Promise<Object>} The output data from the model in the format specified in the ONNX model.
   * @throws {Error} This method must be implemented in subclasses.
   */
  async forward(model_inputs) {
    return await this._forward(this, model_inputs);
  }
  /**
   * Get the model's generation config, if it exists.
   * @returns {GenerationConfig|null} The model's generation config if it exists, otherwise `null`.
   */
  get generation_config() {
    return this.configs?.generation_config ?? null;
  }
  /**
   * This function returns a [`LogitsProcessorList`] list object that contains all relevant [`LogitsWarper`]
   * instances used for multinomial sampling.
   * @param {GenerationConfig} generation_config The generation config.
   * @returns {LogitsProcessorList} generation_config
   */
  _get_logits_warper(generation_config) {
    const warpers = new LogitsProcessorList();
    if (generation_config.temperature !== null && generation_config.temperature !== 1) {
      warpers.push(new TemperatureLogitsWarper(generation_config.temperature));
    }
    if (generation_config.top_k !== null && generation_config.top_k !== 0) {
      warpers.push(new TopKLogitsWarper(generation_config.top_k));
    }
    if (generation_config.top_p !== null && generation_config.top_p < 1) {
      warpers.push(new TopPLogitsWarper(generation_config.top_p));
    }
    return warpers;
  }
  /**
   * @param {GenerationConfig} generation_config
   * @param {number} input_ids_seq_length The starting sequence length for the input ids.
   * @returns {LogitsProcessorList}
   * @private
   */
  _get_logits_processor(generation_config, input_ids_seq_length, logits_processor = null) {
    const processors = new LogitsProcessorList();
    if (generation_config.repetition_penalty !== null && generation_config.repetition_penalty !== 1) {
      processors.push(new RepetitionPenaltyLogitsProcessor(generation_config.repetition_penalty));
    }
    if (generation_config.no_repeat_ngram_size !== null && generation_config.no_repeat_ngram_size > 0) {
      processors.push(new NoRepeatNGramLogitsProcessor(generation_config.no_repeat_ngram_size));
    }
    if (generation_config.bad_words_ids !== null) {
      processors.push(
        new NoBadWordsLogitsProcessor(
          generation_config.bad_words_ids,
          generation_config.eos_token_id
        )
      );
    }
    if (generation_config.min_length !== null && generation_config.eos_token_id !== null && generation_config.min_length > 0) {
      processors.push(new MinLengthLogitsProcessor(generation_config.min_length, generation_config.eos_token_id));
    }
    if (generation_config.min_new_tokens !== null && generation_config.eos_token_id !== null && generation_config.min_new_tokens > 0) {
      processors.push(
        new MinNewTokensLengthLogitsProcessor(
          input_ids_seq_length,
          generation_config.min_new_tokens,
          generation_config.eos_token_id
        )
      );
    }
    if (generation_config.forced_bos_token_id !== null) {
      processors.push(new ForcedBOSTokenLogitsProcessor(generation_config.forced_bos_token_id));
    }
    if (generation_config.forced_eos_token_id !== null) {
      processors.push(
        new ForcedEOSTokenLogitsProcessor(generation_config.max_length, generation_config.forced_eos_token_id)
      );
    }
    if (generation_config.begin_suppress_tokens !== null) {
      const begin_index = input_ids_seq_length > 1 || generation_config.forced_bos_token_id === null ? input_ids_seq_length : input_ids_seq_length + 1;
      processors.push(new SuppressTokensAtBeginLogitsProcessor(generation_config.begin_suppress_tokens, begin_index));
    }
    if (generation_config.guidance_scale !== null && generation_config.guidance_scale > 1) {
      processors.push(new ClassifierFreeGuidanceLogitsProcessor(generation_config.guidance_scale));
    }
    if (logits_processor !== null) {
      processors.extend(logits_processor);
    }
    return processors;
  }
  /**
   * This function merges multiple generation configs together to form a final generation config to be used by the model for text generation.
   * It first creates an empty `GenerationConfig` object, then it applies the model's own `generation_config` property to it. Finally, if a `generation_config` object was passed in the arguments, it overwrites the corresponding properties in the final config with those of the passed config object.
   * @param {GenerationConfig|null} generation_config A `GenerationConfig` object containing generation parameters.
   * @param {Object} kwargs Additional generation parameters to be used in place of those in the `generation_config` object.
   * @returns {GenerationConfig} The final generation config object to be used by the model for text generation.
   */
  _prepare_generation_config(generation_config, kwargs, cls = GenerationConfig) {
    const config = { ...this.config };
    for (const key of ["decoder", "generator", "text_config"]) {
      if (key in config) {
        Object.assign(config, config[key]);
      }
    }
    const gen_config = new cls(config);
    Object.assign(gen_config, this.generation_config ?? {});
    if (generation_config) {
      Object.assign(gen_config, generation_config);
    }
    if (kwargs) {
      Object.assign(gen_config, pick(kwargs, Object.getOwnPropertyNames(gen_config)));
    }
    return gen_config;
  }
  /**
   *
   * @param {GenerationConfig} generation_config
   * @param {StoppingCriteriaList} [stopping_criteria=null]
   */
  _get_stopping_criteria(generation_config, stopping_criteria = null) {
    const criteria = new StoppingCriteriaList();
    if (generation_config.max_length !== null) {
      criteria.push(new MaxLengthCriteria(generation_config.max_length, this.config.max_position_embeddings ?? null));
    }
    if (generation_config.eos_token_id !== null) {
      criteria.push(new EosTokenCriteria(generation_config.eos_token_id));
    }
    if (stopping_criteria) {
      criteria.extend(stopping_criteria);
    }
    return criteria;
  }
  /**
   * Confirms that the model class is compatible with generation.
   * If not, raises an exception that points to the right class to use.
   */
  _validate_model_class() {
    if (!this.can_generate) {
      const generate_compatible_mappings = [
        MODEL_FOR_CAUSAL_LM_MAPPING_NAMES,
        // MODEL_FOR_CAUSAL_IMAGE_MODELING_MAPPING, // TODO
        MODEL_FOR_VISION_2_SEQ_MAPPING_NAMES,
        MODEL_FOR_SPEECH_SEQ_2_SEQ_MAPPING_NAMES
      ];
      const modelName = MODEL_CLASS_TO_NAME_MAPPING.get(this.constructor);
      const generate_compatible_classes = /* @__PURE__ */ new Set();
      const modelType = this.config.model_type;
      for (const model_mapping of generate_compatible_mappings) {
        const supported_models = model_mapping.get(modelType ?? "custom");
        if (supported_models) {
          generate_compatible_classes.add(supported_models[0]);
        }
      }
      let errorMessage = `The current model class (${modelName}) is not compatible with \`.generate()\`, as it doesn't have a language model head.`;
      if (generate_compatible_classes.size > 0) {
        errorMessage += ` Please use the following class instead: ${[...generate_compatible_classes].join(", ")}`;
      }
      throw Error(errorMessage);
    }
  }
  prepare_inputs_for_generation(...args) {
    return this._prepare_inputs_for_generation(this, ...args);
  }
  /**
   *
   * @param {Object} inputs
   * @param {bigint[][]} inputs.generated_input_ids
   * @param {Object} inputs.outputs
   * @param {Object} inputs.model_inputs
   * @param {boolean} inputs.is_encoder_decoder
   * @returns {Object} The updated model inputs for the next generation iteration.
   */
  _update_model_kwargs_for_generation({
    generated_input_ids,
    outputs,
    model_inputs,
    is_encoder_decoder
  }) {
    model_inputs["past_key_values"] = this.getPastKeyValues(outputs, model_inputs.past_key_values);
    model_inputs["input_ids"] = new Tensor3("int64", generated_input_ids.flat(), [generated_input_ids.length, 1]);
    if (!is_encoder_decoder) {
      model_inputs.attention_mask = cat(
        [model_inputs.attention_mask, ones([model_inputs.attention_mask.dims[0], 1])],
        1
      );
    }
    model_inputs["position_ids"] = null;
    return model_inputs;
  }
  /**
   * This function extracts the model-specific `inputs` for generation.
   * @param {Object} params
   * @param {Tensor} [params.inputs=null]
   * @param {number} [params.bos_token_id=null]
   * @param {Record<string, Tensor|number[]>} [params.model_kwargs]
   * @returns {{inputs_tensor: Tensor, model_inputs: Record<string, Tensor>, model_input_name: string}} The model-specific inputs for generation.
   */
  _prepare_model_inputs({
    inputs,
    bos_token_id,
    model_kwargs
  }) {
    const model_inputs = pick(model_kwargs, this.forward_params);
    const input_name = this.main_input_name;
    if (input_name in model_inputs) {
      if (inputs) {
        throw new Error(
          "`inputs`: {inputs}` were passed alongside {input_name} which is not allowed. Make sure to either pass {inputs} or {input_name}=..."
        );
      }
    } else {
      model_inputs[input_name] = inputs;
    }
    const inputs_tensor = model_inputs[input_name];
    return { inputs_tensor, model_inputs, model_input_name: input_name };
  }
  async _prepare_encoder_decoder_kwargs_for_generation({
    inputs_tensor,
    model_inputs,
    model_input_name,
    generation_config
  }) {
    if (this.sessions["model"].inputNames.includes("inputs_embeds") && !model_inputs.inputs_embeds && "_prepare_inputs_embeds" in this) {
      const { input_ids, pixel_values, attention_mask, ...kwargs } = model_inputs;
      const prepared_inputs = await this._prepare_inputs_embeds(model_inputs);
      model_inputs = {
        ...kwargs,
        ...pick(prepared_inputs, ["inputs_embeds", "attention_mask"])
      };
    }
    let { last_hidden_state } = await encoderForward(this, model_inputs);
    if (generation_config.guidance_scale !== null && generation_config.guidance_scale > 1) {
      last_hidden_state = cat([last_hidden_state, full_like(last_hidden_state, 0)], 0);
      if ("attention_mask" in model_inputs) {
        model_inputs["attention_mask"] = cat(
          [model_inputs["attention_mask"], zeros_like(model_inputs["attention_mask"])],
          0
        );
      }
    } else if (model_inputs.decoder_input_ids) {
      const decoder_input_ids_batch_size = toI64Tensor(model_inputs.decoder_input_ids).dims[0];
      if (decoder_input_ids_batch_size !== last_hidden_state.dims[0]) {
        if (last_hidden_state.dims[0] !== 1) {
          throw new Error(
            `The encoder outputs have a different batch size (${last_hidden_state.dims[0]}) than the decoder inputs (${decoder_input_ids_batch_size}).`
          );
        }
        last_hidden_state = cat(
          Array.from({ length: decoder_input_ids_batch_size }, () => last_hidden_state),
          0
        );
      }
    }
    model_inputs["encoder_outputs"] = last_hidden_state;
    return model_inputs;
  }
  /**
   * Prepares `decoder_input_ids` for generation with encoder-decoder models
   * @param {*} param0
   */
  _prepare_decoder_input_ids_for_generation({
    batch_size,
    model_input_name,
    model_kwargs,
    decoder_start_token_id,
    bos_token_id,
    generation_config
  }) {
    let { decoder_input_ids, ...model_inputs } = model_kwargs;
    if (!(decoder_input_ids instanceof Tensor3)) {
      if (!decoder_input_ids) {
        decoder_start_token_id ?? (decoder_start_token_id = bos_token_id);
        if (this.config.model_type === "musicgen") {
          decoder_input_ids = Array.from(
            {
              // @ts-expect-error TS2339
              length: batch_size * this.config.decoder.num_codebooks
            },
            () => [decoder_start_token_id]
          );
        } else if (Array.isArray(decoder_start_token_id)) {
          if (decoder_start_token_id.length !== batch_size) {
            throw new Error(
              `\`decoder_start_token_id\` expcted to have length ${batch_size} but got ${decoder_start_token_id.length}`
            );
          }
          decoder_input_ids = decoder_start_token_id;
        } else {
          decoder_input_ids = Array.from(
            {
              length: batch_size
            },
            () => [decoder_start_token_id]
          );
        }
      } else if (!Array.isArray(decoder_input_ids[0])) {
        decoder_input_ids = Array.from(
          {
            length: batch_size
          },
          () => decoder_input_ids
        );
      }
      decoder_input_ids = toI64Tensor(decoder_input_ids);
    }
    model_kwargs["decoder_attention_mask"] = ones_like(decoder_input_ids);
    return { input_ids: decoder_input_ids, model_inputs };
  }
  /**
   * Generates sequences of token ids for models with a language modeling head.
   * @param {import('./generation/parameters.js').GenerationFunctionParameters} options
   * @returns {Promise<ModelOutput|Tensor>} The output of the model, which can contain the generated token ids, attentions, and scores.
   */
  async generate({
    inputs = null,
    generation_config = null,
    logits_processor = null,
    stopping_criteria = null,
    streamer = null,
    ...kwargs
  }) {
    this._validate_model_class();
    generation_config = this._prepare_generation_config(generation_config, kwargs);
    let { inputs_tensor, model_inputs, model_input_name } = this._prepare_model_inputs({
      inputs,
      bos_token_id: null,
      model_kwargs: kwargs
    });
    const is_encoder_decoder = this.config.is_encoder_decoder;
    if (!is_encoder_decoder) ; else if (!("encoder_outputs" in model_inputs)) {
      model_inputs = await this._prepare_encoder_decoder_kwargs_for_generation({
        inputs_tensor,
        model_inputs,
        model_input_name,
        generation_config
      });
    }
    let input_ids;
    if (is_encoder_decoder) {
      ({ input_ids, model_inputs } = this._prepare_decoder_input_ids_for_generation({
        batch_size: model_inputs[model_input_name].dims.at(0),
        model_input_name,
        model_kwargs: model_inputs,
        decoder_start_token_id: generation_config.decoder_start_token_id,
        bos_token_id: generation_config.bos_token_id,
        generation_config
      }));
    } else {
      input_ids = model_inputs[model_input_name];
    }
    let input_ids_length = input_ids.dims.at(-1);
    if (generation_config && generation_config.max_new_tokens !== null) {
      generation_config.max_length = input_ids_length + generation_config.max_new_tokens;
    }
    const prepared_logits_processor = this._get_logits_processor(generation_config, input_ids_length, logits_processor);
    const prepared_stopping_criteria = this._get_stopping_criteria(generation_config, stopping_criteria);
    const numInputs = model_inputs[model_input_name].dims.at(0);
    const sampler = LogitsSampler.getSampler(generation_config);
    const scores = new Array(numInputs).fill(0);
    const all_input_ids = input_ids.tolist();
    if (streamer) {
      streamer.put(all_input_ids);
    }
    let outputs;
    let attentions = {};
    while (true) {
      model_inputs = this.prepare_inputs_for_generation(all_input_ids, model_inputs, generation_config);
      outputs = await this.forward(model_inputs);
      if (generation_config.output_attentions && generation_config.return_dict_in_generate) {
        const token_attentions = this.getAttentions(outputs);
        for (const key in token_attentions) {
          if (!(key in attentions)) {
            attentions[key] = [];
          }
          attentions[key].push(token_attentions[key]);
        }
      }
      const logits = outputs.logits.slice(null, -1, null);
      const next_tokens_scores = prepared_logits_processor._call(all_input_ids, logits);
      const generated_input_ids = [];
      for (let batch_idx = 0; batch_idx < next_tokens_scores.dims.at(0); ++batch_idx) {
        const logs = next_tokens_scores[batch_idx];
        const sampledTokens = await sampler.sample(logs);
        for (const [newTokenId, logProb] of sampledTokens) {
          const bigint = BigInt(newTokenId);
          scores[batch_idx] += logProb;
          all_input_ids[batch_idx].push(bigint);
          generated_input_ids.push([bigint]);
          break;
        }
      }
      if (streamer) {
        streamer.put(generated_input_ids);
      }
      const stop = prepared_stopping_criteria._call(all_input_ids, scores);
      if (stop.every((x) => x)) {
        break;
      }
      model_inputs = this._update_model_kwargs_for_generation({
        generated_input_ids,
        outputs,
        model_inputs,
        is_encoder_decoder
      });
    }
    if (streamer) {
      streamer.end();
    }
    const past_key_values = this.getPastKeyValues(outputs, model_inputs.past_key_values, true);
    const sequences = new Tensor3("int64", all_input_ids.flat(), [all_input_ids.length, all_input_ids[0].length]);
    if (generation_config.return_dict_in_generate) {
      return {
        sequences,
        past_key_values,
        ...attentions
        // TODO:
        // scores,
        // logits,
      };
    } else {
      for (const tensor of Object.values(outputs)) {
        if (tensor.location === "gpu-buffer") {
          tensor.dispose();
        }
      }
      return sequences;
    }
  }
  /**
   * Returns an object containing past key values from the given decoder results object.
   *
   * @param {Object} decoderResults The decoder results object.
   * @param {Object} pastKeyValues The previous past key values.
   * @returns {Object} An object containing past key values.
   */
  getPastKeyValues(decoderResults, pastKeyValues, disposeEncoderPKVs = false) {
    const pkvs = /* @__PURE__ */ Object.create(null);
    for (const name in decoderResults) {
      if (name.startsWith("present")) {
        const newName = name.replace("present", "past_key_values");
        const is_encoder_pkv = name.includes("encoder");
        if (is_encoder_pkv && pastKeyValues) {
          pkvs[newName] = pastKeyValues[newName];
        } else {
          pkvs[newName] = decoderResults[name];
        }
        if (pastKeyValues && (!is_encoder_pkv || disposeEncoderPKVs)) {
          const t = pastKeyValues[newName];
          if (t.location === "gpu-buffer") {
            t.dispose();
          }
        }
      }
    }
    return pkvs;
  }
  /**
   * Returns an object containing attentions from the given model output object.
   *
   * @param {Object} model_output The output of the model.
   * @returns {{cross_attentions?: Tensor[]}} An object containing attentions.
   */
  getAttentions(model_output) {
    const attentions = {};
    for (const attnName of ["cross_attentions", "encoder_attentions", "decoder_attentions"]) {
      for (const name in model_output) {
        if (name.startsWith(attnName)) {
          if (!(attnName in attentions)) {
            attentions[attnName] = [];
          }
          attentions[attnName].push(model_output[name]);
        }
      }
    }
    return attentions;
  }
  /**
   * Adds past key values to the decoder feeds object. If pastKeyValues is null, creates new tensors for past key values.
   *
   * @param {Object} decoderFeeds The decoder feeds object to add past key values to.
   * @param {Object} pastKeyValues An object containing past key values.
   */
  addPastKeyValues(decoderFeeds, pastKeyValues) {
    if (pastKeyValues) {
      Object.assign(decoderFeeds, pastKeyValues);
    } else {
      const session = this.sessions["decoder_model_merged"] ?? this.sessions["model"];
      const dtype = session?.config?.kv_cache_dtype ?? "float32";
      const empty = dtype === "float16" ? new Uint16Array() : [];
      const batch_size = (decoderFeeds[this.main_input_name] ?? decoderFeeds.attention_mask)?.dims?.[0] ?? 1;
      const shapes = getKeyValueShapes(this.config, { batch_size });
      for (const name in shapes) {
        decoderFeeds[name] = new Tensor3(dtype, empty, shapes[name]);
      }
    }
  }
  async encode_image({ pixel_values }) {
    const features = (await sessionRun(this.sessions["vision_encoder"], { pixel_values })).image_features;
    if (!this.config.num_image_tokens) {
      console.warn(
        `The number of image tokens was not set in the model configuration. Setting it to the number of features detected by the vision encoder (${features.dims[1]}).`
      );
      this.config.num_image_tokens = features.dims[1];
    }
    return features;
  }
  async encode_text({ input_ids }) {
    return (await sessionRun(this.sessions["embed_tokens"], { input_ids })).inputs_embeds;
  }
};
var ModelOutput = class {
};
var ASTPreTrainedModel = class extends PreTrainedModel {
};
var ASTModel = class extends ASTPreTrainedModel {
};
var WhisperPreTrainedModel = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.requires_attention_mask = false;
    this.main_input_name = "input_features";
    this.forward_params = [
      "input_features",
      "attention_mask",
      "decoder_input_ids",
      "decoder_attention_mask",
      "past_key_values"
    ];
  }
};
var WhisperForConditionalGeneration = class extends WhisperPreTrainedModel {
  _prepare_generation_config(generation_config, kwargs) {
    return (
      /** @type {WhisperGenerationConfig} */
      super._prepare_generation_config(
        generation_config,
        kwargs,
        WhisperGenerationConfig
      )
    );
  }
  /**
   *
   * @param {WhisperGenerationConfig} generation_config
   */
  _retrieve_init_tokens(generation_config) {
    const init_tokens = [generation_config.decoder_start_token_id];
    let language = generation_config.language;
    const task = generation_config.task;
    if (generation_config.is_multilingual) {
      if (!language) {
        console.warn("No language specified - defaulting to English (en).");
        language = "en";
      }
      const language_code = whisper_language_to_code(language);
      const language_token = `<|${language_code}|>`;
      if (generation_config.lang_to_id) {
        init_tokens.push(generation_config.lang_to_id[language_token]);
      }
      if (!generation_config.task_to_id) throw new Error("task_to_id mapping is required but was not provided");
      init_tokens.push(generation_config.task_to_id[task ?? "transcribe"]);
    } else if (language || task) {
      throw new Error(
        "Cannot specify `task` or `language` for an English-only model. If the model is intended to be multilingual, pass `is_multilingual=true` to generate, or update the generation config."
      );
    }
    if (!generation_config.return_timestamps && generation_config.no_timestamps_token_id && init_tokens.at(-1) !== generation_config.no_timestamps_token_id) {
      init_tokens.push(generation_config.no_timestamps_token_id);
    } else if (generation_config.return_timestamps && init_tokens.at(-1) === generation_config.no_timestamps_token_id) {
      console.warn(
        "<|notimestamps|> prompt token is removed from generation_config since `return_timestamps` is set to `true`."
      );
      init_tokens.pop();
    }
    return init_tokens.filter((token) => token != null);
  }
  /**
   * Transcribes or translates log-mel input features to a sequence of auto-regressively generated token ids.
   * @param {import('./models/whisper/generation_whisper.js').WhisperGenerationFunctionParameters} options
   * @returns {Promise<ModelOutput|Tensor>} The output of the model, which can contain the generated token ids, attentions, and scores.
   */
  async generate({
    inputs = null,
    generation_config = null,
    logits_processor = null,
    stopping_criteria = null,
    // Whisper-specific options (passed to kwargs)
    // prompt_ids = null,
    // language = null,
    // task = null,
    ...kwargs
  }) {
    generation_config = this._prepare_generation_config(generation_config, kwargs);
    const init_tokens = kwargs.decoder_input_ids ?? this._retrieve_init_tokens(generation_config);
    if (generation_config.return_timestamps) {
      logits_processor ?? (logits_processor = new LogitsProcessorList());
      logits_processor.push(new WhisperTimeStampLogitsProcessor(generation_config, init_tokens));
    }
    if (generation_config.begin_suppress_tokens) {
      logits_processor ?? (logits_processor = new LogitsProcessorList());
      logits_processor.push(
        new SuppressTokensAtBeginLogitsProcessor(generation_config.begin_suppress_tokens, init_tokens.length)
      );
    }
    if (generation_config.return_token_timestamps) {
      if (!generation_config.alignment_heads) {
        throw new Error(
          "Model generation config has no `alignment_heads`, token-level timestamps not available. See https://gist.github.com/hollance/42e32852f24243b748ae6bc1f985b13a on how to add this property to the generation config."
        );
      }
      if (generation_config.task === "translate") {
        console.warn("Token-level timestamps may not be reliable for task 'translate'.");
      }
      generation_config.output_attentions = true;
      generation_config.return_dict_in_generate = true;
    }
    const outputs = await super.generate({
      inputs,
      generation_config,
      logits_processor,
      decoder_input_ids: init_tokens,
      ...kwargs
    });
    if (generation_config.return_token_timestamps) {
      outputs["token_timestamps"] = this._extract_token_timestamps(
        outputs,
        generation_config.alignment_heads,
        generation_config.num_frames
      );
    }
    return outputs;
  }
  /**
   * Calculates token-level timestamps using the encoder-decoder cross-attentions and
   * dynamic time-warping (DTW) to map each output token to a position in the input audio.
   * If `num_frames` is specified, the encoder-decoder cross-attentions will be cropped before applying DTW.
   * @param {Object} generate_outputs Outputs generated by the model
   * @param {Tensor[][]} generate_outputs.cross_attentions The cross attentions output by the model
   * @param {Tensor} generate_outputs.sequences The sequences output by the model
   * @param {number[][]} alignment_heads Alignment heads of the model
   * @param {number} [num_frames=null] Number of frames in the input audio.
   * @param {number} [time_precision=0.02] Precision of the timestamps in seconds
   * @returns {Tensor} tensor containing the timestamps in seconds for each predicted token
   */
  _extract_token_timestamps(generate_outputs, alignment_heads, num_frames = null, time_precision = 0.02) {
    if (!generate_outputs.cross_attentions) {
      throw new Error(
        "Model outputs must contain cross attentions to extract timestamps. This is most likely because the model was not exported with `output_attentions=True`."
      );
    }
    if (num_frames == null) {
      console.warn(
        "`num_frames` has not been set, meaning the entire audio will be analyzed. This may lead to inaccurate token-level timestamps for short audios (< 30 seconds)."
      );
    }
    let median_filter_width = this.config.median_filter_width;
    if (median_filter_width === undefined) {
      console.warn("Model config has no `median_filter_width`, using default value of 7.");
      median_filter_width = 7;
    }
    const batch = generate_outputs.cross_attentions;
    const cross_attentions = Array.from(
      { length: this.config.decoder_layers },
      // Concatenate the cross attentions for each layer across sequence length dimension.
      (_, i) => cat(
        batch.map((x) => x[i]),
        2
      )
    );
    const weights = stack(
      alignment_heads.map(([l, h]) => {
        if (l >= cross_attentions.length) {
          throw new Error(
            `Layer index ${l} is out of bounds for cross attentions (length ${cross_attentions.length}).`
          );
        }
        return num_frames ? cross_attentions[l].slice(null, h, null, [0, num_frames]) : cross_attentions[l].slice(null, h);
      })
    ).transpose(1, 0, 2, 3);
    const [std, calculatedMean] = std_mean(weights, -2, 0, true);
    const smoothedWeights = weights.clone();
    for (let a = 0; a < smoothedWeights.dims[0]; ++a) {
      const aTensor = smoothedWeights[a];
      for (let b = 0; b < aTensor.dims[0]; ++b) {
        const bTensor = aTensor[b];
        const stdTensorData = std[a][b][0].data;
        const meanTensorData = calculatedMean[a][b][0].data;
        for (let c = 0; c < bTensor.dims[0]; ++c) {
          let cTensorData = bTensor[c].data;
          for (let d = 0; d < cTensorData.length; ++d) {
            cTensorData[d] = (cTensorData[d] - meanTensorData[d]) / stdTensorData[d];
          }
          cTensorData.set(medianFilter(cTensorData, median_filter_width));
        }
      }
    }
    const batchedMatrices = [mean(smoothedWeights, 1)];
    const timestampsShape = generate_outputs.sequences.dims;
    const timestamps = new Tensor3(
      "float32",
      new Float32Array(timestampsShape[0] * timestampsShape[1]),
      timestampsShape
    );
    for (let batch_idx = 0; batch_idx < timestampsShape[0]; ++batch_idx) {
      const matrix = batchedMatrices[batch_idx].neg().squeeze_(0);
      const [text_indices, time_indices] = dynamic_time_warping(matrix.tolist());
      const diffs = Array.from({ length: text_indices.length - 1 }, (v, i) => text_indices[i + 1] - text_indices[i]);
      const jumps = mergeArrays([1], diffs).map((x) => !!x);
      const jump_times = [];
      for (let i = 0; i < jumps.length; ++i) {
        if (jumps[i]) {
          jump_times.push(time_indices[i] * time_precision);
        }
      }
      timestamps[batch_idx].data.set(jump_times, 1);
    }
    return timestamps;
  }
};
var MoonshinePreTrainedModel = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.requires_attention_mask = false;
    this.main_input_name = "input_values";
    this.forward_params = ["input_values", "decoder_input_ids", "past_key_values"];
  }
};
var MoonshineForConditionalGeneration = class extends MoonshinePreTrainedModel {
};
var LlavaPreTrainedModel = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.forward_params = ["input_ids", "attention_mask", "pixel_values", "position_ids", "past_key_values"];
  }
};
var LlavaForConditionalGeneration = class extends LlavaPreTrainedModel {
  _merge_input_ids_with_image_features({ inputs_embeds, image_features, input_ids, attention_mask }) {
    const image_token_index = this.config.image_token_index;
    const idsList = input_ids.tolist();
    const indexOfImage = idsList.map((x) => x.findIndex((x2) => x2 == image_token_index));
    const noImages = indexOfImage.every((x) => x === -1);
    const allImages = indexOfImage.every((x) => x !== -1);
    if (!noImages && !allImages) {
      throw new Error("Every input should contain either 0 or 1 image token.");
    }
    if (noImages) {
      return {
        inputs_embeds,
        attention_mask
      };
    }
    const stacked = [];
    const stacked_attention_mask = [];
    for (let i = 0; i < indexOfImage.length; ++i) {
      const index = indexOfImage[i];
      const e = inputs_embeds[i];
      const im = image_features[i];
      const am = attention_mask[i];
      stacked.push(cat([e.slice([0, index]), im, e.slice([index + 1, e.dims[0]])], 0));
      stacked_attention_mask.push(
        cat([am.slice([0, index]), ones([im.dims[0]]), am.slice([index + 1, am.dims[0]])], 0)
      );
    }
    return {
      inputs_embeds: stack(stacked, 0),
      attention_mask: stack(stacked_attention_mask, 0)
    };
  }
};
var LlavaOnevisionForConditionalGeneration = class extends LlavaForConditionalGeneration {
};
var Moondream1ForConditionalGeneration = class extends LlavaForConditionalGeneration {
};
var Florence2PreTrainedModel = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.forward_params = [
      // Encoder inputs
      "input_ids",
      "inputs_embeds",
      "attention_mask",
      "pixel_values",
      // Decoder inputs
      "encoder_outputs",
      "decoder_input_ids",
      "decoder_inputs_embeds",
      "decoder_attention_mask",
      "past_key_values"
    ];
    this.main_input_name = "inputs_embeds";
  }
};
var Florence2ForConditionalGeneration = class extends Florence2PreTrainedModel {
  _merge_input_ids_with_image_features({
    inputs_embeds,
    image_features,
    input_ids,
    attention_mask
  }) {
    return {
      inputs_embeds: cat(
        [
          image_features,
          // image embeds
          inputs_embeds
          // task prefix embeds
        ],
        1
      ),
      attention_mask: cat(
        [
          ones(image_features.dims.slice(0, 2)),
          // image attention mask
          attention_mask
          // task prefix attention mask
        ],
        1
      )
    };
  }
  async _prepare_inputs_embeds({
    input_ids,
    pixel_values,
    inputs_embeds,
    attention_mask
  }) {
    if (!input_ids && !pixel_values) {
      throw new Error("Either `input_ids` or `pixel_values` should be provided.");
    }
    let text_features, image_features;
    if (input_ids) {
      text_features = await this.encode_text({ input_ids });
    }
    if (pixel_values) {
      image_features = await this.encode_image({ pixel_values });
    }
    if (text_features && image_features) {
      ({ inputs_embeds, attention_mask } = this._merge_input_ids_with_image_features({
        inputs_embeds: text_features,
        image_features,
        input_ids,
        attention_mask
      }));
    } else {
      inputs_embeds = text_features || image_features;
    }
    return { inputs_embeds, attention_mask };
  }
  async forward({
    input_ids,
    pixel_values,
    attention_mask,
    decoder_input_ids,
    decoder_attention_mask,
    encoder_outputs,
    past_key_values,
    inputs_embeds,
    decoder_inputs_embeds
  }) {
    if (!inputs_embeds) {
      ({ inputs_embeds, attention_mask } = await this._prepare_inputs_embeds({
        input_ids,
        pixel_values,
        inputs_embeds,
        attention_mask
      }));
    }
    if (!encoder_outputs) {
      let { last_hidden_state } = await encoderForward(this, { inputs_embeds, attention_mask });
      encoder_outputs = last_hidden_state;
    }
    if (!decoder_inputs_embeds) {
      if (!decoder_input_ids) {
        throw new Error("Either `decoder_input_ids` or `decoder_inputs_embeds` should be provided.");
      }
      decoder_inputs_embeds = await this.encode_text({ input_ids: decoder_input_ids });
    }
    const decoderFeeds = {
      inputs_embeds: decoder_inputs_embeds,
      attention_mask: decoder_attention_mask,
      encoder_attention_mask: attention_mask,
      encoder_hidden_states: encoder_outputs,
      past_key_values
    };
    const decoder_outputs = await decoderForward(this, decoderFeeds, true);
    return decoder_outputs;
  }
};
var PaliGemmaPreTrainedModel = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.forward_params = [
      "input_ids",
      // 'inputs_embeds',
      "attention_mask",
      "pixel_values",
      "position_ids",
      "past_key_values"
    ];
  }
};
var PaliGemmaForConditionalGeneration = class extends PaliGemmaPreTrainedModel {
  _merge_input_ids_with_image_features(kwargs) {
    const vision_hidden_size = kwargs.image_features.dims.at(-1);
    const reshaped_image_hidden_states = kwargs.image_features.view(-1, vision_hidden_size);
    return default_merge_input_ids_with_image_features({
      image_token_id: this.config.image_token_index,
      image_features: reshaped_image_hidden_states,
      ...kwargs
    });
  }
};
var Idefics3PreTrainedModel = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.forward_params = [
      "input_ids",
      "attention_mask",
      "pixel_values",
      "pixel_attention_mask",
      "position_ids",
      "past_key_values"
    ];
  }
};
var Idefics3ForConditionalGeneration = class extends Idefics3PreTrainedModel {
  async encode_image({ pixel_values, pixel_attention_mask }) {
    const features = (await sessionRun(this.sessions["vision_encoder"], { pixel_values, pixel_attention_mask })).image_features;
    return features;
  }
  _merge_input_ids_with_image_features(kwargs) {
    const vision_hidden_size = kwargs.image_features.dims.at(-1);
    const reshaped_image_hidden_states = kwargs.image_features.view(-1, vision_hidden_size);
    return default_merge_input_ids_with_image_features({
      image_token_id: this.config.image_token_id,
      ...kwargs,
      image_features: reshaped_image_hidden_states
    });
  }
};
var Phi3VPreTrainedModel = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.forward_params = [
      "input_ids",
      "inputs_embeds",
      "attention_mask",
      "position_ids",
      "pixel_values",
      "image_sizes",
      "past_key_values"
    ];
  }
};
var Phi3VForCausalLM = class extends Phi3VPreTrainedModel {
  async forward({
    // Produced by the tokenizer/processor:
    input_ids = null,
    attention_mask = null,
    pixel_values = null,
    image_sizes = null,
    // Used during generation:
    position_ids = null,
    inputs_embeds = null,
    past_key_values = null,
    // Generic generation parameters
    generation_config = null,
    logits_processor = null,
    // TODO: needed?
    ...kwargs
  }) {
    if (!inputs_embeds) {
      let image_features;
      if (pixel_values && input_ids.dims[1] !== 1) {
        if (!image_sizes) {
          throw new Error("`image_sizes` must be provided when `pixel_values` is provided.");
        }
        ({ image_features } = await sessionRun(this.sessions["vision_encoder"], {
          pixel_values,
          image_sizes
        }));
      } else {
        const hidden_size = this.config.normalized_config.hidden_size;
        image_features = new Tensor3("float32", [], [0, hidden_size]);
      }
      ({ inputs_embeds } = await sessionRun(this.sessions["prepare_inputs_embeds"], {
        input_ids,
        image_features
      }));
    }
    const outputs = await decoderForward(
      this,
      {
        inputs_embeds,
        past_key_values,
        attention_mask,
        position_ids,
        generation_config,
        logits_processor
      },
      false
    );
    return outputs;
  }
};
var CLIPPreTrainedModel = class extends PreTrainedModel {
};
var CLIPModel = class extends CLIPPreTrainedModel {
};
var CLIPTextModelWithProjection = class extends CLIPPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "text_model"
    });
  }
};
var CLIPVisionModelWithProjection = class extends CLIPPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "vision_model"
    });
  }
};
var SiglipPreTrainedModel = class extends PreTrainedModel {
};
var SiglipModel = class extends SiglipPreTrainedModel {
};
var SiglipTextModel = class extends SiglipPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "text_model"
    });
  }
};
var SiglipVisionModel = class extends CLIPPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "vision_model"
    });
  }
};
var ChineseCLIPPreTrainedModel = class extends PreTrainedModel {
};
var ChineseCLIPModel = class extends ChineseCLIPPreTrainedModel {
};
var JinaCLIPPreTrainedModel = class extends PreTrainedModel {
};
var JinaCLIPModel = class extends JinaCLIPPreTrainedModel {
  async forward(model_inputs) {
    const missing_text_inputs = !model_inputs.input_ids;
    const missing_image_inputs = !model_inputs.pixel_values;
    if (missing_text_inputs && missing_image_inputs) {
      throw new Error("Either `input_ids` or `pixel_values` should be provided.");
    }
    if (missing_text_inputs) {
      model_inputs.input_ids = ones([model_inputs.pixel_values.dims[0], 1]);
    }
    if (missing_image_inputs) {
      const { image_size } = this.config.vision_config;
      model_inputs.pixel_values = full([0, 3, image_size, image_size], 0);
    }
    const { text_embeddings, image_embeddings, l2norm_text_embeddings, l2norm_image_embeddings } = await super.forward(
      model_inputs
    );
    const result = {};
    if (!missing_text_inputs) {
      result.text_embeddings = text_embeddings;
      result.l2norm_text_embeddings = l2norm_text_embeddings;
    }
    if (!missing_image_inputs) {
      result.image_embeddings = image_embeddings;
      result.l2norm_image_embeddings = l2norm_image_embeddings;
    }
    return result;
  }
};
var JinaCLIPTextModel = class extends JinaCLIPPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "text_model"
    });
  }
};
var JinaCLIPVisionModel = class extends JinaCLIPPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "vision_model"
    });
  }
};
var GPT2PreTrainedModel = class extends PreTrainedModel {
};
var GPT2Model = class extends GPT2PreTrainedModel {
};
var GPT2LMHeadModel = class extends GPT2PreTrainedModel {
};
var JAISPreTrainedModel = class extends PreTrainedModel {
};
var JAISModel = class extends JAISPreTrainedModel {
};
var JAISLMHeadModel = class extends JAISPreTrainedModel {
};
var GPTNeoPreTrainedModel = class extends PreTrainedModel {
};
var GPTNeoModel = class extends GPTNeoPreTrainedModel {
};
var GPTNeoForCausalLM = class extends GPTNeoPreTrainedModel {
};
var GPTNeoXPreTrainedModel = class extends PreTrainedModel {
};
var GPTNeoXModel = class extends GPTNeoXPreTrainedModel {
};
var GPTNeoXForCausalLM = class extends GPTNeoXPreTrainedModel {
};
var GPTJPreTrainedModel = class extends PreTrainedModel {
};
var GPTJModel = class extends GPTJPreTrainedModel {
};
var GPTJForCausalLM = class extends GPTJPreTrainedModel {
};
var GPTBigCodePreTrainedModel = class extends PreTrainedModel {
};
var GPTBigCodeModel = class extends GPTBigCodePreTrainedModel {
};
var GPTBigCodeForCausalLM = class extends GPTBigCodePreTrainedModel {
};
var CodeGenPreTrainedModel = class extends PreTrainedModel {
};
var CodeGenModel = class extends CodeGenPreTrainedModel {
};
var CodeGenForCausalLM = class extends CodeGenPreTrainedModel {
};
var LlamaPreTrainedModel = class extends PreTrainedModel {
};
var LlamaModel = class extends LlamaPreTrainedModel {
};
var LlamaForCausalLM = class extends LlamaPreTrainedModel {
};
var ExaonePreTrainedModel = class extends PreTrainedModel {
};
var ExaoneModel = class extends ExaonePreTrainedModel {
};
var ExaoneForCausalLM = class extends ExaonePreTrainedModel {
};
var MobileLLMPreTrainedModel = class extends PreTrainedModel {
};
var MobileLLMModel = class extends MobileLLMPreTrainedModel {
};
var MobileLLMForCausalLM = class extends MobileLLMPreTrainedModel {
};
var OlmoPreTrainedModel = class extends PreTrainedModel {
};
var OlmoModel = class extends OlmoPreTrainedModel {
};
var OlmoForCausalLM = class extends OlmoPreTrainedModel {
};
var Olmo2PreTrainedModel = class extends PreTrainedModel {
};
var Olmo2Model = class extends Olmo2PreTrainedModel {
};
var Olmo2ForCausalLM = class extends Olmo2PreTrainedModel {
};
var GranitePreTrainedModel = class extends PreTrainedModel {
};
var GraniteModel = class extends GranitePreTrainedModel {
};
var GraniteForCausalLM = class extends GranitePreTrainedModel {
};
var CoherePreTrainedModel = class extends PreTrainedModel {
};
var CohereModel = class extends CoherePreTrainedModel {
};
var CohereForCausalLM = class extends CoherePreTrainedModel {
};
var GemmaPreTrainedModel = class extends PreTrainedModel {
};
var GemmaModel = class extends GemmaPreTrainedModel {
};
var GemmaForCausalLM = class extends GemmaPreTrainedModel {
};
var Gemma2PreTrainedModel = class extends PreTrainedModel {
};
var Gemma2Model = class extends Gemma2PreTrainedModel {
};
var Gemma2ForCausalLM = class extends Gemma2PreTrainedModel {
};
var OpenELMPreTrainedModel = class extends PreTrainedModel {
};
var OpenELMModel = class extends OpenELMPreTrainedModel {
};
var OpenELMForCausalLM = class extends OpenELMPreTrainedModel {
};
var Qwen2PreTrainedModel = class extends PreTrainedModel {
};
var Qwen2Model = class extends Qwen2PreTrainedModel {
};
var Qwen2ForCausalLM = class extends Qwen2PreTrainedModel {
};
var PhiPreTrainedModel = class extends PreTrainedModel {
};
var PhiModel = class extends PhiPreTrainedModel {
};
var PhiForCausalLM = class extends PhiPreTrainedModel {
};
var Phi3PreTrainedModel = class extends PreTrainedModel {
};
var Phi3Model = class extends Phi3PreTrainedModel {
};
var Phi3ForCausalLM = class extends Phi3PreTrainedModel {
};
var BloomPreTrainedModel = class extends PreTrainedModel {
};
var BloomModel = class extends BloomPreTrainedModel {
};
var BloomForCausalLM = class extends BloomPreTrainedModel {
};
var MptPreTrainedModel = class extends PreTrainedModel {
};
var MptModel = class extends MptPreTrainedModel {
};
var MptForCausalLM = class extends MptPreTrainedModel {
};
var OPTPreTrainedModel = class extends PreTrainedModel {
};
var OPTModel = class extends OPTPreTrainedModel {
};
var OPTForCausalLM = class extends OPTPreTrainedModel {
};
var VitPosePreTrainedModel = class extends PreTrainedModel {
};
var VitPoseForPoseEstimation = class extends VitPosePreTrainedModel {
};
var ViTMAEPreTrainedModel = class extends PreTrainedModel {
};
var ViTMAEModel = class extends ViTMAEPreTrainedModel {
};
var GroupViTPreTrainedModel = class extends PreTrainedModel {
};
var GroupViTModel = class extends GroupViTPreTrainedModel {
};
var VitMattePreTrainedModel = class extends PreTrainedModel {
};
var VitMatteForImageMatting = class extends VitMattePreTrainedModel {
  /**
   * @param {any} model_inputs
   */
  async _call(model_inputs) {
    return new ImageMattingOutput(await super._call(model_inputs));
  }
};
var Swin2SRPreTrainedModel = class extends PreTrainedModel {
};
var Swin2SRModel = class extends Swin2SRPreTrainedModel {
};
var Swin2SRForImageSuperResolution = class extends Swin2SRPreTrainedModel {
};
var SamPreTrainedModel = class extends PreTrainedModel {
};
var SamModel = class extends SamPreTrainedModel {
  /**
   * Compute image embeddings and positional image embeddings, given the pixel values of an image.
   * @param {Object} model_inputs Object containing the model inputs.
   * @param {Tensor} model_inputs.pixel_values Pixel values obtained using a `SamProcessor`.
   * @returns {Promise<{ image_embeddings: Tensor, image_positional_embeddings: Tensor }>} The image embeddings and positional image embeddings.
   */
  async get_image_embeddings({ pixel_values }) {
    return await encoderForward(this, { pixel_values });
  }
  /**
   * @typedef {Object} SamModelInputs Object containing the model inputs.
   * @property {Tensor} pixel_values Pixel values as a Tensor with shape `(batch_size, num_channels, height, width)`.
   * These can be obtained using a `SamProcessor`.
   * @property {Tensor} [input_points] Input 2D spatial points with shape `(batch_size, num_points, 2)`.
   * This is used by the prompt encoder to encode the prompt.
   * @property {Tensor} [input_labels] Input labels for the points, as a Tensor of shape `(batch_size, point_batch_size, num_points)`.
   * This is used by the prompt encoder to encode the prompt. There are 4 types of labels:
   *  - `1`: the point is a point that contains the object of interest
   *  - `0`: the point is a point that does not contain the object of interest
   *  - `-1`: the point corresponds to the background
   *  - `-10`: the point is a padding point, thus should be ignored by the prompt encoder
   * @property {Tensor} [input_boxes] Input bounding boxes with shape `(batch_size, num_boxes, 4)`.
   * @property {Tensor} [image_embeddings] Image embeddings used by the mask decoder.
   * @property {Tensor} [image_positional_embeddings] Image positional embeddings used by the mask decoder.
   */
  /**
   * @param {SamModelInputs} model_inputs Object containing the model inputs.
   * @returns {Promise<Object>} The output of the model.
   */
  async forward(model_inputs) {
    if (!model_inputs.image_embeddings || !model_inputs.image_positional_embeddings) {
      model_inputs = {
        ...model_inputs,
        ...await this.get_image_embeddings(model_inputs)
      };
    }
    if (!model_inputs.input_labels && model_inputs.input_points) {
      const shape = model_inputs.input_points.dims.slice(0, -1);
      const numElements = shape.reduce((a, b) => a * b, 1);
      model_inputs.input_labels = new Tensor3("int64", new BigInt64Array(numElements).fill(1n), shape);
    }
    const decoder_inputs = {
      image_embeddings: model_inputs.image_embeddings,
      image_positional_embeddings: model_inputs.image_positional_embeddings,
      input_points: null,
      input_labels: null,
      input_boxes: null
    };
    if (model_inputs.input_points) {
      decoder_inputs.input_points = model_inputs.input_points;
    }
    if (model_inputs.input_labels) {
      decoder_inputs.input_labels = model_inputs.input_labels;
    }
    if (model_inputs.input_boxes) {
      decoder_inputs.input_boxes = model_inputs.input_boxes;
    }
    return await sessionRun(this.sessions["prompt_encoder_mask_decoder"], decoder_inputs);
  }
  /**
   * Runs the model with the provided inputs
   * @param {Object} model_inputs Model inputs
   * @returns {Promise<SamImageSegmentationOutput>} Object containing segmentation outputs
   */
  async _call(model_inputs) {
    return new SamImageSegmentationOutput(await super._call(model_inputs));
  }
};
var SamImageSegmentationOutput = class extends ModelOutput {
  constructor({ iou_scores, pred_masks }) {
    super();
    this.iou_scores = iou_scores;
    this.pred_masks = pred_masks;
  }
};
var Wav2Vec2PreTrainedModel = class extends PreTrainedModel {
};
var Wav2Vec2Model = class extends Wav2Vec2PreTrainedModel {
};
var Wav2Vec2ForCTC = class extends Wav2Vec2PreTrainedModel {
  /**
   * @param {Object} model_inputs
   * @param {Tensor} model_inputs.input_values Float values of input raw speech waveform.
   * @param {Tensor} model_inputs.attention_mask Mask to avoid performing convolution and attention on padding token indices. Mask values selected in [0, 1]
   */
  async _call(model_inputs) {
    return new CausalLMOutput(await super._call(model_inputs));
  }
};
var PyAnnotePreTrainedModel = class extends PreTrainedModel {
};
var PyAnnoteModel = class extends PyAnnotePreTrainedModel {
};
var WeSpeakerResNetPreTrainedModel = class extends PreTrainedModel {
};
var WeSpeakerResNetModel = class extends WeSpeakerResNetPreTrainedModel {
};
var UniSpeechPreTrainedModel = class extends PreTrainedModel {
};
var UniSpeechModel = class extends UniSpeechPreTrainedModel {
};
var UniSpeechForCTC = class extends UniSpeechPreTrainedModel {
  /**
   * @param {Object} model_inputs
   * @param {Tensor} model_inputs.input_values Float values of input raw speech waveform.
   * @param {Tensor} model_inputs.attention_mask Mask to avoid performing convolution and attention on padding token indices. Mask values selected in [0, 1]
   */
  async _call(model_inputs) {
    return new CausalLMOutput(await super._call(model_inputs));
  }
};
var UniSpeechSatPreTrainedModel = class extends PreTrainedModel {
};
var UniSpeechSatModel = class extends UniSpeechSatPreTrainedModel {
};
var UniSpeechSatForCTC = class extends UniSpeechSatPreTrainedModel {
  /**
   * @param {Object} model_inputs
   * @param {Tensor} model_inputs.input_values Float values of input raw speech waveform.
   * @param {Tensor} model_inputs.attention_mask Mask to avoid performing convolution and attention on padding token indices. Mask values selected in [0, 1]
   */
  async _call(model_inputs) {
    return new CausalLMOutput(await super._call(model_inputs));
  }
};
var Wav2Vec2BertPreTrainedModel = class extends PreTrainedModel {
};
var Wav2Vec2BertModel = class extends Wav2Vec2BertPreTrainedModel {
};
var Wav2Vec2BertForCTC = class extends Wav2Vec2BertPreTrainedModel {
  /**
   * @param {Object} model_inputs
   * @param {Tensor} model_inputs.input_features Float values of input mel-spectrogram.
   * @param {Tensor} model_inputs.attention_mask Mask to avoid performing convolution and attention on padding token indices. Mask values selected in [0, 1]
   */
  async _call(model_inputs) {
    return new CausalLMOutput(await super._call(model_inputs));
  }
};
var HubertModel = class extends Wav2Vec2PreTrainedModel {
};
var HubertForCTC = class extends Wav2Vec2PreTrainedModel {
  /**
   * @param {Object} model_inputs
   * @param {Tensor} model_inputs.input_values Float values of input raw speech waveform.
   * @param {Tensor} model_inputs.attention_mask Mask to avoid performing convolution and attention on padding token indices. Mask values selected in [0, 1]
   */
  async _call(model_inputs) {
    return new CausalLMOutput(await super._call(model_inputs));
  }
};
var WavLMPreTrainedModel = class extends PreTrainedModel {
};
var WavLMModel = class extends WavLMPreTrainedModel {
};
var WavLMForCTC = class extends WavLMPreTrainedModel {
  /**
   * @param {Object} model_inputs
   * @param {Tensor} model_inputs.input_values Float values of input raw speech waveform.
   * @param {Tensor} model_inputs.attention_mask Mask to avoid performing convolution and attention on padding token indices. Mask values selected in [0, 1]
   */
  async _call(model_inputs) {
    return new CausalLMOutput(await super._call(model_inputs));
  }
};
var WavLMForXVector = class extends WavLMPreTrainedModel {
  /**
   * Calls the model on new inputs.
   * @param {Object} model_inputs The inputs to the model.
   * @returns {Promise<XVectorOutput>} An object containing the model's output logits and speaker embeddings.
   */
  async _call(model_inputs) {
    return new XVectorOutput(await super._call(model_inputs));
  }
};
var SpeechT5PreTrainedModel = class extends PreTrainedModel {
};
var SpeechT5ForSpeechToText = class extends SpeechT5PreTrainedModel {
};
var SpeechT5ForTextToSpeech = class extends SpeechT5PreTrainedModel {
  /**
   * @typedef {Object} SpeechOutput
   * @property {Tensor} [spectrogram] The predicted log-mel spectrogram of shape
   * `(output_sequence_length, config.num_mel_bins)`. Returned when no `vocoder` is provided
   * @property {Tensor} [waveform] The predicted waveform of shape `(num_frames,)`. Returned when a `vocoder` is provided.
   * @property {Tensor} [cross_attentions] The outputs of the decoder's cross-attention layers of shape
   * `(config.decoder_layers, config.decoder_attention_heads, output_sequence_length, input_sequence_length)`. returned when `output_cross_attentions` is `true`.
   */
  /**
   * Converts a sequence of input tokens into a sequence of mel spectrograms, which are subsequently turned into a speech waveform using a vocoder.
   * @param {Tensor} input_values Indices of input sequence tokens in the vocabulary.
   * @param {Tensor} speaker_embeddings Tensor containing the speaker embeddings.
   * @param {Object} options Optional parameters for generating speech.
   * @param {number} [options.threshold=0.5] The generated sequence ends when the predicted stop token probability exceeds this value.
   * @param {number} [options.minlenratio=0.0] Used to calculate the minimum required length for the output sequence.
   * @param {number} [options.maxlenratio=20.0] Used to calculate the maximum allowed length for the output sequence.
   * @param {Object} [options.vocoder=null] The vocoder that converts the mel spectrogram into a speech waveform. If `null`, the output is the mel spectrogram.
   * @param {boolean} [options.output_cross_attentions=false] Whether or not to return the attentions tensors of the decoder's cross-attention layers.
   * @returns {Promise<SpeechOutput>} A promise which resolves to an object containing the spectrogram, waveform, and cross-attention tensors.
   */
  async generate_speech(input_values, speaker_embeddings, {
    threshold = 0.5,
    minlenratio = 0,
    maxlenratio = 20,
    vocoder = null
    // output_cross_attentions = false, // TODO add
  } = {}) {
    const model_inputs = {
      input_ids: input_values
    };
    const { encoder_outputs, encoder_attention_mask } = await encoderForward(this, model_inputs);
    const r = encoder_outputs.dims[1] / this.config.reduction_factor;
    const maxlen = Math.floor(r * maxlenratio);
    const minlen = Math.floor(r * minlenratio);
    const num_mel_bins = this.config.num_mel_bins;
    let spectrogramParts = [];
    let past_key_values = null;
    let decoder_outputs = null;
    let idx = 0;
    while (true) {
      ++idx;
      const use_cache_branch = boolTensor(!!decoder_outputs);
      let output_sequence;
      if (decoder_outputs) {
        output_sequence = decoder_outputs.output_sequence_out;
      } else {
        output_sequence = new Tensor3("float32", new Float32Array(num_mel_bins), [1, 1, num_mel_bins]);
      }
      let decoderFeeds = {
        use_cache_branch,
        output_sequence,
        encoder_attention_mask,
        speaker_embeddings,
        encoder_hidden_states: encoder_outputs
      };
      this.addPastKeyValues(decoderFeeds, past_key_values);
      decoder_outputs = await sessionRun(this.sessions["decoder_model_merged"], decoderFeeds);
      past_key_values = this.getPastKeyValues(decoder_outputs, past_key_values);
      const { prob, spectrum } = decoder_outputs;
      spectrogramParts.push(spectrum);
      if (idx >= minlen && // Finished when stop token or maximum length is reached.
      (Array.from(prob.data).filter((p) => p >= threshold).length > 0 || idx >= maxlen)) {
        break;
      }
    }
    const spectrogram2 = cat(spectrogramParts);
    const { waveform } = await sessionRun(vocoder.sessions["model"], { spectrogram: spectrogram2 });
    return {
      spectrogram: spectrogram2,
      waveform
      // cross_attentions: null, // TODO add
    };
  }
};
var SpeechT5HifiGan = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    this.main_input_name = "spectrogram";
  }
};
var TrOCRPreTrainedModel = class extends PreTrainedModel {
};
var TrOCRForCausalLM = class extends TrOCRPreTrainedModel {
};
var MistralPreTrainedModel = class extends PreTrainedModel {
};
var MistralModel = class extends MistralPreTrainedModel {
};
var MistralForCausalLM = class extends MistralPreTrainedModel {
};
var Starcoder2PreTrainedModel = class extends PreTrainedModel {
};
var Starcoder2Model = class extends Starcoder2PreTrainedModel {
};
var Starcoder2ForCausalLM = class extends Starcoder2PreTrainedModel {
};
var FalconPreTrainedModel = class extends PreTrainedModel {
};
var FalconModel = class extends FalconPreTrainedModel {
};
var FalconForCausalLM = class extends FalconPreTrainedModel {
};
var ClapPreTrainedModel = class extends PreTrainedModel {
};
var ClapModel = class extends ClapPreTrainedModel {
};
var ClapTextModelWithProjection = class extends ClapPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "text_model"
    });
  }
};
var ClapAudioModelWithProjection = class extends ClapPreTrainedModel {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    return super.from_pretrained(pretrained_model_name_or_path, {
      ...options,
      // Update default model file name if not provided
      model_file_name: options.model_file_name ?? "audio_model"
    });
  }
};
var VitsPreTrainedModel = class extends PreTrainedModel {
};
var VitsModel = class extends VitsPreTrainedModel {
  /**
   * Calls the model on new inputs.
   * @param {Object} model_inputs The inputs to the model.
   * @returns {Promise<VitsModelOutput>} The outputs for the VITS model.
   */
  async _call(model_inputs) {
    return new VitsModelOutput(await super._call(model_inputs));
  }
};
var StableLmPreTrainedModel = class extends PreTrainedModel {
};
var StableLmModel = class extends StableLmPreTrainedModel {
};
var StableLmForCausalLM = class extends StableLmPreTrainedModel {
};
var MusicgenForConditionalGeneration = class extends PreTrainedModel {
  constructor() {
    super(...arguments);
    // NOTE: not MusicgenPreTrainedModel
    this.forward_params = [
      "input_ids",
      "attention_mask",
      "encoder_outputs",
      "decoder_input_ids",
      "decoder_attention_mask",
      "past_key_values"
    ];
  }
  /**
   * Apply the pattern mask to the final ids,
   * then revert the pattern delay mask by filtering the pad token id in a single step.
   * @param {Tensor} outputs The output tensor from the model.
   * @returns {Tensor} The filtered output tensor.
   */
  _apply_and_filter_by_delay_pattern_mask(outputs) {
    const [bs_x_codebooks, seqLength] = outputs.dims;
    const num_codebooks = this.config.decoder.num_codebooks;
    const upperBound = seqLength - num_codebooks;
    let newDataSize = 0;
    for (let i = 0; i < outputs.size; ++i) {
      if (outputs.data[i] === this.config.decoder.pad_token_id) {
        continue;
      }
      const row = i % seqLength;
      const col = Math.floor(i / seqLength) % num_codebooks;
      const diff = row - col;
      if (diff > 0 && diff <= upperBound) {
        outputs.data[newDataSize++] = outputs.data[i];
      }
    }
    const batch_size = Math.floor(bs_x_codebooks / num_codebooks);
    const inferred = newDataSize / (batch_size * num_codebooks);
    return new Tensor3(outputs.type, outputs.data.slice(0, newDataSize), [batch_size, num_codebooks, inferred]);
  }
  prepare_inputs_for_generation(input_ids, model_inputs, generation_config) {
    let clonedInputIds = structuredClone(input_ids);
    for (let i = 0; i < clonedInputIds.length; ++i) {
      for (let j = 0; j < clonedInputIds[i].length; ++j) {
        if (i % this.config.decoder.num_codebooks >= j) {
          clonedInputIds[i][j] = BigInt(this.config.decoder.pad_token_id);
        }
      }
    }
    if (generation_config.guidance_scale !== null && generation_config.guidance_scale > 1) {
      clonedInputIds = clonedInputIds.concat(clonedInputIds);
    }
    const prepped = super.prepare_inputs_for_generation(clonedInputIds, model_inputs, generation_config);
    return prepped;
  }
  /**
   * Generates sequences of token ids for models with a language modeling head.
   * @param {import('./generation/parameters.js').GenerationFunctionParameters} options
   * @returns {Promise<ModelOutput|Tensor>} The output of the model, which can contain the generated token ids, attentions, and scores.
   */
  async generate(options) {
    const output_ids = await super.generate(options);
    const audio_codes = this._apply_and_filter_by_delay_pattern_mask(
      /** @type {Tensor} */
      output_ids
    ).unsqueeze_(0);
    const { audio_values } = await sessionRun(this.sessions["encodec_decode"], { audio_codes });
    return audio_values;
  }
};
var DecisionTransformerPreTrainedModel = class extends PreTrainedModel {
};
var DecisionTransformerModel = class extends DecisionTransformerPreTrainedModel {
};
var MultiModalityPreTrainedModel = class extends PreTrainedModel {
};
var MultiModalityCausalLM = class extends MultiModalityPreTrainedModel {
  constructor(args) {
    super(args, {}, {});
    this.forward_params = [
      // prepare_inputs_embeds
      "input_ids",
      "pixel_values",
      "images_seq_mask",
      "images_emb_mask",
      // language_model
      "attention_mask",
      "position_ids",
      "past_key_values"
    ];
    this._generation_mode = "text";
  }
  async forward(model_inputs) {
    const mode = this._generation_mode ?? "text";
    let output_1;
    if (mode === "text" || !model_inputs.past_key_values) {
      const session = this.sessions["prepare_inputs_embeds"];
      const prep_inputs = pick(model_inputs, session.inputNames);
      output_1 = await sessionRun(session, prep_inputs);
    } else {
      const session = this.sessions["gen_img_embeds"];
      const prep_inputs = pick(
        {
          image_ids: model_inputs.input_ids
        },
        session.inputNames
      );
      output_1 = await sessionRun(session, prep_inputs);
    }
    const input_2 = { ...model_inputs, ...output_1 };
    const output_2 = await decoderForward(this, input_2);
    const head = this.sessions[mode === "text" ? "lm_head" : "gen_head"];
    if (!head) {
      throw new Error(`Unable to find "${head}" generation head`);
    }
    const output_3 = await sessionRun(head, pick(output_2, head.inputNames));
    return {
      ...output_1,
      ...output_2,
      ...output_3
    };
  }
  /**
   * @param {import('./generation/parameters.js').GenerationFunctionParameters} options
   */
  async generate(options) {
    this._generation_mode = "text";
    return super.generate(options);
  }
  /**
   * @param {import('./generation/parameters.js').GenerationFunctionParameters} options
   */
  async generate_images(options) {
    this._generation_mode = "image";
    const start_num_tokens = (options.inputs ?? options[this.main_input_name]).dims[1];
    const all_tokens = await super.generate(options);
    const generated_tokens = (
      /** @type {Tensor} */
      all_tokens.slice(null, [start_num_tokens, null])
    );
    const image_decode = this.sessions["image_decode"];
    const { decoded_image } = await sessionRun(image_decode, {
      generated_tokens
    });
    const clamped = decoded_image.add_(1).mul_(255 / 2).clamp_(0, 255).to("uint8");
    const images = [];
    for (const tensor of clamped) {
      const img = RawImage.fromTensor(tensor);
      images.push(img);
    }
    return images;
  }
};
var MgpstrModelOutput = class extends ModelOutput {
  constructor({ char_logits, bpe_logits, wp_logits }) {
    super();
    this.char_logits = char_logits;
    this.bpe_logits = bpe_logits;
    this.wp_logits = wp_logits;
  }
  get logits() {
    return [this.char_logits, this.bpe_logits, this.wp_logits];
  }
};
var MgpstrPreTrainedModel = class extends PreTrainedModel {
};
var MgpstrForSceneTextRecognition = class extends MgpstrPreTrainedModel {
  /**
   * @param {any} model_inputs
   */
  async _call(model_inputs) {
    return new MgpstrModelOutput(await super._call(model_inputs));
  }
};
var PatchTSTPreTrainedModel = class extends PreTrainedModel {
};
var PatchTSTModel = class extends PatchTSTPreTrainedModel {
};
var StyleTextToSpeech2PreTrainedModel = class extends PreTrainedModel {
};
var StyleTextToSpeech2Model = class extends StyleTextToSpeech2PreTrainedModel {
};
var PatchTSMixerPreTrainedModel = class extends PreTrainedModel {
};
var PatchTSMixerModel = class extends PatchTSMixerPreTrainedModel {
};
var PretrainedMixin = class {
  /** @type {typeof PreTrainedModel.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, {
    progress_callback = null,
    config = null,
    cache_dir = null,
    local_files_only = false,
    revision = "main",
    model_file_name = null,
    subfolder = "onnx",
    device = null,
    dtype = null,
    use_external_data_format = null,
    session_options = {}
  } = {}) {
    const options = {
      progress_callback,
      config,
      cache_dir,
      local_files_only,
      revision,
      model_file_name,
      subfolder,
      device,
      dtype,
      use_external_data_format,
      session_options
    };
    options.config = await AutoConfig.from_pretrained(pretrained_model_name_or_path, options);
    if (!this.MODEL_CLASS_MAPPINGS) {
      throw new Error("`MODEL_CLASS_MAPPINGS` not implemented for this type of `AutoClass`: " + this.name);
    }
    for (const MODEL_CLASS_MAPPING of this.MODEL_CLASS_MAPPINGS) {
      const modelInfo = MODEL_CLASS_MAPPING.get(options.config?.model_type);
      if (!modelInfo) {
        continue;
      }
      return await modelInfo[1].from_pretrained(pretrained_model_name_or_path, options);
    }
    if (this.BASE_IF_FAIL) {
      console.warn(`Unknown model class "${options.config?.model_type}", attempting to construct from base class.`);
      return await PreTrainedModel.from_pretrained(pretrained_model_name_or_path, options);
    } else {
      throw Error(`Unsupported model type: ${options.config?.model_type}`);
    }
  }
};
/**
 * Mapping from model type to model class.
 * @type {Map<string, any>[] | null}
 */
PretrainedMixin.MODEL_CLASS_MAPPINGS = null;
/**
 * Whether to attempt to instantiate the base class (`PretrainedModel`) if
 * the model type is not found in the mapping.
 */
PretrainedMixin.BASE_IF_FAIL = false;
var MODEL_MAPPING_NAMES_ENCODER_ONLY = /* @__PURE__ */ new Map([
  ["clap", ["ClapModel", ClapModel]],
  ["clip", ["CLIPModel", CLIPModel]],
  ["chinese_clip", ["ChineseCLIPModel", ChineseCLIPModel]],
  ["siglip", ["SiglipModel", SiglipModel]],
  ["jina_clip", ["JinaCLIPModel", JinaCLIPModel]],
  ["wav2vec2", ["Wav2Vec2Model", Wav2Vec2Model]],
  ["wav2vec2-bert", ["Wav2Vec2BertModel", Wav2Vec2BertModel]],
  ["unispeech", ["UniSpeechModel", UniSpeechModel]],
  ["unispeech-sat", ["UniSpeechSatModel", UniSpeechSatModel]],
  ["hubert", ["HubertModel", HubertModel]],
  ["wavlm", ["WavLMModel", WavLMModel]],
  ["audio-spectrogram-transformer", ["ASTModel", ASTModel]],
  ["vits", ["VitsModel", VitsModel]],
  ["pyannote", ["PyAnnoteModel", PyAnnoteModel]],
  ["wespeaker-resnet", ["WeSpeakerResNetModel", WeSpeakerResNetModel]],
  ["vit_mae", ["ViTMAEModel", ViTMAEModel]],
  ["groupvit", ["GroupViTModel", GroupViTModel]],
  ["swin2sr", ["Swin2SRModel", Swin2SRModel]],
  ["hifigan", ["SpeechT5HifiGan", SpeechT5HifiGan]],
  ["decision_transformer", ["DecisionTransformerModel", DecisionTransformerModel]],
  ["patchtst", ["PatchTSTForPrediction", PatchTSTModel]],
  ["patchtsmixer", ["PatchTSMixerForPrediction", PatchTSMixerModel]],
  ["mgp-str", ["MgpstrForSceneTextRecognition", MgpstrForSceneTextRecognition]],
  ["style_text_to_speech_2", ["StyleTextToSpeech2Model", StyleTextToSpeech2Model]]
]);
var MODEL_MAPPING_NAMES_DECODER_ONLY = /* @__PURE__ */ new Map([
  ["bloom", ["BloomModel", BloomModel]],
  ["jais", ["JAISModel", JAISModel]],
  ["gpt2", ["GPT2Model", GPT2Model]],
  ["gptj", ["GPTJModel", GPTJModel]],
  ["gpt_bigcode", ["GPTBigCodeModel", GPTBigCodeModel]],
  ["gpt_neo", ["GPTNeoModel", GPTNeoModel]],
  ["gpt_neox", ["GPTNeoXModel", GPTNeoXModel]],
  ["codegen", ["CodeGenModel", CodeGenModel]],
  ["llama", ["LlamaModel", LlamaModel]],
  ["exaone", ["ExaoneModel", ExaoneModel]],
  ["olmo", ["OlmoModel", OlmoModel]],
  ["olmo2", ["Olmo2Model", Olmo2Model]],
  ["mobilellm", ["MobileLLMModel", MobileLLMModel]],
  ["granite", ["GraniteModel", GraniteModel]],
  ["cohere", ["CohereModel", CohereModel]],
  ["gemma", ["GemmaModel", GemmaModel]],
  ["gemma2", ["Gemma2Model", Gemma2Model]],
  ["openelm", ["OpenELMModel", OpenELMModel]],
  ["qwen2", ["Qwen2Model", Qwen2Model]],
  ["phi", ["PhiModel", PhiModel]],
  ["phi3", ["Phi3Model", Phi3Model]],
  ["mpt", ["MptModel", MptModel]],
  ["opt", ["OPTModel", OPTModel]],
  ["mistral", ["MistralModel", MistralModel]],
  ["starcoder2", ["Starcoder2Model", Starcoder2Model]],
  ["falcon", ["FalconModel", FalconModel]],
  ["stablelm", ["StableLmModel", StableLmModel]]
]);
var MODEL_FOR_SPEECH_SEQ_2_SEQ_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["speecht5", ["SpeechT5ForSpeechToText", SpeechT5ForSpeechToText]],
  ["whisper", ["WhisperForConditionalGeneration", WhisperForConditionalGeneration]],
  ["moonshine", ["MoonshineForConditionalGeneration", MoonshineForConditionalGeneration]]
]);
var MODEL_FOR_TEXT_TO_SPECTROGRAM_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["speecht5", ["SpeechT5ForTextToSpeech", SpeechT5ForTextToSpeech]]
]);
var MODEL_FOR_TEXT_TO_WAVEFORM_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["vits", ["VitsModel", VitsModel]],
  ["musicgen", ["MusicgenForConditionalGeneration", MusicgenForConditionalGeneration]]
]);
var MODEL_FOR_CAUSAL_LM_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["bloom", ["BloomForCausalLM", BloomForCausalLM]],
  ["gpt2", ["GPT2LMHeadModel", GPT2LMHeadModel]],
  ["jais", ["JAISLMHeadModel", JAISLMHeadModel]],
  ["gptj", ["GPTJForCausalLM", GPTJForCausalLM]],
  ["gpt_bigcode", ["GPTBigCodeForCausalLM", GPTBigCodeForCausalLM]],
  ["gpt_neo", ["GPTNeoForCausalLM", GPTNeoForCausalLM]],
  ["gpt_neox", ["GPTNeoXForCausalLM", GPTNeoXForCausalLM]],
  ["codegen", ["CodeGenForCausalLM", CodeGenForCausalLM]],
  ["llama", ["LlamaForCausalLM", LlamaForCausalLM]],
  ["exaone", ["ExaoneForCausalLM", ExaoneForCausalLM]],
  ["olmo", ["OlmoForCausalLM", OlmoForCausalLM]],
  ["olmo2", ["Olmo2ForCausalLM", Olmo2ForCausalLM]],
  ["mobilellm", ["MobileLLMForCausalLM", MobileLLMForCausalLM]],
  ["granite", ["GraniteForCausalLM", GraniteForCausalLM]],
  ["cohere", ["CohereForCausalLM", CohereForCausalLM]],
  ["gemma", ["GemmaForCausalLM", GemmaForCausalLM]],
  ["gemma2", ["Gemma2ForCausalLM", Gemma2ForCausalLM]],
  ["openelm", ["OpenELMForCausalLM", OpenELMForCausalLM]],
  ["qwen2", ["Qwen2ForCausalLM", Qwen2ForCausalLM]],
  ["phi", ["PhiForCausalLM", PhiForCausalLM]],
  ["phi3", ["Phi3ForCausalLM", Phi3ForCausalLM]],
  ["mpt", ["MptForCausalLM", MptForCausalLM]],
  ["opt", ["OPTForCausalLM", OPTForCausalLM]],
  ["mistral", ["MistralForCausalLM", MistralForCausalLM]],
  ["starcoder2", ["Starcoder2ForCausalLM", Starcoder2ForCausalLM]],
  ["falcon", ["FalconForCausalLM", FalconForCausalLM]],
  ["trocr", ["TrOCRForCausalLM", TrOCRForCausalLM]],
  ["stablelm", ["StableLmForCausalLM", StableLmForCausalLM]],
  // Also image-text-to-text
  ["phi3_v", ["Phi3VForCausalLM", Phi3VForCausalLM]]
]);
var MODEL_FOR_MULTIMODALITY_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["multi_modality", ["MultiModalityCausalLM", MultiModalityCausalLM]]
]);
var MODEL_FOR_VISION_2_SEQ_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["idefics3", ["Idefics3ForConditionalGeneration", Idefics3ForConditionalGeneration]]
]);
var MODEL_FOR_IMAGE_TEXT_TO_TEXT_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["llava", ["LlavaForConditionalGeneration", LlavaForConditionalGeneration]],
  ["llava_onevision", ["LlavaOnevisionForConditionalGeneration", LlavaOnevisionForConditionalGeneration]],
  ["moondream1", ["Moondream1ForConditionalGeneration", Moondream1ForConditionalGeneration]],
  ["florence2", ["Florence2ForConditionalGeneration", Florence2ForConditionalGeneration]],
  //   ['qwen2-vl', ['Qwen2VLForConditionalGeneration', Qwen2VLForConditionalGeneration]],
  ["idefics3", ["Idefics3ForConditionalGeneration", Idefics3ForConditionalGeneration]],
  ["paligemma", ["PaliGemmaForConditionalGeneration", PaliGemmaForConditionalGeneration]]
]);
var MODEL_FOR_MASK_GENERATION_MAPPING_NAMES = /* @__PURE__ */ new Map([["sam", ["SamModel", SamModel]]]);
var MODEL_FOR_CTC_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["wav2vec2", ["Wav2Vec2ForCTC", Wav2Vec2ForCTC]],
  ["wav2vec2-bert", ["Wav2Vec2BertForCTC", Wav2Vec2BertForCTC]],
  ["unispeech", ["UniSpeechForCTC", UniSpeechForCTC]],
  ["unispeech-sat", ["UniSpeechSatForCTC", UniSpeechSatForCTC]],
  ["wavlm", ["WavLMForCTC", WavLMForCTC]],
  ["hubert", ["HubertForCTC", HubertForCTC]]
]);
var MODEL_FOR_AUDIO_XVECTOR_MAPPING_NAMES = /* @__PURE__ */ new Map([["wavlm", ["WavLMForXVector", WavLMForXVector]]]);
var MODEL_FOR_IMAGE_MATTING_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["vitmatte", ["VitMatteForImageMatting", VitMatteForImageMatting]]
]);
var MODEL_FOR_TIME_SERIES_PREDICTION_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["patchtst", ["PatchTSTForPrediction", PatchTSTModel]],
  ["patchtsmixer", ["PatchTSMixerForPrediction", PatchTSMixerModel]]
]);
var MODEL_FOR_IMAGE_TO_IMAGE_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["swin2sr", ["Swin2SRForImageSuperResolution", Swin2SRForImageSuperResolution]]
]);
var MODEL_FOR_POSE_ESTIMATION_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["vitpose", ["VitPoseForPoseEstimation", VitPoseForPoseEstimation]]
]);
var MODEL_FOR_IMAGE_FEATURE_EXTRACTION_MAPPING_NAMES = /* @__PURE__ */ new Map([
  ["clip", ["CLIPVisionModelWithProjection", CLIPVisionModelWithProjection]],
  ["siglip", ["SiglipVisionModel", SiglipVisionModel]],
  ["jina_clip", ["JinaCLIPVisionModel", JinaCLIPVisionModel]]
]);
var MODEL_CLASS_TYPE_MAPPING = [
  [MODEL_MAPPING_NAMES_ENCODER_ONLY, MODEL_TYPES.EncoderOnly],
  [MODEL_MAPPING_NAMES_DECODER_ONLY, MODEL_TYPES.DecoderOnly],
  [MODEL_FOR_SPEECH_SEQ_2_SEQ_MAPPING_NAMES, MODEL_TYPES.Seq2Seq],
  [MODEL_FOR_CAUSAL_LM_MAPPING_NAMES, MODEL_TYPES.DecoderOnly],
  [MODEL_FOR_MULTIMODALITY_MAPPING_NAMES, MODEL_TYPES.MultiModality],
  [MODEL_FOR_VISION_2_SEQ_MAPPING_NAMES, MODEL_TYPES.Vision2Seq],
  [MODEL_FOR_IMAGE_TEXT_TO_TEXT_MAPPING_NAMES, MODEL_TYPES.ImageTextToText],
  [MODEL_FOR_IMAGE_MATTING_MAPPING_NAMES, MODEL_TYPES.EncoderOnly],
  [MODEL_FOR_TIME_SERIES_PREDICTION_MAPPING_NAMES, MODEL_TYPES.EncoderOnly],
  [MODEL_FOR_IMAGE_TO_IMAGE_MAPPING_NAMES, MODEL_TYPES.EncoderOnly],
  [MODEL_FOR_POSE_ESTIMATION_MAPPING_NAMES, MODEL_TYPES.EncoderOnly],
  [MODEL_FOR_MASK_GENERATION_MAPPING_NAMES, MODEL_TYPES.MaskGeneration],
  [MODEL_FOR_CTC_MAPPING_NAMES, MODEL_TYPES.EncoderOnly],
  [MODEL_FOR_TEXT_TO_SPECTROGRAM_MAPPING_NAMES, MODEL_TYPES.Seq2Seq],
  [MODEL_FOR_TEXT_TO_WAVEFORM_MAPPING_NAMES, MODEL_TYPES.EncoderOnly],
  [MODEL_FOR_AUDIO_XVECTOR_MAPPING_NAMES, MODEL_TYPES.EncoderOnly],
  // Custom:
  [MODEL_FOR_IMAGE_FEATURE_EXTRACTION_MAPPING_NAMES, MODEL_TYPES.EncoderOnly]
];
for (const [mappings, type] of MODEL_CLASS_TYPE_MAPPING) {
  for (const [name, model] of mappings.values()) {
    MODEL_TYPE_MAPPING.set(name, type);
    MODEL_CLASS_TO_NAME_MAPPING.set(model, name);
    MODEL_NAME_TO_CLASS_MAPPING.set(name, model);
  }
}
var CUSTOM_MAPPING = [
  // OVERRIDE:
  // TODO: Refactor to allow class to specify model
  ["MusicgenForConditionalGeneration", MusicgenForConditionalGeneration, MODEL_TYPES.Musicgen],
  ["Phi3VForCausalLM", Phi3VForCausalLM, MODEL_TYPES.Phi3V],
  ["CLIPTextModelWithProjection", CLIPTextModelWithProjection, MODEL_TYPES.EncoderOnly],
  ["SiglipTextModel", SiglipTextModel, MODEL_TYPES.EncoderOnly],
  ["JinaCLIPTextModel", JinaCLIPTextModel, MODEL_TYPES.EncoderOnly],
  ["ClapTextModelWithProjection", ClapTextModelWithProjection, MODEL_TYPES.EncoderOnly],
  ["ClapAudioModelWithProjection", ClapAudioModelWithProjection, MODEL_TYPES.EncoderOnly]
];
for (const [name, model, type] of CUSTOM_MAPPING) {
  MODEL_TYPE_MAPPING.set(name, type);
  MODEL_CLASS_TO_NAME_MAPPING.set(model, name);
  MODEL_NAME_TO_CLASS_MAPPING.set(name, model);
}
var AutoModel = class extends PretrainedMixin {
};
AutoModel.MODEL_CLASS_MAPPINGS = MODEL_CLASS_TYPE_MAPPING.map((x) => x[0]);
AutoModel.BASE_IF_FAIL = true;
var AutoModelForSpeechSeq2Seq = class extends PretrainedMixin {
};
AutoModelForSpeechSeq2Seq.MODEL_CLASS_MAPPINGS = [MODEL_FOR_SPEECH_SEQ_2_SEQ_MAPPING_NAMES];
var AutoModelForTextToSpectrogram = class extends PretrainedMixin {
};
AutoModelForTextToSpectrogram.MODEL_CLASS_MAPPINGS = [MODEL_FOR_TEXT_TO_SPECTROGRAM_MAPPING_NAMES];
var AutoModelForTextToWaveform = class extends PretrainedMixin {
};
AutoModelForTextToWaveform.MODEL_CLASS_MAPPINGS = [MODEL_FOR_TEXT_TO_WAVEFORM_MAPPING_NAMES];
var AutoModelForCausalLM = class extends PretrainedMixin {
};
AutoModelForCausalLM.MODEL_CLASS_MAPPINGS = [MODEL_FOR_CAUSAL_LM_MAPPING_NAMES];
var AutoModelForVision2Seq = class extends PretrainedMixin {
};
AutoModelForVision2Seq.MODEL_CLASS_MAPPINGS = [MODEL_FOR_VISION_2_SEQ_MAPPING_NAMES];
var AutoModelForMaskGeneration = class extends PretrainedMixin {
};
AutoModelForMaskGeneration.MODEL_CLASS_MAPPINGS = [MODEL_FOR_MASK_GENERATION_MAPPING_NAMES];
var AutoModelForCTC = class extends PretrainedMixin {
};
AutoModelForCTC.MODEL_CLASS_MAPPINGS = [MODEL_FOR_CTC_MAPPING_NAMES];
var AutoModelForXVector = class extends PretrainedMixin {
};
AutoModelForXVector.MODEL_CLASS_MAPPINGS = [MODEL_FOR_AUDIO_XVECTOR_MAPPING_NAMES];
var AutoModelForImageMatting = class extends PretrainedMixin {
};
AutoModelForImageMatting.MODEL_CLASS_MAPPINGS = [MODEL_FOR_IMAGE_MATTING_MAPPING_NAMES];
var AutoModelForImageToImage = class extends PretrainedMixin {
};
AutoModelForImageToImage.MODEL_CLASS_MAPPINGS = [MODEL_FOR_IMAGE_TO_IMAGE_MAPPING_NAMES];
var AutoModelForPoseEstimation = class extends PretrainedMixin {
};
AutoModelForPoseEstimation.MODEL_CLASS_MAPPINGS = [MODEL_FOR_POSE_ESTIMATION_MAPPING_NAMES];
var AutoModelForImageFeatureExtraction = class extends PretrainedMixin {
};
AutoModelForImageFeatureExtraction.MODEL_CLASS_MAPPINGS = [MODEL_FOR_IMAGE_FEATURE_EXTRACTION_MAPPING_NAMES];
var XVectorOutput = class extends ModelOutput {
  constructor({ logits, embeddings }) {
    super();
    this.logits = logits;
    this.embeddings = embeddings;
  }
};
var CausalLMOutput = class extends ModelOutput {
  constructor({ logits }) {
    super();
    this.logits = logits;
  }
};
var ImageMattingOutput = class extends ModelOutput {
  constructor({ alphas }) {
    super();
    this.alphas = alphas;
  }
};
var VitsModelOutput = class extends ModelOutput {
  constructor({ waveform, spectrogram: spectrogram2 }) {
    super();
    this.waveform = waveform;
    this.spectrogram = spectrogram2;
  }
};

// src/libs/transformers/base/processing_utils.ts
var Processor = class extends Callable {
  constructor(config, components) {
    super();
    this.config = config;
    this.components = components;
  }
  /**
   * @returns {import('./image_processors_utils.js').ImageProcessor|undefined} The image processor of the processor, if it exists.
   */
  get image_processor() {
    return this.components.image_processor;
  }
  /**
   * @returns {PreTrainedTokenizer|undefined} The tokenizer of the processor, if it exists.
   */
  get tokenizer() {
    return this.components.tokenizer;
  }
  /**
   * @returns {import('./feature_extraction_utils.js').FeatureExtractor|undefined} The feature extractor of the processor, if it exists.
   */
  get feature_extractor() {
    return this.components.feature_extractor;
  }
  /**
   * @param {Parameters<PreTrainedTokenizer['apply_chat_template']>[0]} messages
   * @param {Parameters<PreTrainedTokenizer['apply_chat_template']>[1]} options
   * @returns {ReturnType<PreTrainedTokenizer['apply_chat_template']>}
   */
  apply_chat_template(messages, options = {}) {
    if (!this.tokenizer) {
      throw new Error("Unable to apply chat template without a tokenizer.");
    }
    return this.tokenizer.apply_chat_template(messages, {
      tokenize: false,
      // default to false
      ...options
    });
  }
  /**
   * @param {Parameters<PreTrainedTokenizer['batch_decode']>} args
   * @returns {ReturnType<PreTrainedTokenizer['batch_decode']>}
   */
  batch_decode(...args) {
    if (!this.tokenizer) {
      throw new Error("Unable to decode without a tokenizer.");
    }
    return this.tokenizer.batch_decode(...args);
  }
  /**
   * Calls the feature_extractor function with the given input.
   * @param {any} input The input to extract features from.
   * @param {...any} args Additional arguments.
   * @returns {Promise<any>} A Promise that resolves with the extracted features.
   */
  async _call(input, ...args) {
    for (const item of [this.image_processor, this.feature_extractor, this.tokenizer]) {
      if (item) {
        return item(input, ...args);
      }
    }
    throw new Error("No image processor, feature extractor, or tokenizer found.");
  }
  /**
   * Instantiate one of the processor classes of the library from a pretrained model.
   *
   * The processor class to instantiate is selected based on the `image_processor_type` (or `feature_extractor_type`; legacy)
   * property of the config object (either passed as an argument or loaded from `pretrained_model_name_or_path` if possible)
   *
   * @param {string} pretrained_model_name_or_path The name or path of the pretrained model. Can be either:
   * - A string, the *model id* of a pretrained processor hosted inside a model repo on huggingface.co.
   *   Valid model ids can be located at the root-level, like `bert-base-uncased`, or namespaced under a
   *   user or organization name, like `dbmdz/bert-base-german-cased`.
   * - A path to a *directory* containing processor files, e.g., `./my_model_directory/`.
   * @param {PretrainedProcessorOptions} options Additional options for loading the processor.
   *
   * @returns {Promise<Processor>} A new instance of the Processor class.
   */
  static async from_pretrained(pretrained_model_name_or_path, options) {
    const [config, components] = await Promise.all([
      // TODO:
      this.uses_processor_config ? getModelJSON(pretrained_model_name_or_path, PROCESSOR_NAME, true, options) : {},
      Promise.all(
        this.classes.filter((cls) => cls in this).map(async (cls) => {
          const component = await this[cls].from_pretrained(pretrained_model_name_or_path, options);
          return [cls.replace(/_class$/, ""), component];
        })
      ).then(Object.fromEntries)
    ]);
    return new this(config, components);
  }
};
Processor.classes = ["image_processor_class", "tokenizer_class", "feature_extractor_class"];
Processor.uses_processor_config = false;

// src/libs/transformers/models/processors.ts
var processors_exports = {};
__export(processors_exports, {
  Florence2Processor: () => Florence2Processor,
  Idefics3Processor: () => Idefics3Processor,
  JinaCLIPProcessor: () => JinaCLIPProcessor,
  MgpstrProcessor: () => MgpstrProcessor,
  MoonshineProcessor: () => MoonshineProcessor,
  PaliGemmaProcessor: () => PaliGemmaProcessor,
  Phi3VProcessor: () => Phi3VProcessor,
  PyAnnoteProcessor: () => PyAnnoteProcessor,
  Qwen2VLProcessor: () => Qwen2VLProcessor,
  SamProcessor: () => SamProcessor,
  SpeechT5Processor: () => SpeechT5Processor,
  VLChatProcessor: () => VLChatProcessor,
  Wav2Vec2ProcessorWithLM: () => Wav2Vec2ProcessorWithLM,
  WhisperProcessor: () => WhisperProcessor
});

// src/libs/transformers/base/image_processors_utils.ts
function constraint_to_multiple_of(val, multiple, minVal = 0, maxVal = null) {
  const a = val / multiple;
  let x = bankers_round(a) * multiple;
  if (maxVal !== null && x > maxVal) {
    x = Math.floor(a) * multiple;
  }
  if (x < minVal) {
    x = Math.ceil(a) * multiple;
  }
  return x;
}
function enforce_size_divisibility(size, divisor) {
  return [Math.max(Math.floor(size[0] / divisor), 1) * divisor, Math.max(Math.floor(size[1] / divisor), 1) * divisor];
}
function smart_resize(height, width, factor = 28, min_pixels = 56 * 56, max_pixels = 14 * 14 * 4 * 1280) {
  if (height < factor || width < factor) {
    throw new Error(`height:${height} or width:${width} must be larger than factor:${factor}`);
  } else if (Math.max(height, width) / Math.min(height, width) > 200) {
    throw new Error(
      `absolute aspect ratio must be smaller than 200, got ${Math.max(height, width) / Math.min(height, width)}`
    );
  }
  let h_bar = Math.round(height / factor) * factor;
  let w_bar = Math.round(width / factor) * factor;
  if (h_bar * w_bar > max_pixels) {
    const beta = Math.sqrt(height * width / max_pixels);
    h_bar = Math.floor(height / beta / factor) * factor;
    w_bar = Math.floor(width / beta / factor) * factor;
  } else if (h_bar * w_bar < min_pixels) {
    const beta = Math.sqrt(min_pixels / (height * width));
    h_bar = Math.ceil(height * beta / factor) * factor;
    w_bar = Math.ceil(width * beta / factor) * factor;
  }
  return [h_bar, w_bar];
}
var ImageProcessor = class extends Callable {
  /**
   * Constructs a new `ImageProcessor`.
   * @param {ImageProcessorConfig} config The configuration object.
   */
  constructor(config) {
    super();
    this.image_mean = config.image_mean ?? config.mean;
    this.image_std = config.image_std ?? config.std;
    this.resample = config.resample ?? 2;
    this.do_rescale = config.do_rescale ?? true;
    this.rescale_factor = config.rescale_factor ?? 1 / 255;
    this.do_normalize = config.do_normalize;
    this.do_thumbnail = config.do_thumbnail;
    this.size = config.size ?? config.image_size;
    this.do_resize = config.do_resize ?? this.size !== undefined;
    this.size_divisibility = config.size_divisibility ?? config.size_divisor;
    this.do_center_crop = config.do_center_crop;
    this.crop_size = config.crop_size;
    this.do_convert_rgb = config.do_convert_rgb ?? true;
    this.do_crop_margin = config.do_crop_margin;
    this.pad_size = config.pad_size;
    this.do_pad = config.do_pad;
    if (this.do_pad && !this.pad_size && this.size && typeof this.size === "object" && "width" in this.size && "height" in this.size) {
      this.pad_size = this.size;
    }
    this.do_flip_channel_order = config.do_flip_channel_order ?? false;
    this.config = config;
  }
  /**
   * Resize the image to make a thumbnail. The image is resized so that no dimension is larger than any
   * corresponding dimension of the specified size.
   * @param {RawImage} image The image to be resized.
   * @param {{height:number, width:number}} size The size `{"height": h, "width": w}` to resize the image to.
   * @param {string | 0 | 1 | 2 | 3 | 4 | 5} [resample=2] The resampling filter to use.
   * @returns {Promise<RawImage>} The resized image.
   */
  async thumbnail(image, size, resample = 2) {
    const input_height = image.height;
    const input_width = image.width;
    const output_height = size.height;
    const output_width = size.width;
    let height = Math.min(input_height, output_height);
    let width = Math.min(input_width, output_width);
    if (height === input_height && width === input_width) {
      return image;
    }
    if (input_height > input_width) {
      width = Math.floor(input_width * height / input_height);
    } else if (input_width > input_height) {
      height = Math.floor(input_height * width / input_width);
    }
    return await image.resize(width, height, { resample });
  }
  /**
   * Crops the margin of the image. Gray pixels are considered margin (i.e., pixels with a value below the threshold).
   * @param {RawImage} image The image to be cropped.
   * @param {number} gray_threshold Value below which pixels are considered to be gray.
   * @returns {Promise<RawImage>} The cropped image.
   */
  async crop_margin(image, gray_threshold = 200) {
    const gray_image = image.clone().grayscale();
    const minValue = min(gray_image.data)[0];
    const maxValue = max(gray_image.data)[0];
    const diff = BigInt(maxValue) - BigInt(minValue);
    if (Number(diff) === 0) {
      return image;
    }
    const threshold = gray_threshold / 255;
    let x_min = gray_image.width, y_min = gray_image.height, x_max = 0, y_max = 0;
    const gray_image_data = gray_image.data;
    for (let j = 0; j < gray_image.height; ++j) {
      const row = j * gray_image.width;
      for (let i = 0; i < gray_image.width; ++i) {
        if ((Number(gray_image_data[row + i]) - Number(minValue)) / Number(diff) < threshold) {
          x_min = Math.min(x_min, i);
          y_min = Math.min(y_min, j);
          x_max = Math.max(x_max, i);
          y_max = Math.max(y_max, j);
        }
      }
    }
    image = await image.crop([x_min, y_min, x_max, y_max]);
    return image;
  }
  /**
   * Pad the image by a certain amount.
   * @param {Float32Array} pixelData The pixel data to pad.
   * @param {number[]} imgDims The dimensions of the image (height, width, channels).
   * @param {{width:number; height:number}|number|'square'} padSize The dimensions of the padded image.
   * @param {Object} options The options for padding.
   * @param {'constant'|'symmetric'} [options.mode='constant'] The type of padding to add.
   * @param {boolean} [options.center=false] Whether to center the image.
   * @param {number|number[]} [options.constant_values=0] The constant value to use for padding.
   * @returns {[Float32Array, number[]]} The padded pixel data and image dimensions.
   */
  pad_image(pixelData, imgDims, padSize, constant_values = 0, mode = "constant", center = false) {
    const [imageHeight, imageWidth, imageChannels] = imgDims;
    let paddedImageWidth, paddedImageHeight;
    if (typeof padSize === "number") {
      paddedImageWidth = padSize;
      paddedImageHeight = padSize;
    } else if (padSize === "square") {
      paddedImageWidth = paddedImageHeight = Math.max(imageHeight, imageWidth);
    } else {
      paddedImageWidth = padSize.width;
      paddedImageHeight = padSize.height;
    }
    if (paddedImageWidth !== imageWidth || paddedImageHeight !== imageHeight) {
      const paddedPixelData = new Float32Array(paddedImageWidth * paddedImageHeight * imageChannels);
      if (Array.isArray(constant_values)) {
        for (let i = 0; i < paddedPixelData.length; ++i) {
          paddedPixelData[i] = constant_values[i % imageChannels];
        }
      } else if (constant_values !== 0) {
        paddedPixelData.fill(constant_values);
      }
      const [left, top] = center ? [Math.floor((paddedImageWidth - imageWidth) / 2), Math.floor((paddedImageHeight - imageHeight) / 2)] : [0, 0];
      for (let i = 0; i < imageHeight; ++i) {
        const a = (i + top) * paddedImageWidth;
        const b = i * imageWidth;
        for (let j = 0; j < imageWidth; ++j) {
          const c = (a + j + left) * imageChannels;
          const d = (b + j) * imageChannels;
          for (let k = 0; k < imageChannels; ++k) {
            paddedPixelData[c + k] = pixelData[d + k];
          }
        }
      }
      if (mode === "symmetric") {
        if (center) {
          throw new Error("`center` padding is not supported when `mode` is set to `symmetric`.");
        }
        const h1 = imageHeight - 1;
        const w1 = imageWidth - 1;
        for (let i = 0; i < paddedImageHeight; ++i) {
          const a = i * paddedImageWidth;
          const b = calculateReflectOffset(i, h1) * imageWidth;
          for (let j = 0; j < paddedImageWidth; ++j) {
            if (i < imageHeight && j < imageWidth) continue;
            const c = (a + j) * imageChannels;
            const d = (b + calculateReflectOffset(j, w1)) * imageChannels;
            for (let k = 0; k < imageChannels; ++k) {
              paddedPixelData[c + k] = pixelData[d + k];
            }
          }
        }
      }
      pixelData = paddedPixelData;
      imgDims = [paddedImageHeight, paddedImageWidth, imageChannels];
    }
    return [pixelData, imgDims];
  }
  /**
   * Rescale the image' pixel values by `this.rescale_factor`.
   * @param {Float32Array} pixelData The pixel data to rescale.
   * @returns {void}
   */
  rescale(pixelData) {
    for (let i = 0; i < pixelData.length; ++i) {
      pixelData[i] = this.rescale_factor * pixelData[i];
    }
  }
  /**
   * Find the target (width, height) dimension of the output image after
   * resizing given the input image and the desired size.
   * @param {RawImage} image The image to resize.
   * @param {any} size The size to use for resizing the image.
   * @returns {[number, number]} The target (width, height) dimension of the output image after resizing.
   */
  get_resize_output_image_size(image, size) {
    const [srcWidth, srcHeight] = image.size;
    let shortest_edge;
    let longest_edge;
    if (this.do_thumbnail) {
      const { height, width } = size;
      shortest_edge = Math.min(height, width);
    } else if (Number.isInteger(size)) {
      shortest_edge = size;
      longest_edge = this.config.max_size ?? shortest_edge;
    } else if (size !== undefined) {
      shortest_edge = size.shortest_edge;
      longest_edge = size.longest_edge;
    }
    if (shortest_edge !== undefined || longest_edge !== undefined) {
      const shortResizeFactor = shortest_edge === undefined ? 1 : Math.max(shortest_edge / srcWidth, shortest_edge / srcHeight);
      const newWidth = srcWidth * shortResizeFactor;
      const newHeight = srcHeight * shortResizeFactor;
      const longResizeFactor = longest_edge === undefined ? 1 : Math.min(longest_edge / newWidth, longest_edge / newHeight);
      let finalWidth = Math.floor(Number((newWidth * longResizeFactor).toFixed(2)));
      let finalHeight = Math.floor(Number((newHeight * longResizeFactor).toFixed(2)));
      if (this.size_divisibility !== undefined) {
        [finalWidth, finalHeight] = enforce_size_divisibility([finalWidth, finalHeight], this.size_divisibility);
      }
      return [finalWidth, finalHeight];
    } else if (size !== undefined && size.width !== undefined && size.height !== undefined) {
      let newWidth = size.width;
      let newHeight = size.height;
      if (this.config.keep_aspect_ratio && this.config.ensure_multiple_of) {
        let scale_height = newHeight / srcHeight;
        let scale_width = newWidth / srcWidth;
        if (Math.abs(1 - scale_width) < Math.abs(1 - scale_height)) {
          scale_height = scale_width;
        } else {
          scale_width = scale_height;
        }
        newHeight = constraint_to_multiple_of(scale_height * srcHeight, this.config.ensure_multiple_of);
        newWidth = constraint_to_multiple_of(scale_width * srcWidth, this.config.ensure_multiple_of);
      }
      return [newWidth, newHeight];
    } else if (this.size_divisibility !== undefined) {
      return enforce_size_divisibility([srcWidth, srcHeight], this.size_divisibility);
    } else if (size.min_pixels !== undefined && size.max_pixels !== undefined) {
      const { min_pixels, max_pixels } = size;
      const factor = this.config.patch_size * this.config.merge_size;
      return smart_resize(srcHeight, srcWidth, factor, min_pixels, max_pixels);
    } else {
      throw new Error(
        `Could not resize image due to unsupported \`this.size\` option in config: ${JSON.stringify(size)}`
      );
    }
  }
  /**
   * Resizes the image.
   * @param {RawImage} image The image to resize.
   * @returns {Promise<RawImage>} The resized image.
   */
  async resize(image) {
    const [newWidth, newHeight] = this.get_resize_output_image_size(image, this.size);
    return await image.resize(newWidth, newHeight, {
      resample: this.resample
    });
  }
  /**
   * @typedef {object} PreprocessedImage
   * @property {HeightWidth} original_size The original size of the image.
   * @property {HeightWidth} reshaped_input_size The reshaped input size of the image.
   * @property {Tensor} pixel_values The pixel values of the preprocessed image.
   */
  /**
   * Preprocesses the given image.
   *
   * @param {RawImage} image The image to preprocess.
   * @param {Object} overrides The overrides for the preprocessing options.
   * @returns {Promise<PreprocessedImage>} The preprocessed image.
   */
  async preprocess(image, {
    do_normalize = null,
    do_pad = null,
    do_convert_rgb = null,
    do_convert_grayscale = null,
    do_flip_channel_order = null
  } = {}) {
    if (this.do_crop_margin) {
      image = await this.crop_margin(image);
    }
    const [srcWidth, srcHeight] = image.size;
    if (do_convert_rgb ?? this.do_convert_rgb) {
      image = image.rgb();
    } else if (do_convert_grayscale) {
      image = image.grayscale();
    }
    if (this.do_resize) {
      image = await this.resize(image);
    }
    if (this.do_thumbnail) {
      image = await this.thumbnail(image, this.size, this.resample);
    }
    if (this.do_center_crop) {
      let crop_width;
      let crop_height;
      if (Number.isInteger(this.crop_size)) {
        crop_width = this.crop_size;
        crop_height = this.crop_size;
      } else if (typeof this.crop_size === "object" && this.crop_size !== null) {
        crop_width = this.crop_size.width;
        crop_height = this.crop_size.height;
      }
      image = await image.center_crop(crop_width, crop_height);
    }
    const reshaped_input_size = [image.height, image.width];
    let pixelData = Float32Array.from(image.data);
    let imgDims = [image.height, image.width, image.channels];
    if (this.do_rescale) {
      this.rescale(pixelData);
    }
    if (do_normalize ?? this.do_normalize) {
      let image_mean = this.image_mean;
      if (!Array.isArray(this.image_mean)) {
        image_mean = new Array(image.channels).fill(image_mean);
      }
      let image_std = this.image_std;
      if (!Array.isArray(this.image_std)) {
        image_std = new Array(image.channels).fill(image_mean);
      }
      if (!image_mean || !image_std) {
        throw new Error("image_mean and image_std must be defined when normalizing");
      }
      if (image_mean.length !== image.channels || image_std.length !== image.channels) {
        throw new Error(
          `When set to arrays, the length of \`image_mean\` (${image_mean.length}) and \`image_std\` (${image_std.length}) must match the number of channels in the image (${image.channels}).`
        );
      }
      for (let i = 0; i < pixelData.length; i += image.channels) {
        for (let j = 0; j < image.channels; ++j) {
          pixelData[i + j] = (pixelData[i + j] - image_mean[j]) / image_std[j];
        }
      }
    }
    if (do_pad ?? this.do_pad) {
      if (this.pad_size) {
        const padded = this.pad_image(pixelData, [image.height, image.width, image.channels], this.pad_size);
        [pixelData, imgDims] = padded;
      } else if (this.size_divisibility) {
        const [paddedWidth, paddedHeight] = enforce_size_divisibility([imgDims[1], imgDims[0]], this.size_divisibility);
        [pixelData, imgDims] = this.pad_image(pixelData, imgDims, { width: paddedWidth, height: paddedHeight });
      }
    }
    if (do_flip_channel_order ?? this.do_flip_channel_order) {
      if (imgDims[2] !== 3) {
        throw new Error("Flipping channel order is only supported for RGB images.");
      }
      for (let i = 0; i < pixelData.length; i += 3) {
        const temp = pixelData[i];
        pixelData[i] = pixelData[i + 2];
        pixelData[i + 2] = temp;
      }
    }
    const pixel_values = new Tensor3("float32", pixelData, imgDims).permute(2, 0, 1);
    return {
      original_size: [srcHeight, srcWidth],
      reshaped_input_size,
      pixel_values
    };
  }
  /**
   * Calls the feature extraction process on an array of images,
   * preprocesses each image, and concatenates the resulting
   * features into a single Tensor.
   * @param {RawImage[]} images The image(s) to extract features from.
   * @param {...any} args Additional arguments.
   * @returns {Promise<ImageProcessorResult>} An object containing the concatenated pixel values (and other metadata) of the preprocessed images.
   */
  async _call(images, ...args) {
    if (!Array.isArray(images)) {
      images = [images];
    }
    const imageData = await Promise.all(images.map((x) => this.preprocess(x)));
    const pixel_values = stack(
      imageData.map((x) => x.pixel_values),
      0
    );
    return {
      pixel_values,
      // Original sizes of images
      original_sizes: imageData.map((x) => x.original_size),
      // Reshaped sizes of images, before padding or cropping
      reshaped_input_sizes: imageData.map((x) => x.reshaped_input_size)
    };
  }
  /**
   * Instantiate one of the processor classes of the library from a pretrained model.
   *
   * The processor class to instantiate is selected based on the `image_processor_type` (or `feature_extractor_type`; legacy)
   * property of the config object (either passed as an argument or loaded from `pretrained_model_name_or_path` if possible)
   *
   * @param {string} pretrained_model_name_or_path The name or path of the pretrained model. Can be either:
   * - A string, the *model id* of a pretrained processor hosted inside a model repo on huggingface.co.
   *   Valid model ids can be located at the root-level, like `bert-base-uncased`, or namespaced under a
   *   user or organization name, like `dbmdz/bert-base-german-cased`.
   * - A path to a *directory* containing processor files, e.g., `./my_model_directory/`.
   * @param {import('../utils/hub.js').PretrainedOptions} options Additional options for loading the processor.
   *
   * @returns {Promise<ImageProcessor>} A new instance of the Processor class.
   */
  static async from_pretrained(pretrained_model_name_or_path, options) {
    const preprocessorConfig = await getModelJSON(pretrained_model_name_or_path, IMAGE_PROCESSOR_NAME, true, options);
    return new this(preprocessorConfig);
  }
};

// src/libs/transformers/models/auto/image_processing_auto.ts
var AutoImageProcessor = class {
  /** @type {typeof ImageProcessor.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    const preprocessorConfig = await getModelJSON(pretrained_model_name_or_path, IMAGE_PROCESSOR_NAME, true, options);
    const key = preprocessorConfig.image_processor_type ?? preprocessorConfig.feature_extractor_type;
    let image_processor_class;
    if (key !== undefined) {
      console.warn(
        `Image processor type '${key}' not found, assuming base ImageProcessor. Please report this at ${GITHUB_ISSUE_URL}.`
      );
    }
    image_processor_class = ImageProcessor;
    return new image_processor_class(preprocessorConfig);
  }
};

// src/libs/transformers/models/florence2/processing_florence2.ts
var Florence2Processor = class extends Processor {
  constructor(config, components) {
    super(config, components);
    const { tasks_answer_post_processing_type, task_prompts_without_inputs, task_prompts_with_input } = this.image_processor.config;
    this.tasks_answer_post_processing_type = new Map(Object.entries(tasks_answer_post_processing_type ?? {}));
    this.task_prompts_without_inputs = new Map(Object.entries(task_prompts_without_inputs ?? {}));
    this.task_prompts_with_input = new Map(Object.entries(task_prompts_with_input ?? {}));
    this.regexes = {
      quad_boxes: /(.+?)<loc_(\d+)><loc_(\d+)><loc_(\d+)><loc_(\d+)><loc_(\d+)><loc_(\d+)><loc_(\d+)><loc_(\d+)>/gm,
      bboxes: /([^<]+)?<loc_(\d+)><loc_(\d+)><loc_(\d+)><loc_(\d+)>/gm
    };
    this.size_per_bin = 1e3;
  }
  /**
   * Helper function to construct prompts from input texts
   * @param {string|string[]} text
   * @returns {string[]}
   */
  construct_prompts(text) {
    if (typeof text === "string") {
      text = [text];
    }
    const prompts = [];
    for (const t of text) {
      if (this.task_prompts_without_inputs.has(t)) {
        prompts.push(this.task_prompts_without_inputs.get(t));
      } else {
        for (const [task, prompt] of this.task_prompts_with_input) {
          if (t.includes(task)) {
            prompts.push(prompt.replaceAll("{input}", t).replaceAll(task, ""));
            break;
          }
        }
        if (prompts.length !== text.length) {
          prompts.push(t);
        }
      }
    }
    return prompts;
  }
  /**
   * Post-process the output of the model to each of the task outputs.
   * @param {string} text The text to post-process.
   * @param {string} task The task to post-process the text for.
   * @param {[number, number]} image_size The size of the image. height x width.
   */
  post_process_generation(text, task, image_size) {
    const task_answer_post_processing_type = this.tasks_answer_post_processing_type.get(task) ?? "pure_text";
    text = text.replaceAll("<s>", "").replaceAll("</s>", "");
    let final_answer;
    switch (task_answer_post_processing_type) {
      case "pure_text":
        final_answer = text;
        break;
      case "description_with_bboxes":
      case "bboxes":
      case "phrase_grounding":
      case "ocr":
        const key = task_answer_post_processing_type === "ocr" ? "quad_boxes" : "bboxes";
        const matches = text.matchAll(this.regexes[key]);
        const labels = [];
        const items = [];
        for (const [_, label, ...locations] of matches) {
          labels.push(label ? label.trim() : labels.at(-1) ?? "");
          items.push(
            locations.map(
              (x, i) => (
                // NOTE: Add 0.5 to use the center position of the bin as the coordinate.
                (Number(x) + 0.5) / this.size_per_bin * image_size[i % 2]
              )
            )
          );
        }
        final_answer = { labels, [key]: items };
        break;
      default:
        throw new Error(`Task "${task}" (of type "${task_answer_post_processing_type}") not yet implemented.`);
    }
    return { [task]: final_answer };
  }
  // NOTE: images and text are switched from the python version
  // `images` is required, `text` is optional
  async _call(images, text = null, kwargs = {}) {
    if (!images && !text) {
      throw new Error("Either text or images must be provided");
    }
    const image_inputs = await this.image_processor(images, kwargs);
    const text_inputs = text ? this.tokenizer(text, kwargs) : {};
    return {
      ...image_inputs,
      ...text_inputs
    };
  }
};
Florence2Processor.tokenizer_class = AutoTokenizer;
Florence2Processor.image_processor_class = AutoImageProcessor;

// src/libs/transformers/models/mgp_str/processing_mgp_str.ts
var DECODE_TYPE_MAPPING = {
  char: ["char_decode", 1],
  bpe: ["bpe_decode", 2],
  wp: ["wp_decode", 102]
};
var MgpstrProcessor = class extends Processor {
  /**
   * @returns {import('../../tokenizers.js').MgpstrTokenizer} The character tokenizer.
   */
  get char_tokenizer() {
    return this.components.char_tokenizer;
  }
  /**
   * @returns {import('../../tokenizers.js').GPT2Tokenizer} The BPE tokenizer.
   */
  get bpe_tokenizer() {
    return this.components.bpe_tokenizer;
  }
  /**
   * @returns {import('../../tokenizers.js').BertTokenizer} The WordPiece tokenizer.
   */
  get wp_tokenizer() {
    return this.components.wp_tokenizer;
  }
  /**
   * Helper function to decode the model prediction logits.
   * @param {import('../../utils/tensor.js').Tensor} pred_logits Model prediction logits.
   * @param {string} format Type of model prediction. Must be one of ['char', 'bpe', 'wp'].
   * @returns {[string[], number[]]} The decoded sentences and their confidence scores.
   */
  _decode_helper(pred_logits, format) {
    if (!DECODE_TYPE_MAPPING.hasOwnProperty(format)) {
      throw new Error(`Format ${format} is not supported.`);
    }
    const [decoder_name, eos_token] = DECODE_TYPE_MAPPING[format];
    const decoder = this[decoder_name].bind(this);
    const [batch_size, batch_max_length] = pred_logits.dims;
    const conf_scores = [];
    const all_ids = [];
    const pred_logits_list = pred_logits.tolist();
    for (let i = 0; i < batch_size; ++i) {
      const logits = pred_logits_list[i];
      const ids = [];
      const scores = [];
      for (let j = 1; j < batch_max_length; ++j) {
        const [max_prob, max_prob_index] = max(softmax(logits[j]));
        scores.push(max_prob);
        if (max_prob_index == eos_token) {
          break;
        }
        ids.push(max_prob_index);
      }
      const confidence_score = scores.length > 0 ? scores.reduce((a, b) => a * b, 1) : 0;
      all_ids.push(ids);
      conf_scores.push(confidence_score);
    }
    const decoded = decoder(all_ids);
    return [decoded, conf_scores];
  }
  /**
   * Convert a list of lists of char token ids into a list of strings by calling char tokenizer.
   * @param {number[][]} sequences List of tokenized input ids.
   * @returns {string[]} The list of char decoded sentences.
   */
  char_decode(sequences) {
    return this.char_tokenizer.batch_decode(sequences).map((str) => str.replaceAll(" ", ""));
  }
  /**
   * Convert a list of lists of BPE token ids into a list of strings by calling BPE tokenizer.
   * @param {number[][]} sequences List of tokenized input ids.
   * @returns {string[]} The list of BPE decoded sentences.
   */
  bpe_decode(sequences) {
    return this.bpe_tokenizer.batch_decode(sequences);
  }
  /**
   * Convert a list of lists of word piece token ids into a list of strings by calling word piece tokenizer.
   * @param {number[][]} sequences List of tokenized input ids.
   * @returns {string[]} The list of wp decoded sentences.
   */
  wp_decode(sequences) {
    return this.wp_tokenizer.batch_decode(sequences).map((str) => str.replaceAll(" ", ""));
  }
  /**
   * Convert a list of lists of token ids into a list of strings by calling decode.
   * @param {import('../../utils/tensor.js').Tensor[]} sequences List of tokenized input ids.
   * @returns {{generated_text: string[], scores: number[], char_preds: string[], bpe_preds: string[], wp_preds: string[]}}
   * Dictionary of all the outputs of the decoded results.
   * - generated_text: The final results after fusion of char, bpe, and wp.
   * - scores: The final scores after fusion of char, bpe, and wp.
   * - char_preds: The list of character decoded sentences.
   * - bpe_preds: The list of BPE decoded sentences.
   * - wp_preds: The list of wp decoded sentences.
   */
  // @ts-expect-error The type of this method is not compatible with the one
  // in the base class. It might be a good idea to fix this.
  batch_decode([char_logits, bpe_logits, wp_logits]) {
    const [char_preds, char_scores] = this._decode_helper(char_logits, "char");
    const [bpe_preds, bpe_scores] = this._decode_helper(bpe_logits, "bpe");
    const [wp_preds, wp_scores] = this._decode_helper(wp_logits, "wp");
    const generated_text = [];
    const scores = [];
    for (let i = 0; i < char_preds.length; ++i) {
      const [max_score, max_score_index] = max([char_scores[i], bpe_scores[i], wp_scores[i]]);
      generated_text.push([char_preds[i], bpe_preds[i], wp_preds[i]][max_score_index]);
      scores.push(max_score);
    }
    return {
      generated_text,
      scores,
      char_preds,
      bpe_preds,
      wp_preds
    };
  }
  /** @type {typeof Processor.from_pretrained} */
  static async from_pretrained(...args) {
    const base = await super.from_pretrained(...args);
    const bpe_tokenizer = await AutoTokenizer.from_pretrained("Xenova/gpt2");
    const wp_tokenizer = await AutoTokenizer.from_pretrained("Xenova/bert-base-uncased");
    base.components = {
      image_processor: base.image_processor,
      char_tokenizer: base.tokenizer,
      bpe_tokenizer,
      wp_tokenizer
    };
    return base;
  }
  async _call(images, text = null) {
    const result = await this.image_processor(images);
    if (text) {
      result.labels = this.tokenizer(text).input_ids;
    }
    return result;
  }
};
MgpstrProcessor.tokenizer_class = AutoTokenizer;
MgpstrProcessor.image_processor_class = AutoImageProcessor;

// src/libs/transformers/models/feature_extractors.ts
var feature_extractors_exports = {};
__export(feature_extractors_exports, {
  ASTFeatureExtractor: () => ASTFeatureExtractor,
  ClapFeatureExtractor: () => ClapFeatureExtractor,
  ImageFeatureExtractor: () => ImageProcessor,
  MoonshineFeatureExtractor: () => MoonshineFeatureExtractor,
  PyAnnoteFeatureExtractor: () => PyAnnoteFeatureExtractor,
  SeamlessM4TFeatureExtractor: () => SeamlessM4TFeatureExtractor,
  SpeechT5FeatureExtractor: () => SpeechT5FeatureExtractor,
  Wav2Vec2FeatureExtractor: () => Wav2Vec2FeatureExtractor,
  WeSpeakerFeatureExtractor: () => WeSpeakerFeatureExtractor,
  WhisperFeatureExtractor: () => WhisperFeatureExtractor
});

// src/libs/transformers/base/feature_extraction_utils.ts
var FeatureExtractor = class extends Callable {
  /**
   * Constructs a new FeatureExtractor instance.
   *
   * @param {Object} config The configuration for the feature extractor.
   */
  constructor(config) {
    super();
    this.config = config;
  }
  /**
   * Instantiate one of the feature extractor classes of the library from a pretrained model.
   *
   * The feature extractor class to instantiate is selected based on the `feature_extractor_type` property of
   * the config object (either passed as an argument or loaded from `pretrained_model_name_or_path` if possible)
   *
   * @param {string} pretrained_model_name_or_path The name or path of the pretrained model. Can be either:
   * - A string, the *model id* of a pretrained feature_extractor hosted inside a model repo on huggingface.co.
   *   Valid model ids can be located at the root-level, like `bert-base-uncased`, or namespaced under a
   *   user or organization name, like `dbmdz/bert-base-german-cased`.
   * - A path to a *directory* containing feature_extractor files, e.g., `./my_model_directory/`.
   * @param {import('../utils/hub.js').PretrainedOptions} options Additional options for loading the feature_extractor.
   *
   * @returns {Promise<FeatureExtractor>} A new instance of the Feature Extractor class.
   */
  static async from_pretrained(pretrained_model_name_or_path, options) {
    const config = await getModelJSON(pretrained_model_name_or_path, FEATURE_EXTRACTOR_NAME, true, options);
    return new this(config);
  }
};
function validate_audio_inputs(audio, feature_extractor) {
  if (!(audio instanceof Float32Array || audio instanceof Float64Array)) {
    throw new Error(
      `${feature_extractor} expects input to be a Float32Array or a Float64Array, but got ${audio?.constructor?.name ?? typeof audio} instead. If using the feature extractor directly, remember to use \`read_audio(url, sampling_rate)\` to obtain the raw audio data of the file/url.`
    );
  }
}

// src/libs/transformers/utils/audio.ts
async function read_audio(url2, sampling_rate) {
  if (typeof AudioContext === "undefined") {
    throw Error(
      "Unable to load audio from path/URL since `AudioContext` is not available in your environment. Instead, audio data should be passed directly to the pipeline/processor. For more information and some example code, see https://huggingface.co/docs/transformers.js/guides/node-audio-processing."
    );
  }
  const response = await (await getFile(url2)).arrayBuffer();
  const audioCTX = new AudioContext({ sampleRate: sampling_rate });
  if (typeof sampling_rate === "undefined") {
    console.warn(`No sampling rate provided, using default of ${audioCTX.sampleRate}Hz.`);
  }
  const decoded = await audioCTX.decodeAudioData(response);
  let audio;
  if (decoded.numberOfChannels === 2) {
    const SCALING_FACTOR = Math.sqrt(2);
    const left = decoded.getChannelData(0);
    const right = decoded.getChannelData(1);
    audio = new Float32Array(left.length);
    for (let i = 0; i < decoded.length; ++i) {
      audio[i] = SCALING_FACTOR * (left[i] + right[i]) / 2;
    }
  } else {
    audio = decoded.getChannelData(0);
  }
  return audio;
}
function generalized_cosine_window(M, a_0) {
  if (M < 1) {
    return new Float64Array();
  }
  if (M === 1) {
    return new Float64Array([1]);
  }
  const a_1 = 1 - a_0;
  const factor = 2 * Math.PI / (M - 1);
  const cos_vals = new Float64Array(M);
  for (let i = 0; i < M; ++i) {
    cos_vals[i] = a_0 - a_1 * Math.cos(i * factor);
  }
  return cos_vals;
}
function hanning(M) {
  return generalized_cosine_window(M, 0.5);
}
function hamming(M) {
  return generalized_cosine_window(M, 0.54);
}
var HERTZ_TO_MEL_MAPPING = {
  htk: (freq) => 2595 * Math.log10(1 + freq / 700),
  kaldi: (freq) => 1127 * Math.log(1 + freq / 700),
  slaney: (freq, min_log_hertz = 1e3, min_log_mel = 15, logstep = 27 / Math.log(6.4)) => freq >= min_log_hertz ? min_log_mel + Math.log(freq / min_log_hertz) * logstep : 3 * freq / 200
};
function hertz_to_mel(freq, mel_scale = "htk") {
  const fn = HERTZ_TO_MEL_MAPPING[mel_scale];
  if (!fn) {
    throw new Error('mel_scale should be one of "htk", "slaney" or "kaldi".');
  }
  return typeof freq === "number" ? fn(freq) : freq.map((x) => fn(x));
}
var MEL_TO_HERTZ_MAPPING = {
  htk: (mels) => 700 * (10 ** (mels / 2595) - 1),
  kaldi: (mels) => 700 * (Math.exp(mels / 1127) - 1),
  slaney: (mels, min_log_hertz = 1e3, min_log_mel = 15, logstep = Math.log(6.4) / 27) => mels >= min_log_mel ? min_log_hertz * Math.exp(logstep * (mels - min_log_mel)) : 200 * mels / 3
};
function mel_to_hertz(mels, mel_scale = "htk") {
  const fn = MEL_TO_HERTZ_MAPPING[mel_scale];
  if (!fn) {
    throw new Error('mel_scale should be one of "htk", "slaney" or "kaldi".');
  }
  return typeof mels === "number" ? fn(mels) : mels.map((x) => fn(x));
}
function _create_triangular_filter_bank(fft_freqs, filter_freqs) {
  const filter_diff = Float64Array.from(
    { length: filter_freqs.length - 1 },
    (_, i) => filter_freqs[i + 1] - filter_freqs[i]
  );
  const slopes = Array.from(
    {
      length: fft_freqs.length
    },
    () => new Array(filter_freqs.length)
  );
  for (let j = 0; j < fft_freqs.length; ++j) {
    const slope = slopes[j];
    for (let i = 0; i < filter_freqs.length; ++i) {
      slope[i] = filter_freqs[i] - fft_freqs[j];
    }
  }
  const numFreqs = filter_freqs.length - 2;
  const ret = Array.from({ length: numFreqs }, () => new Array(fft_freqs.length));
  for (let j = 0; j < fft_freqs.length; ++j) {
    const slope = slopes[j];
    for (let i = 0; i < numFreqs; ++i) {
      const down = -slope[i] / filter_diff[i];
      const up = slope[i + 2] / filter_diff[i + 1];
      ret[i][j] = Math.max(0, Math.min(down, up));
    }
  }
  return ret;
}
function linspace(start, end, num) {
  const step = (end - start) / (num - 1);
  return Float64Array.from({ length: num }, (_, i) => start + step * i);
}
function mel_filter_bank(num_frequency_bins, num_mel_filters, min_frequency, max_frequency, sampling_rate, norm = null, mel_scale = "htk", triangularize_in_mel_space = false) {
  if (norm !== null && norm !== "slaney") {
    throw new Error('norm must be one of null or "slaney"');
  }
  const mel_min = hertz_to_mel(min_frequency, mel_scale);
  const mel_max = hertz_to_mel(max_frequency, mel_scale);
  const mel_freqs = linspace(mel_min, mel_max, num_mel_filters + 2);
  let filter_freqs = mel_to_hertz(mel_freqs, mel_scale);
  let fft_freqs;
  if (triangularize_in_mel_space) {
    const fft_bin_width = sampling_rate / (num_frequency_bins * 2);
    fft_freqs = hertz_to_mel(
      Float64Array.from({ length: num_frequency_bins }, (_, i) => i * fft_bin_width),
      mel_scale
    );
    filter_freqs = mel_freqs;
  } else {
    fft_freqs = linspace(0, Math.floor(sampling_rate / 2), num_frequency_bins);
  }
  const mel_filters = _create_triangular_filter_bank(fft_freqs, filter_freqs);
  if (norm !== null && norm === "slaney") {
    for (let i = 0; i < num_mel_filters; ++i) {
      const filter = mel_filters[i];
      const enorm = 2 / (filter_freqs[i + 2] - filter_freqs[i]);
      for (let j = 0; j < num_frequency_bins; ++j) {
        filter[j] *= enorm;
      }
    }
  }
  return mel_filters;
}
function padReflect(array, left, right) {
  const padded = new array.constructor(array.length + left + right);
  const w = array.length - 1;
  for (let i = 0; i < array.length; ++i) {
    padded[left + i] = array[i];
  }
  for (let i = 1; i <= left; ++i) {
    padded[left - i] = array[calculateReflectOffset(i, w)];
  }
  for (let i = 1; i <= right; ++i) {
    padded[w + left + i] = array[calculateReflectOffset(w - i, w)];
  }
  return padded;
}
function _db_conversion_helper(spectrogram2, factor, reference, min_value, db_range) {
  if (reference <= 0) {
    throw new Error("reference must be greater than zero");
  }
  if (min_value <= 0) {
    throw new Error("min_value must be greater than zero");
  }
  reference = Math.max(min_value, reference);
  const logReference = Math.log10(reference);
  for (let i = 0; i < spectrogram2.length; ++i) {
    spectrogram2[i] = factor * Math.log10(Math.max(min_value, spectrogram2[i]) - logReference);
  }
  if (db_range !== null) {
    if (db_range <= 0) {
      throw new Error("db_range must be greater than zero");
    }
    const maxValue = Number(max(spectrogram2)[0]) - db_range;
    for (let i = 0; i < spectrogram2.length; ++i) {
      spectrogram2[i] = Math.max(spectrogram2[i], maxValue);
    }
  }
  return spectrogram2;
}
function amplitude_to_db(spectrogram2, reference = 1, min_value = 1e-5, db_range = null) {
  return _db_conversion_helper(spectrogram2, 20, reference, min_value, db_range);
}
function power_to_db(spectrogram2, reference = 1, min_value = 1e-10, db_range = null) {
  return _db_conversion_helper(spectrogram2, 10, reference, min_value, db_range);
}
async function spectrogram(waveform, window2, frame_length, hop_length, {
  fft_length = null,
  power = 1,
  center = true,
  pad_mode = "reflect",
  onesided = true,
  preemphasis = null,
  mel_filters = null,
  mel_floor = 1e-10,
  log_mel = null,
  reference = 1,
  min_value = 1e-10,
  db_range = null,
  remove_dc_offset = null,
  // Custom parameters for efficiency reasons
  min_num_frames = null,
  max_num_frames = null,
  do_pad = true,
  transpose = false
} = {}) {
  const window_length = window2.length;
  if (fft_length === null) {
    fft_length = frame_length;
  }
  if (frame_length > fft_length) {
    throw Error(`frame_length (${frame_length}) may not be larger than fft_length (${fft_length})`);
  }
  if (window_length !== frame_length) {
    throw new Error(`Length of the window (${window_length}) must equal frame_length (${frame_length})`);
  }
  if (hop_length <= 0) {
    throw new Error("hop_length must be greater than zero");
  }
  if (power === null && mel_filters !== null) {
    throw new Error(
      "You have provided `mel_filters` but `power` is `None`. Mel spectrogram computation is not yet supported for complex-valued spectrogram. Specify `power` to fix this issue."
    );
  }
  if (center) {
    if (pad_mode !== "reflect") {
      throw new Error(`pad_mode="${pad_mode}" not implemented yet.`);
    }
    const half_window = Math.floor((fft_length - 1) / 2) + 1;
    waveform = padReflect(waveform, half_window, half_window);
  }
  let num_frames = Math.floor(1 + Math.floor((waveform.length - frame_length) / hop_length));
  if (min_num_frames !== null && num_frames < min_num_frames) {
    num_frames = min_num_frames;
  }
  const num_frequency_bins = onesided ? Math.floor(fft_length / 2) + 1 : fft_length;
  let d1 = num_frames;
  let d1Max = num_frames;
  if (max_num_frames !== null) {
    if (max_num_frames > num_frames) {
      if (do_pad) {
        d1Max = max_num_frames;
      }
    } else {
      d1Max = d1 = max_num_frames;
    }
  }
  const fft = new FFT(fft_length);
  const inputBuffer = new Float64Array(fft_length);
  const outputBuffer = new Float64Array(fft.outputBufferSize);
  const transposedMagnitudeData = new Float32Array(num_frequency_bins * d1Max);
  for (let i = 0; i < d1; ++i) {
    const offset = i * hop_length;
    const buffer_size = Math.min(waveform.length - offset, frame_length);
    if (buffer_size !== frame_length) {
      inputBuffer.fill(0, 0, frame_length);
    }
    for (let j = 0; j < buffer_size; ++j) {
      inputBuffer[j] = waveform[offset + j];
    }
    if (remove_dc_offset) {
      let sum = 0;
      for (let j = 0; j < buffer_size; ++j) {
        sum += inputBuffer[j];
      }
      const mean2 = sum / buffer_size;
      for (let j = 0; j < buffer_size; ++j) {
        inputBuffer[j] -= mean2;
      }
    }
    if (preemphasis !== null) {
      for (let j = buffer_size - 1; j >= 1; --j) {
        inputBuffer[j] -= preemphasis * inputBuffer[j - 1];
      }
      inputBuffer[0] *= 1 - preemphasis;
    }
    for (let j = 0; j < window2.length; ++j) {
      inputBuffer[j] *= window2[j];
    }
    fft.realTransform(outputBuffer, inputBuffer);
    for (let j = 0; j < num_frequency_bins; ++j) {
      const j2 = j << 1;
      transposedMagnitudeData[j * d1Max + i] = outputBuffer[j2] ** 2 + outputBuffer[j2 + 1] ** 2;
    }
  }
  if (power !== null && power !== 2) {
    const pow = 2 / power;
    for (let i = 0; i < transposedMagnitudeData.length; ++i) {
      transposedMagnitudeData[i] **= pow;
    }
  }
  if (!mel_filters) {
    throw new Error("mel_filters must be provided");
  }
  const num_mel_filters = mel_filters.length;
  let mel_spec = await matmul(
    // TODO: Make `mel_filters` a Tensor during initialization
    new Tensor3("float32", mel_filters.flat(), [num_mel_filters, num_frequency_bins]),
    new Tensor3("float32", transposedMagnitudeData, [num_frequency_bins, d1Max])
  );
  if (transpose) {
    mel_spec = mel_spec.transpose(1, 0);
  }
  const mel_spec_data = (
    /** @type {Float32Array} */
    mel_spec.data
  );
  for (let i = 0; i < mel_spec_data.length; ++i) {
    mel_spec_data[i] = Math.max(mel_floor, mel_spec_data[i]);
  }
  if (power !== null && log_mel !== null) {
    const o = Math.min(mel_spec_data.length, d1 * num_mel_filters);
    switch (log_mel) {
      case "log":
        for (let i = 0; i < o; ++i) {
          mel_spec_data[i] = Math.log(mel_spec_data[i]);
        }
        break;
      case "log10":
        for (let i = 0; i < o; ++i) {
          mel_spec_data[i] = Math.log10(mel_spec_data[i]);
        }
        break;
      case "dB":
        if (power === 1) {
          amplitude_to_db(mel_spec_data, reference, min_value, db_range);
        } else if (power === 2) {
          power_to_db(mel_spec_data, reference, min_value, db_range);
        } else {
          throw new Error(`Cannot use log_mel option '${log_mel}' with power ${power}`);
        }
        break;
      default:
        throw new Error(`log_mel must be one of null, 'log', 'log10' or 'dB'. Got '${log_mel}'`);
    }
  }
  return mel_spec;
}
function window_function(window_length, name, { periodic = true, frame_length = null, center = true } = {}) {
  const length = periodic ? window_length + 1 : window_length;
  let window2;
  switch (name) {
    case "boxcar":
      window2 = new Float64Array(length).fill(1);
      break;
    case "hann":
    case "hann_window":
      window2 = hanning(length);
      break;
    case "hamming":
      window2 = hamming(length);
      break;
    case "povey":
      window2 = hanning(length).map((x) => Math.pow(x, 0.85));
      break;
    default:
      throw new Error(`Unknown window type ${name}.`);
  }
  if (periodic) {
    window2 = window2.subarray(0, window_length);
  }
  if (frame_length === null) {
    return window2;
  }
  if (window_length > frame_length) {
    throw new Error(`Length of the window (${window_length}) may not be larger than frame_length (${frame_length})`);
  }
  return window2;
}
function encodeWAV(samples, rate) {
  let offset = 44;
  const buffer = new ArrayBuffer(offset + samples.length * 4);
  const view = new DataView(buffer);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * 4, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 3, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 4, true);
  view.setUint16(32, 4, true);
  view.setUint16(34, 32, true);
  writeString(view, 36, "data");
  view.setUint32(40, samples.length * 4, true);
  for (let i = 0; i < samples.length; ++i, offset += 4) {
    view.setFloat32(offset, samples[i], true);
  }
  return buffer;
}
function writeString(view, offset, string) {
  for (let i = 0; i < string.length; ++i) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
var RawAudio = class {
  constructor(audio, sampling_rate) {
    this.audio = audio;
    this.sampling_rate = sampling_rate;
  }
  /**
   * Convert the audio to a wav file buffer.
   * @returns {ArrayBuffer} The WAV file.
   */
  toWav() {
    return encodeWAV(this.audio, this.sampling_rate);
  }
  /**
   * Convert the audio to a blob.
   * @returns {Blob}
   */
  toBlob() {
    const wav = this.toWav();
    const blob = new Blob([wav], { type: "audio/wav" });
    return blob;
  }
  toArray() {
    return this.audio;
  }
  /**
   * Save the audio to a wav file.
   * @param {string} path
   */
  async save(path3) {
    let fn;
    if (apis.IS_BROWSER_ENV) {
      if (apis.IS_WEBWORKER_ENV) {
        throw new Error("Unable to save a file from a Web Worker.");
      }
      fn = saveBlob;
    } else if (apis.IS_FS_AVAILABLE) {
      throw new Error("Unable to save because filesystem is disabled in this environment.");
    } else {
      throw new Error("Unable to save because filesystem is disabled in this environment.");
    }
    await fn(path3, this.toBlob());
  }
};

// src/libs/transformers/models/audio_spectrogram_transformer/feature_extraction_audio_spectrogram_transformer.ts
var ASTFeatureExtractor = class extends FeatureExtractor {
  constructor(config) {
    super(config);
    const sampling_rate = this.config.sampling_rate;
    const mel_filters = mel_filter_bank(
      256,
      // num_frequency_bins
      this.config.num_mel_bins,
      // num_mel_filters
      20,
      // min_frequency
      Math.floor(sampling_rate / 2),
      // max_frequency
      sampling_rate,
      // sampling_rate
      null,
      // norm
      "kaldi",
      // mel_scale
      true
      // triangularize_in_mel_space
    );
    for (let i = 0; i < mel_filters.length; ++i) {
      mel_filters[i].push(0);
    }
    this.mel_filters = mel_filters;
    this.window = window_function(400, "hann", {
      periodic: false
    });
    this.mean = this.config.mean;
    this.std = this.config.std;
  }
  /**
   * Computes the log-Mel spectrogram of the provided audio waveform.
   * @param {Float32Array|Float64Array} waveform The audio waveform to process.
   * @param {number} max_length The maximum number of frames to return.
   * @returns {Promise<Tensor>} An object containing the log-Mel spectrogram data as a Float32Array and its dimensions as an array of numbers.
   */
  async _extract_fbank_features(waveform, max_length) {
    return spectrogram(
      waveform,
      this.window,
      // window
      400,
      // frame_length
      160,
      // hop_length
      {
        fft_length: 512,
        power: 2,
        center: false,
        preemphasis: 0.97,
        mel_filters: this.mel_filters,
        log_mel: "log",
        mel_floor: 1192092955078125e-22,
        remove_dc_offset: true,
        // Custom
        max_num_frames: max_length,
        transpose: true
      }
    );
  }
  /**
   * Asynchronously extracts features from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @returns {Promise<{ input_values: Tensor }>} A Promise resolving to an object containing the extracted input features as a Tensor.
   */
  async _call(audio) {
    validate_audio_inputs(audio, "ASTFeatureExtractor");
    const features = await this._extract_fbank_features(audio, this.config.max_length);
    if (this.config.do_normalize) {
      const denom = this.std * 2;
      const features_data = features.data;
      for (let i = 0; i < features_data.length; ++i) {
        features_data[i] = (features_data[i] - this.mean) / denom;
      }
    }
    return {
      input_values: features.unsqueeze_(0)
    };
  }
};

// src/libs/transformers/models/clap/feature_extraction_clap.ts
var ClapFeatureExtractor = class extends FeatureExtractor {
  constructor(config) {
    super(config);
    this.mel_filters = mel_filter_bank(
      this.config.nb_frequency_bins,
      // num_frequency_bins
      this.config.feature_size,
      // num_mel_filters
      this.config.frequency_min,
      // min_frequency
      this.config.frequency_max,
      // max_frequency
      this.config.sampling_rate,
      // sampling_rate
      null,
      // norm
      "htk"
      // mel_scale
    );
    this.mel_filters_slaney = mel_filter_bank(
      this.config.nb_frequency_bins,
      // num_frequency_bins
      this.config.feature_size,
      // num_mel_filters
      this.config.frequency_min,
      // min_frequency
      this.config.frequency_max,
      // max_frequency
      this.config.sampling_rate,
      // sampling_rate
      "slaney",
      // norm
      "slaney"
      // mel_scale
    );
    this.window = window_function(this.config.fft_window_size, "hann");
  }
  /**
   * Extracts the mel spectrogram and prepares it for the mode based on the `truncation` and `padding` arguments.
   *
   * Four different path are possible:
   *   - `truncation="fusion"` and the length of the waveform is greater than the max length: the mel spectrogram
   *     will be computed on the entire audio. 3 random crops and a dowsampled version of the full mel spectrogram
   *     are then stacked together. They will later be used for `feature_fusion`.
   *   - `truncation="rand_trunc"` and the length of the waveform is smaller than the max length: the audio is
   *     padded based on `padding`.
   *   - `truncation="fusion"` and the length of the waveform is smaller than the max length: the audio is padded
   *     based on `padding`, and is repeated `4` times.
   *   - `truncation="rand_trunc"` and the length of the waveform is greater than the max length: the mel
   *     spectrogram will be computed on a random crop of the waveform.
   *
   * @param {Float32Array|Float64Array} waveform The input waveform.
   * @param {number} max_length The maximum length of the waveform.
   * @param {string} truncation The truncation strategy to use.
   * @param {string} padding The padding strategy to use.
   * @returns {Promise<Tensor>} An object containing the mel spectrogram data as a Float32Array, its dimensions as an array of numbers, and a boolean indicating whether the waveform was longer than the max length.
   * @private
   */
  async _get_input_mel(waveform, max_length, truncation, padding) {
    let input_mel;
    const diff = waveform.length - max_length;
    if (diff > 0) {
      if (truncation === "rand_trunc") {
        const idx = Math.floor(Math.random() * (diff + 1));
        waveform = waveform.subarray(idx, idx + max_length);
        input_mel = await this._extract_fbank_features(waveform, this.mel_filters_slaney, this.config.nb_max_samples);
      } else {
        throw new Error(`Truncation strategy "${truncation}" not implemented`);
      }
    } else {
      if (diff < 0) {
        let padded = new Float64Array(max_length);
        padded.set(waveform);
        if (padding === "repeat") {
          for (let i = waveform.length; i < max_length; i += waveform.length) {
            padded.set(waveform.subarray(0, Math.min(waveform.length, max_length - i)), i);
          }
        } else if (padding === "repeatpad") {
          for (let i = waveform.length; i < -diff; i += waveform.length) {
            padded.set(waveform, i);
          }
        }
        waveform = padded;
      }
      if (truncation === "fusion") {
        throw new Error(`Truncation strategy "${truncation}" not implemented`);
      }
      input_mel = await this._extract_fbank_features(waveform, this.mel_filters_slaney, this.config.nb_max_samples);
    }
    return input_mel.unsqueeze_(0);
  }
  /**
   * Compute the log-mel spectrogram of the provided `waveform` using the Hann window.
   * In CLAP, two different filter banks are used depending on the truncation pattern:
   *  - `self.mel_filters`: they correspond to the default parameters of `torchaudio` which can be obtained from
   *    calling `torchaudio.transforms.MelSpectrogram().mel_scale.fb`. These filters are used when `truncation`
   *    is set to `"fusion"`.
   *  - `self.mel_filteres_slaney` : they correspond to the default parameters of `librosa` which used
   *    `librosa.filters.mel` when computing the mel spectrogram. These filters were only used in the original
   *    implementation when the truncation mode is not `"fusion"`.
   *
   * @param {Float32Array|Float64Array} waveform The audio waveform to process.
   * @param {number[][]} mel_filters The mel filters to use.
   * @param {number} [max_length=null] The maximum number of frames to return.
   * @returns {Promise<Tensor>} An object containing the log-Mel spectrogram data as a Float32Array and its dimensions as an array of numbers.
   */
  async _extract_fbank_features(waveform, mel_filters, max_length = null) {
    return spectrogram(
      waveform,
      this.window,
      // window
      this.config.fft_window_size,
      // frame_length
      this.config.hop_length,
      // hop_length
      {
        power: 2,
        mel_filters,
        log_mel: "dB",
        // Custom
        max_num_frames: max_length,
        do_pad: false,
        transpose: true
      }
    );
  }
  /**
   * Asynchronously extracts features from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @returns {Promise<{ input_features: Tensor }>} A Promise resolving to an object containing the extracted input features as a Tensor.
   */
  async _call(audio, { max_length = null } = {}) {
    validate_audio_inputs(audio, "ClapFeatureExtractor");
    const padded_inputs = await this._get_input_mel(
      audio,
      max_length ?? this.config.nb_max_samples,
      this.config.truncation,
      this.config.padding
    );
    return {
      input_features: padded_inputs.unsqueeze_(0)
    };
  }
};

// src/libs/transformers/models/moonshine/feature_extraction_moonshine.ts
var MoonshineFeatureExtractor = class extends FeatureExtractor {
  /**
   * Asynchronously extracts input values from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @returns {Promise<{ input_values: Tensor; }>} The extracted input values.
   */
  async _call(audio) {
    validate_audio_inputs(audio, "MoonshineFeatureExtractor");
    if (audio instanceof Float64Array) {
      audio = new Float32Array(audio);
    }
    const shape = [
      1,
      audio.length
      /* num_samples */
    ];
    return {
      input_values: new Tensor3("float32", audio, shape)
    };
  }
};

// src/libs/transformers/models/pyannote/feature_extraction_pyannote.ts
var PyAnnoteFeatureExtractor = class extends FeatureExtractor {
  /**
   * Asynchronously extracts features from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @returns {Promise<{ input_values: Tensor; }>} The extracted input features.
   */
  async _call(audio) {
    validate_audio_inputs(audio, "PyAnnoteFeatureExtractor");
    if (audio instanceof Float64Array) {
      audio = new Float32Array(audio);
    }
    const shape = [
      1,
      1,
      audio.length
      /* num_samples */
    ];
    return {
      input_values: new Tensor3("float32", audio, shape)
    };
  }
  /**
   * NOTE: Can return fractional values. `Math.ceil` will ensure correct value.
   * @param {number} samples The number of frames in the audio.
   * @returns {number} The number of frames in the audio.
   */
  samples_to_frames(samples) {
    return (samples - this.config.offset) / this.config.step;
  }
  /**
   * Post-processes the speaker diarization logits output by the model.
   * @param {import('../../utils/tensor.js').Tensor} logits The speaker diarization logits output by the model.
   * @param {number} num_samples Number of samples in the input audio.
   * @returns {Array<Array<{ id: number, start: number, end: number, confidence: number }>>} The post-processed speaker diarization results.
   */
  post_process_speaker_diarization(logits, num_samples) {
    const ratio = num_samples / this.samples_to_frames(num_samples) / this.config.sampling_rate;
    const results = [];
    for (const scores of logits.tolist()) {
      const accumulated_segments = [];
      let current_speaker = -1;
      for (let i = 0; i < scores.length; ++i) {
        const probabilities = softmax(scores[i]);
        const [score, id] = max(probabilities);
        const [start, end] = [i, i + 1];
        if (id !== current_speaker) {
          current_speaker = id;
          accumulated_segments.push({ id, start, end, score });
        } else {
          accumulated_segments.at(-1).end = end;
          accumulated_segments.at(-1).score += score;
        }
      }
      results.push(
        accumulated_segments.map(
          // Convert frame-space to time-space
          // and compute the confidence
          ({ id, start, end, score }) => ({
            id,
            start: start * ratio,
            end: end * ratio,
            confidence: score / (end - start)
          })
        )
      );
    }
    return results;
  }
};

// src/libs/transformers/models/seamless_m4t/feature_extraction_seamless_m4t.ts
var SeamlessM4TFeatureExtractor = class extends FeatureExtractor {
  constructor(config) {
    super(config);
    const sampling_rate = this.config.sampling_rate;
    const mel_filters = mel_filter_bank(
      256,
      // num_frequency_bins
      this.config.num_mel_bins,
      // num_mel_filters
      20,
      // min_frequency
      Math.floor(sampling_rate / 2),
      // max_frequency
      sampling_rate,
      // sampling_rate
      null,
      // norm
      "kaldi",
      // mel_scale
      true
      // triangularize_in_mel_space
    );
    for (let i = 0; i < mel_filters.length; ++i) {
      mel_filters[i].push(0);
    }
    this.mel_filters = mel_filters;
    this.window = window_function(400, "povey", {
      periodic: false
    });
  }
  /**
   * Computes the log-Mel spectrogram of the provided audio waveform.
   * @param {Float32Array|Float64Array} waveform The audio waveform to process.
   * @param {number} max_length The maximum number of frames to return.
   * @returns {Promise<Tensor>} An object containing the log-Mel spectrogram data as a Float32Array and its dimensions as an array of numbers.
   */
  async _extract_fbank_features(waveform, max_length) {
    waveform = waveform.map((x) => x * 32768);
    return spectrogram(
      waveform,
      this.window,
      // window
      400,
      // frame_length
      160,
      // hop_length
      {
        fft_length: 512,
        power: 2,
        center: false,
        preemphasis: 0.97,
        mel_filters: this.mel_filters,
        log_mel: "log",
        mel_floor: 1192092955078125e-22,
        remove_dc_offset: true,
        // Custom
        max_num_frames: max_length,
        transpose: true
      }
    );
  }
  /**
   * Asynchronously extracts features from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @param {Object} options Optional parameters for feature extraction.
   * @param {boolean} [options.padding=true] Whether to pad the sequence to a multiple of `pad_to_multiple_of`.
   * @param {number} [options.pad_to_multiple_of=2] The number to pad the sequence to a multiple of.
   * @param {boolean} [options.do_normalize_per_mel_bins=true] Whether or not to zero-mean unit-variance normalize the input per mel-channel.
   * @param {boolean} [options.return_attention_mask=true] Whether to return the attention mask.
   * @returns {Promise<{ input_features: Tensor, attention_mask?: Tensor }>} A Promise resolving to an object containing the extracted input features and attention masks as Tensors.
   */
  async _call(audio, { padding = true, pad_to_multiple_of = 2, do_normalize_per_mel_bins = true, return_attention_mask = true } = {}) {
    validate_audio_inputs(audio, "SeamlessM4TFeatureExtractor");
    let features = await this._extract_fbank_features(audio, this.config.max_length);
    if (do_normalize_per_mel_bins) {
      const [num_features, feature_size] = features.dims;
      const data = features.data;
      for (let i = 0; i < feature_size; ++i) {
        let sum = 0;
        for (let j = 0; j < num_features; ++j) {
          sum += data[j * feature_size + i];
        }
        const mean2 = sum / num_features;
        let variance = 0;
        for (let j = 0; j < num_features; ++j) {
          variance += (data[j * feature_size + i] - mean2) ** 2;
        }
        variance /= num_features - 1;
        const std = Math.sqrt(variance + 1e-7);
        for (let j = 0; j < num_features; ++j) {
          const index = j * feature_size + i;
          data[index] = (data[index] - mean2) / std;
        }
      }
    }
    let padded_attention_mask;
    if (padding) {
      const [num_frames2, num_channels2] = features.dims;
      const data = (
        /** @type {Float32Array} */
        features.data
      );
      const pad_size = num_frames2 % pad_to_multiple_of;
      if (pad_size > 0) {
        const padded_data = new Float32Array(num_channels2 * (num_frames2 + pad_size));
        padded_data.set(data);
        padded_data.fill(this.config.padding_value, data.length);
        const numPaddedFrames = num_frames2 + pad_size;
        features = new Tensor3(features.type, padded_data, [numPaddedFrames, num_channels2]);
        if (return_attention_mask) {
          padded_attention_mask = new Tensor3("int64", new BigInt64Array(numPaddedFrames), [1, numPaddedFrames]);
          padded_attention_mask.data.fill(1n, 0, num_frames2);
        }
      }
    }
    const [num_frames, num_channels] = features.dims;
    const stride = this.config.stride;
    const remainder = num_frames % stride;
    if (remainder !== 0) {
      throw new Error(`The number of frames (${num_frames}) must be a multiple of the stride (${stride}).`);
    }
    const input_features = features.view(1, Math.floor(num_frames / stride), num_channels * stride);
    const result = { input_features };
    if (return_attention_mask) {
      const reshapedNumFrames = input_features.dims[1];
      const attention_mask_data = new BigInt64Array(reshapedNumFrames);
      if (padded_attention_mask) {
        const padded_attention_mask_data = padded_attention_mask.data;
        for (let i = 1, j = 0; i < num_frames; i += stride, ++j) {
          attention_mask_data[j] = padded_attention_mask_data[i];
        }
      } else {
        attention_mask_data.fill(1n);
      }
      result.attention_mask = new Tensor3("int64", attention_mask_data, [1, reshapedNumFrames]);
    }
    return result;
  }
};

// src/libs/transformers/models/speecht5/feature_extraction_speecht5.ts
var SpeechT5FeatureExtractor = class extends FeatureExtractor {
};

// src/libs/transformers/models/wav2vec2/feature_extraction_wav2vec2.ts
var Wav2Vec2FeatureExtractor = class extends FeatureExtractor {
  /**
   * @param {Float32Array} input_values
   * @returns {Float32Array}
   */
  _zero_mean_unit_var_norm(input_values) {
    const sum = input_values.reduce((a, b) => a + b, 0);
    const mean2 = sum / input_values.length;
    const variance = input_values.reduce((a, b) => a + (b - mean2) ** 2, 0) / input_values.length;
    return input_values.map((x) => (x - mean2) / Math.sqrt(variance + 1e-7));
  }
  /**
   * Asynchronously extracts features from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @returns {Promise<{ input_values: Tensor; attention_mask: Tensor }>} A Promise resolving to an object containing the extracted input features and attention mask as Tensors.
   */
  async _call(audio) {
    validate_audio_inputs(audio, "Wav2Vec2FeatureExtractor");
    if (audio instanceof Float64Array) {
      audio = new Float32Array(audio);
    }
    let input_values = audio;
    if (this.config.do_normalize) {
      input_values = this._zero_mean_unit_var_norm(input_values);
    }
    const shape = [1, input_values.length];
    return {
      input_values: new Tensor3("float32", input_values, shape),
      attention_mask: new Tensor3("int64", new BigInt64Array(input_values.length).fill(1n), shape)
    };
  }
};

// src/libs/transformers/models/wespeaker/feature_extraction_wespeaker.ts
var WeSpeakerFeatureExtractor = class extends FeatureExtractor {
  constructor(config) {
    super(config);
    const sampling_rate = this.config.sampling_rate;
    const mel_filters = mel_filter_bank(
      256,
      // num_frequency_bins
      this.config.num_mel_bins,
      // num_mel_filters
      20,
      // min_frequency
      Math.floor(sampling_rate / 2),
      // max_frequency
      sampling_rate,
      // sampling_rate
      null,
      // norm
      "kaldi",
      // mel_scale
      true
      // triangularize_in_mel_space
    );
    for (let i = 0; i < mel_filters.length; ++i) {
      mel_filters[i].push(0);
    }
    this.mel_filters = mel_filters;
    this.window = window_function(400, "hamming", {
      periodic: false
    });
    this.min_num_frames = this.config.min_num_frames;
  }
  /**
   * Computes the log-Mel spectrogram of the provided audio waveform.
   * @param {Float32Array|Float64Array} waveform The audio waveform to process.
   * @returns {Promise<Tensor>} An object containing the log-Mel spectrogram data as a Float32Array and its dimensions as an array of numbers.
   */
  async _extract_fbank_features(waveform) {
    waveform = waveform.map((x) => x * 32768);
    return spectrogram(
      waveform,
      this.window,
      // window
      400,
      // frame_length
      160,
      // hop_length
      {
        fft_length: 512,
        power: 2,
        center: false,
        preemphasis: 0.97,
        mel_filters: this.mel_filters,
        log_mel: "log",
        mel_floor: 1192092955078125e-22,
        remove_dc_offset: true,
        // Custom
        transpose: true,
        min_num_frames: this.min_num_frames
      }
    );
  }
  /**
   * Asynchronously extracts features from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @returns {Promise<{ input_features: Tensor }>} A Promise resolving to an object containing the extracted input features as a Tensor.
   */
  async _call(audio) {
    validate_audio_inputs(audio, "WeSpeakerFeatureExtractor");
    const features = (await this._extract_fbank_features(audio)).unsqueeze_(0);
    if (this.config.fbank_centering_span === null) {
      const meanData = (
        /** @type {Float32Array} */
        features.mean(1).data
      );
      const featuresData = (
        /** @type {Float32Array} */
        features.data
      );
      const [batch_size, num_frames, feature_size] = features.dims;
      for (let i = 0; i < batch_size; ++i) {
        const offset1 = i * num_frames * feature_size;
        const offset2 = i * feature_size;
        for (let j = 0; j < num_frames; ++j) {
          const offset3 = offset1 + j * feature_size;
          for (let k = 0; k < feature_size; ++k) {
            featuresData[offset3 + k] -= meanData[offset2 + k];
          }
        }
      }
    }
    return {
      input_features: features
    };
  }
};

// src/libs/transformers/models/whisper/feature_extraction_whisper.ts
var WhisperFeatureExtractor = class extends FeatureExtractor {
  constructor(config) {
    var _a;
    super(config);
    (_a = this.config).mel_filters ?? (_a.mel_filters = mel_filter_bank(
      Math.floor(1 + this.config.n_fft / 2),
      // num_frequency_bins
      this.config.feature_size,
      // num_mel_filters
      0,
      // min_frequency
      8e3,
      // max_frequency
      this.config.sampling_rate,
      // sampling_rate
      "slaney",
      // norm
      "slaney"
      // mel_scale
    ));
    this.window = window_function(this.config.n_fft, "hann");
  }
  /**
   * Computes the log-Mel spectrogram of the provided audio waveform.
   * @param {Float32Array|Float64Array} waveform The audio waveform to process.
   * @returns {Promise<Tensor>} An object containing the log-Mel spectrogram data as a Float32Array and its dimensions as an array of numbers.
   */
  async _extract_fbank_features(waveform) {
    const features = await spectrogram(
      waveform,
      this.window,
      // window
      this.config.n_fft,
      // frame_length
      this.config.hop_length,
      // hop_length
      {
        power: 2,
        mel_filters: this.config.mel_filters,
        log_mel: "log10",
        // Custom
        max_num_frames: this.config.nb_max_frames
        // 3000
      }
    );
    const data = features.data;
    const maxValue = max(
      /** @type {Float32Array} */
      data
    )[0];
    for (let i = 0; i < data.length; ++i) {
      data[i] = (Math.max(data[i], maxValue - 8) + 4) / 4;
    }
    return features;
  }
  /**
   * Asynchronously extracts features from a given audio using the provided configuration.
   * @param {Float32Array|Float64Array} audio The audio data as a Float32Array/Float64Array.
   * @returns {Promise<{ input_features: Tensor }>} A Promise resolving to an object containing the extracted input features as a Tensor.
   */
  async _call(audio) {
    validate_audio_inputs(audio, "WhisperFeatureExtractor");
    let waveform;
    if (audio.length > this.config.n_samples) {
      console.warn(
        "Attempting to extract features for audio longer than 30 seconds. If using a pipeline to extract transcript from a long audio clip, remember to specify `chunk_length_s` and/or `stride_length_s`."
      );
      waveform = audio.slice(0, this.config.n_samples);
    } else {
      waveform = new Float32Array(this.config.n_samples);
      waveform.set(audio);
    }
    const features = await this._extract_fbank_features(waveform);
    return {
      input_features: features.unsqueeze_(0)
    };
  }
};

// src/libs/transformers/models/auto/feature_extraction_auto.ts
var AutoFeatureExtractor = class {
  /** @type {typeof FeatureExtractor.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    const preprocessorConfig = await getModelJSON(pretrained_model_name_or_path, FEATURE_EXTRACTOR_NAME, true, options);
    const key = preprocessorConfig.feature_extractor_type;
    const feature_extractor_class = feature_extractors_exports[key];
    if (!feature_extractor_class) {
      throw new Error(`Unknown feature_extractor_type: '${key}'. Please report this at ${GITHUB_ISSUE_URL}.`);
    }
    return new feature_extractor_class(preprocessorConfig);
  }
};

// src/libs/transformers/models/moonshine/processing_moonshine.ts
var MoonshineProcessor = class extends Processor {
  /**
   * Calls the feature_extractor function with the given audio input.
   * @param {any} audio The audio input to extract features from.
   * @returns {Promise<any>} A Promise that resolves with the extracted features.
   */
  async _call(audio) {
    return await this.feature_extractor(audio);
  }
};
MoonshineProcessor.tokenizer_class = AutoTokenizer;
MoonshineProcessor.feature_extractor_class = AutoFeatureExtractor;

// src/libs/transformers/models/idefics3/processing_idefics3.ts
function _prompt_split_image(image_seq_len, image_rows, image_cols, fake_token_around_image, image_token, global_img_token) {
  let text_split_images = "";
  for (let n_h = 0; n_h < image_rows; ++n_h) {
    for (let n_w = 0; n_w < image_cols; ++n_w) {
      text_split_images += fake_token_around_image + `<row_${n_h + 1}_col_${n_w + 1}>` + image_token.repeat(image_seq_len);
    }
    text_split_images += "\n";
  }
  text_split_images += `
${fake_token_around_image}${global_img_token}` + image_token.repeat(image_seq_len) + `${fake_token_around_image}`;
  return text_split_images;
}
function _prompt_single_image(image_seq_len, fake_token_around_image, image_token, global_img_token) {
  return `${fake_token_around_image}${global_img_token}` + image_token.repeat(image_seq_len) + `${fake_token_around_image}`;
}
function get_image_prompt_string(image_rows, image_cols, image_seq_len, fake_token_around_image, image_token, global_img_token) {
  if (image_rows === 0 && image_cols === 0) {
    return _prompt_single_image(image_seq_len, fake_token_around_image, image_token, global_img_token);
  }
  return _prompt_split_image(
    image_seq_len,
    image_rows,
    image_cols,
    fake_token_around_image,
    image_token,
    global_img_token
  );
}
var Idefics3Processor = class extends Processor {
  constructor() {
    super(...arguments);
    this.fake_image_token = "<fake_token_around_image>";
    this.image_token = "<image>";
    this.global_img_token = "<global-img>";
  }
  /**
   *
   * @param {string|string[]} text
   * @param {RawImage|RawImage[]|RawImage[][]} images
   * @returns {Promise<any>}
   */
  async _call(text, images, options = {}) {
    options.return_row_col_info ?? (options.return_row_col_info = true);
    let image_inputs;
    if (images) {
      image_inputs = await this.image_processor(images, options);
    }
    if (!Array.isArray(text)) {
      text = [text];
    }
    const image_rows = image_inputs.rows ?? [new Array(text.length).fill(0)];
    const image_cols = image_inputs.cols ?? [new Array(text.length).fill(0)];
    const image_seq_len = this.config.image_seq_len;
    const n_images_in_text = [];
    const prompt_strings = [];
    for (let i = 0; i < text.length; ++i) {
      const sample = text[i];
      const sample_rows = image_rows[i];
      const sample_cols = image_cols[i];
      n_images_in_text.push(count(sample, this.image_token));
      const image_prompt_strings = sample_rows.map(
        (n_rows, j) => get_image_prompt_string(
          n_rows,
          sample_cols[j],
          image_seq_len,
          this.fake_image_token,
          this.image_token,
          this.global_img_token
        )
      );
      const split_sample = sample.split(this.image_token);
      if (split_sample.length === 0) {
        throw new Error("The image token should be present in the text.");
      }
      let new_sample = split_sample[0];
      for (let j = 0; j < image_prompt_strings.length; ++j) {
        new_sample += image_prompt_strings[j] + split_sample[j + 1];
      }
      prompt_strings.push(new_sample);
    }
    const text_inputs = this.tokenizer(prompt_strings);
    return {
      ...text_inputs,
      ...image_inputs
    };
  }
};
Idefics3Processor.image_processor_class = AutoImageProcessor;
Idefics3Processor.tokenizer_class = AutoTokenizer;
Idefics3Processor.uses_processor_config = true;

// src/libs/transformers/models/janus/processing_janus.ts
var VLChatProcessor = class extends Processor {
  constructor(config, components) {
    super(config, components);
    this.image_tag = this.config.image_tag;
    this.image_start_tag = this.config.image_start_tag;
    this.image_end_tag = this.config.image_end_tag;
    this.num_image_tokens = this.config.num_image_tokens;
  }
  /**
   * @typedef {Object} MultimodalMessageProperties Additional properties for multimodal messages.
   * @property {(RawImage | string | URL)[]} [images] The images in the message.
   * @typedef {(import('../../tokenizers.js').Message & MultimodalMessageProperties)[]} MultimodalConversation The conversation possibly containing multimodal inputs.
   */
  /**
   * @typedef {Object} VLCChatProcessorResult The processed input.
   * @property {Tensor} input_ids The input IDs.
   * @property {Tensor} attention_mask The attention mask.
   * @property {Tensor} images_seq_mask The image sequence mask.
   * @property {Tensor} images_emb_mask The image embedding mask.
   */
  /**
   * @param {MultimodalConversation} conversation The chat messages to process.
   * @param {Object} options Additional options for processing.
   * @param {RawImage|RawImage[]} [options.images] The images to process, if not set in the conversation.
   * @param {string} [options.chat_template="default"] The chat template to use.
   * @returns {Promise<VLCChatProcessorResult | VLCChatProcessorResult & import('../../base/image_processors_utils.js').ImageProcessorResult>} The processed input.
   */
  async _call(conversation, { images = null, chat_template = "default" } = {}) {
    if (!images) {
      images = await Promise.all(
        conversation.filter((msg) => msg.images).flatMap((msg) => msg.images).map((img) => RawImage.read(img))
      );
    } else if (!Array.isArray(images)) {
      images = [images];
    }
    const tokenizer = this.tokenizer;
    const result = tokenizer.apply_chat_template(conversation, {
      tokenize: false,
      add_generation_prompt: true,
      chat_template
    });
    const encode = (text) => tokenizer.encode(text, { add_special_tokens: false });
    const parts = (
      /** @type {string} */
      result.split(this.image_tag)
    );
    const num_images = parts.length - 1;
    if (images.length !== num_images) {
      throw new Error(
        `Number of images provided (${images.length}) does not match number of "${this.image_tag}" image tags (${num_images})`
      );
    }
    const [image_placeholder_tag_id, image_start_tag_id, image_end_tag_id] = tokenizer.model.convert_tokens_to_ids([
      this.image_tag,
      this.image_start_tag,
      this.image_end_tag
    ]);
    let input_ids = encode(parts[0]);
    let images_seq_mask = new Array(input_ids.length).fill(false);
    for (let i = 1; i < parts.length; ++i) {
      const placeholder_image_tokens = new Array(this.num_image_tokens).fill(image_placeholder_tag_id);
      const tokens = encode(parts[i]);
      input_ids = mergeArrays(input_ids, [image_start_tag_id], placeholder_image_tokens, [image_end_tag_id], tokens);
      const image_mask = new Array(this.num_image_tokens).fill(true);
      images_seq_mask = mergeArrays(
        images_seq_mask,
        [false],
        image_mask,
        [false],
        new Array(tokens.length).fill(false)
      );
    }
    const dims = [1, input_ids.length];
    const final = {
      input_ids: new Tensor3("int64", input_ids, dims),
      attention_mask: new Tensor3("int64", new Array(input_ids.length).fill(1), dims),
      images_seq_mask: new Tensor3("bool", images_seq_mask, dims),
      images_emb_mask: new Tensor3("bool", new Array(num_images * this.num_image_tokens).fill(true), [
        1,
        num_images,
        this.num_image_tokens
      ])
    };
    if (images && images.length > 0) {
      const image_inputs = await this.image_processor(images);
      image_inputs.pixel_values.unsqueeze_(0);
      return { ...final, ...image_inputs };
    }
    return final;
  }
};
VLChatProcessor.image_processor_class = AutoImageProcessor;
VLChatProcessor.tokenizer_class = AutoTokenizer;
VLChatProcessor.uses_processor_config = true;

// src/libs/transformers/models/jina_clip/processing_jina_clip.ts
var JinaCLIPProcessor = class extends Processor {
  async _call(text = null, images = null, kwargs = {}) {
    if (!text && !images) {
      throw new Error("Either text or images must be provided");
    }
    const text_inputs = text ? this.tokenizer(text, kwargs) : {};
    const image_inputs = images ? await this.image_processor(images, kwargs) : {};
    return {
      ...text_inputs,
      ...image_inputs
    };
  }
};
JinaCLIPProcessor.tokenizer_class = AutoTokenizer;
JinaCLIPProcessor.image_processor_class = AutoImageProcessor;

// src/libs/transformers/models/phi3_v/processing_phi3_v.ts
var IMAGE_TOKEN = "<|image|>";
var IMAGE_TOKEN_PATTERN = /<\|image_\d+\|>/g;
var Phi3VProcessor = class extends Processor {
  /**
   *
   * @param {string|string[]} text
   * @param {RawImage|RawImage[]} images
   * @param  { { padding?: boolean, truncation?: boolean, num_crops?: number } | undefined } options
   * @returns {Promise<any>}
   */
  async _call(text, images = null, { padding = true, truncation = true, num_crops = null } = {}) {
    if (!Array.isArray(text)) {
      text = [text];
    }
    let text_inputs, image_inputs;
    if (images) {
      image_inputs = await this.image_processor(images, { num_crops });
      const { num_img_tokens } = image_inputs;
      const prompt_chunks = text.map(
        (t, i) => t.split(IMAGE_TOKEN_PATTERN).join(IMAGE_TOKEN.repeat(num_img_tokens[i]))
      );
      text_inputs = this.tokenizer(prompt_chunks, { padding, truncation });
      const image_token_id = this.tokenizer.model.convert_tokens_to_ids([IMAGE_TOKEN])[0];
      text_inputs.input_ids.map_((id) => id == image_token_id ? -id : id);
    } else {
      text_inputs = this.tokenizer(text);
    }
    return {
      ...text_inputs,
      ...image_inputs
    };
  }
};
Phi3VProcessor.image_processor_class = AutoImageProcessor;
Phi3VProcessor.tokenizer_class = AutoTokenizer;

// src/libs/transformers/models/paligemma/processing_paligemma.ts
var IMAGE_TOKEN2 = "<image>";
function build_string_from_input(prompt, bos_token, image_seq_len, image_token, num_images) {
  return `${image_token.repeat(image_seq_len * num_images)}${bos_token}${prompt}
`;
}
var PaliGemmaProcessor = class extends Processor {
  /**
   * @typedef {import('../../utils/image.js').RawImage} RawImage
   */
  // `images` is required, `text` is optional
  async _call(images, text = null, kwargs = {}) {
    if (!text) {
      console.warn("You are using PaliGemma without a text prefix. It will perform as a picture-captioning model.");
      text = "";
    }
    if (!Array.isArray(images)) {
      images = [images];
    }
    if (!Array.isArray(text)) {
      text = [text];
    }
    const bos_token = this.tokenizer.bos_token;
    const image_seq_length = this.image_processor.config.image_seq_length;
    let input_strings;
    if (text.some((t) => t.includes(IMAGE_TOKEN2))) {
      input_strings = text.map((sample) => {
        const expanded_sample = sample.replaceAll(IMAGE_TOKEN2, IMAGE_TOKEN2.repeat(image_seq_length));
        const bos_rfind_index = expanded_sample.lastIndexOf(IMAGE_TOKEN2);
        const bos_index = bos_rfind_index === -1 ? 0 : bos_rfind_index + IMAGE_TOKEN2.length;
        return expanded_sample.slice(0, bos_index) + bos_token + expanded_sample.slice(bos_index) + "\n";
      });
    } else {
      console.warn(
        "You are passing both `text` and `images` to `PaliGemmaProcessor`. The processor expects special image tokens in the text, as many tokens as there are images per each text. It is recommended to add `<image>` tokens in the very beginning of your text. For this call, we will infer how many images each text has and add special tokens."
      );
      input_strings = text.map(
        (sample) => build_string_from_input(sample, bos_token, image_seq_length, IMAGE_TOKEN2, images.length)
      );
    }
    const text_inputs = this.tokenizer(input_strings, kwargs);
    const image_inputs = await this.image_processor(images, kwargs);
    return {
      ...image_inputs,
      ...text_inputs
    };
  }
};
PaliGemmaProcessor.tokenizer_class = AutoTokenizer;
PaliGemmaProcessor.image_processor_class = AutoImageProcessor;
PaliGemmaProcessor.uses_processor_config = false;

// src/libs/transformers/models/pyannote/processing_pyannote.ts
var PyAnnoteProcessor = class extends Processor {
  /**
   * Calls the feature_extractor function with the given audio input.
   * @param {any} audio The audio input to extract features from.
   * @returns {Promise<any>} A Promise that resolves with the extracted features.
   */
  async _call(audio) {
    return await this.feature_extractor(audio);
  }
  /** @type {PyAnnoteFeatureExtractor['post_process_speaker_diarization']} */
  post_process_speaker_diarization(...args) {
    return (
      /** @type {PyAnnoteFeatureExtractor} */
      this.feature_extractor.post_process_speaker_diarization(...args)
    );
  }
  get sampling_rate() {
    return this.feature_extractor.config.sampling_rate;
  }
};
PyAnnoteProcessor.feature_extractor_class = PyAnnoteFeatureExtractor;

// src/libs/transformers/models/qwen2_vl/processing_qwen2_vl.ts
var Qwen2VLProcessor = class extends Processor {
  /**
   *
   * @param {string|string[]} text
   * @param {RawImage|RawImage[]} images
   * @param  {...any} args
   * @returns {Promise<any>}
   */
  async _call(text, images = null, ...args) {
    if (!Array.isArray(text)) {
      text = [text];
    }
    let image_inputs, image_grid_thw;
    if (images) {
      image_inputs = await this.image_processor(images);
      image_grid_thw = image_inputs.image_grid_thw;
    }
    if (image_grid_thw) {
      let merge_length = this.image_processor.config.merge_size ** 2;
      let index = 0;
      const image_grid_thw_list = image_grid_thw.tolist();
      text = text.map((t) => {
        while (t.includes("<|image_pad|>")) {
          const prod = Number(image_grid_thw_list[index++].reduce((a, b) => a * b, 1n));
          t = t.replace("<|image_pad|>", "<|placeholder|>".repeat(Math.floor(prod / merge_length)));
        }
        return t.replaceAll("<|placeholder|>", "<|image_pad|>");
      });
    }
    const text_inputs = this.tokenizer(text);
    return {
      ...text_inputs,
      ...image_inputs
      // TODO: ...videos_inputs,
    };
  }
};
Qwen2VLProcessor.image_processor_class = AutoImageProcessor;
Qwen2VLProcessor.tokenizer_class = AutoTokenizer;

// src/libs/transformers/models/sam/processing_sam.ts
var SamProcessor = class extends Processor {
  async _call(...args) {
    return await this.image_processor(...args);
  }
  post_process_masks(...args) {
    return this.image_processor.post_process_masks(...args);
  }
  reshape_input_points(...args) {
    return this.image_processor.reshape_input_points(...args);
  }
};
SamProcessor.image_processor_class = AutoImageProcessor;

// src/libs/transformers/models/speecht5/processing_speecht5.ts
var SpeechT5Processor = class extends Processor {
  /**
   * Calls the feature_extractor function with the given input.
   * @param {any} input The input to extract features from.
   * @returns {Promise<any>} A Promise that resolves with the extracted features.
   */
  async _call(input) {
    return await this.feature_extractor(input);
  }
};
SpeechT5Processor.tokenizer_class = AutoTokenizer;
SpeechT5Processor.feature_extractor_class = AutoFeatureExtractor;

// src/libs/transformers/models/wav2vec2/processing_wav2vec2.ts
var Wav2Vec2ProcessorWithLM = class extends Processor {
  /**
   * Calls the feature_extractor function with the given audio input.
   * @param {any} audio The audio input to extract features from.
   * @returns {Promise<any>} A Promise that resolves with the extracted features.
   */
  async _call(audio) {
    return await this.feature_extractor(audio);
  }
};
Wav2Vec2ProcessorWithLM.feature_extractor_class = AutoFeatureExtractor;

// src/libs/transformers/models/whisper/processing_whisper.ts
var WhisperProcessor = class extends Processor {
  /**
   * Calls the feature_extractor function with the given audio input.
   * @param {any} audio The audio input to extract features from.
   * @returns {Promise<any>} A Promise that resolves with the extracted features.
   */
  async _call(audio) {
    return await this.feature_extractor(audio);
  }
};
WhisperProcessor.tokenizer_class = AutoTokenizer;
WhisperProcessor.feature_extractor_class = AutoFeatureExtractor;

// src/libs/transformers/models/image_processors.ts
var image_processors_exports = {};
__export(image_processors_exports, {
  BitImageProcessor: () => BitImageProcessor,
  CLIPFeatureExtractor: () => CLIPFeatureExtractor,
  CLIPImageProcessor: () => CLIPImageProcessor,
  ChineseCLIPFeatureExtractor: () => ChineseCLIPFeatureExtractor,
  ConvNextFeatureExtractor: () => ConvNextFeatureExtractor,
  ConvNextImageProcessor: () => ConvNextImageProcessor,
  DeiTFeatureExtractor: () => DeiTFeatureExtractor,
  DeiTImageProcessor: () => DeiTImageProcessor,
  Idefics3ImageProcessor: () => Idefics3ImageProcessor,
  JinaCLIPImageProcessor: () => JinaCLIPImageProcessor,
  LlavaOnevisionImageProcessor: () => LlavaOnevisionImageProcessor,
  Phi3VImageProcessor: () => Phi3VImageProcessor,
  Qwen2VLImageProcessor: () => Qwen2VLImageProcessor,
  SamImageProcessor: () => SamImageProcessor,
  SiglipImageProcessor: () => SiglipImageProcessor,
  Swin2SRImageProcessor: () => Swin2SRImageProcessor,
  VLMImageProcessor: () => VLMImageProcessor,
  VitMatteImageProcessor: () => VitMatteImageProcessor,
  VitPoseImageProcessor: () => VitPoseImageProcessor
});

// src/libs/transformers/models/bit/image_processing_bit.ts
var BitImageProcessor = class extends ImageProcessor {
};

// src/libs/transformers/models/chinese_clip/image_processing_chinese_clip.ts
var ChineseCLIPFeatureExtractor = class extends ImageProcessor {
};

// src/libs/transformers/models/clip/image_processing_clip.ts
var CLIPImageProcessor = class extends ImageProcessor {
};
var CLIPFeatureExtractor = class extends CLIPImageProcessor {
};

// src/libs/transformers/models/convnext/image_processing_convnext.ts
var ConvNextImageProcessor = class extends ImageProcessor {
  constructor(config) {
    super(config);
    this.crop_pct = this.config.crop_pct ?? 224 / 256;
  }
  async resize(image) {
    const shortest_edge = this.size?.shortest_edge;
    if (shortest_edge === undefined) {
      throw new Error(`Size dictionary must contain 'shortest_edge' key.`);
    }
    if (shortest_edge < 384) {
      const resize_shortest_edge = Math.floor(shortest_edge / this.crop_pct);
      const [newWidth, newHeight] = this.get_resize_output_image_size(image, {
        shortest_edge: resize_shortest_edge
      });
      image = await image.resize(newWidth, newHeight, {
        resample: this.resample
      });
      image = await image.center_crop(shortest_edge, shortest_edge);
    } else {
      image = await image.resize(shortest_edge, shortest_edge, {
        resample: this.resample
      });
    }
    return image;
  }
};
var ConvNextFeatureExtractor = class extends ConvNextImageProcessor {
};

// src/libs/transformers/models/deit/image_processing_deit.ts
var DeiTImageProcessor = class extends ImageProcessor {
};
var DeiTFeatureExtractor = class extends DeiTImageProcessor {
};

// src/libs/transformers/models/idefics3/image_processing_idefics3.ts
var Idefics3ImageProcessor = class extends ImageProcessor {
  constructor(config) {
    super(config);
    this.do_image_splitting = config.do_image_splitting ?? true;
    this.max_image_size = config.max_image_size;
  }
  /**
   * @typedef {import('../../utils/image.js').RawImage} RawImage
   * @typedef {import('../../utils/tensor.js').Tensor} Tensor
   */
  /**
   * Calculate size to resize images to, to be multiples of `vision_encoder_max_size` while preserving the aspect ratio.
   * @param {Tensor} pixel_values Tensor of the image to resize.
   * @param {number} vision_encoder_max_size Maximum size of the output image. If the image is larger than this size,
   * it will be split into patches of this size, and the original image will be concatenated with the patches, resized to max_size.
   */
  get_resize_for_vision_encoder(pixel_values, vision_encoder_max_size) {
    let [height, width] = pixel_values.dims.slice(-2);
    const aspect_ratio = width / height;
    if (width >= height) {
      width = Math.ceil(width / vision_encoder_max_size) * vision_encoder_max_size;
      height = Math.floor(width / aspect_ratio);
      height = Math.ceil(height / vision_encoder_max_size) * vision_encoder_max_size;
    } else {
      height = Math.ceil(height / vision_encoder_max_size) * vision_encoder_max_size;
      width = Math.floor(height * aspect_ratio);
      width = Math.ceil(width / vision_encoder_max_size) * vision_encoder_max_size;
    }
    return { height, width };
  }
  /** @param {RawImage|RawImage[]|RawImage[][]} images */
  async _call(images, { do_image_splitting = null, return_row_col_info = false } = {}) {
    let batched_2d_images;
    if (!Array.isArray(images)) {
      batched_2d_images = [[images]];
    } else {
      if (images.length === 0 || !images[0]) {
        throw new Error("No images provided.");
      }
      if (!Array.isArray(images[0])) {
        batched_2d_images = [
          /** @type {RawImage[]} */
          images
        ];
      } else {
        batched_2d_images = /** @type {RawImage[][]} */
        images;
      }
    }
    let all_pixel_values = [];
    let images_list_rows = [];
    let images_list_cols = [];
    const original_sizes = [];
    const reshaped_input_sizes = [];
    for (const image_batch of batched_2d_images) {
      let images_list = await Promise.all(image_batch.map((x) => this.preprocess(x)));
      original_sizes.push(...images_list.map((x) => x.original_size));
      reshaped_input_sizes.push(...images_list.map((x) => x.reshaped_input_size));
      images_list.forEach((x) => x.pixel_values.unsqueeze_(0));
      const longest_edge = this.max_image_size[0];
      let images_tensor;
      if (do_image_splitting ?? this.do_image_splitting) {
        let image_rows = new Array(images_list.length);
        let image_cols = new Array(images_list.length);
        images_tensor = await Promise.all(
          images_list.map(async (x, i) => {
            const new_size = this.get_resize_for_vision_encoder(x.pixel_values, longest_edge);
            const resized = await interpolate_4d(x.pixel_values, {
              size: [new_size.height, new_size.width]
            });
            const { frames, num_splits_h, num_splits_w } = await this.split_image(resized, {
              longest_edge: this.max_image_size[0]
            });
            image_rows[i] = num_splits_h;
            image_cols[i] = num_splits_w;
            return cat(frames, 0);
          })
        );
        images_list_rows.push(image_rows);
        images_list_cols.push(image_cols);
      } else {
        const size = [longest_edge, longest_edge];
        images_tensor = await Promise.all(images_list.map((x) => interpolate_4d(x.pixel_values, { size })));
        images_list_rows.push(new Array(images_list.length).fill(0));
        images_list_cols.push(new Array(images_list.length).fill(0));
      }
      all_pixel_values.push(cat(images_tensor, 0));
    }
    const batch_size = all_pixel_values.length;
    const [n, c, h, w] = all_pixel_values[0].dims;
    let pixel_values;
    let pixel_attention_mask;
    if (batch_size === 1) {
      pixel_values = all_pixel_values[0].unsqueeze_(0);
      pixel_attention_mask = full([batch_size, n, h, w], true);
    } else {
      const max_num_patches = Math.max(...all_pixel_values.map((x) => x.dims.at(0)));
      pixel_attention_mask = full([batch_size, max_num_patches, h, w], true);
      const pixel_attention_mask_data = pixel_attention_mask.data;
      const pixel_attention_mask_stride = max_num_patches * h * w;
      for (let i = 0; i < batch_size; ++i) {
        const num_patches = all_pixel_values[i].dims[0];
        if (num_patches < max_num_patches) {
          all_pixel_values[i] = cat([all_pixel_values[i], full([max_num_patches - num_patches, c, h, w], 0)], 0);
          const start_offset = i * pixel_attention_mask_stride + num_patches * h * w;
          const end_offset = (i + 1) * pixel_attention_mask_stride;
          pixel_attention_mask_data.fill(false, start_offset, end_offset);
        }
      }
      pixel_values = stack(all_pixel_values, 0);
    }
    return {
      pixel_values,
      pixel_attention_mask,
      original_sizes,
      reshaped_input_sizes,
      ...return_row_col_info ? { rows: images_list_rows, cols: images_list_cols } : {}
    };
  }
  async split_image(pixel_values, { longest_edge }) {
    const max_height = longest_edge;
    const max_width = longest_edge;
    const frames = [];
    const [height, width] = pixel_values.dims.slice(-2);
    let num_splits_h = 0, num_splits_w = 0;
    if (height > max_height || width > max_width) {
      num_splits_h = Math.ceil(height / max_height);
      num_splits_w = Math.ceil(width / max_width);
      const optimal_height = Math.ceil(height / num_splits_h);
      const optimal_width = Math.ceil(width / num_splits_w);
      for (let r = 0; r < num_splits_h; ++r) {
        for (let c = 0; c < num_splits_w; ++c) {
          let start_x, start_y, end_x, end_y;
          if (r === num_splits_h - 1) {
            start_y = height - optimal_height;
            end_y = height;
          } else {
            start_y = r * optimal_height;
            end_y = (r + 1) * optimal_height;
          }
          if (c === num_splits_w - 1) {
            start_x = width - optimal_width;
            end_x = width;
          } else {
            start_x = c * optimal_width;
            end_x = (c + 1) * optimal_width;
          }
          const starts = [start_y, start_x];
          const ends = [end_y, end_x];
          const patch = await slice(pixel_values, starts, ends, [2, 3]);
          frames.push(patch);
        }
      }
      const global_image_height = max_height;
      const global_image_width = max_width;
      if (height !== global_image_height || width !== global_image_width) {
        pixel_values = await interpolate_4d(pixel_values, {
          size: [global_image_height, global_image_width]
        });
      }
    }
    frames.push(pixel_values);
    return { frames, num_splits_h, num_splits_w };
  }
};

// src/libs/transformers/models/janus/image_processing_janus.ts
var VLMImageProcessor = class extends ImageProcessor {
  constructor(config) {
    super({
      do_pad: true,
      pad_size: {
        width: config.image_size,
        height: config.image_size
      },
      ...config
    });
    this.constant_values = this.config.background_color.map((x) => x * this.rescale_factor);
  }
  pad_image(pixelData, imgDims, padSize, options) {
    return super.pad_image(pixelData, imgDims, padSize, {
      constant_values: this.constant_values,
      center: true,
      ...options
    });
  }
};

// src/libs/transformers/models/jina_clip/image_processing_jina_clip.ts
var JinaCLIPImageProcessor = class extends ImageProcessor {
  constructor(config) {
    const { resize_mode, fill_color, interpolation, size, ...other } = config;
    const new_size = resize_mode === "squash" ? { width: size, height: size } : resize_mode === "shortest" ? { shortest_edge: size } : { longest_edge: size };
    const resample = interpolation === "bicubic" ? 3 : 2;
    super({
      ...other,
      size: new_size,
      resample,
      do_center_crop: true,
      crop_size: size,
      do_normalize: true
    });
  }
};

// src/libs/transformers/models/llava_onevision/image_processing_llava_onevision.ts
var LlavaOnevisionImageProcessor = class extends ImageProcessor {
};

// src/libs/transformers/models/phi3_v/image_processing_phi3_v.ts
var IMAGE_SIZE = 336;
var SLICE_AXES = [2, 3];
var { ceil, floor, sqrt } = Math;
var Phi3VImageProcessor = class extends ImageProcessor {
  constructor(config) {
    super({
      ...config,
      do_normalize: true,
      do_pad: true,
      pad_size: "custom",
      do_convert_rgb: true,
      do_resize: true
      // Smart resizing "hd_transform"
    });
    this._num_crops = config.num_crops;
  }
  calc_num_image_tokens_from_image_size(width, height) {
    const { num_img_tokens } = this.config;
    return floor(
      (floor(height / IMAGE_SIZE) * floor(width / IMAGE_SIZE) + 1) * num_img_tokens + 1 + (floor(height / IMAGE_SIZE) + 1) * sqrt(num_img_tokens)
    );
  }
  /** @type {ImageProcessor['get_resize_output_image_size']} */
  get_resize_output_image_size(image, size) {
    const hd_num = this._num_crops;
    const [width, height] = image.size;
    let ratio = width / height;
    let scale = 1;
    while (scale * Math.ceil(scale / ratio) <= hd_num) {
      scale += 1;
    }
    scale -= 1;
    const new_w = Math.floor(scale * 336);
    const new_h = Math.floor(new_w / ratio);
    return [new_w, new_h];
  }
  /** @type {ImageProcessor['pad_image']} */
  pad_image(pixelData, imgDims, padSize, options = {}) {
    const [imageHeight, imageWidth] = imgDims;
    const height = IMAGE_SIZE * ceil(imageHeight / IMAGE_SIZE);
    const width = IMAGE_SIZE * ceil(imageWidth / IMAGE_SIZE);
    const constant_values = [1, 1, 1].map((x, i) => (x - this.image_mean[i]) / this.image_std[i]);
    return super.pad_image(
      pixelData,
      imgDims,
      { width, height },
      {
        center: true,
        constant_values,
        ...options
      }
    );
  }
  async _call(images, { num_crops = null } = {}) {
    this._num_crops = num_crops ?? (num_crops = this.config.num_crops);
    if (num_crops < 4 || sqrt(num_crops) % 1 !== 0) {
      throw new Error("num_crops must be a square number >= 4");
    }
    if (!Array.isArray(images)) {
      images = [images];
    }
    const num_images = images.length;
    const imageData = await Promise.all(images.map((x) => this.preprocess(x)));
    const original_sizes = imageData.map((x) => x.original_size);
    const reshaped_input_sizes = imageData.map((x) => x.reshaped_input_size);
    const all_pixel_values = [];
    for (const { pixel_values: pixel_values2 } of imageData) {
      pixel_values2.unsqueeze_(0);
      const [height, width] = pixel_values2.dims.slice(-2);
      const batch_pixel_values = await interpolate_4d(pixel_values2, {
        size: [IMAGE_SIZE, IMAGE_SIZE],
        mode: "bicubic"
      });
      if (num_crops > 0) {
        const patches = [];
        const sqrt_patches = sqrt(num_crops);
        const patch_width = floor(width / sqrt_patches);
        const patch_height = floor(height / sqrt_patches);
        for (let y = 0; y < sqrt_patches; ++y) {
          for (let x = 0; x < sqrt_patches; ++x) {
            let start_x, start_y, end_x, end_y;
            if (y === sqrt_patches - 1) {
              start_y = height - patch_height;
              end_y = height;
            } else {
              start_y = y * patch_height;
              end_y = (y + 1) * patch_height;
            }
            if (x === sqrt_patches - 1) {
              start_x = width - patch_width;
              end_x = width;
            } else {
              start_x = x * patch_width;
              end_x = (x + 1) * patch_width;
            }
            const starts = [start_y, start_x];
            const ends = [end_y, end_x];
            const patch = await slice(pixel_values2, starts, ends, SLICE_AXES);
            patches.push(patch);
          }
        }
        const resized_tensors = await interpolate_4d(cat(patches, 0), {
          size: [IMAGE_SIZE, IMAGE_SIZE],
          mode: "bicubic"
        });
        all_pixel_values.push(cat([batch_pixel_values, resized_tensors], 0));
      } else {
        all_pixel_values.push(batch_pixel_values);
      }
    }
    const pixel_values = stack(all_pixel_values, 0);
    const sizes = reshaped_input_sizes.map((x) => x.map((y) => IMAGE_SIZE * ceil(y / IMAGE_SIZE)));
    const image_sizes = new Tensor3("int64", sizes.flat(), [num_images, 2]);
    const num_img_tokens = sizes.map(([height, width]) => this.calc_num_image_tokens_from_image_size(width, height));
    return { pixel_values, original_sizes, reshaped_input_sizes, image_sizes, num_img_tokens };
  }
};

// src/libs/transformers/models/qwen2_vl/image_processing_qwen2_vl.ts
var Qwen2VLImageProcessor = class extends ImageProcessor {
  async _call(images, ...args) {
    const { pixel_values, original_sizes, reshaped_input_sizes } = await super._call(images, ...args);
    let patches = pixel_values;
    const { temporal_patch_size, merge_size, patch_size } = this.config;
    if (patches.dims[0] === 1) {
      patches = cat(
        Array.from({ length: temporal_patch_size }, () => patches),
        0
      );
    }
    const grid_t = patches.dims[0] / temporal_patch_size;
    const channel = patches.dims[1];
    const grid_h = Math.floor(patches.dims[2] / patch_size);
    const grid_w = Math.floor(patches.dims[3] / patch_size);
    const flatten_patches = patches.view(
      grid_t,
      temporal_patch_size,
      channel,
      Math.floor(grid_h / merge_size),
      merge_size,
      patch_size,
      Math.floor(grid_w / merge_size),
      merge_size,
      patch_size
    ).permute(0, 3, 6, 4, 7, 2, 1, 5, 8).view(grid_t * grid_h * grid_w, channel * temporal_patch_size * patch_size * patch_size);
    const image_grid_thw = new Tensor3("int64", [grid_t, grid_h, grid_w], [1, 3]);
    return {
      pixel_values: flatten_patches,
      image_grid_thw,
      original_sizes,
      reshaped_input_sizes
    };
  }
};

// src/libs/transformers/models/sam/image_processing_sam.ts
var SamImageProcessor = class extends ImageProcessor {
  /**
   *
   * @param {any} input_points
   * @param {import("../../base/image_processors_utils.js").HeightWidth[]} original_sizes
   * @param {import("../../base/image_processors_utils.js").HeightWidth[]} reshaped_input_sizes
   * @returns {Tensor}
   */
  reshape_input_points(input_points, original_sizes, reshaped_input_sizes, is_bounding_box = false) {
    input_points = structuredClone(input_points);
    let shape = calculateDimensions(input_points);
    if (shape.length === 3) {
      if (!is_bounding_box) {
        shape = [1, ...shape];
      }
      input_points = [input_points];
    } else if (shape.length !== 4) {
      throw Error(
        "The input_points must be a 4D tensor of shape `batch_size`, `point_batch_size`, `nb_points_per_image`, `2`."
      );
    }
    for (let i = 0; i < input_points.length; ++i) {
      let originalImageSize = original_sizes[i];
      let reshapedImageSize = reshaped_input_sizes[i];
      let resizeFactors = [reshapedImageSize[0] / originalImageSize[0], reshapedImageSize[1] / originalImageSize[1]];
      for (let j = 0; j < input_points[i].length; ++j) {
        for (let k = 0; k < input_points[i][j].length; ++k) {
          for (let w = 0; w < input_points[i][j][k].length; ++w) {
            input_points[i][j][k][w] *= resizeFactors[w % 2];
          }
        }
      }
    }
    return new Tensor3("float32", Float32Array.from(input_points.flat(Infinity)), shape);
  }
  /**
   *
   * @param {any} input_labels
   * @param {Tensor} input_points
   * @returns {Tensor}
   */
  add_input_labels(input_labels, input_points) {
    let shape = calculateDimensions(input_labels);
    if (shape.length === 2) {
      shape = [1, ...shape];
      input_labels = [input_labels];
    } else if (shape.length !== 3) {
      throw Error(
        "The input_points must be a 4D tensor of shape `batch_size`, `point_batch_size`, `nb_points_per_image`, `2`."
      );
    }
    if (shape.some((x, i) => x !== input_points.dims[i])) {
      throw Error(`The first ${shape.length} dimensions of 'input_points' and 'input_labels' must be the same.`);
    }
    return new Tensor3("int64", input_labels.flat(Infinity).map(BigInt), shape);
  }
  /**
   * @param {any[]} images The URL(s) of the image(s) to extract features from.
   * @param {Object} [options] Additional options for the processor.
   * @param {any} [options.input_points=null] A 3D or 4D array, representing the input points provided by the user.
   * - 3D: `[point_batch_size, nb_points_per_image, 2]`. In this case, `batch_size` is assumed to be 1.
   * - 4D: `[batch_size, point_batch_size, nb_points_per_image, 2]`.
   * @param {any} [options.input_labels=null] A 2D or 3D array, representing the input labels for the points, used by the prompt encoder to encode the prompt.
   * - 2D: `[point_batch_size, nb_points_per_image]`. In this case, `batch_size` is assumed to be 1.
   * - 3D: `[batch_size, point_batch_size, nb_points_per_image]`.
   * @param {number[][][]} [options.input_boxes=null] A 3D array of shape `(batch_size, num_boxes, 4)`, representing the input boxes provided by the user.
   * This is used by the prompt encoder to encode the prompt. Generally yields to much better generated masks.
   * The processor will generate a tensor, with each dimension corresponding respectively to the image batch size,
   * the number of boxes per image and the coordinates of the top left and botton right point of the box.
   * In the order (`x1`, `y1`, `x2`, `y2`):
   * - `x1`: the x coordinate of the top left point of the input box
   * - `y1`: the y coordinate of the top left point of the input box
   * - `x2`: the x coordinate of the bottom right point of the input box
   * - `y2`: the y coordinate of the bottom right point of the input box
   * @returns {Promise<SamImageProcessorResult>}
   */
  async _call(images, { input_points = null, input_labels = null, input_boxes = null } = {}) {
    const processed = await super._call(images);
    if (input_points) {
      processed.input_points = this.reshape_input_points(
        input_points,
        processed.original_sizes,
        processed.reshaped_input_sizes
      );
    }
    if (input_labels) {
      if (!processed.input_points) {
        throw Error("`input_points` must be provided if `input_labels` are provided.");
      }
      processed.input_labels = this.add_input_labels(input_labels, processed.input_points);
    }
    if (input_boxes) {
      processed.input_boxes = this.reshape_input_points(
        input_boxes,
        processed.original_sizes,
        processed.reshaped_input_sizes,
        true
      );
    }
    return processed;
  }
  /**
   * Remove padding and upscale masks to the original image size.
   * @param {Tensor} masks Batched masks from the mask_decoder in (batch_size, num_channels, height, width) format.
   * @param {[number, number][]} original_sizes The original sizes of each image before it was resized to the model's expected input shape, in (height, width) format.
   * @param {[number, number][]} reshaped_input_sizes The size of each image as it is fed to the model, in (height, width) format. Used to remove padding.
   * @param {Object} options Optional parameters for post-processing.
   * @param {number} [options.mask_threshold] The threshold to use for binarizing the masks.
   * @param {boolean} [options.binarize] Whether to binarize the masks.
   * @param {Object} [options.pad_size] The target size the images were padded to before being passed to the model. If `null`, the target size is assumed to be the processor's `pad_size`.
   * @param {number} [options.pad_size.height] The height the images were padded to.
   * @param {number} [options.pad_size.width] The width the images were padded to.
   * @returns {Promise<Tensor[]>} Batched masks in batch_size, num_channels, height, width) format, where (height, width) is given by original_size.
   */
  async post_process_masks(masks, original_sizes, reshaped_input_sizes, { mask_threshold = 0, binarize = true, pad_size = null } = {}) {
    const output_masks = [];
    pad_size = pad_size ?? this.pad_size;
    const target_image_size = [pad_size.height, pad_size.width];
    for (let i = 0; i < original_sizes.length; ++i) {
      const original_size = original_sizes[i];
      const reshaped_input_size = reshaped_input_sizes[i];
      let interpolated_mask = await interpolate_4d(masks[i], { mode: "bilinear", size: target_image_size });
      interpolated_mask = interpolated_mask.slice(null, null, [0, reshaped_input_size[0]], [0, reshaped_input_size[1]]);
      interpolated_mask = await interpolate_4d(interpolated_mask, { mode: "bilinear", size: original_size });
      if (binarize) {
        const data = interpolated_mask.data;
        const binarizedMaskData = new Uint8Array(data.length);
        for (let i2 = 0; i2 < data.length; ++i2) {
          if (data[i2] > mask_threshold) {
            binarizedMaskData[i2] = 1;
          }
        }
        interpolated_mask = new Tensor3("bool", binarizedMaskData, interpolated_mask.dims);
      }
      output_masks.push(interpolated_mask);
    }
    return output_masks;
  }
  /**
   * Generates a list of crop boxes of different sizes. Each layer has (2**i)**2 boxes for the ith layer.
   * @param {import("../../utils/image.js").RawImage} image Input original image
   * @param {number} target_size Target size of the resized image
   * @param {Object} options Options for generating crop boxes
   * @param {number} [options.crop_n_layers] If >0, mask prediction will be run again on crops of the image.
   * Sets the number of layers to run, where each layer has 2**i_layer number of image crops.
   * @param {number} [options.overlap_ratio] Sets the degree to which crops overlap. In the first crop layer,
   * crops will overlap by this fraction of the image length. Later layers with more crops scale down this overlap.
   * @param {number} [options.points_per_crop] Number of points to sample from each crop.
   * @param {number} [options.crop_n_points_downscale_factor] The number of points-per-side sampled in layer n is
   * scaled down by crop_n_points_downscale_factor**n.
   * @returns {Object} An object containing the crop boxes, number of points per crop, cropped images, and input labels.
   */
  generate_crop_boxes(image, target_size, { crop_n_layers = 0, overlap_ratio = 512 / 1500, points_per_crop = 32, crop_n_points_downscale_factor = 1 } = {}) {
  }
};

// src/libs/transformers/models/siglip/image_processing_siglip.ts
var SiglipImageProcessor = class extends ImageProcessor {
};

// src/libs/transformers/models/swin2sr/image_processing_swin2sr.ts
var Swin2SRImageProcessor = class extends ImageProcessor {
  pad_image(pixelData, imgDims, padSize, options = {}) {
    const [imageHeight, imageWidth, imageChannels] = imgDims;
    return super.pad_image(
      pixelData,
      imgDims,
      {
        // NOTE: For Swin2SR models, the original python implementation adds padding even when the image's width/height is already
        // a multiple of `pad_size`. However, this is most likely a bug (PR: https://github.com/mv-lab/swin2sr/pull/19).
        // For this reason, we only add padding when the image's width/height is not a multiple of `pad_size`.
        width: imageWidth + (padSize - imageWidth % padSize) % padSize,
        height: imageHeight + (padSize - imageHeight % padSize) % padSize
      },
      {
        mode: "symmetric",
        center: false,
        constant_values: -1,
        ...options
      }
    );
  }
};

// src/libs/transformers/models/vitmatte/image_processing_vitmatte.ts
var VitMatteImageProcessor = class extends ImageProcessor {
  /**
   * Calls the feature extraction process on an array of images, preprocesses
   * each image, and concatenates the resulting features into a single Tensor.
   * @param {import("../../utils/image.js").RawImage[]} images The image(s) to extract features from.
   * @param {import("../../utils/image.js").RawImage[]} trimaps The trimaps(s) to extract features from.
   * @returns {Promise<import("../../base/image_processors_utils.js").ImageProcessorResult>} An object containing the concatenated pixel values of the preprocessed images.
   */
  async _call(images, trimaps) {
    if (!Array.isArray(images)) {
      images = [images];
    }
    if (!Array.isArray(trimaps)) {
      trimaps = [trimaps];
    }
    const imageData = await Promise.all(images.map((x) => this.preprocess(x)));
    const trimapData = await Promise.all(
      trimaps.map(
        (x) => this.preprocess(x, {
          do_normalize: false,
          do_convert_rgb: false,
          do_convert_grayscale: true
        })
      )
    );
    const pixel_values = stack(
      imageData.map(
        // Concatenate images and trimaps
        (x, i) => cat([x.pixel_values, trimapData[i].pixel_values], 0)
      ),
      0
    );
    return {
      pixel_values,
      // Original sizes of images
      original_sizes: imageData.map((x) => x.original_size),
      // Reshaped sizes of images, before padding or cropping
      reshaped_input_sizes: imageData.map((x) => x.reshaped_input_size)
    };
  }
};

// src/libs/transformers/models/vitpose/image_processing_vitpose.ts
var VitPoseImageProcessor = class extends ImageProcessor {
  /**
   * Transform the heatmaps into keypoint predictions and transform them back to the image.
   * NOTE: This is a naive implementation and does not include advanced post-processing techniques,
   * so the results may not be as accurate as the original implementation.
   * @param {import('../../utils/tensor.js').Tensor} outputs The model outputs.
   * @param {[number, number, number, number][][]} boxes List or array of bounding boxes for each image.
   * Each box should be a list of 4 floats representing the bounding box coordinates in COCO format (top_left_x, top_left_y, width, height).
   * @returns {{
   *   bbox: [number, number, number, number],
   *   scores: number[],
   *   labels: number[],
   *   keypoints: [number, number][]
   * }[][]} List of keypoints predictions for each image.
   */
  post_process_pose_estimation(outputs, boxes, {
    threshold = null
    // TODO:
    // kernel_size = 11,
    // target_sizes = null,
  } = {}) {
    const heatmaps = outputs.tolist();
    const [batch_size, num_classes, height, width] = outputs.dims;
    const results = [];
    for (let b = 0; b < batch_size; ++b) {
      const heatmap = heatmaps[b];
      const bboxes = boxes[b];
      const batch_results = [];
      for (let n = 0; n < bboxes.length; ++n) {
        const bbox = bboxes[n];
        const keypoints = [];
        const scores = [];
        const labels = [];
        const xScale = bbox.at(-2) / width;
        const yScale = bbox.at(-1) / height;
        for (let c = 0; c < heatmap.length; ++c) {
          let [xWeightedSum, yWeightedSum] = [0, 0];
          let sum = 0;
          let score = -Infinity;
          const row = heatmap[c];
          for (let y = 0; y < row.length; ++y) {
            const col = row[y];
            for (let x = 0; x < col.length; ++x) {
              const value = col[x];
              sum += value;
              score = Math.max(score, value);
              xWeightedSum += (x + 0.5) * value;
              yWeightedSum += y * value;
            }
          }
          if (threshold != null && score < threshold) continue;
          const keypoint = [xScale * xWeightedSum / sum, yScale * yWeightedSum / sum];
          keypoints.push(keypoint);
          labels.push(c);
          scores.push(score);
        }
        batch_results.push({
          bbox,
          scores,
          labels,
          keypoints
        });
      }
      results.push(batch_results);
    }
    return results;
  }
};

// src/libs/transformers/models/auto/processing_auto.ts
var AutoProcessor = class {
  /** @type {typeof Processor.from_pretrained} */
  static async from_pretrained(pretrained_model_name_or_path, options = {}) {
    const preprocessorConfig = await getModelJSON(pretrained_model_name_or_path, IMAGE_PROCESSOR_NAME, true, options);
    const { image_processor_type, feature_extractor_type, processor_class } = preprocessorConfig;
    if (processor_class && processors_exports[processor_class]) {
      return processors_exports[processor_class].from_pretrained(
        pretrained_model_name_or_path,
        options
      );
    }
    if (!image_processor_type && !feature_extractor_type) {
      throw new Error("No `image_processor_type` or `feature_extractor_type` found in the config.");
    }
    const components = {};
    if (image_processor_type) {
      const image_processor_class = image_processors_exports[image_processor_type];
      if (!image_processor_class) {
        throw new Error(`Unknown image_processor_type: '${image_processor_type}'.`);
      }
      components.image_processor = new image_processor_class(preprocessorConfig);
    }
    if (feature_extractor_type) {
      const image_processor_class = image_processors_exports[feature_extractor_type];
      if (image_processor_class) {
        components.image_processor = new image_processor_class(preprocessorConfig);
      } else {
        const feature_extractor_class = feature_extractors_exports[feature_extractor_type];
        if (!feature_extractor_class) {
          throw new Error(`Unknown feature_extractor_type: '${feature_extractor_type}'.`);
        }
        components.feature_extractor = new feature_extractor_class(preprocessorConfig);
      }
    }
    const config = {};
    return new Processor(config, components);
  }
};

// src/libs/transformers/pipelines.ts
async function prepareImages(images) {
  if (!Array.isArray(images)) {
    images = [images];
  }
  return await Promise.all(images.map((x) => RawImage.read(x)));
}
async function prepareAudios(audios, sampling_rate) {
  if (!Array.isArray(audios)) {
    audios = [audios];
  }
  return await Promise.all(
    audios.map((x) => {
      if (typeof x === "string" || x instanceof URL) {
        return read_audio(x, sampling_rate);
      } else if (x instanceof Float64Array) {
        return new Float32Array(x);
      }
      return x;
    })
  );
}
var Pipeline = class extends Callable {
  constructor({
    task,
    model,
    tokenizer = null,
    processor = null
  }) {
    super();
    this.task = task;
    this.model = model;
    this.tokenizer = tokenizer;
    this.processor = processor;
  }
  async dispose() {
    await this.model.dispose();
  }
};
var TextGenerationPipeline = class extends /** @type {new (options: TextPipelineConstructorArgs) => TextGenerationPipelineType} */
Pipeline {
  /**
   * Create a new TextGenerationPipeline.
   * @param {TextPipelineConstructorArgs} options An object used to instantiate the pipeline.
   */
  constructor(options) {
    super(options);
  }
  /** @type {TextGenerationPipelineCallback} */
  async _call(texts, generate_kwargs = {}) {
    let isBatched = false;
    let isChatInput = false;
    if (typeof texts === "string") {
      texts = [texts];
    } else if (Array.isArray(texts) && texts.every((x) => typeof x === "string")) {
      isBatched = true;
    } else {
      if (isChat(texts)) {
        texts = [texts];
      } else if (Array.isArray(texts) && texts.every(isChat)) {
        isBatched = true;
      } else {
        throw new Error("Input must be a string, an array of strings, a Chat, or an array of Chats");
      }
      isChatInput = true;
      /** @type {string[]} */
      /** @type {Chat[]} */
      texts.map(
        (x) => this.tokenizer.apply_chat_template(x, {
          tokenize: false,
          add_generation_prompt: true
        })
      );
    }
    const add_special_tokens = generate_kwargs.add_special_tokens ?? false;
    const return_full_text = isChatInput ? false : generate_kwargs.return_full_text ?? true;
    this.tokenizer.padding_side = "left";
    const model_inputs = this.tokenizer(texts, {
      add_special_tokens,
      padding: true,
      truncation: true
    });
    const outputTokenIds = (
      /** @type {Tensor} */
      await this.model.generate({
        ...model_inputs,
        ...generate_kwargs
      })
    );
    const decoded = this.tokenizer.batch_decode(outputTokenIds, {
      skip_special_tokens: true
    });
    let promptLengths;
    if (!return_full_text && model_inputs.input_ids.dims.at(-1) > 0) {
      promptLengths = this.tokenizer.batch_decode(model_inputs.input_ids, {
        skip_special_tokens: true
      }).map((x) => x.length);
    }
    const toReturn = Array.from({ length: texts.length }, (_) => []);
    for (let i = 0; i < decoded.length; ++i) {
      const textIndex = Math.floor(i / outputTokenIds.dims[0] * texts.length);
      if (promptLengths) {
        decoded[i] = decoded[i].slice(promptLengths[textIndex]);
      }
      toReturn[textIndex].push({
        generated_text: isChatInput ? [.../** @type {Chat[]} */
        texts[textIndex], { role: "assistant", content: decoded[i] }] : decoded[i]
      });
    }
    return !isBatched && toReturn.length === 1 ? toReturn[0] : toReturn;
  }
};
var FeatureExtractionPipeline = class extends /** @type {new (options: TextPipelineConstructorArgs) => FeatureExtractionPipelineType} */
Pipeline {
  /**
   * Create a new FeatureExtractionPipeline.
   * @param {TextPipelineConstructorArgs} options An object used to instantiate the pipeline.
   */
  constructor(options) {
    super(options);
  }
  /** @type {FeatureExtractionPipelineCallback} */
  async _call(texts, {
    pooling = (
      /** @type {'none'} */
      "none"
    ),
    normalize = false,
    quantize = false,
    precision = (
      /** @type {'binary'} */
      "binary"
    )
  } = {}) {
    const model_inputs = this.tokenizer(texts, {
      padding: true,
      truncation: true
    });
    const outputs = await this.model(model_inputs);
    let result = outputs.last_hidden_state ?? outputs.logits ?? outputs.token_embeddings;
    if (pooling === "none") ; else if (pooling === "mean") {
      result = mean_pooling(result, model_inputs.attention_mask);
    } else if (pooling === "cls") {
      result = result.slice(null, 0);
    } else {
      throw Error(`Pooling method '${pooling}' not supported.`);
    }
    if (normalize) {
      result = result.normalize(2, -1);
    }
    if (quantize) {
      result = quantize_embeddings(result, precision);
    }
    return result;
  }
};
var ImageFeatureExtractionPipeline = class extends /** @type {new (options: ImagePipelineConstructorArgs) => ImageFeatureExtractionPipelineType} */
Pipeline {
  /**
   * Create a new ImageFeatureExtractionPipeline.
   * @param {ImagePipelineConstructorArgs} options An object used to instantiate the pipeline.
   */
  constructor(options) {
    super(options);
  }
  /** @type {ImageFeatureExtractionPipelineCallback} */
  async _call(images, { pool = null } = {}) {
    const preparedImages = await prepareImages(images);
    const { pixel_values } = await this.processor(preparedImages);
    const outputs = await this.model({ pixel_values });
    let result;
    if (pool) {
      if (!("pooler_output" in outputs)) {
        throw Error(
          `No pooled output was returned. Make sure the model has a 'pooler' layer when using the 'pool' option.`
        );
      }
      result = outputs.pooler_output;
    } else {
      result = outputs.last_hidden_state ?? outputs.logits ?? outputs.image_embeds;
    }
    return result;
  }
};
var AutomaticSpeechRecognitionPipeline = class extends /** @type {new (options: TextAudioPipelineConstructorArgs) => AutomaticSpeechRecognitionPipelineType} */
Pipeline {
  /**
   * Create a new AutomaticSpeechRecognitionPipeline.
   * @param {TextAudioPipelineConstructorArgs} options An object used to instantiate the pipeline.
   */
  constructor(options) {
    super(options);
  }
  /** @type {AutomaticSpeechRecognitionPipelineCallback} */
  async _call(audio, kwargs = {}) {
    switch (this.model.config.model_type) {
      case "whisper":
        return this._call_whisper(audio, kwargs);
      case "wav2vec2":
      case "wav2vec2-bert":
      case "unispeech":
      case "unispeech-sat":
      case "hubert":
        return this._call_wav2vec2(audio, kwargs);
      case "moonshine":
        return this._call_moonshine(audio, kwargs);
      default:
        throw new Error(
          `AutomaticSpeechRecognitionPipeline does not support model type '${this.model.config.model_type}'.`
        );
    }
  }
  /**
   * @type {AutomaticSpeechRecognitionPipelineCallback}
   * @private
   */
  async _call_wav2vec2(audio, kwargs) {
    if (kwargs.language) {
      console.warn('`language` parameter is not yet supported for `wav2vec2` models, defaulting to "English".');
    }
    if (kwargs.task) {
      console.warn('`task` parameter is not yet supported for `wav2vec2` models, defaulting to "transcribe".');
    }
    const single = !Array.isArray(audio);
    if (single) {
      audio = [audio];
    }
    const sampling_rate = this.processor.feature_extractor.config.sampling_rate;
    const preparedAudios = await prepareAudios(audio, sampling_rate);
    const toReturn = [];
    for (const aud of preparedAudios) {
      const inputs = await this.processor(aud);
      const output = await this.model(inputs);
      const logits = output.logits[0];
      const predicted_ids = [];
      for (const item of logits) {
        predicted_ids.push(max(item.data)[1]);
      }
      const predicted_sentences = this.tokenizer.decode(predicted_ids);
      toReturn.push({ text: predicted_sentences });
    }
    return single ? toReturn[0] : toReturn;
  }
  /**
   * @type {AutomaticSpeechRecognitionPipelineCallback}
   * @private
   */
  async _call_whisper(audio, kwargs) {
    const return_timestamps = kwargs.return_timestamps ?? false;
    const chunk_length_s = kwargs.chunk_length_s ?? 0;
    const force_full_sequences = kwargs.force_full_sequences ?? false;
    let stride_length_s = kwargs.stride_length_s ?? null;
    const generation_config = { ...kwargs };
    if (return_timestamps === "word") {
      generation_config["return_token_timestamps"] = true;
      generation_config["return_timestamps"] = false;
    }
    const single = !Array.isArray(audio);
    if (single) {
      audio = [audio];
    }
    const time_precision = this.processor.feature_extractor.config.chunk_length / this.model.config.max_source_positions;
    const hop_length = this.processor.feature_extractor.config.hop_length;
    const sampling_rate = this.processor.feature_extractor.config.sampling_rate;
    const preparedAudios = await prepareAudios(audio, sampling_rate);
    const toReturn = [];
    for (const aud of preparedAudios) {
      let chunks = [];
      if (chunk_length_s > 0) {
        if (stride_length_s === null) {
          stride_length_s = chunk_length_s / 6;
        } else if (chunk_length_s <= stride_length_s) {
          throw Error("`chunk_length_s` must be larger than `stride_length_s`.");
        }
        const window2 = sampling_rate * chunk_length_s;
        const stride = sampling_rate * stride_length_s;
        const jump = window2 - 2 * stride;
        let offset = 0;
        while (true) {
          const offset_end = offset + window2;
          const subarr = aud.subarray(offset, offset_end);
          const feature = await this.processor(subarr);
          const is_first = offset === 0;
          const is_last = offset_end >= aud.length;
          chunks.push({
            stride: [subarr.length, is_first ? 0 : stride, is_last ? 0 : stride],
            input_features: feature.input_features,
            is_last
          });
          if (is_last) break;
          offset += jump;
        }
      } else {
        chunks = [
          {
            stride: [aud.length, 0, 0],
            input_features: (await this.processor(aud)).input_features,
            is_last: true,
            tokens: []
          }
        ];
      }
      for (const chunk of chunks) {
        generation_config.num_frames = Math.floor(chunk.stride[0] / hop_length);
        const data = await this.model.generate({
          inputs: chunk.input_features,
          ...generation_config
        });
        if (return_timestamps === "word") {
          chunk.tokens = data.sequences.tolist()[0];
          chunk.token_timestamps = data.token_timestamps.tolist()[0].map((x) => round(x, 2));
        } else {
          chunk.tokens = data[0].tolist();
        }
        chunk.stride = chunk.stride.map((x) => x / sampling_rate);
      }
      const [full_text, optional] = this.tokenizer._decode_asr(chunks, {
        time_precision,
        return_timestamps,
        force_full_sequences
      });
      toReturn.push({ text: full_text, ...optional });
    }
    return single ? toReturn[0] : toReturn;
  }
  /**
   * @type {AutomaticSpeechRecognitionPipelineCallback}
   * @private
   */
  async _call_moonshine(audio, kwargs) {
    const single = !Array.isArray(audio);
    if (single) {
      audio = [audio];
    }
    const sampling_rate = this.processor.feature_extractor.config.sampling_rate;
    const preparedAudios = await prepareAudios(audio, sampling_rate);
    const toReturn = [];
    for (const aud of preparedAudios) {
      const inputs = await this.processor(aud);
      const max_new_tokens = Math.floor(aud.length / sampling_rate) * 6;
      const outputs = await this.model.generate({ max_new_tokens, ...kwargs, ...inputs });
      const text = this.processor.batch_decode(
        /** @type {Tensor} */
        outputs,
        {
          skip_special_tokens: true
        }
      )[0];
      toReturn.push({ text });
    }
    return single ? toReturn[0] : toReturn;
  }
};
var ImageToTextPipeline = class extends /** @type {new (options: TextImagePipelineConstructorArgs) => ImageToTextPipelineType} */
Pipeline {
  /**
   * Create a new ImageToTextPipeline.
   * @param {TextImagePipelineConstructorArgs} options An object used to instantiate the pipeline.
   */
  constructor(options) {
    super(options);
  }
  /** @type {ImageToTextPipelineCallback} */
  async _call(images, generate_kwargs = {}) {
    const isBatched = Array.isArray(images);
    const preparedImages = await prepareImages(images);
    const { pixel_values } = await this.processor(preparedImages);
    const toReturn = [];
    for (const batch of pixel_values) {
      batch.dims = [1, ...batch.dims];
      const output = await this.model.generate({ inputs: batch, ...generate_kwargs });
      const decoded = this.tokenizer.batch_decode(
        /** @type {Tensor} */
        output,
        {
          skip_special_tokens: true
        }
      ).map((x) => ({ generated_text: x.trim() }));
      toReturn.push(decoded);
    }
    return isBatched ? toReturn : toReturn[0];
  }
};
var TextToAudioPipeline = class extends /** @type {new (options: TextToAudioPipelineConstructorArgs) => TextToAudioPipelineType} */
Pipeline {
  constructor(options) {
    super(options);
    this.DEFAULT_VOCODER_ID = "Xenova/speecht5_hifigan";
    this.vocoder = options.vocoder ?? null;
  }
  /** @type {TextToAudioPipelineCallback} */
  async _call(text_inputs, { speaker_embeddings = null }) {
    if (this.processor) {
      return this._call_text_to_spectrogram(text_inputs, { speaker_embeddings });
    } else {
      console.log("No Processor found, running waveform");
      return this._call_text_to_waveform(text_inputs);
    }
  }
  async _call_text_to_waveform(text_inputs) {
    const inputs = this.tokenizer(text_inputs, {
      padding: true,
      truncation: true
    });
    const { waveform } = await this.model(inputs);
    const sampling_rate = this.model.config.sampling_rate;
    return new RawAudio(
      waveform.data,
      sampling_rate
    );
  }
  async _call_text_to_spectrogram(text_inputs, { speaker_embeddings = null }) {
    if (!this.vocoder) {
      console.log("No vocoder specified, using default HifiGan vocoder.");
      this.vocoder = await AutoModel.from_pretrained(this.DEFAULT_VOCODER_ID, { dtype: "fp32" });
    }
    if (typeof speaker_embeddings === "string" || speaker_embeddings instanceof URL) {
      speaker_embeddings = new Float32Array(await (await fetch(speaker_embeddings)).arrayBuffer());
    }
    if (speaker_embeddings instanceof Float32Array) {
      speaker_embeddings = new Tensor3("float32", speaker_embeddings, [1, speaker_embeddings.length]);
    } else if (!(speaker_embeddings instanceof Tensor3)) {
      throw new Error("Speaker embeddings must be a `Tensor`, `Float32Array`, `string`, or `URL`.");
    }
    const { input_ids } = this.tokenizer(text_inputs, {
      padding: true,
      truncation: true
    });
    const { waveform } = await this.model.generate_speech(input_ids, speaker_embeddings, {
      vocoder: this.vocoder
    });
    const sampling_rate = this.processor.feature_extractor.config.sampling_rate;
    return new RawAudio(
      waveform.data,
      sampling_rate
    );
  }
};
var ImageToImagePipeline = class extends /** @type {new (options: ImagePipelineConstructorArgs) => ImageToImagePipelineType} */
Pipeline {
  constructor(options) {
    super(options);
    this.processor = options.processor;
  }
  /** @type {ImageToImagePipelineCallback} */
  async _call(images) {
    const preparedImages = await prepareImages(images);
    const inputs = await this.processor(preparedImages);
    const outputs = await this.model(inputs);
    const toReturn = [];
    for (const batch of outputs.reconstruction) {
      const output = batch.squeeze().clamp_(0, 1).mul_(255).round_().to("uint8");
      toReturn.push(RawImage.fromTensor(output));
    }
    return toReturn.length > 1 ? toReturn : toReturn[0];
  }
};
var SUPPORTED_TASKS = {
  "text-generation": {
    tokenizer: AutoTokenizer,
    pipeline: TextGenerationPipeline,
    model: AutoModelForCausalLM,
    default: {
      // TODO: replace with original
      // "model": "gpt2",
      model: "Xenova/gpt2"
    },
    type: "text"
  },
  "automatic-speech-recognition": {
    tokenizer: AutoTokenizer,
    pipeline: AutomaticSpeechRecognitionPipeline,
    model: [AutoModelForSpeechSeq2Seq, AutoModelForCTC],
    processor: AutoProcessor,
    default: {
      // TODO: replace with original
      // "model": "openai/whisper-tiny.en",
      model: "Xenova/whisper-tiny.en"
    },
    type: "multimodal"
  },
  "text-to-audio": {
    tokenizer: AutoTokenizer,
    pipeline: TextToAudioPipeline,
    model: [AutoModelForTextToWaveform, AutoModelForTextToSpectrogram],
    processor: [
      AutoProcessor,
      /* Some don't use a processor */
      null
    ],
    default: {
      // TODO: replace with original
      // "model": "microsoft/speecht5_tts",
      model: "Xenova/speecht5_tts"
    },
    type: "text"
  },
  "image-to-text": {
    tokenizer: AutoTokenizer,
    pipeline: ImageToTextPipeline,
    model: AutoModelForVision2Seq,
    processor: AutoProcessor,
    default: {
      // TODO: replace with original
      // "model": "nlpconnect/vit-gpt2-image-captioning",
      model: "Xenova/vit-gpt2-image-captioning"
    },
    type: "multimodal"
  },
  "image-to-image": {
    // no tokenizer
    pipeline: ImageToImagePipeline,
    model: AutoModelForImageToImage,
    processor: AutoProcessor,
    default: {
      // TODO: replace with original
      // "model": "caidas/swin2SR-classical-sr-x2-64",
      model: "Xenova/swin2SR-classical-sr-x2-64"
    },
    type: "image"
  },
  // This task serves as a useful interface for dealing with sentence-transformers (https://huggingface.co/sentence-transformers).
  "feature-extraction": {
    tokenizer: AutoTokenizer,
    pipeline: FeatureExtractionPipeline,
    model: AutoModel,
    default: {
      // TODO: replace with original
      // "model": "sentence-transformers/all-MiniLM-L6-v2",
      model: "Xenova/all-MiniLM-L6-v2"
    },
    type: "text"
  },
  "image-feature-extraction": {
    processor: AutoProcessor,
    pipeline: ImageFeatureExtractionPipeline,
    model: [AutoModelForImageFeatureExtraction, AutoModel],
    default: {
      // TODO: replace with original
      // "model": "google/vit-base-patch16-224",
      model: "Xenova/vit-base-patch16-224-in21k"
    },
    type: "image"
  }
};
var TASK_ALIASES = {
  asr: "automatic-speech-recognition",
  "text-to-speech": "text-to-audio",
  // Add for backwards compatibility
  embeddings: "feature-extraction"
};
async function pipeline(task, model = null, {
  progress_callback = null,
  config = null,
  cache_dir = null,
  local_files_only = false,
  revision = "main",
  device = null,
  dtype = null,
  model_file_name = null,
  session_options = {}
} = {}) {
  task = TASK_ALIASES[task] ?? task;
  const pipelineInfo = SUPPORTED_TASKS[task.split("_", 1)[0]];
  if (!pipelineInfo) {
    throw Error(`Unsupported pipeline: ${task}. Must be one of [${Object.keys(SUPPORTED_TASKS)}]`);
  }
  if (!model) {
    model = pipelineInfo.default.model;
    console.log(`No model specified. Using default model: "${model}".`);
  }
  const pretrainedOptions = {
    progress_callback,
    config,
    cache_dir,
    local_files_only,
    revision,
    device,
    dtype,
    model_file_name,
    session_options
  };
  const classes = /* @__PURE__ */ new Map([
    ["tokenizer", pipelineInfo.tokenizer],
    ["model", pipelineInfo.model],
    ["processor", pipelineInfo.processor]
  ]);
  const results = await loadItems(classes, model, pretrainedOptions);
  results.task = task;
  dispatchCallback(progress_callback, {
    status: "ready",
    task,
    model
  });
  const pipelineClass = pipelineInfo.pipeline;
  return new pipelineClass(results);
}
async function loadItems(mapping, model, pretrainedOptions) {
  const result = /* @__PURE__ */ Object.create(null);
  const promises = [];
  for (const [name, cls] of mapping.entries()) {
    if (!cls) continue;
    let promise;
    if (Array.isArray(cls)) {
      promise = new Promise(async (resolve, reject) => {
        let e;
        for (const c of cls) {
          if (c === null) {
            resolve(null);
            return;
          }
          try {
            resolve(await c.from_pretrained(model, pretrainedOptions));
            return;
          } catch (err) {
            if (err.message?.includes("Unsupported model type")) {
              e = err;
            } else if (err.message?.includes("Could not locate file")) {
              e = err;
            } else {
              reject(err);
              return;
            }
          }
        }
        reject(e);
      });
    } else {
      promise = cls.from_pretrained(model, pretrainedOptions);
    }
    result[name] = promise;
    promises.push(promise);
  }
  await Promise.all(promises);
  for (const [name, promise] of Object.entries(result)) {
    result[name] = await promise;
  }
  return result;
}
function isChat(obj) {
  return Array.isArray(obj) && obj.every((x) => typeof x === "object" && "role" in x && "content" in x);
}

// src/libs/transformers/generation/streamers.ts
apis.IS_PROCESS_AVAILABLE ? (x) => process.stdout.write(x) : (x) => console.log(x);
function split(text, regex) {
  const result = [];
  let prev = 0;
  for (const match of text.matchAll(regex)) {
    const fullMatch = match[0];
    if (prev < match.index) {
      result.push({ match: false, text: text.slice(prev, match.index) });
    }
    if (fullMatch.length > 0) {
      result.push({ match: true, text: fullMatch });
    }
    prev = match.index + fullMatch.length;
  }
  if (prev < text.length) {
    result.push({ match: false, text: text.slice(prev) });
  }
  return result;
}
function split_num(match) {
  if (match.includes(".")) {
    return match;
  } else if (match.includes(":")) {
    let [h, m] = match.split(":").map(Number);
    if (m === 0) {
      return `${h} o'clock`;
    } else if (m < 10) {
      return `${h} oh ${m}`;
    }
    return `${h} ${m}`;
  }
  let year = parseInt(match.slice(0, 4), 10);
  if (year < 1100 || year % 1e3 < 10) {
    return match;
  }
  let left = match.slice(0, 2);
  let right = parseInt(match.slice(2, 4), 10);
  let suffix = match.endsWith("s") ? "s" : "";
  if (year % 1e3 >= 100 && year % 1e3 <= 999) {
    if (right === 0) {
      return `${left} hundred${suffix}`;
    } else if (right < 10) {
      return `${left} oh ${right}${suffix}`;
    }
  }
  return `${left} ${right}${suffix}`;
}
function flip_money(match) {
  const bill = match[0] === "$" ? "dollar" : "pound";
  if (isNaN(Number(match.slice(1)))) {
    return `${match.slice(1)} ${bill}s`;
  } else if (!match.includes(".")) {
    let suffix = match.slice(1) === "1" ? "" : "s";
    return `${match.slice(1)} ${bill}${suffix}`;
  }
  const [b, c] = match.slice(1).split(".");
  const d = parseInt(c.padEnd(2, "0"), 10);
  let coins = match[0] === "$" ? d === 1 ? "cent" : "cents" : d === 1 ? "penny" : "pence";
  return `${b} ${bill}${b === "1" ? "" : "s"} and ${d} ${coins}`;
}
function point_num(match) {
  let [a, b] = match.split(".");
  return `${a} point ${b.split("").join(" ")}`;
}
function normalize_text(text) {
  return text.replace(/[‘’]/g, "'").replace(/«/g, "\u201C").replace(/»/g, "\u201D").replace(/[“”]/g, '"').replace(/\(/g, "\xAB").replace(/\)/g, "\xBB").replace(/、/g, ", ").replace(/。/g, ". ").replace(/！/g, "! ").replace(/，/g, ", ").replace(/：/g, ": ").replace(/；/g, "; ").replace(/？/g, "? ").replace(/[^\S \n]/g, " ").replace(/  +/, " ").replace(/(?<=\n) +(?=\n)/g, "").replace(/\bD[Rr]\.(?= [A-Z])/g, "Doctor").replace(/\b(?:Mr\.|MR\.(?= [A-Z]))/g, "Mister").replace(/\b(?:Ms\.|MS\.(?= [A-Z]))/g, "Miss").replace(/\b(?:Mrs\.|MRS\.(?= [A-Z]))/g, "Mrs").replace(/\betc\.(?! [A-Z])/gi, "etc").replace(/\b(y)eah?\b/gi, "$1e'a").replace(/\d*\.\d+|\b\d{4}s?\b|(?<!:)\b(?:[1-9]|1[0-2]):[0-5]\d\b(?!:)/g, split_num).replace(/(?<=\d),(?=\d)/g, "").replace(/[$£]\d+(?:\.\d+)?(?: hundred| thousand| (?:[bm]|tr)illion)*\b|[$£]\d+\.\d\d?\b/gi, flip_money).replace(/\d*\.\d+/g, point_num).replace(/(?<=\d)-(?=\d)/g, " to ").replace(/(?<=\d)S/g, " S").replace(/(?<=[BCDFGHJ-NP-TV-Z])'?s\b/g, "'S").replace(/(?<=X')S\b/g, "s").replace(/(?:[A-Za-z]\.){2,} [a-z]/g, (m) => m.replace(/\./g, "-")).replace(/(?<=[A-Z])\.(?=[A-Z])/gi, "-").trim();
}
function escapeRegExp2(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
var PUNCTUATION = ';:,.!?\xA1\xBF\u2014\u2026"\xAB\xBB\u201C\u201D(){}[]';
var PUNCTUATION_PATTERN = new RegExp(`(\\s*[${escapeRegExp2(PUNCTUATION)}]+\\s*)+`, "g");
async function phonemize(text, language = "a", norm = true) {
  if (norm) {
    text = normalize_text(text);
  }
  const sections = split(text, PUNCTUATION_PATTERN);
  const lang = language === "a" ? "en-us" : "en";
  const ps = (await Promise.all(sections.map(async ({ match, text: text2 }) => match ? text2 : (await phonemize$1(text2, lang)).join(" ")))).join("");
  let processed = ps.replace(/kəkˈoːɹoʊ/g, "k\u02C8o\u028Ak\u0259\u0279o\u028A").replace(/kəkˈɔːɹəʊ/g, "k\u02C8\u0259\u028Ak\u0259\u0279\u0259\u028A").replace(/ʲ/g, "j").replace(/r/g, "\u0279").replace(/x/g, "k").replace(/ɬ/g, "l").replace(/(?<=[a-zɹː])(?=hˈʌndɹɪd)/g, " ").replace(/ z(?=[;:,.!?¡¿—…"«»“” ]|$)/g, "z");
  if (language === "a") {
    processed = processed.replace(/(?<=nˈaɪn)ti(?!ː)/g, "di");
  }
  return processed.trim();
}

// src/libs/transformers/utils/voices.ts
var VOICES = Object.freeze({
  af: {
    // Default voice is a 50-50 mix of Bella & Sarah
    name: "Default",
    language: "en-us",
    gender: "Female"
  },
  af_bella: {
    name: "Bella",
    language: "en-us",
    gender: "Female"
  },
  af_nicole: {
    name: "Nicole",
    language: "en-us",
    gender: "Female"
  },
  af_sarah: {
    name: "Sarah",
    language: "en-us",
    gender: "Female"
  },
  af_sky: {
    name: "Sky",
    language: "en-us",
    gender: "Female"
  },
  am_adam: {
    name: "Adam",
    language: "en-us",
    gender: "Male"
  },
  am_michael: {
    name: "Michael",
    language: "en-us",
    gender: "Male"
  },
  bf_emma: {
    name: "Emma",
    language: "en-gb",
    gender: "Female"
  },
  bf_isabella: {
    name: "Isabella",
    language: "en-gb",
    gender: "Female"
  },
  bm_george: {
    name: "George",
    language: "en-gb",
    gender: "Male"
  },
  bm_lewis: {
    name: "Lewis",
    language: "en-gb",
    gender: "Male"
  }
});
var VOICE_DATA_URL = "https://huggingface.co/onnx-community/Kokoro-82M-ONNX/resolve/main/voices";
async function getVoiceFile(id) {
  const url2 = `${VOICE_DATA_URL}/${id}.bin`;
  let cache;
  try {
    cache = await caches.open("kokoro-voices");
    const cachedResponse = await cache.match(url2);
    if (cachedResponse) {
      return await cachedResponse.arrayBuffer();
    }
  } catch (e) {
    console.warn("Unable to open cache", e);
  }
  const response = await fetch(url2);
  const buffer = await response.arrayBuffer();
  if (cache) {
    try {
      await cache.put(
        url2,
        new Response(buffer, {
          headers: response.headers
        })
      );
    } catch (e) {
      console.warn("Unable to cache file", e);
    }
  }
  return buffer;
}
var VOICE_CACHE = /* @__PURE__ */ new Map();
async function getVoiceData(voice) {
  if (VOICE_CACHE.has(voice)) {
    return VOICE_CACHE.get(voice);
  }
  const buffer = new Float32Array(await getVoiceFile(voice));
  VOICE_CACHE.set(voice, buffer);
  return buffer;
}

// src/engines/tts-engine.ts
var STYLE_DIM = 256;
var SAMPLE_RATE = 24e3;
var TTSEngine = class {
  constructor() {
    this.model = null;
    this.tokenizer = null;
    this.model = null;
    this.tokenizer = null;
  }
  async loadModel(modelConfig, options = {}) {
    try {
      this.model = await StyleTextToSpeech2Model.from_pretrained(modelConfig.repo, {
        progress_callback: options.onProgress,
        dtype: options.dtype || "q4",
        device: "wasm"
      });
      this.tokenizer = await AutoTokenizer.from_pretrained(modelConfig.repo, {
        progress_callback: options.onProgress
      });
    } catch (error) {
      console.error("Error loading TTS model:", error);
      throw error;
    }
  }
  async generateSpeech(text, options = {}) {
    if (!this.model || !this.tokenizer) {
      throw new Error("TTS model not initialized");
    }
    const { voice = "af", speed = 1 } = options;
    if (!VOICES.hasOwnProperty(voice)) {
      console.error(`Voice "${voice}" not found. Available voices:`);
      console.table(VOICES);
      throw new Error(`Voice "${voice}" not found. Should be one of: ${Object.keys(VOICES).join(", ")}.`);
    }
    try {
      const language = voice.at(0);
      const phonemes = await phonemize(text, language);
      const { input_ids } = this.tokenizer(phonemes, {
        truncation: true
      });
      const num_tokens = Math.max(
        input_ids.dims.at(-1) - 2,
        // Without padding
        0
      );
      const data = await getVoiceData(voice);
      const offset = num_tokens * STYLE_DIM;
      const voiceData = data.slice(offset, offset + STYLE_DIM);
      const inputs = {
        input_ids,
        style: new Tensor3("float32", voiceData, [1, STYLE_DIM]),
        speed: new Tensor3("float32", [speed], [1])
      };
      const output = await this.model._call(inputs);
      if (!output || !output.waveform) {
        throw new Error("Model returned null or undefined waveform");
      }
      let audioData = new Float32Array(output.waveform.data);
      if (audioData.length === 0) {
        throw new Error("Generated audio data is empty");
      }
      const maxValue = Math.max(...audioData.map(Math.abs));
      if (maxValue > 0) {
        audioData = audioData.map((x) => x / maxValue);
      }
      const int16Array = new Int16Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        const s = Math.max(-1, Math.min(1, audioData[i]));
        int16Array[i] = s < 0 ? s * 32768 : s * 32767;
      }
      const wavHeader = createWAVHeader({
        numChannels: 1,
        sampleRate: SAMPLE_RATE,
        numSamples: int16Array.length
      });
      const wavBytes = new Uint8Array(44 + int16Array.byteLength);
      wavBytes.set(new Uint8Array(wavHeader), 0);
      wavBytes.set(new Uint8Array(int16Array.buffer), 44);
      return wavBytes.buffer;
    } catch (error) {
      console.error("Detailed error in generateSpeech:", error);
      throw error;
    }
  }
};
function createWAVHeader({ numChannels, sampleRate, numSamples }) {
  const buffer = new ArrayBuffer(44);
  const view = new DataView(buffer);
  writeString2(view, 0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString2(view, 8, "WAVE");
  writeString2(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString2(view, 36, "data");
  view.setUint32(40, numSamples * 2, true);
  return buffer;
}
function writeString2(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

// src/engines/transformer-engine-wrapper.ts
var TransformersEngineWrapper = class {
  constructor() {
    this.transformersPipeline = null;
    this.modelType = null;
    this.ttsEngine = null;
    this.transformersPipeline = null;
    this.modelType = null;
  }
  async loadModel(modelConfig, options = {}) {
    try {
      if (!modelConfig.modelType) {
        throw new Error('Model configuration missing required "modelType" property');
      }
      if (!modelConfig.repo) {
        throw new Error('Model configuration missing required "repo" property');
      }
      this.modelType = modelConfig.modelType;
      options.device = "webgpu";
      const pipelineOptions = {
        progress_callback: options.onProgress,
        ...options
      };
      if (modelConfig.modelType === "text-to-speech") {
        this.ttsEngine = new TTSEngine();
        console.log("Loading TTS model...");
        await this.ttsEngine.loadModel(modelConfig, options);
        console.log("TTS model loaded");
        return;
      }
      const pipelineType = modelConfig.pipeline;
      this.transformersPipeline = await pipeline(pipelineType, modelConfig.repo, pipelineOptions);
    } catch (error) {
      console.error("Error loading Transformers model:", error);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to load Transformers model "${modelConfig.modelName}": ${message}`);
    }
  }
  // Text generation specific method
  async generateText(input, options = {}) {
    if (!this.transformersPipeline || this.modelType !== "text-generation") {
      throw new Error("Text generation pipeline not initialized.");
    }
    let messages = Array.isArray(input) ? input : [];
    if (typeof input === "string") {
      if (options.system_prompt) {
        messages.push({ role: "system", content: options.system_prompt });
      }
      messages.push({ role: "user", content: input });
    }
    const prompt = messages.map((m) => `${m.role}: ${m.content}`).join("\n");
    const result = await this.transformersPipeline(prompt, {
      temperature: options.temperature ?? 0.1,
      max_new_tokens: options.max_new_tokens ?? 300,
      repetition_penalty: options.repetition_penalty,
      no_repeat_ngram_size: options.no_repeat_ngram_size,
      num_beams: options.num_beams,
      num_return_sequences: options.num_return_sequences,
      ...options
    });
    return result;
  }
  // Feature extraction specific method
  async extractFeatures(text, options = {}) {
    if (!this.transformersPipeline || this.modelType !== "feature-extraction") {
      throw new Error("Feature extraction pipeline not initialized.");
    }
    return await this.transformersPipeline(text, {
      pooling: options.pooling ?? "mean",
      normalize: options.normalize ?? true,
      ...options
    });
  }
  // Speech recognition specific method
  async transcribe(audioInput, options = {}) {
    if (!this.transformersPipeline || this.modelType !== "automatic-speech-recognition") {
      throw new Error("Speech recognition pipeline not initialized.");
    }
    const input = audioInput instanceof Blob ? new Float32Array(await audioInput.arrayBuffer()) : audioInput;
    const result = await this.transformersPipeline(input, {
      language: options.language,
      task: options.task,
      return_timestamps: options.return_timestamps,
      chunk_length_s: options.chunk_length_s,
      stride_length_s: options.stride_length_s,
      ...options
    });
    return result;
  }
  // Add text-to-speech method
  async textToSpeech(text, options = {}) {
    if (!this.ttsEngine) {
      throw new Error("Text-to-speech engine not initialized.");
    }
    try {
      return await this.ttsEngine.generateSpeech(text, options);
    } catch (error) {
      console.error("Error in text-to-speech:", error);
      throw error;
    }
  }
  // Generic method for backward compatibility
  async generate(prompt, options = {}) {
    switch (this.modelType) {
      case "text-generation":
        return this.generateText(prompt, options);
      case "feature-extraction":
        return this.extractFeatures(prompt, options);
      case "automatic-speech-recognition":
        return this.transcribe(prompt, options);
      case "text-to-speech":
        return this.textToSpeech(prompt);
      default:
        throw new Error(`Unsupported model type: ${this.modelType}`);
    }
  }
};

// src/config/models/mlc-models.json
var mlc_models_default = {
  "llama-3.2-1b-instruct": {
    engine: "mlc",
    modelName: "Llama-3.2-1b-Instruct",
    modelType: "text-generation",
    repo: "mlc-ai/Llama-3.2-1B-Instruct-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1",
      "q0f32",
      "q0f16"
    ],
    defaultQuantization: "q4f16_1",
    defaultParams: {
      temperature: 0.7,
      maxTokens: 2048
    },
    pipeline: "text-generation"
  },
  "smollm2-135m-instruct": {
    engine: "mlc",
    modelName: "SmolLM2-135M-Instruct",
    modelType: "text-generation",
    repo: "mlc-ai/SmolLM2-135M-Instruct-{quantization}-MLC",
    quantizations: [
      "q0f32",
      "q0f16"
    ],
    defaultQuantization: "q0f16",
    defaultParams: {
      temperature: 0.7,
      maxTokens: 2048
    },
    required_features: [
      "shader-f16"
    ],
    overrides: {
      context_window_size: 4096
    },
    pipeline: "text-generation"
  },
  "smollm2-360m-instruct": {
    engine: "mlc",
    modelName: "SmolLM2-360M-Instruct",
    modelType: "text-generation",
    repo: "mlc-ai/SmolLM2-360M-Instruct-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1",
      "q0f32",
      "q0f16"
    ],
    defaultQuantization: "q4f16_1",
    required_features: [
      "shader-f16"
    ],
    defaultParams: {
      temperature: 0.1,
      maxTokens: 2048
    },
    pipeline: "text-generation"
  },
  "smollm2-1.7b-instruct": {
    engine: "mlc",
    modelName: "SmolLM2-1.7B-Instruct",
    modelType: "text-generation",
    repo: "mlc-ai/SmolLM2-1.7B-Instruct-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation",
    defaultParams: {
      temperature: 0.1,
      maxTokens: 2048
    }
  },
  "qwen2.5-0.5b-instruct": {
    engine: "mlc",
    modelName: "Qwen2.5-0.5B-Instruct",
    modelType: "text-generation",
    repo: "mlc-ai/Qwen2.5-0.5B-Instruct-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1",
      "q0f32",
      "q0f16"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation"
  },
  "gemma-2b-it": {
    engine: "mlc",
    modelName: "gemma-2b-it",
    modelType: "text-generation",
    repo: "mlc-ai/gemma-2b-it-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation",
    required_features: [
      "shader-f16"
    ]
  },
  "tinyllama-1.1b-chat-v0.4": {
    engine: "mlc",
    modelName: "TinyLlama-1.1B-Chat-v0.4",
    modelType: "text-generation",
    repo: "mlc-ai/TinyLlama-1.1B-Chat-v0.4-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation"
  },
  "phi-3.5-mini-instruct": {
    engine: "mlc",
    modelName: "Phi-3.5-mini-instruct",
    modelType: "text-generation",
    repo: "mlc-ai/Phi-3.5-mini-instruct-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation"
  },
  "qwen2.5-1.5b-instruct": {
    engine: "mlc",
    modelName: "Qwen2.5-1.5B-Instruct",
    modelType: "text-generation",
    repo: "mlc-ai/Qwen2.5-1.5B-Instruct-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation"
  },
  "deepseek-r1-distill-qwen-1.5b": {
    engine: "mlc",
    modelName: "DeepSeek-R1-Distill-Qwen-1.5B-MLC",
    modelType: "text-generation",
    modelLibrary: "Qwen2-1.5B-Instruct-q4f32_1-ctx4k_cs1k-webgpu.wasm",
    repo: "mlc-ai/DeepSeek-R1-Distill-Qwen-1.5B-{quantization}-MLC",
    quantizations: [
      "q4f32_1"
    ],
    defaultQuantization: "q4f32_1",
    pipeline: "text-generation"
  },
  "deepseek-r1-distill-qwen-7b": {
    engine: "mlc",
    modelName: "DeepSeek-R1-Distill-Qwen-7B",
    modelType: "text-generation",
    repo: "mlc-ai/DeepSeek-R1-Distill-Qwen-7B-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation"
  },
  "deepseek-r1-distill-llama-8b": {
    engine: "mlc",
    modelName: "DeepSeek-R1-Distill-Llama-8B",
    modelType: "text-generation",
    repo: "mlc-ai/DeepSeek-R1-Distill-Llama-8B-{quantization}-MLC",
    quantizations: [
      "q4f16_1",
      "q4f32_1"
    ],
    defaultQuantization: "q4f16_1",
    pipeline: "text-generation"
  }
};

// src/config/models/transformers-models.json
var transformers_models_default = {
  "llama-3.2-1b-instruct": {
    engine: "transformers",
    modelName: "Llama-3.2-1b-Instruct",
    modelType: "text-generation",
    repo: "Xenova/llama-3.2-1b-instruct",
    pipeline: "text-generation",
    defaultParams: {
      temperature: 0.7,
      maxLength: 2048
    },
    defaultQuantization: "q4"
  },
  "whisper-tiny-en": {
    engine: "transformers",
    modelName: "whisper-tiny-en",
    modelType: "automatic-speech-recognition",
    repo: "onnx-community/whisper-tiny.en",
    pipeline: "automatic-speech-recognition",
    defaultQuantization: "q4",
    quantizations: ["q4", "fp16", "int4"]
  },
  "kokoro-tts": {
    engine: "transformers",
    modelName: "kokoro-tts",
    modelType: "text-to-speech",
    repo: "onnx-community/Kokoro-82M-ONNX",
    pipeline: "text-to-speech",
    defaultQuantization: "q4",
    quantizations: ["q4", "q8", "fp32", "fp16", "q4f16"],
    defaultParams: {
      language: "en"
    }
  }
};

// src/core/llm/index.ts
var MODEL_CONFIG = {
  ...mlc_models_default,
  ...transformers_models_default
};
var BrowserAI = class {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.modelIdentifier = null;
    this.ttsEngine = null;
    this.engine = null;
    this.currentModel = null;
  }
  async loadModel(modelIdentifier, options = {}) {
    this.modelIdentifier = modelIdentifier;
    const modelConfig = MODEL_CONFIG[this.modelIdentifier];
    if (!modelConfig) {
      throw new Error(`Model identifier "${this.modelIdentifier}" not recognized.`);
    }
    const mlcVersion = mlc_models_default[this.modelIdentifier];
    let engineToUse = modelConfig.engine;
    if (modelConfig.modelType === "text-generation" && mlcVersion) {
      engineToUse = "mlc";
    }
    switch (engineToUse) {
      case "mlc":
        this.engine = new MLCEngineWrapper();
        await this.engine.loadModel(mlcVersion || modelConfig, options);
        break;
      case "transformers":
        this.engine = new TransformersEngineWrapper();
        await this.engine.loadModel(modelConfig, options);
        break;
      default:
        throw new Error(`Engine "${engineToUse}" not supported.`);
    }
    this.currentModel = modelConfig;
  }
  async generateText(prompt, options = {}) {
    if (!this.engine) {
      throw new Error("No model loaded. Please call loadModel first.");
    }
    try {
      const result = await this.engine.generateText(prompt, options);
      return result;
    } catch (error) {
      console.error("Error generating text:", error);
      throw error;
    }
  }
  async transcribeAudio(audio, options = {}) {
    if (!this.engine) {
      throw new Error("No model loaded. Please call loadModel first.");
    }
    try {
      if (this.engine instanceof TransformersEngineWrapper) {
        if (audio instanceof Blob) {
          const audioContext = new AudioContext({
            sampleRate: 16e3
            // Force 16kHz sample rate
          });
          const arrayBuffer = await audio.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const float32Data = new Float32Array(Math.floor(audioBuffer.length));
          audioBuffer.copyFromChannel(float32Data, 0);
          audioContext.close();
          return await this.engine.transcribe(float32Data, options);
        }
        return await this.engine.transcribe(audio, options);
      } else {
        throw new Error("Engine does not support transcribe method.");
      }
    } catch (error) {
      console.error("Transcription error:", error);
      throw error;
    }
  }
  async startRecording() {
    if (this.mediaRecorder) {
      throw new Error("Recording already in progress");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.mediaRecorder = new MediaRecorder(stream);
    this.audioChunks = [];
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };
    this.mediaRecorder.start();
  }
  async stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") {
        reject(new Error("No recording in progress"));
        return;
      }
      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: "audio/webm" });
        this.audioChunks = [];
        this.mediaRecorder = null;
        resolve(audioBlob);
      };
      this.mediaRecorder.stop();
    });
  }
  async generateResponse(text) {
    if (!this.modelIdentifier) {
      throw new Error("No model loaded. Please call loadModel first.");
    }
    if (this.currentModel?.modelName !== this.modelIdentifier) {
      await this.loadModel(this.modelIdentifier);
    }
    const response = await this.generateText(text);
    return response;
  }
  async textToSpeech(text, options = {}) {
    if (!this.ttsEngine) {
      this.ttsEngine = new TTSEngine();
      await this.ttsEngine.loadModel(MODEL_CONFIG["kokoro-tts"], {
        quantized: true,
        device: "webgpu",
        ...options
      });
    }
    try {
      const audioData = await this.ttsEngine.generateSpeech(text);
      return audioData;
    } catch (error) {
      console.error("Error generating speech:", error);
      throw error;
    }
  }
};

// src/core/database/implementation/indexeddb.ts
var IndexedDBStorage = class {
  constructor(config) {
    this.db = null;
    this.dbName = config.databaseName;
    this.version = config.version || 1;
    this.storeName = "defaultStore";
  }
  async openDatabase() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }
      const request = indexedDB.open(this.dbName, this.version);
      request.onerror = (event) => {
        reject(`Database error: ${event.target.error}`);
      };
      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: "id" });
        }
      };
    });
  }
  async store(data) {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.add(data);
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error storing data: ${event.target.error}`);
    });
  }
  async get(id) {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);
      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => reject(`Error getting data: ${event.target.error}`);
    });
  }
  async getAll() {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();
      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => reject(`Error getting data: ${event.target.error}`);
    });
  }
  async update(data) {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error updating data: ${event.target.error}`);
    });
  }
  async delete(id) {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error deleting data: ${event.target.error}`);
    });
  }
  async clear() {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(`Error clearing data: ${event.target.error}`);
    });
  }
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
};
var IndexedDBStorageFactory = class {
  async createStorage(type, config) {
    if (type !== "indexeddb") {
      throw new Error(`invalid type ${type}`);
    }
    return new IndexedDBStorage(config);
  }
};

// src/core/database/implementation/sqlite.ts
var SqliteStorage = class {
  constructor(config) {
    this.db = null;
    this.storeName = config.databaseName || "defaultStore";
    this.initDatabase();
  }
  async initDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.storeName, 1);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: "id" });
        }
      };
    });
  }
  async store(data) {
    await this.ensureDbReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.add(data);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
  async get(id) {
    await this.ensureDbReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || undefined);
    });
  }
  async getAll() {
    await this.ensureDbReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }
  async update(data) {
    await this.ensureDbReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put(data);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
  async delete(id) {
    await this.ensureDbReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
  async clear() {
    await this.ensureDbReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
  async ensureDbReady() {
    if (!this.db) {
      await this.initDatabase();
    }
  }
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
};
var SqliteStorageFactory = class {
  async createStorage(type, config) {
    if (type !== "sqlite") {
      throw new Error(`invalid type ${type}`);
    }
    return new SqliteStorage(config);
  }
};

// src/core/database/index.ts
var vectorStoreFactories = {};
var storageFactories = {
  "indexeddb": new IndexedDBStorageFactory(),
  "sqlite": new SqliteStorageFactory()
};
var DatabaseImpl = class {
  constructor() {
  }
  async initialize(options) {
    if (!storageFactories[options.type]) {
      throw new Error(`storage type not supported ${options.type}`);
    }
    this.storage = await storageFactories[options.type].createStorage(options.type, options.config);
  }
  async store(data) {
    return this.storage.store(data);
  }
  async get(id) {
    return this.storage.get(id);
  }
  async getAll() {
    return this.storage.getAll();
  }
  async update(data) {
    return this.storage.update(data);
  }
  async delete(id) {
    return this.storage.delete(id);
  }
  async clear() {
    return this.storage.clear();
  }
  close() {
    if (this.storage) {
      this.storage.close();
    }
  }
  setVectorStore(type) {
    if (!vectorStoreFactories[type]) {
      throw new Error(`Vector store type not supported ${type}`);
    }
    this.vectorStore = vectorStoreFactories[type].createVectorStore(type);
  }
  async addVector(id, vector) {
    if (!this.vectorStore) {
      throw new Error("Vector store not initialized");
    }
    await this.vectorStore.add(id, vector);
  }
  async searchVector(vector, topK) {
    if (!this.vectorStore) {
      throw new Error("Vector store not initialized");
    }
    return await this.vectorStore.search(vector, topK);
  }
};

export { BrowserAI, DatabaseImpl, MLCEngineWrapper, TransformersEngineWrapper, mlc_models_default as mlcModels, transformers_models_default as transformersModels };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map